import type { Language, Translation } from "../types/i18n";
import { moduleStrings, type ModuleStrings } from "./modules";

/** The keys defined in this file; the area modules in ./modules add the rest. */
export type BaseTranslation = Omit<Translation, keyof ModuleStrings>;

export const baseTranslations: Record<Language, BaseTranslation> = {
  en: {
    // Navigation
    login: "Login",
    register: "Register",
    dashboard: "Dashboard",
    profile: "Profile",
    health: "Health",
    medications: "Medications",
    travel: "Travel",
    documents: "Documents",
    chat: "Chat",
    settings: "Settings",
    logout: "Logout",
    appearance: "Appearance",
    security: "Security",

    // Basic required fields
    welcomeBack: "Travel with peace of mind",
    signInToAccount: "Sign in to your account",
    createAccount: "Create your account",
    createAccountSubtitle: "Set up your HealthPass account",
    settingsGeneralDescription: "Notifications, data and account overview",
    settingsSecurityDescription:
      "Sign-in, two-factor authentication and privacy",
    settingsAppearanceDescription: "Language and colour theme",
    themeTitle: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    themeHint: "System follows your device setting and changes with it.",
    orientationTitle: "Screen orientation",
    orientationPortrait: "Fixed portrait",
    orientationSystem: "System orientation",
    orientationHint:
      "Fixed portrait keeps the app upright however you hold the phone. System orientation follows your device's rotation setting.",
    accountStatistics: "Account statistics",
    notificationsEmailHint: "Important updates and reminders by email",
    remindersTitle: "Reminders",
    fhirStandardNote: "Medical data in a standardised format.",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    firstName: "First Name",
    lastName: "Last Name",
    forgotPassword: "Forgot Password?",
    rememberMe: "Remember Me",
    signIn: "Sign In",
    signUp: "Sign Up",
    alreadyHaveAccount: "Already have an account?",
    noAccountYet: "New to HealthPass?",
    createFreeAccount: "Create free account",
    backToLogin: "Back to login",

    // Common
    loading: "Loading...",
    error: "Error",
    success: "Success",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    viewAll: "View All",
    close: "Close",
    pleaseSelect: "Please select...",

    // All other required fields with placeholder values
    resetPassword: "Reset Password",
    resetPasswordDescription: "Reset Password Description",
    sendResetLink: "Send Reset Link",
    emailSent: "Email Sent",
    checkYourEmail: "Check Your Email",
    resetEmailSent: "Reset Email Sent",
    invalidEmail: "Invalid Email",
    passwordTooShort: "Password Too Short",
    passwordsNotMatch: "Passwords Not Match",
    healthPass: "HealthPass",
    secureHealthData: "Secure Health Data",
    manageHealthSecurely: "Manage Health Securely",
    testAccount: "Test Account",
    useTestAccount: "Use Test Account",
    termsAndConditions: "Terms and Conditions",
    privacyPolicy: "Privacy Policy",
    acceptTermsRequired: "Please accept the terms to continue",
    consentTerms: "I accept the {terms} and have read the {privacy}.",
    consentTermsLink: "Terms of Service",
    consentHealth:
      "I explicitly consent to HealthPass storing the health information I enter (conditions, allergies, medications, vaccinations, documents) so it can show it to me and on my emergency card (Art. 9(2)(a) GDPR). I can withdraw this at any time.",
    consentHealthWithout:
      "Without it you can plan trips and use the checklist, but not store health information.",
    consentAi:
      "I consent to the relevant part of my health data being sent to OpenAI in the United States when I use AI health advice or the AI assistant. I can withdraw this at any time.",
    consentAiWithout:
      "Without it the AI features stay off; the destination guidance still works.",
    consentMarketing:
      "Send me tips and news about travel health by email. Unsubscribe at any time.",
    consentOptional: "Optional",
    consentGateTitle: "Your data, your choice",
    consentGateLead:
      "Please tell us what HealthPass may do with your data. Nothing new is stored and nothing is sent to the AI until you decide.",
    consentGateSave: "Save my choices",
    consentSaveFailed: "Your choices could not be saved. Please try again.",
    consentNeededHealthTitle: "Allow HealthPass to store health information?",
    consentNeededAiTitle: "Allow AI health advice?",
    consentAllow: "Allow",
    consentNotNow: "Not now",
    consentWithdrawHint: "You can withdraw this at any time in Settings.",
    settingsConsentsTitle: "Your consents",
    settingsConsentsLead:
      "Withdrawing stops new processing. What is already stored stays until you delete it.",
    consentSaved: "Saved",
    immediateStartTitle: "Before you pay",
    immediateStartAck:
      "Start my plan now. I know that if I withdraw within 14 days, I pay for the days I have already used.",
    immediateStartTrial:
      "If your plan starts with a free trial, nothing is charged before it ends, and cancelling before then costs nothing.",
    immediateStartContinue: "Continue to payment",
    immediateStartBack: "Go back",
    withdrawTitle: "Withdraw from contract",
    withdrawLead:
      "You can withdraw from this plan without giving a reason until {deadline}.",
    withdrawRefund:
      "{amount} would be refunded for the days you have not used.",
    withdrawTrial:
      "You are in your free trial, so nothing has been charged. Withdrawing ends the plan now.",
    withdrawButton: "Withdraw from contract",
    withdrawConfirm:
      "Withdraw now? Your plan ends immediately and your account continues on the free plan. Your data stays.",
    withdrawConfirmYes: "Yes, withdraw",
    withdrawDone:
      "Your withdrawal is confirmed. {refund}We have sent you a confirmation by email.",
    withdrawRefundIssued:
      "{amount} is on its way back to your original payment method. ",
    withdrawRefundPending:
      "{amount} will be refunded to your original payment method within 14 days. ",
    withdrawFailed:
      "The withdrawal could not be completed. Please try again or write to support.",
    copyright: "Copyright",

    // Landing Page
    landingHeroTitle: "Travel with peace of mind",
    landingHeroSubtitle:
      "Travel with greater peace of mind. Keep important health information ready for travel preparation, check-in, and urgent medical situations.",
    landingStartFree: "Start Free",
    landingSeeHowItWorks: "See how it works",
    landingNoCreditCard: "No credit card required",
    landingFreeTrial: "FREE lifetime subscription plan",
    landingFeaturesTitle: "Everything You Need for Better Prepared Travel",
    landingFeaturesSubtitle:
      "One secure place for key travel health information, emergency access, and digital travel readiness.",
    landingHowItWorksTitle: "Get Started in 3 Easy Steps",
    landingHowItWorksSubtitle:
      "From sign-up to travel-ready in under 10 minutes",
    landingPricingTitle: "Simple, Transparent Pricing",
    landingPricingSubtitle:
      "Start with the FREE lifetime plan and upgrade anytime.",
    landingCtaTitle: "Ready to Travel Better Prepared?",
    landingCtaSubtitle:
      "Create your free HEALTHPASS account and keep key information ready for your next trip.",
    landingCtaButton: "Create Free Account",

    // FAQ Page
    faqTitle: "Frequently Asked Questions",
    faqSubtitle: "Everything you need to know about HealthPass",
    faqQ1: "What is HealthPass?",
    faqA1:
      "HealthPass is a digital health platform that stores your emergency medical information and makes it accessible worldwide via QR code. It's designed specifically for travelers who need instant access to critical health data in emergencies.",
    faqQ2: "How does HealthPass work?",
    faqA2:
      "Simply create an account, upload your medical information (allergies, blood type, medications, emergency contacts), and generate your personal QR code. First responders and medical professionals can scan this code to access your vital health information instantly, even if you're unconscious.",
    faqQ3: "Is HealthPass GDPR compliant?",
    faqA3:
      "Yes. HealthPass is built to the GDPR: your data travels encrypted, you decide who may see what, and every time someone opens your emergency card or a link you shared, it is recorded and you can see it. Our servers are operated for us by Hetzner in the United States under the EU Standard Contractual Clauses — the Privacy Policy says exactly what that means. We never share your data with third parties without your explicit consent.",
    faqQ4: "How much does HealthPass cost?",
    faqA4:
      "Your card and emergency access are free. PLUS costs 9 and PREMIUM 15 a month, in euros or US dollars depending on where you are, and both start with a 14-day free trial. For a single trip, PLUS costs 25 and PREMIUM 39, paid once for 90 days. First responders never pay to read your emergency data.",
    faqQ5: "Can HealthPass save lives?",
    faqA5:
      "Yes. In medical emergencies abroad, language barriers and lack of medical history can be life-threatening. HealthPass provides instant access to critical information like allergies, blood type, and current medications, enabling faster, safer treatment decisions.",
    faqQ6: "How does QR code access work?",
    faqA6:
      "Your HealthPass QR code can be printed, added to your phone lock screen, or worn as a bracelet. When scanned, it opens a secure emergency view with your critical health information. No app installation required for medical staff.",
    faqQ7: "What are common travel use cases?",
    faqA7:
      "HealthPass is essential for:\n• Adventure travelers visiting remote areas\n• Seniors traveling internationally\n• People with chronic conditions or allergies\n• Families traveling with children\n• Solo travelers in non-English speaking countries\n• Anyone requiring regular medications",
    faqQ8: "How secure is my data?",
    faqA8:
      "Your data is encrypted in transit and at rest, sign-in runs through a dedicated identity server with short-lived tokens, and every read of a health record is written to an audit log you can ask us for. Our servers are operated for us by Hetzner in the United States under the EU Standard Contractual Clauses. The Security page describes all of this in full.",
    faqQ9: "Which languages are supported?",
    faqA9:
      "HealthPass is available in English, German, French, Spanish, and Portuguese. Your emergency health information can be displayed in multiple languages simultaneously, ensuring medical staff worldwide can read it.",
    faqQ10: "How do emergency services access my data?",
    faqA10:
      "Emergency services scan your QR code with any smartphone camera. They instantly see your critical information without needing to log in or download an app. Access is logged for your security, and you can revoke emergency access codes anytime.",
    faqStillHaveQuestions: "Still have questions?",
    faqContactUs: "Our support team is here to help you 24/7",
    contactSupport: "Contact Support",

    overview: "Overview",
    healthRecords: "Health Records",
    appointments: "Appointments",
    reports: "Reports",
    recentActivity: "Recent Activity",
    quickActions: "Quick Actions",
    upcomingAppointments: "Upcoming Appointments",
    healthSummary: "Health Summary",
    addNewRecord: "Add New Record",
    viewAllRecords: "View All Records",
    noDataAvailable: "No Data Available",
    upcomingTravels: "Upcoming Travels",
    noUpcomingTravels: "No upcoming travels",
    planNewTravel: "Plan New Travel",
    today: "Today",
    tomorrow: "Tomorrow",
    days: "days",
    healthRecommendations: "Health Recommendations",
    stayHydrated: "Stay hydrated throughout the day",
    drinkWaterRecommendation: "Aim for 2-3 liters of water daily",
    regularExercise: "Regular physical activity",
    exerciseRecommendation: "30 minutes moderate activity, 5 days/week",
    qualitySleep: "Quality sleep",
    sleepRecommendation: "7-9 hours of sleep per night",
    thinkAbout: "Think About",
    travelInsurance: "Travel Insurance",
    travelInsuranceDesc: "Protect your trip",
    firstAidKit: "First Aid Kit",
    firstAidKitDesc: "Be prepared",
    medicalCheckup: "Medical Check-up",
    medicalCheckupDesc: "Annual health review",
    vaccinationAppointment: "Vaccination Appointment",
    vaccinationAppointmentDesc: "Stay protected",
    tryOutPlus: "Try HealthPass PLUS",
    plusDescription: "Unlock premium features for enhanced health management",
    plusFeature1: "Advanced health analytics",
    plusFeature2: "Priority support",
    plusFeature3: "Unlimited document storage",
    plusFeature4: "AI-powered insights",
    upgradeToPlusButton: "Upgrade to PLUS",
    addHealthRecord: "Add Health Record",
    editHealthRecord: "Edit Health Record",
    deleteHealthRecord: "Delete Health Record",
    recordType: "Record Type",
    date: "Date",
    description: "Description",
    attachments: "Attachments",
    bloodPressure: "Blood Pressure",
    weight: "Weight",
    height: "Height",
    temperature: "Temperature",
    heartRate: "Heart Rate",
    currentMedications: "Current Medications",
    addMedication: "Add Medication",
    prescribedBy: "Prescribed By",
    sideEffects: "Side Effects",
    instructions: "Instructions",
    travelPlans: "Travel Plans",
    addTravelPlan: "Add Travel Plan",
    destination: "Destination",
    departureDate: "Departure Date",
    returnDate: "Return Date",
    travelType: "Travel Type",
    requiredVaccinations: "Required Vaccinations",

    // Travel List Page
    travels: "Travels",
    myTravels: "My Travels",
    planYourTrips:
      "Plan your trips and manage travel-related health requirements",
    newTravel: "New Travel",
    pastTravels: "Past Travels",
    currentTravels: "Current Travels",
    plannedTravels: "Planned Travels",
    draftTravels: "Draft Travels",
    noTravelsYet: "No travels yet",
    startPlanningTravel:
      "Start planning your first travel to get health recommendations and requirements",
    travelTitle: "Travel Title",
    travelPurpose: "Travel Purpose",
    travelDestination: "Travel Destination",
    startDate: "Start Date",
    endDate: "End Date",
    status: "Status",
    actions: "Actions",
    viewTravel: "View Travel",
    editTravel: "Edit Travel",
    deleteTravel: "Delete Travel",
    archiveTravel: "Archive Travel",
    confirmDeleteTravel: "Are you sure you want to delete this travel?",
    travelDeleted: "Travel deleted successfully",
    travelArchived: "Travel archived successfully",

    // Travel Status
    statusDraft: "Draft",
    statusPlanned: "Planned",
    statusCurrent: "Current",
    statusCompleted: "Completed",
    statusCancelled: "Cancelled",
    statusArchived: "Archived",
    statusModeManual: "Set manually",
    statusModeAutoHint: "Status is calculated automatically from travel dates.",

    // Travel Purpose
    purposeLeisure: "Leisure",
    purposeBusiness: "Business",
    purposeMedical: "Medical",
    purposeEducation: "Education",
    purposeVolunteering: "Volunteering",
    purposeOther: "Other",
    tripTitle: "Trip Title",
    tripType: "Trip Type",
    tripOperator: "Tour Operator",
    bookingReference: "Booking Reference",
    startLocation: "Start Location",
    endLocation: "End Location",
    tripTypeCruise: "Cruise",
    tripTypeBeach: "Beach",
    tripTypeMountains: "Mountains",
    tripTypeSports: "Sports",
    tripTypeEducation: "Education",
    tripTypeEvent: "Event",
    tripTypeCulture: "Culture",
    tripTypeExpedition: "Expedition",
    plannedActivities: "Planned Activities",
    activity_diving: "Diving",
    activity_climbing: "Climbing",
    activity_skiing: "Skiing",
    activity_hiking: "Hiking",
    activity_biking: "Biking",
    activity_swimming: "Swimming",
    activity_safari: "Safari",
    healthcareFacilities: "Healthcare Facilities",
    nearbyHospitals: "Nearby Hospitals",
    nearbyPharmacies: "Nearby Pharmacies",
    nearbyClinics: "Nearby Clinics",
    noFacilitiesFound: "No facilities found in this area",
    facilityDistance: "Distance",
    forumHealthSummary: "Traveler Reports",
    commonHealthIssues: "Common Health Issues",
    travelerTips: "Traveler Tips",
    unexpectedRisks: "Unexpected Risks",
    forumSummaryDisclaimer:
      "AI-generated summary. Verify with current traveler reports.",
    searchForumsFurther: "Search current reports",
    loadForumSummary: "Load Traveler Reports",
    operatorHealthInfo: "Operator Health Info",
    cruiseHealthRequirements: "Cruise Health Requirements",
    tripSegments: "Trip Segments",
    editSegment: "Edit Segment",
    deleteSegment: "Delete Segment",
    noSegmentsYet: "No segments added yet",
    addSegmentsDescription:
      "Add flight, hotel, or activity segments to organize your trip",
    addFirstSegment: "Add First Segment",
    operator: "Operator",
    transportation: "Transportation",
    segmentInformation: "Segment Information",
    segmentTitle: "Segment Title",
    segmentType: "Segment Type",
    segmentTypeTransfer: "Transfer",
    segmentTypeDestination: "Destination",
    segmentTypeAccommodation: "Accommodation",
    segmentTypeActivity: "Activity",
    locations: "Locations",
    startCountry: "Start Country",
    endCountry: "End Country",
    dateTime: "Date & Time",
    startDateTime: "Start Date & Time",
    endDateTime: "End Date & Time",
    selectOption: "Select...",
    transportFlight: "Flight",
    transportTrain: "Train",
    transportBus: "Bus",
    transportCar: "Car",
    transportShip: "Ship",
    accommodation: "Accommodation",
    accommodationHotel: "Hotel",
    accommodationHostel: "Hostel",
    accommodationApartment: "Apartment",
    accommodationCruiseCabin: "Cruise Cabin",
    healthRisk: "Health Risk",
    travelReadiness: "Travel Readiness",
    vaccinations: "Vaccinations",
    recommendations: "Recommendations",
    required: "required",
    available: "available",
    riskManagement: "Risk Management",
    vaccinationDetails: "Vaccination Details",
    readinessChecklist: "Readiness Checklist",
    personalRecommendations: "Personal Recommendations",
    bookAppointment: "Book Appointment",

    // Travel Details
    travelCompanions: "Travel Companions",
    companions: "Companions",
    tourOperator: "Tour Operator",
    travelNotes: "Travel Notes",
    notes: "Notes",
    segments: "Segments",
    addSegment: "Add Segment",
    travelSegment: "Travel Segment",
    transportationType: "Transportation Type",
    accommodationType: "Accommodation Type",

    // Filter & Sort
    filterByStatus: "Filter by Status",
    filterByPurpose: "Filter by Purpose",
    sortByDate: "Sort by Date",
    sortByDestination: "Sort by Destination",
    searchTravels: "Search travels...",
    showAll: "Show All",
    showActive: "Show Active",
    showCompleted: "Show Completed",

    // Time Indicators
    daysUntilDeparture: "Days until departure",
    daysRemaining: "Days remaining",
    inProgress: "In Progress",
    departed: "Departed",
    returned: "Returned",

    // Travel Form
    travelDates: "Travel Dates",
    travelDetails: "Travel Details",
    healthInformationNotice: "Health Information Notice",
    healthRequirementsDescription:
      "Travel health requirements will be displayed here based on your destination. This information is sourced from CDC, WHO, and local health authorities.",
    vaccinationInfoLoading:
      "Vaccination requirements will be loaded based on your destination and travel dates.",
    healthWarningsLoading:
      "Health warnings and advisories will be displayed here.",
    apiIntegrationPending: "API integration with CDC/WHO pending",
    checkBackLater:
      "Information will be available once destination health data is integrated",
    healthWarnings: "Health Warnings",
    lastUpdated: "Last updated",
    source: "Source",
    noVaccinationsRequired: "No specific vaccinations required",
    noHealthWarnings: "No specific health warnings",
    risk: "risk",

    // Health Recommendations
    departure: "Departure",
    mandatory: "Mandatory",
    recommended: "Recommended",
    suggested: "Suggested",
    recommendedVaccinations: "Recommended Vaccinations",
    activeDiseaseOutbreaks: "Active Disease Outbreaks",
    dataSources: "Data Sources",
    from: "From",
    to: "To",
    isoCode: "ISO Code",
    riskLevel: "Risk Level",
    healthDataLoadError: "Could not load health data.",
    dataFrom: "Data from",
    overallRisk: "Overall Risk Assessment",
    quickInstallButton: "Install app",
    quickInstallInstalled: "Already installed",
    updateAvailable: "Load new app version.",
    quickInstallAndroidApkStep1: "Scan the code, then tap Download.",
    quickInstallAndroidApkStep2:
      "Open the downloaded file and allow installs from Chrome.",
    quickInstallAndroidApkStep3: "Open HEALTHPASS from your home screen.",
    quickInstallTitle: "Install the app in three steps",
    quickInstallSubtitle:
      "Point your phone camera at the code. HEALTHPASS installs like a normal app, runs full screen and works offline.",
    quickInstallScanHint: "Scan to open on your phone",
    quickInstallAndroidTitle: "Android · Chrome",
    quickInstallAndroidStep1:
      "Scan the code, or open travelhealthpass.app in Chrome.",
    quickInstallAndroidStep2: "Tap the three-dot menu, then “Install app”.",
    quickInstallAndroidStep3: "Open HEALTHPASS from your home screen.",
    quickInstallIosTitle: "iPhone & iPad · Safari",
    quickInstallIosStep1:
      "Scan the code, or open travelhealthpass.app in Safari.",
    quickInstallIosStep2: "Tap Share, then “Add to Home Screen”.",
    quickInstallIosStep3: "Open HEALTHPASS from your home screen.",
    quickInstallIosNote:
      "Safari only — Chrome and Firefox on iPhone cannot install web apps.",
    appInstallTitle: "Get HealthPass on your phone",
    appInstallSubtitle:
      "Install HealthPass on Android or iPhone and carry your health record, emergency card and travel requirements with you offline.",
    appInstallAndroidTitle: "Android",
    appInstallAndroidSubtitle: "Install the APK directly",
    appInstallScanToDownload:
      "Scan this code with your phone's camera to download the APK, or use the button below.",
    appInstallDownloadApk: "Download HealthPass App",
    appInstallApkUnavailable: "The Android APK is not available yet.",
    appInstallApkUnavailableHint:
      'HealthPass runs as an installable web app in the meantime: open this site in Chrome on your Android phone, tap the menu and choose "Install app".',
    appInstallAndroidStep1:
      'Scan the QR code or tap "Download APK" on your Android phone.',
    appInstallAndroidStep2:
      'When Android warns that the file can harm your device, choose "Download anyway" - this is the standard warning for any app installed outside the Play Store.',
    appInstallAndroidStep3:
      'Open the downloaded file. Android will ask whether to allow installs from this source; tap "Settings" and turn the permission on.',
    appInstallAndroidStep4: 'Go back and tap "Install".',
    appInstallAndroidStep5:
      "Open HealthPass and sign in with your existing account.",
    appInstallAndroidPwaTitle: "Install from Chrome instead",
    appInstallAndroidPwaStep1:
      "Open this site in Chrome on your Android phone - scan the code above or type the address.",
    appInstallAndroidPwaStep2:
      "Tap the three-dot menu in the top right corner.",
    appInstallAndroidPwaStep3: 'Tap "Add to Home screen" or "Install app".',
    appInstallAndroidPwaStep4: 'Confirm with "Install".',
    appInstallAndroidPwaStep5:
      "Launch HealthPass from your home screen and sign in. It runs full screen and works offline.",
    appInstallIosTitle: "iPhone and iPad",
    appInstallIosSubtitle: "Add to the Home Screen from Safari",
    appInstallScanToOpen:
      "Scan this code with your iPhone camera to open HealthPass in Safari.",
    appInstallIosStep1:
      "Open this site in Safari. Chrome and Firefox on iOS cannot install web apps.",
    appInstallIosStep2:
      "Tap the Share button - the square with an arrow pointing up, at the bottom of the screen.",
    appInstallIosStep3: 'Scroll down and tap "Add to Home Screen".',
    appInstallIosStep4: 'Tap "Add" in the top right corner.',
    appInstallIosStep5:
      "Launch HealthPass from your Home Screen and sign in. It now runs full screen and works offline.",
    appInstallIosSafariNote:
      "Apple only allows Safari to install web apps, so this must be done in Safari even if another browser is your default.",
    appInstallAndroidGuideLink: "Full Android install guide",
    androidInstallPageTitle: "Install HealthPass on Android",
    androidInstallPageSubtitle:
      "Get HealthPass onto your Android phone in a few steps. It runs full screen and works offline.",
    androidInstallScanPageQr:
      "Reading this on a computer? Scan this code to open this page on the Android phone you want HealthPass on.",
    androidInstallSecurityNote:
      "Android asks for permission before installing an app from outside the Play Store. That prompt is expected here. The permission applies only to the browser you grant it to, and you can withdraw it once the install is finished.",
    androidInstallIosHint:
      "Using an iPhone? Follow the iOS instructions instead.",
    androidInstallBackHome: "Back to homepage",
    aiAnalysisError: "AI analysis not available.",
    personalizedHealthRecommendations: "Personalized Health Recommendations",
    recommendationsIntro:
      "Based on your destination, trip duration, and health profile – evidence-based, multi-source recommendations.",
    aiTripAnalysis: "AI Trip Health Analysis",
    analyzing: "Analyzing…",
    runAiAnalysis: "Run AI Analysis",
    aiAnalysisHint:
      "Climate impact and personal health profile analysis powered by AI.",
    climateRiskSummary: "Climate Risk Summary",
    healthProfileRisks: "Your Health Profile Risks",
    urgentActions: "Urgent Actions",
    noRecommendationsAvailable:
      "No specific recommendations found for this destination.",
    travelStatus: "Travel Status",
    readiness: "Readiness",
    overallReadiness: "Overall Readiness",
    urgent: "Urgent",
    addCustomTask: "Add Custom Task",
    taskDescription: "Task description…",
    vaccinationRequired: "Required",
    vaccinationRecommended: "Recommended",
    atLeast: "At least",
    daysBeforeDeparture: "days before departure",
    validFor: "Valid for",
    loadingHealthData: "Loading health data…",
    noRequiredVaccinations: "No required vaccinations for this destination.",
    noRecommendedVaccinations: "No additional vaccinations recommended.",
    vaccinationTimeline: "Vaccination Timeline",
    before: "before",
    riskLevelDescLow: "Low travel health risk for this destination",
    riskLevelDescMedium: "Moderate travel health risk - take precautions",
    riskLevelDescHigh:
      "High travel health risk - seek medical advice before departure",
    riskLevelDescCritical:
      "Critical travel health risk - travel is not advised without specialist advice",
    activeOutbreaks: "Active Disease Outbreaks",
    activeOutbreaksDetected: "active disease outbreak(s) detected",
    cases: "Cases",
    lastReported: "Last reported",
    travelWarnings: "Travel Warnings",
    transportModeRisks: "Transport Mode Risks",
    activitySpecificRisks: "Activity-Specific Health Risks",
    riskCategoryVaccination: "Vaccinations",
    riskCategoryMedication: "Medication",
    riskCategoryPrecaution: "Precautions",
    riskCategoryAdvisory: "Advisories",
    riskCategoryWarning: "Warnings",
    trend: "Trend",
    increasing: "Increasing",
    decreasing: "Decreasing",
    stable: "Stable",
    casesReported: "Cases Reported",
    lastReport: "Last Report",
    mayAffectTravel: "May affect travel",
    validity: "Validity",
    years: "years",
    minDaysBeforeDeparture: "Min. days before departure",
    boosterRequired: "Booster required",
    contraindications: "Contraindications",
    relatedDiseases: "Related diseases",
    ageRestrictions: "Age restrictions",
    updated: "Updated",
    noHealthDataAvailable: "No health data available",
    failedToLoadHealthRecommendations: "Failed to load health recommendations",
    backToDashboard: "Back to Dashboard",

    uploadDocument: "Upload Document",
    documentType: "Document Type",
    uploadDate: "Upload Date",
    fileSize: "File Size",
    downloadDocument: "Download Document",
    documentName: "Document Name",

    // Chat
    chatWithTina: "Chat with Tina",
    tinaHealthAssistant: "Tina - Your Health Assistant",
    askQuestion: "Ask Question",
    askMeAnything: "Ask me anything about your health",
    typeMessage: "Type Message",
    typeYourMessage: "Type your message...",
    sendMessage: "Send Message",
    chatHistory: "Chat History",
    newConversation: "New Conversation",
    conversations: "Conversations",
    includeHealthContext: "Include my health data",
    healthContextEnabled:
      "Health context enabled - Tina can see your medications, allergies, and vital signs",
    healthContextDisabled:
      "Health context disabled - Tina will respond without your health data",
    aiThinking: "Tina is thinking...",
    noConversationsYet: "No conversations yet",
    startNewConversation: "Start a new conversation with Tina",
    deleteConversation: "Delete conversation",
    conversationDeleted: "Conversation deleted",
    aiUnavailable: "AI Assistant unavailable",
    aiUnavailableMessage:
      "The AI assistant is currently not available. Please try again later.",
    messagePlaceholder: "Ask about medications, symptoms, travel health...",
    exampleQuestions: "Example questions:",
    example1: "What are my current medications?",
    example2: "What vaccinations do I need for travel?",
    example3: "Explain my latest vital signs",

    // Profile
    personalInformation: "Personal Information",
    contactInformation: "Contact Information",
    emergencyContact: "Emergency Contact",
    medicalInformation: "Medical Information",
    updateProfile: "Update Profile",
    changePassword: "Change Password",
    phone: "Phone",
    dateOfBirth: "Date of Birth",
    country: "Country",
    nationality: "Nationality",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    languageSettings: "Language Settings",
    notificationSettings: "Notification Settings",
    privacySettings: "Privacy Settings",
    accountSettings: "Account Settings",
    dataExport: "Data Export",
    deleteAccount: "Delete Account",

    // Account Deletion
    dangerZone: "Danger Zone",
    deleteAccountWarning:
      "Once you delete your account, there is no going back. All your health data, documents, and personal information will be permanently deleted from our systems.",
    deleteAccountConfirmationPrompt:
      "This action cannot be undone. All your data will be permanently deleted.",
    typeToConfirm: "Type to confirm",
    enterYourPassword: "Enter your password",
    deleteAccountWarningDetails:
      "All your health records, documents, travel plans, and conversations will be permanently deleted. A paid subscription is cancelled first and not charged again. Audit logs will be retained for compliance purposes.",
    confirmDeletion: "Permanently Delete Account",
    deleting: "Deleting...",
    accountDeletedSuccessfully:
      "Your account has been successfully deleted. You will be logged out shortly.",
    deleteAccountError:
      "Failed to delete account. Please try again or contact support.",
    deleteAccountConfirmationError:
      'Please type "DELETE MY ACCOUNT" exactly to confirm',
    invalidPassword: "Invalid password. Please try again.",
    passwordRequired: "Password is required",
    warning: "Warning",

    // Emergency Profile
    emergencyProfile: "Emergency Profile",
    myEmergencyProfile: "My Emergency Profile",
    emergencyData: "Emergency Data",
    completenessScore: "Completeness Score",
    dataQuality: "Data Quality",
    dataQualityGood: "Good - All critical data complete",
    dataQualityFair: "Fair - Some data missing",
    dataQualityPoor: "Poor - Critical data missing",
    criticalData: "Critical Data",
    priority1Data: "Priority 1: Critical",
    priority2Data: "Priority 2: Important",
    priority3Data: "Priority 3: Helpful",
    missingCriticalData: "Missing Critical Data",
    quickAdd: "Quick Add",
    editEmergencyData: "Edit Emergency Data",
    shareEmergencyAccess: "Share Emergency Access",
    accessDuration: "Access Duration",
    duration15min: "15 Minutes",
    duration1hour: "1 Hour",
    duration6hours: "6 Hours",
    duration24hours: "24 Hours",
    durationCustom: "Custom Duration",
    dataScope: "Data Scope",
    selectDataToShare: "Select Data to Share",
    emergencyQRCode: "Emergency QR Code",
    refreshQRCode: "Refresh QR Code",
    downloadQRCode: "Download QR Code",
    printQRCode: "Print QR Code",
    copyAccessLink: "Copy Access Link",
    qrCodeExpiry: "QR Code Expiry",
    addEmergencyContact: "Add Emergency Contact",
    primaryContact: "Primary Contact",
    secondaryContact: "Secondary Contact",
    tertiaryContact: "Tertiary Contact",
    contactName: "Contact Name",
    relationship: "Relationship",
    phoneNumber: "Phone Number",
    notifyOnAccess: "Notify on Emergency Access",
    availabilityNotes: "Availability Notes",
    reorderContacts: "Reorder Contacts",
    devices: "Medical Devices",
    allergySubstance: "Allergy Substance",
    allergySeverity: "Severity",
    allergyReaction: "Reaction",
    conditionName: "Condition Name",
    conditionStatus: "Status",
    deviceType: "Device Type",
    deviceUDI: "Device UDI",
    verifiedDate: "Verified Date",
    outdatedWarning: "This data is outdated and needs verification",
    incompleteWarning: "Your emergency profile is incomplete",
    updateRequired: "Update Required",
    dataVerifiedRecently: "Data verified recently",

    add: "Add",
    update: "Update",
    remove: "Remove",
    search: "Search",
    filter: "Filter",
    sort: "Sort",
    export: "Export",
    import: "Import",
    refresh: "Refresh",
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    completed: "Completed",
    cancelled: "Cancelled",
    savedSuccessfully: "Saved Successfully",
    deletedSuccessfully: "Deleted Successfully",
    errorOccurred: "Error Occurred",
    confirmDelete: "Confirm Delete",
    areYouSure: "Are You Sure",
    cannotBeUndone: "Cannot Be Undone",
    healthStatusOptimal: "Health Status Optimal",
    allDataCurrent: "All Data Current",
    pulse: "Pulse",
    normal: "Normal",

    // Vital Signs
    vitalSigns: "Vital Signs",
    systolic: "Systolic",
    diastolic: "Diastolic",
    restingHeartRate: "Resting Heart Rate",
    bpm: "bpm",
    mmHg: "mmHg",
    lowBloodPressure: "Low Blood Pressure",
    elevated: "Elevated",
    hypertensionGrade1: "Hypertension Grade 1",
    hypertensionGrade2: "Hypertension Grade 2",
    hypertensionGrade3: "Hypertension Grade 3",
    bradycardia: "Bradycardia",
    tachycardia: "Tachycardia",
    systolicBpError: "Systolic blood pressure must be between 70 and 250 mmHg",
    diastolicBpError:
      "Diastolic blood pressure must be between 40 and 150 mmHg",
    heartRateError: "Resting heart rate must be between 30 and 220 bpm",
    systolicNormal: "Systolic Normal: 120",
    systolicHigh: "Systolic High: 140",
    diastolicNormal: "Diastolic Normal: 80",
    systolicBloodPressure: "Systolic Blood Pressure",
    diastolicBloodPressure: "Diastolic Blood Pressure",
    noVitalSignsData: "No vital signs data available",

    // Lab Results
    labResults: "Lab Results",
    currentValues: "Current Values",
    allTests: "All Tests",
    laboratoryTest: "Laboratory Test",
    evaluation: "Evaluation",
    value: "Value",
    unit: "Unit",
    performingDoctor: "Performing Doctor/Lab (optional)",
    pdfReport: "PDF Report",
    noLabResultsDocumented: "No lab results documented",
    addNewLabResult: "Add New Lab Result",
    showChart: "Show Chart",
    hideChart: "Hide Chart",
    noWeightDataAvailable: "No weight data available",
    firstEntriesWillBeShown: "First entries will be shown here",
    weightKg: "Weight (kg)",
    current: "Current",
    dataPoints: "Data Points",
    fhirCompatibleData: "FHIR Compatible Data",
    recorded: "Recorded",
    bodyFat: "Body Fat",
    bodyFatPercent: "Body Fat (%)",
    raised: "Raised",
    decreased: "Decreased",
    critical: "Critical",
    lastMeasurement: "Last Measurement",
    vitalSignsHistory: "Vital Signs History (365 Days)",

    // ✅ LOINC-Compliant Wellbeing (72133-2, 72134-0)
    wellbeing: "Wellbeing",
    physicalWellbeing: "Physical Wellbeing",
    physicalWellness: "Physical Wellbeing", // ✅ LOINC-compliant
    emotionalWellbeing: "Emotional Wellbeing",
    emotionalWellness: "Emotional Wellbeing", // ✅ LOINC-compliant
    howDoYouFeelPhysically: "How do you feel physically?",
    howDoYouFeelEmotionally: "How do you feel emotionally?",
    currentStatus: "Currently",
    lastUpdate: "Last Update",
    trendChart: "Trend Chart",
    trendHistory: "Trend of the last 365 days",
    noTrendData: "No trend data available",
    noTrendDataDesc: "Track your wellbeing regularly to see your progress",
    noWellbeingData: "No wellbeing data available",
    saveFirstWellbeingEntry:
      "Save your first wellbeing assessment to see trends",
    wellnessDataPoints: "Data Points",
    wellnessScore: "Wellbeing Score (1-5)", // ✅ LOINC-compliant
    average: "Average",
    good: "Good",
    low: "Low",
    // Wellness levels (1-5 scale)
    veryPoor: "Very Poor",
    poor: "Poor",
    fair: "Fair",
    wellGood: "Good",
    excellent: "Excellent",
    // Physical wellness labels
    physicalVeryPoor: "Very Poor",
    physicalPoor: "Poor",
    physicalFair: "Fair",
    physicalGood: "Good",
    physicalExcellent: "Excellent",
    // Emotional wellness labels
    emotionalVeryPoor: "Very Depressed",
    emotionalPoor: "Sad",
    emotionalFair: "Balanced",
    emotionalGood: "Happy",
    emotionalExcellent: "Very Happy",

    addNewDocument: "Add New Document",
    addDocumentDesc: "Add Document Description",
    addMedicationDesc: "Add Medication Description",
    planTravel: "Plan Travel",
    planTravelDesc: "Plan Travel Description",
    addHealthDataDesc: "Add Health Data Description",
    upcomingReminders: "Upcoming Reminders",
    highPriority: "High Priority",
    mediumPriority: "Medium Priority",
    lowPriority: "Low Priority",
    vaccination: "Vaccination",
    daysAgo: "Days Ago",
    weeksAgo: "Weeks Ago",
    inDays: "In Days",
    immunizations: "Immunizations",
    manageImmunizations: "Manage Immunizations",
    newImmunization: "New Immunization",
    editImmunization: "Edit Immunization",
    deleteImmunization: "Delete Immunization",
    vaccineName: "Vaccine Name",
    vaccineCode: "Vaccine Code",
    manufacturer: "Manufacturer",
    lotNumber: "Lot Number",
    doseNumber: "Dose Number",
    totalDoses: "Total Doses",
    administeredDate: "Administered Date",
    expirationDate: "Expiration Date",
    nextDueDate: "Next Due Date",
    location: "Location",
    administrator: "Administrator",
    requiredForCountries: "Required For Countries",
    uploadCertificate: "Upload Certificate",
    downloadCertificate: "Download Certificate",
    certificateUploaded: "Certificate Uploaded",
    noCertificateFound: "No Certificate Found",
    certificateUploadFailed: "Certificate Upload Failed",
    fhirData: "FHIR Data",
    internationalCertificate: "International Certificate",
    internationalVaccinationCertificate:
      "International Vaccination Certificate",
    passportNumber: "Passport Number",
    healthPassDocumentId: "HealthPass Document ID",
    certificateNumber: "Certificate Number",
    issuerId: "Issuer ID",
    publicKeyFingerprint: "Public Key Fingerprint",
    signatureTimestamp: "Signature Timestamp",
    issuedBy: "Issued By",
    issuedDate: "Issued Date",
    validFrom: "Valid From",
    patientInformation: "Patient Information",
    certificateInformation: "Certificate Information",
    digitalSignature: "Digital Signature",
    qrCodeValidation: "QR Code Validation",
    printCertificate: "Print Certificate",
    copyCertificateData: "Copy Certificate Data",
    deleteConfirmation: "Delete Confirmation",
    noImmunizationsFound: "No Immunizations Found",
    addFirstImmunization: "Add First Immunization",
    certificateValidUntil: "Certificate Valid Until",
    dosesCompleted: "Doses Completed",
    of: "of",
    loginSuccessful: "Login Successful",
    loginFailed: "Login Failed",
    loginInvalidOrPaused:
      "Wrong email or password. After 10 failed attempts, sign-in for this account pauses for up to 15 minutes.",
    registrationSuccessful: "Registration Successful",
    registrationFailed: "Registration Failed",
    logoutSuccessful: "Logout Successful",
    sessionExpired: "Session Expired",
    networkError: "Network Error",
    accessDenied: "Access Denied",
    validationError: "Validation Error",
    tooManyRequests: "Too Many Requests",
    serverError: "Server Error",
    labResultsSaved: "Lab results saved successfully",

    // Medication System
    medicationsPage: "Medications",
    medicationItem: "Medication",
    medicationPlan: "Medication Plan",
    addNewMedication: "Add New Medication",
    editExistingMedication: "Edit Existing Medication",
    deleteMedicationItem: "Delete Medication Item",
    medicationName: "Medication Name",
    genericName: "Generic Name",
    medicationDosage: "Medication Dosage",
    dosageForm: "Dosage Form",
    medicationStrength: "Medication Strength",
    medicationFrequency: "Medication Frequency",
    timingInstructions: "Timing Instructions",
    mealTiming: "Meal Timing",
    medicationStartDate: "Medication Start Date",
    medicationEndDate: "Medication End Date",
    medicationPrescribedBy: "Medication Prescribed By",
    medicationPharmacy: "Medication Pharmacy",
    medicationInstructions: "Medication Instructions",
    medicationSideEffects: "Medication Side Effects",
    medicationInteractions: "Medication Interactions",
    eanCode: "EAN Code",
    pznCode: "PZN Code",
    atcCode: "ATC Code",
    tablet: "Tablet",
    capsule: "Capsule",
    syrup: "Syrup",
    injection: "Injection",
    cream: "Cream",
    ointment: "Ointment",
    drops: "Drops",
    spray: "Spray",
    patch: "Patch",
    suppository: "Suppository",
    powder: "Powder",
    other: "Other",
    inhaler: "Inhaler",
    gel: "Gel",
    lotion: "Lotion",
    solution: "Solution",
    beforeMeal: "Before Meal",
    afterMeal: "After Meal",
    withMeal: "With Meal",
    independent: "Independent",
    onEmptyStomach: "On Empty Stomach",
    medicationRegular: "Regular Medication",
    medicationTravel: "Travel Medication",
    medicationEmergency: "Emergency Medication",
    medicationProphylaxis: "Prophylaxis Medication",
    regular: "Regular",
    emergency: "Emergency",
    travelMedication: "Travel Medication",
    prophylaxis: "Prophylaxis",
    cruise: "Cruise",
    trekking: "Trekking",
    diving: "Diving",
    tropical: "Tropical",
    altitude: "Altitude",
    business: "Business",
    adventure: "Adventure",
    urban: "Urban",
    recordIntake: "Record Intake",
    intakeHistory: "Intake History",
    adherence: "Adherence",
    doseTaken: "Dose Taken",
    intakeNotes: "Intake Notes",
    timeline: "Timeline",
    lastTaken: "Last Taken",
    nextDue: "Next Due",
    internationalMedicationPlan: "International Medication Plan",
    generatePlan: "Generate Plan",
    uploadGermanPlan: "Upload German Plan",
    downloadPlan: "Download Plan",
    printPlan: "Print Plan",
    qrCodePlan: "QR Code Plan",
    packageImage: "Package Image",
    medicationImage: "Medication/Pill Image",
    uploadPackageImage: "Upload Package Image",
    uploadMedicationImage: "Upload Medication Image",
    takePhoto: "Take Photo",
    scanBarcode: "Scan Barcode",
    tradeName: "Trade Name",
    activeIngredient: "Active Ingredient",
    manufacturerName: "Manufacturer",
    internationalCode: "International Code",
    codeSystem: "Code System",
    removeImage: "Remove Image",
    imageUploadSuccess: "Image uploaded successfully",
    imageUploadFailed: "Image upload failed",
    imageDeleteSuccess: "Image deleted successfully",
    imageDeleteFailed: "Image deletion failed",
    optional: "Optional",
    imagesSection: "Images",
    packagePhoto: "Package Photo",
    pillPhoto: "Pill Photo",
    gtinCode: "GTIN Code",
    ndcCode: "NDC Code (US)",
    handelsname: "Trade Name",
    wirkstoff: "Active Ingredient",
    hersteller: "Manufacturer",
    pharmaCode: "Pharmaceutical Code",
    basicInformation: "Basic Information",
    fhirExtendedFields: "Extended FHIR R5 Fields",
    medicationImages: "Medication Images",
    medicationInformation: "Medication Information",
    dosageInformation: "Dosage",
    prescription: "Prescription",
    note: "Note",
    selfSignedInfo:
      'When you enter the medication yourself, it will automatically be marked as "Self Signed" and linked to your Traveler account.',
    medicationImagesDescription:
      "Upload images of the medication package and pills (optional)",
    medicationImagesHint:
      "High-quality images help with identification and can be useful for travel documentation.",
    saveChanges: "Save Changes",
    saveMedication: "Save Medication",
    choosePackageImage: "Choose Package Image",
    chooseMedicationImage: "Choose Medication Image",
    category: "Category",
    dosage: "Dosage",
    pharmacy: "Pharmacy",
    editMedication: "Edit Medication",
    updateMedication: "Update Medication",
    uploadingImages: "Uploading Images...",
    saving: "Saving...",
    travelRecommendations: "Travel Recommendations",
    supplementaryMedications: "Supplementary Medications",
    interactionWarnings: "Interaction Warnings",
    implementRecommendation: "Implement Recommendation",
    medicationAdded: "Medication Added",
    medicationUpdated: "Medication Updated",
    medicationDeleted: "Medication Deleted",
    intakeRecorded: "Intake Recorded",
    planGenerated: "Plan Generated",
    imageUploaded: "Image Uploaded",

    // Avatar Upload
    profilePicture: "Profile Picture",
    managePersonalDataSettings: "Manage your personal data and settings",
    uploadPhoto: "Upload Photo",
    deletePhoto: "Delete Photo",
    invalidFileType: "Invalid file type. Only JPEG, PNG, WebP, GIF allowed",
    fileTooLarge: "File too large. Maximum size is 5MB",
    maxFileSize: "Max file size: 5MB. Supported formats: JPEG, PNG, WebP, GIF",
    avatarUploadedSuccessfully: "Avatar uploaded successfully",
    avatarUploadFailed: "Failed to upload avatar",
    confirmDeleteAvatar: "Are you sure you want to delete your avatar?",
    avatarDeletedSuccessfully: "Avatar deleted successfully",
    avatarDeleteFailed: "Failed to delete avatar",
    cropImage: "Crop Image",
    rotate: "Rotate",

    // Profile Page - Form Fields
    notSpecified: "Not specified",
    day: "Day",
    month: "Month",
    year: "Year",
    yearsOld: "years old",

    // Date/Month names
    january: "January",
    february: "February",
    march: "March",
    april: "April",
    may: "May",
    june: "June",
    july: "July",
    august: "August",
    september: "September",
    october: "October",
    november: "November",
    december: "December",

    // Address Fields
    address: "Address",
    street: "Street",
    houseNumber: "House Number",
    addressSupplement: "Address Supplement",
    postalCode: "Postal Code",
    city: "City",
    state: "State/Region",
    streetName: "Street name",
    houseNo: "No.",
    apartmentFloorEtc: "Apartment, Floor, etc. (optional)",
    postalCodePlaceholder: "Postal code",
    cityPlaceholder: "City",

    // Nationalities
    nationality_german: "German",
    nationality_austrian: "Austrian",
    nationality_swiss: "Swiss",
    nationality_french: "French",
    nationality_italian: "Italian",
    nationality_spanish: "Spanish",
    nationality_british: "British",
    nationality_american: "American",
    nationality_canadian: "Canadian",
    nationality_dutch: "Dutch",
    nationality_belgian: "Belgian",
    nationality_polish: "Polish",
    nationality_turkish: "Turkish",
    nationality_russian: "Russian",
    nationality_other: "Other",

    // Profile Update Messages
    profileUpdatedSuccessfully: "Profile updated successfully!",
    profileUpdateMaybeSuccessful:
      "Profile may have been updated - please check the changes.",
    profileUpdatedButDataNotLoaded:
      "Profile updated, but data was not loaded correctly. Please reload the page.",
    correctFollowingInputs: "Please correct the following inputs:",
    inputError: "Input error",
    invalidInputs: "Invalid inputs. Please check all fields and try again.",
    unauthorizedPleaseLogin: "Unauthorized. Please log in again.",
    noPermission: "No permission for this action.",
    userNotFoundPleaseLogin: "User not found. Please log in again.",
    serverErrorTryLater:
      "Server error. Please try again later or contact support.",
    unknownError: "Unknown error",
    noServerConnection:
      "No connection to server possible. Please check your internet connection and try again.",
    unexpectedError:
      "An unexpected error occurred. Please try again or contact support.",

    apply: "Apply",
    capture: "Capture",
    retake: "Retake",
    usePhoto: "Use Photo",
    switchCamera: "Switch Camera",
    cameraAccessDenied: "Camera access denied",
    photoCaptureFailed: "Could not use the photo. Please try again.",
    checkCameraPermissions:
      "Please check camera permissions in your browser settings",

    // Version History
    version: "Version",
    versionHistory: "Version History",
    versionHistoryDescription: "All versions and changes of HealthPass",
    currentVersion: "Current Version",
    newFeatures: "New Features",
    changes: "Changes",
    bugFixes: "Bug Fixes",
    versionHistoryNote:
      "This version history is automatically generated from Git commits and release notes.",
    back: "Back",
    subscription: "Subscription",

    // Medical Incidents & Intolerances
    medicalIncidents: "Medical Incidents",
    addNewIncident: "Add New Incident",
    icdCodeOptional: "ICD Code (optional)",
    diagnosisDesignation: "Diagnosis/Designation",
    diagnosisPlaceholder: "e.g. Heart attack, fracture, surgery...",
    incidentDetailsPlaceholder: "Further details about the incident...",
    addIncident: "Add Incident",
    noMedicalIncidentsDocumented: "No medical incidents documented",
    intolerances: "Intolerances",
    foodIntolerances: "Food Intolerances",
    foodIntolerancesPlaceholder: "e.g. Lactose, Gluten",
    specificDiseases: "Specific Diseases",
    coagulationDisorder: "Coagulation Disorder",
    heartAttack: "Heart Attack",
    heartAttackDatePlaceholder: "Date of heart attack",
    stroke: "Stroke",
    strokeDatePlaceholder: "Date of stroke",

    // Extended Health Components
    allergiesAndIntolerances: "Allergies & Intolerances",
    noAllergiesDocumented: "No allergies documented",
    medicalConditions: "Medical Conditions",
    noMedicalConditionsDocumented: "No medical conditions documented",
    medicalProcedures: "Medical Procedures",
    noMedicalProceduresDocumented: "No medical procedures documented",
    treatmentHistory: "Treatment History",
    noTreatmentHistoryDocumented: "No treatment history documented",

    // Vital Signs
    allFieldsRequired: "All fields are required",
    systolicMustBeHigher: "Systolic must be higher than diastolic",
    recordFirstMeasurement: "Record first measurement",
    loadingHistory: "Loading history...",
    noHistoryData: "No history data available",
    firstMeasurementsWillAppear: "First measurements will appear here",

    // Unit Inputs
    enterValue: "Enter value",

    // Gender
    gender: "Gender",
    male: "Male",
    female: "Female",
    preferNotToSay: "Prefer not to say",

    // Emergency System
    emergencyQRDescription: "Share your medical data with emergency responders",
    noActiveQRCode: "No Active QR Code",
    generateQRCodeDescription:
      "Generate a QR code to share your emergency data",
    qrCodeDuration: "QR Code Duration",
    generateQRCode: "Generate QR Code",
    regenerate: "Regenerate",
    generating: "Generating...",
    qrCodeGenerated: "QR code generated successfully",
    qrCodeGenerationFailed: "Failed to generate QR code",
    timeRemaining: "Time Remaining",
    accessCount: "Access Count",
    accessUrl: "Access URL",
    urlCopied: "URL copied to clipboard",
    copyFailed: "Failed to copy URL",
    copied: "Copied",
    copy: "Copy",
    downloadQR: "Download QR Code",
    qrCodeDownloaded: "QR code downloaded",
    shareEmergencyData: "Share Emergency Data",
    shareDialogDescription: "Choose how you want to share your emergency data",
    expired: "Expired",
    pleaseRegenerate: "Please regenerate",
    howItWorks: "How It Works",
    emergencyQRHowItWorksText:
      "Emergency responders scan this QR code with any smartphone",
    share: "Share",
    emergencyDataAccess: "Emergency Data Access",
    forEmergencyRespondersOnly: "For Emergency Responders Only",
    scanQROrEnterToken: "Scan QR Code or Enter Access Token",
    qrScannerPlaceholder: "QR Scanner",
    qrScannerInstructions: "Point your camera at the QR code",
    orEnterTokenManually: "Or enter the access token manually",
    enterAccessToken: "Enter access token",
    continue: "Continue",
    responderInformation: "Responder Information",
    responderInformationDescription: "Please provide your information",
    fullName: "Full Name",
    enterYourFullName: "Enter your full name",
    organization: "Organization",
    hospitalOrganizationName: "Hospital or organization name",
    professionalId: "Professional ID",
    licenseBadgeNumber: "License or badge number",
    yourEmail: "Your email address",
    yourPhone: "Your phone number",
    reasonForAccess: "Reason for Access",
    briefDescriptionEmergency: "Brief description",
    emergencyAccessConsent: "Emergency Access Consent",
    emergencyAccessConsentText:
      "I confirm I am accessing this data in an emergency",
    pleaseAcceptConsent: "Please accept the consent",
    accessEmergencyData: "Access Emergency Data",
    accessing: "Accessing...",
    emergencyDataAccessGranted: "Emergency data access granted",
    pleaseEnterToken: "Please enter an access token",
    pleaseProvideResponderInfo: "Please provide your information",
    accessTokenInvalid: "Invalid or expired access token",
    criticalAllergies: "CRITICAL ALLERGIES",
    emergencyContacts: "Emergency Contacts",
    noEmergencyContactsAvailable: "No emergency contacts available",
    bloodType: "Blood Type",
    age: "Age",
    name: "Name",
    unknown: "Unknown",
    disclaimer: "Disclaimer",
    emergencyDataDisclaimerText: "This information is provided by the patient",

    // PWA & Offline
    installApp: "Install HealthPass",
    installAppDescription:
      "Install HealthPass for quick access and offline use",
    install: "Install",
    later: "Later",
    connectionRestored: "Connection restored",
    connectionRestoredSyncing: "Connection restored - Data is syncing",
    offlineMode: "Offline Mode",
    offlineModeDescription: "Offline mode - Already loaded data is available",
    slowConnection: "Slow connection",
    slowConnectionDescription: "Slow connection - Some features may be delayed",

    // Sync Status
    never: "Never",
    justNow: "Just now",
    pendingSync: "pending",
    lastSync: "Last sync",

    // Trip Wizard
    tripWizardTitle: "Plan New Trip",
    tripWizardStep1: "Basics",
    tripWizardStep2: "Details",
    tripWizardStep3: "Segments",
    tripWizardStep4: "Health Check",
    tripWizardStep5: "Review",
    tripWizardNext: "Next",
    tripWizardPrevious: "Back",
    tripWizardSave: "Create Trip",
    tripWizardSaving: "Creating...",
    tripWizardStep1Title: "Basic Information",
    tripWizardStep2Title: "Trip Details",
    tripWizardStep3Title: "Segments / Stages",
    tripWizardStep4Title: "Health Check",
    tripWizardStep5Title: "Review & Save",
    tripWizardStep1Desc: "Title, destination, and dates",
    tripWizardStep2Desc: "Purpose, type, operator & companions",
    tripWizardStep3Desc: "Add flights, hotels and activities",
    tripWizardStep4Desc: "Health risks and vaccination requirements",
    tripWizardStep5Desc: "Review everything before creating",
    tripWizardReviewSummary: "Trip Summary",
    tripWizardDuration: "Duration",
    tripWizardSegmentCount: "Segments",
    tripWizardAddSegment: "Add Segment",
    tripWizardSkipSegments: "Skip – add later",
    tripHealthOptional:
      "This step is optional. You can continue planning and save your trip without it.",
    tripHealthUpsellTitle: "More confidence for a healthy trip",
    tripHealthUpsellIntro:
      "The destination health check is part of PLUS. With it, you can be better prepared for {country}:",
    tripHealthBenefitVaccinations:
      "Required and recommended vaccinations for your destination",
    tripHealthBenefitWarnings:
      "Malaria risk and current health warnings for your destination",
    tripHealthBenefitTracking:
      "Vaccination tracking for your own immunization record",
    tripHealthBenefitDocuments:
      "Document storage for certificates and travel papers",
    tripHealthBenefitOffline:
      "Offline access to your health data while you travel",
    tripHealthSingleTrip:
      "Planning just this one trip? PLUS Single Trip is a one-time payment that covers 90 days, with no subscription afterwards.",
    tripHealthUpgradeCta: "Upgrade to PLUS",
    tripHealthSkipCta: "Continue without health check",
    tripHealthDraftKept:
      "Your trip draft is kept. Come back to Plan New Trip to continue where you left off.",
    tripHealthEmpty:
      "Our sources list no vaccinations or health warnings for {country}.",
    tripHealthLoadFailed:
      "The health check could not be loaded right now. You can continue and check again later on the trip page.",

    // Trip Types (missing)
    tripTypeHiking: "Hiking",
    tripTypeCamping: "Camping",
    tripTypeRoadTrip: "Road Trip",
    purposeScience: "Science",

    // Companion Management
    inviteCompanion: "Invite Companion",
    companionEmail: "Companion Email",
    companionEmailPlaceholder: "email@example.com",
    sendInvitation: "Send Invitation",
    companionInviteSent: "Invitation sent successfully",
    companionInviteFailed: "Failed to send invitation",
    companionStatusPending: "Pending",
    companionStatusAccepted: "Accepted",
    companionStatusDeclined: "Declined",
    removeCompanion: "Remove",
    companionRemoved: "Companion removed",
    noCompanionsYet: "No companions invited yet",
    companionInviteDesc: "Invite other HealthPass users to join this trip",
    companionRequiresPLUS: "Inviting companions requires a PLUS subscription",

    // Segment Documents
    segmentDocuments: "Documents",
    uploadSegmentDocument: "Upload Document",
    docCategoryTicket: "Ticket",
    docCategoryBooking: "Booking Confirmation",
    docCategoryVoucher: "Voucher",
    docCategoryInsurance: "Insurance",
    docCategoryOther: "Other",
    documentUploaded: "Document uploaded successfully",
    documentDeleted: "Document deleted",
    documentUploadFailed: "Upload failed",
    maxFileSizeHint: "Max. 10 MB · PDF, JPEG, PNG",

    // Trip Map
    tripMap: "Trip Map",
    tripMapLoading: "Loading map...",
    tripMapNoData: "No segment coordinates available",
    tripMapRequiresPREMIUM: "Interactive map requires a PREMIUM subscription",

    // Subscription Gate
    featureRequiresPLUS: "This feature requires a PLUS subscription",
    featureRequiresPREMIUM: "This feature requires a PREMIUM subscription",
    upgradeToUnlock: "Upgrade to unlock",
    upgradePlanLabel: "Upgrade Plan",

    // Status override
    statusAutomatic: "Automatic",
    statusManual: "Manual",
    statusOverrideHint:
      "Status is set automatically based on dates. You can override it manually.",
    markAsManual: "Set manually",

    // HealthPass Card
    healthpassCard: "HealthPass Card",
    cardWallet: "Card Wallet",
    cardNumber: "Card Number",
    validUntil: "Valid Until",
    showFront: "Show Front",
    showBack: "Show Wallet",
    downloadPDF: "Download PDF",
    shareCard: "Share Card",
    generateQR: "Generate QR Code",
    cardShares: "Shared Links",
    createShareLink: "Create Share Link",
    shareExpiry: "Link expires in",
    shareExpiry24h: "24 hours",
    shareExpiry72h: "72 hours",
    shareExpiry7d: "7 days",
    sharePassword: "Password (optional)",
    revokeShare: "Revoke Access",
    shareRevoked: "Link revoked",
    shareCreated: "Share link created",
    shareCopied: "Link copied to clipboard",
    noSharesYet: "No active share links",
    shareAccessCount: "Accessed",
    shareTimes: "times",
    walletEmergency: "Emergency Health Record",
    walletMedications: "Medication Plan",
    walletVaccinations: "Vaccination Book",
    walletCheckin: "Check-in Documents",
    walletAppointments: "Appointments",
    walletInsurance: "Insurance",
    walletTravelDocs: "Travel Documents",
    walletLabResults: "Lab Results",
    upgradeForWalletItem: "Upgrade to {tier} to unlock this",
    cardLoading: "Loading your card...",
    cardError: "Could not load card",
    printPreview: "Print Preview",
    printFormatA4: "A4",
    printFormatA5: "A5",
    printFormatLetter: "US Letter",
    printFormatCard: "Credit Card Size",
    // Traveller navigation (UX_CONCEPT.md §4)
    navHome: "Home",
    navTrips: "Trips",
    navCardWallet: "Card & wallet",
    navCard: "Card",
    navHealth: "Health",
    navMe: "Me",
    navWallet: "Wallet",
    navShares: "Shared links",
    navAccessRequests: "Access requests",
    navCheckIn: "Check-in",
    navConsentRequests: "Consent requests",
    navAssistant: "Assistant",
    navPlan: "Plan",
    navAccount: "Account",
    navSubscriptions: "Subscriptions",
    navPayment: "Payment",
    navInvoices: "Invoices",
    navCompliance: "Compliance",
    navNotifications: "Notifications",
    complianceDescription:
      "Your data and your rights: what is stored, who looked at it, what you agreed to, a copy of everything, and deleting your account.",
    complianceStatistics: "Statistics",
    complianceLog: "Log",
    complianceLogLead:
      "Every time someone opened your emergency data, with who and when.",
    complianceConsents: "Consents",
    complianceExport: "Export",
    complianceDeleteAccount: "Delete account",
    statsMemberSince: "Member since",
    statsTrips: "Trips",
    statsMedications: "Medications",
    statsImmunizations: "Immunizations",
    statsUnavailable: "Not available right now",
    settingsNotificationsDescription:
      "Choose what HealthPass tells you about, and how.",
    navCollapseMenu: "Collapse menu",
    navExpandMenu: "Expand menu",
    navSectionPages: "Pages in this section",

    // Home (UX_CONCEPT.md §5.1)
    homeGoodMorning: "Good morning",
    homeGoodAfternoon: "Good afternoon",
    homeGoodEvening: "Good evening",
    homeBetaNotice:
      "HealthPass is in beta and still rolling out its services. We will keep you posted.",
    homeDismiss: "Dismiss",
    homeNextTrip: "Next trip",
    homeInDays: "in {n} days",
    homeToday: "today",
    homeTomorrow: "tomorrow",
    homeDepart: "Depart",
    homeReturn: "Return",
    homeNights: "{n} nights",
    homeCheckTrip: "See what this trip needs",
    homeMoreTrips: "{n} more trips planned",
    homeNoTripTitle: "Where to next?",
    homeNoTripBody:
      "Plan a trip and HealthPass works out what you need for it.",
    homePlanTrip: "Plan a trip",
    homeYourCard: "Your HealthPass",
    homeShowWallet: "Show wallet side",
    homeShowFront: "Show front side",
    homeCardHintFront:
      "Show the front at check-in. Tap the card to see your wallet.",
    homeCardHintBack:
      "Your wallet: everything the card carries. Tap to turn it back.",
    homeNextStep: "Next best step",
    homeNotNow: "Not now",
    stepEmergencyTitle: "Add your blood type",
    stepEmergencyBody:
      "It goes on your card, so a first responder can read it anywhere in the world.",
    stepEmergencyAction: "Complete emergency profile",
    stepBpTitle: "Re-check your blood pressure",
    stepBpBodyAbnormal:
      "Your last reading, {value}, was {status}. A fresh reading takes two minutes.",
    stepBpBodyStale:
      "Your last reading, {value}, was taken {age}. A fresh one takes two minutes.",
    stepBpAction: "Log a reading",
    stepTripTitle: "Get ready for {destination}",
    stepTripBody: "See which vaccines, medication and papers this trip needs.",
    stepTripAction: "Open the checklist",
    stepPlanTitle: "Plan your next trip",
    stepPlanBody:
      "Tell us where and when, and we check what you need before you go.",
    homeAtAGlance: "At a glance",
    homeAllHealthData: "All health data",
    homeNoVitals: "No readings yet. Add one and it shows here.",
    homeVitalsError: "Your readings could not be loaded.",
    homeRetry: "Try again",
    ageToday: "today",
    ageDays: "{n} d ago",
    ageMonths: "{n} mo ago",
    ageYears: "{n} yr ago",
    ageUnknown: "date unknown",
    vitalLow: "Low",
    vitalElevated: "Elevated",
    vitalHigh: "High",
    vitalUnderweight: "Underweight",
    vitalOverweight: "Overweight",
    vitalObese: "Obese",
    vitalRecorded: "Recorded",
    weightLabel: "Weight",
    bmiLabel: "BMI",
    stepBpBodyUndated:
      "Your last reading, {value}, has no date. A fresh one takes two minutes.",
    homeDayUnit: "day",
    homeDaysUnit: "days",

    // Trip readiness (UX_CONCEPT.md §6.1)
    readinessCaption: "trip-ready",
    readinessAllDone: "Trip-ready!",
    readinessStepsLeft: "steps left",
    readinessSaveFailed:
      "Your checklist could not be saved, so ticks will not be kept. Reload to try again.",
    readinessNotForMe: "Not for me",
    readinessCountAgain: "Count it again",
    readinessNotChecked: "Not checked yet",
    homeRemindTonight: "Remind me tonight",
    homeReminderSet: "Reminder set · {time}",
    homeReminderFailed: "Could not set it — try again",
    // Passport stamps and levels (UX_CONCEPT.md §7)
    passportTitle: "Your passport",
    passportStamps: "{count} stamps",
    passportOneStamp: "1 stamp",
    passportAt100: "at 100%",
    passportNextStamp: "Get trip-ready for {city} and the stamp is yours.",
    passportOneMore: "One more and you reach {level}.",
    passportFirst:
      "Get a trip to 100% before you leave, and its stamp lands here.",
    passportToNext: "{count} more to {level}.",
    passportTop: "Top level reached. Travel well.",
    passportFailed: "Your passport could not be loaded.",
    passportOpenTrip: "Open the checklist for {city}",
    levelStarter: "Starter",
    levelExplorer: "Explorer",
    levelGlobetrotter: "Globetrotter",
    levelNomad: "Nomad",
    stampEarned: "Stamp earned",
    stampEarnedBody: "It is in your passport. Have a wonderful trip.",
    stampEarnedLevelUp: "You are {level} now. Have a wonderful trip.",
    // First run (UX_CONCEPT.md §5.5)
    frStepOf: "Step {n} of 3",
    frTitleYou: "Let's make your card.",
    frLeadYou: "Each answer lands on your card right away.",
    frTitleBasics: "Your card is taking shape.",
    frLeadBasics:
      "A few more answers and a first responder can help you anywhere in the world.",
    frTitleTrip: "Your card is ready.",
    frLeadTrip: "Scan it, show it, share it. Now: where are you going next?",
    frTitleDone: "Your first trip is planned.",
    frLeadDone:
      "We have started a list of what {city} needs. Tick it off and earn your first passport stamp.",
    frStepYou: "You on the card",
    frStepYouHint: "Name, photo, nationality",
    frStepBasics: "Emergency basics",
    frStepBasicsHint: "Blood type, allergies, medication, a contact",
    frStepTrip: "Your next trip",
    frStepTripHint: "Where to, and when — we check what you need",
    frYourSteps: "Your three steps",
    frDone: "Done",
    frMinLeft: "{n} min left",
    frQrAfter: "QR after step 2",
    frBasicsLabel: "EMERGENCY BASICS · {n} OF 4",
    frYouQ: "Who is this card for?",
    frFirstName: "First name",
    frLastName: "Last name",
    frDateOfBirth: "Date of birth",
    frNationality: "Nationality",
    frNationalityPlaceholder: "e.g. Canadian",
    frAddPhoto: "Add a photo",
    frChangePhoto: "Change photo",
    frPhotoFailed: "The photo could not be uploaded. Try another one.",
    frPutOnCard: "Put it on my card",
    frAddToCard: "Add to my card",
    frBloodQ: "What is your blood type?",
    frBloodHint: "It is the first thing a hospital checks.",
    frBloodUnknown: "I don't know",
    frBloodChip: "Blood type {type}",
    frBloodChipEmpty: "Blood type?",
    frAllergyQ: "Are you allergic to anything?",
    frAllergyHint:
      "Shown in English to anyone who scans your card in an emergency.",
    frAlreadyOnCard: "Already on your card",
    frSomethingElse: "Something else",
    frAllergyPlaceholder: "e.g. latex, bee stings",
    frNoAllergies: "No allergies",
    frNothingToAdd: "Nothing to add",
    frAllergyChips: "{n} allergies",
    frAllergyChipOne: "1 allergy",
    frAllergyChipNone: "No allergies",
    frAllergyChipEmpty: "Allergies?",
    frMedsQ: "Do you take any medication regularly?",
    frMedsHint: "Responders need to know before they give you anything.",
    frMedsPlaceholder: "e.g. Metformin 500 mg",
    frAdd: "Add",
    frRemove: "Remove",
    frNoMeds: "None",
    frContactQ: "Who should we call?",
    frContactHint:
      "Your emergency contact, shown to responders who scan your card.",
    frContactName: "Name",
    frContactPhone: "Phone, with country code",
    frContactPhoneInvalid:
      "Enter a phone number in digits, e.g. +1 416 555 0100.",
    frRelationship: "Relationship",
    frRelPartner: "Partner",
    frRelParent: "Parent",
    frRelChild: "Child",
    frRelSibling: "Sibling",
    frRelFriend: "Friend",
    frSkip: "Skip for now",
    frTripQ: "Where are you going next?",
    frTripCity: "City or place",
    frTripCountry: "Country",
    frTripDeparture: "Departure",
    frTripReturn: "Return",
    frTripPlan: "Plan this trip",
    frTripLater: "No trip planned yet",
    frOpenChecklist: "See what {city} needs",
    frGoHome: "Go to Home",
    frSaveFailed: "That did not save. Check your connection and try again.",
    frPrivacy:
      "Your health data is encrypted and stays yours. You decide who sees it, and you can see every time someone did.",
    // Card page (UX_CONCEPT.md §5.2)
    cardSideFront: "Front",
    cardSideWallet: "Wallet side",
    cardDownload: "Download card",
    cardDownloadFailed: "The card could not be downloaded. Try again.",
    cardOfflineCodeShow: "No signal? Show the offline code",
    cardOnlineCodeShow: "Show the online code",
    cardOfflineCodeHint:
      "This code holds your emergency summary and works without internet.",
    // Shared card page
    sharedCardTitle: "A shared HealthPass card",
    sharedCardLead:
      "Its holder shared this card with you. It shows who they are, not their health records.",
    sharedCardNoCode: "Shared card",
    sharedCardPasswordLead: "The holder protected this card with a password.",
    sharedCardPassword: "Password",
    sharedCardOpen: "Open the card",
    sharedCardWrongPassword: "That password is not right.",
    sharedCardGone: "This link has expired or was withdrawn by its holder.",
    sharedCardMissing:
      "This link does not exist. Check that it was copied whole.",
    sharedCardError: "The card could not be loaded. Try again in a moment.",
    settingsExportLead:
      "Download everything HealthPass holds about you, as a file you can keep or take to another service.",
    settingsExportDone: "Your data was downloaded.",
    settingsExportFailed: "The download failed. Please try again.",
    // Payment success page
    payConfirming: "Confirming your payment…",
    payConfirmingLine: "This takes a few seconds.",
    payWelcome: "Welcome to {plan}",
    payTrialLine:
      "Your free trial runs until {date}. After that your plan renews monthly unless you cancel before then.",
    payTripLine: "Your trip plan runs until {date}.",
    payActiveLine: "Your plan is active now.",
    payPendingTitle: "Your payment is on its way",
    payPendingLine:
      "Some payment methods take longer. Your plan switches on as soon as the payment arrives, and we will tell you.",
    payFailedTitle: "This checkout did not finish",
    payFailedLine:
      "Nothing was charged. You can choose a plan again whenever you like.",
    payContinue: "Continue",
    payBuildCard: "Build your card",
    payBackToPlans: "Back to the plans",
    renewTermsMonthly:
      "New members get 14 days free, then {price} a month. It renews every month until you cancel, which you can do at any time in your billing settings.",
    renewTermsTrip: "One payment of {price} for 90 days. It does not renew.",
    // Partner offers (REQ-025)
    marketplaceTitle: "Partner offers",
    marketplaceIntro:
      "Insurance offers from our partners for this trip. HealthPass does not compare or recommend them; the insurer answers questions about cover.",
    marketplaceDisclosurePerContract:
      "Offer from {insurer}. HealthPass receives a fee if you take out a contract.",
    marketplaceDisclosurePerVisit:
      "Offer from {insurer}. HealthPass receives a fee when you visit {insurer}.",
    marketplaceVisit: "Go to {insurer}",
    marketplaceSeeAll: "Open the partner offers page",
    marketplaceEmpty: "There are no partner offers for this trip.",
    marketplaceBackToTrip: "Back to the trip",
    marketplaceOpenFailed: "The offer could not be opened. Please try again.",
    marketplaceSettingTitle: "Show partner offers",
    marketplaceSettingHint:
      "Insurance offers from partners on your trips. They are chosen by destination, dates and country of residence only, never by your health data. Turning this off hides them everywhere.",
    marketplaceSettingSaved: "Saved.",
    marketplaceSettingFailed: "The setting could not be saved.",
  },

  de: {
    // Navigation
    login: "Anmelden",
    register: "Registrieren",
    dashboard: "Dashboard",
    profile: "Profil",
    health: "Gesundheit",
    medications: "Medikamente",
    travel: "Reisen",
    documents: "Dokumente",
    chat: "Mitteilungen",
    settings: "Einstellungen",
    logout: "Abmelden",
    appearance: "Erscheinungsbild",
    security: "Sicherheit",

    // Auth
    welcomeBack: "Reisen Sie mit gutem Gefühl",
    signInToAccount: "Melden Sie sich in Ihrem Konto an",
    createAccount: "Erstellen Sie Ihr kostenloses Konto",
    createAccountSubtitle: "Richten Sie Ihr HealthPass-Konto ein",
    settingsGeneralDescription: "Benachrichtigungen, Daten und Kontoübersicht",
    settingsSecurityDescription:
      "Anmeldung, Zwei-Faktor-Authentifizierung und Datenschutz",
    settingsAppearanceDescription: "Sprache und Farbschema",
    themeTitle: "Erscheinungsbild",
    themeLight: "Hell",
    themeDark: "Dunkel",
    themeSystem: "System",
    themeHint:
      "System folgt der Einstellung Ihres Geräts und ändert sich mit ihr.",
    orientationTitle: "Bildschirmausrichtung",
    orientationPortrait: "Feste Hochformat-Ansicht",
    orientationSystem: "Systemausrichtung",
    orientationHint:
      "Feste Hochformat-Ansicht hält die App aufrecht, egal wie Sie das Telefon halten. Systemausrichtung folgt der Drehungseinstellung Ihres Geräts.",
    accountStatistics: "Konto-Statistiken",
    notificationsEmailHint: "Wichtige Updates und Erinnerungen per E-Mail",
    remindersTitle: "Erinnerungen",
    fhirStandardNote: "Medizinische Daten in standardisiertem Format.",
    email: "E-Mail Adresse",
    password: "Passwort",
    confirmPassword: "Passwort bestätigen",
    firstName: "Vorname",
    lastName: "Nachname",
    forgotPassword: "Passwort vergessen?",
    rememberMe: "Angemeldet bleiben",
    signIn: "Anmelden",
    signUp: "Registrieren",
    alreadyHaveAccount: "Bereits ein Konto?",
    noAccountYet: "Neu bei HealthPass?",
    createFreeAccount: "Kostenloses Konto erstellen",
    backToLogin: "Zurück zur Anmeldung",

    // Common
    loading: "Laden...",
    error: "Fehler",
    success: "Erfolg",
    cancel: "Abbrechen",
    save: "Speichern",
    delete: "Löschen",
    edit: "Bearbeiten",
    view: "Anzeigen",
    close: "Schließen",
    pleaseSelect: "Bitte wählen...",

    // Dashboard
    viewAll: "Alle anzeigen",

    // Profile

    // Health Records - nur fehlende
    bloodType: "Blutgruppe",

    // Medications - nur fehlende

    // Documents - nur fehlende

    // Body Data - nur fehlende

    // Lab Results - nur fehlende

    // All other fields with German translations
    resetPassword: "Passwort zurücksetzen",
    resetPasswordDescription:
      "Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Reset-Link",
    sendResetLink: "Reset-Link senden",
    emailSent: "E-Mail gesendet!",
    checkYourEmail: "Überprüfen Sie Ihr E-Mail-Postfach",
    resetEmailSent: "Wir haben eine Passwort-Reset E-Mail gesendet an",
    required: "Dieses Feld ist erforderlich",
    invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    passwordTooShort: "Passwort muss mindestens 8 Zeichen lang sein",
    passwordsNotMatch: "Passwörter stimmen nicht überein",

    // Add all other required fields with German translations
    healthPass: "HealthPass",
    secureHealthData: "Sichere Gesundheitsdatenverwaltung",
    manageHealthSecurely: "Verwalten Sie Ihre Gesundheitsdaten sicher",
    testAccount: "Test-Konto",
    useTestAccount: "Test-Konto verwenden",
    termsAndConditions: "Nutzungsbedingungen",
    privacyPolicy: "Datenschutzerklärung",
    acceptTermsRequired:
      "Bitte akzeptieren Sie die Bedingungen, um fortzufahren",
    consentTerms: "Ich akzeptiere die {terms} und habe die {privacy} gelesen.",
    consentTermsLink: "Nutzungsbedingungen",
    consentHealth:
      "Ich willige ausdrücklich ein, dass HealthPass die Gesundheitsangaben speichert, die ich eingebe (Erkrankungen, Allergien, Medikamente, Impfungen, Dokumente), um sie mir und auf meinem Notfallausweis anzuzeigen (Art. 9 Abs. 2 lit. a DSGVO). Ich kann das jederzeit widerrufen.",
    consentHealthWithout:
      "Ohne diese Einwilligung kannst du Reisen planen und die Checkliste nutzen, aber keine Gesundheitsangaben speichern.",
    consentAi:
      "Ich willige ein, dass der jeweils nötige Teil meiner Gesundheitsdaten an OpenAI in den USA übermittelt wird, wenn ich KI-Gesundheitstipps oder den KI-Assistenten nutze. Ich kann das jederzeit widerrufen.",
    consentAiWithout:
      "Ohne diese Einwilligung bleiben die KI-Funktionen aus; die Hinweise zum Reiseziel funktionieren weiter.",
    consentMarketing:
      "Schickt mir Tipps und Neuigkeiten zur Reisegesundheit per E-Mail. Abmelden jederzeit möglich.",
    consentOptional: "Freiwillig",
    consentGateTitle: "Deine Daten, deine Entscheidung",
    consentGateLead:
      "Bitte sag uns, was HealthPass mit deinen Daten tun darf. Bis du entschieden hast, wird nichts Neues gespeichert und nichts an die KI gesendet.",
    consentGateSave: "Auswahl speichern",
    consentSaveFailed:
      "Deine Auswahl konnte nicht gespeichert werden. Bitte versuche es noch einmal.",
    consentNeededHealthTitle: "Darf HealthPass Gesundheitsangaben speichern?",
    consentNeededAiTitle: "KI-Gesundheitstipps erlauben?",
    consentAllow: "Erlauben",
    consentNotNow: "Nicht jetzt",
    consentWithdrawHint:
      "Du kannst das jederzeit in den Einstellungen widerrufen.",
    settingsConsentsTitle: "Deine Einwilligungen",
    settingsConsentsLead:
      "Ein Widerruf stoppt jede neue Verarbeitung. Bereits Gespeichertes bleibt, bis du es löschst.",
    consentSaved: "Gespeichert",
    immediateStartTitle: "Bevor du bezahlst",
    immediateStartAck:
      "Mein Plan soll sofort beginnen. Mir ist bekannt, dass ich bei einem Widerruf innerhalb von 14 Tagen die bereits genutzten Tage bezahle.",
    immediateStartTrial:
      "Beginnt dein Plan mit einem kostenlosen Test, wird vor dessen Ende nichts abgebucht, und eine Kündigung bis dahin kostet nichts.",
    immediateStartContinue: "Weiter zur Zahlung",
    immediateStartBack: "Zurück",
    withdrawTitle: "Vertrag widerrufen",
    withdrawLead:
      "Du kannst diesen Plan bis {deadline} ohne Angabe von Gründen widerrufen.",
    withdrawRefund: "Für die nicht genutzten Tage würden {amount} erstattet.",
    withdrawTrial:
      "Du bist im kostenlosen Test, es wurde also nichts abgebucht. Ein Widerruf beendet den Plan sofort.",
    withdrawButton: "Vertrag widerrufen",
    withdrawConfirm:
      "Jetzt widerrufen? Dein Plan endet sofort, und dein Konto läuft im kostenlosen Plan weiter. Deine Daten bleiben erhalten.",
    withdrawConfirmYes: "Ja, widerrufen",
    withdrawDone:
      "Dein Widerruf ist bestätigt. {refund}Wir haben dir eine Bestätigung per E-Mail geschickt.",
    withdrawRefundIssued:
      "{amount} sind auf dem Weg zurück auf dein ursprüngliches Zahlungsmittel. ",
    withdrawRefundPending:
      "{amount} werden dir innerhalb von 14 Tagen auf dein ursprüngliches Zahlungsmittel erstattet. ",
    withdrawFailed:
      "Der Widerruf konnte nicht abgeschlossen werden. Bitte versuche es noch einmal oder schreib dem Support.",
    copyright: "Ihre Gesundheitsdaten sicher verwalten",

    // Landing Page
    landingHeroTitle: "Reisen Sie mit Sicherheit",
    landingHeroSubtitle:
      "Ihre Notfall-Gesundheitsdaten, weltweit in Sekunden verfügbar. QR-Code-Zugriff für Ersthelfer. DSGVO-konform. Immer bei Ihnen.",
    landingStartFree: "Jetzt kostenlos starten",
    landingSeeHowItWorks: "So funktioniert es",
    landingNoCreditCard: "Keine Kreditkarte erforderlich",
    landingFreeTrial: "30 Tage kostenlos testen",
    landingFeaturesTitle: "Alles für sicheres Reisen",
    landingFeaturesSubtitle:
      "Von Reisenden für Reisende entwickelt. Alle wichtigen Funktionen auf einer sicheren Plattform.",
    landingHowItWorksTitle: "In 3 einfachen Schritten starten",
    landingHowItWorksSubtitle:
      "Von der Anmeldung bis zur Reisebereitschaft in unter 10 Minuten",
    landingPricingTitle: "Einfache, transparente Preise",
    landingPricingSubtitle:
      "Kostenlos starten, jederzeit upgraden. Keine versteckten Kosten.",
    landingCtaTitle: "Bereit für sicheres Reisen?",
    landingCtaSubtitle:
      "Schließen Sie sich 50.000+ Reisenden an, die HealthPass für ihre Notfall-Gesundheitsdaten vertrauen.",
    landingCtaButton: "Jetzt kostenlos starten",

    overview: "Übersicht",
    healthRecords: "Gesundheitsdaten",
    appointments: "Termine",
    reports: "Berichte",
    recentActivity: "Letzte Aktivität",
    quickActions: "Schnellaktionen",
    upcomingAppointments: "Anstehende Termine",
    healthSummary: "Gesundheitsübersicht",
    addNewRecord: "Neuen Eintrag hinzufügen",
    viewAllRecords: "Alle Einträge anzeigen",
    noDataAvailable: "Keine Daten verfügbar",
    upcomingTravels: "Bevorstehende Reisen",
    noUpcomingTravels: "Keine bevorstehenden Reisen",
    planNewTravel: "Neue Reise planen",
    today: "Heute",
    tomorrow: "Morgen",
    days: "Tage",
    healthRecommendations: "Gesundheitsempfehlungen",
    stayHydrated: "Bleiben Sie den ganzen Tag hydratisiert",
    drinkWaterRecommendation: "Streben Sie täglich 2-3 Liter Wasser an",
    regularExercise: "Regelmäßige körperliche Aktivität",
    exerciseRecommendation: "30 Minuten moderate Aktivität, 5 Tage/Woche",
    qualitySleep: "Qualitätsschlaf",
    sleepRecommendation: "7-9 Stunden Schlaf pro Nacht",
    thinkAbout: "Denken Sie an",
    travelInsurance: "Reiseversicherung",
    travelInsuranceDesc: "Schützen Sie Ihre Reise",
    firstAidKit: "Erste-Hilfe-Set",
    firstAidKitDesc: "Seien Sie vorbereitet",
    medicalCheckup: "Medizinischer Check-up",
    medicalCheckupDesc: "Jährliche Gesundheitsuntersuchung",
    vaccinationAppointment: "Impftermin",
    vaccinationAppointmentDesc: "Bleiben Sie geschützt",
    tryOutPlus: "Testen Sie HealthPass PLUS",
    plusDescription:
      "Schalten Sie Premium-Funktionen für verbessertes Gesundheitsmanagement frei",
    plusFeature1: "Erweiterte Gesundheitsanalysen",
    plusFeature2: "Prioritätsunterstützung",
    plusFeature3: "Unbegrenzter Dokumentenspeicher",
    plusFeature4: "KI-gestützte Einblicke",
    upgradeToPlusButton: "Auf PLUS upgraden",
    addHealthRecord: "Gesundheitsdaten hinzufügen",
    editHealthRecord: "Gesundheitsdaten bearbeiten",
    deleteHealthRecord: "Gesundheitsdaten löschen",
    recordType: "Eintragstyp",
    date: "Datum",
    description: "Beschreibung",
    attachments: "Anhänge",
    bloodPressure: "Blutdruck",
    weight: "Gewicht",
    height: "Größe",
    temperature: "Temperatur",
    heartRate: "Herzfrequenz",
    currentMedications: "Aktuelle Medikamente",
    addMedication: "Medikament hinzufügen",
    prescribedBy: "Verschrieben von",
    sideEffects: "Nebenwirkungen",
    instructions: "Anweisungen",
    travelPlans: "Reisepläne",
    addTravelPlan: "Reiseplan hinzufügen",
    destination: "Reiseziel",
    departureDate: "Abreisedatum",
    returnDate: "Rückkehrdatum",
    travelType: "Reiseart",
    requiredVaccinations: "Erforderliche Impfungen",

    // Travel List Page
    travels: "Reisen",
    myTravels: "Meine Reisen",
    planYourTrips:
      "Planen Sie Ihre Reisen und verwalten Sie reisebezogene Gesundheitsanforderungen",
    newTravel: "Neue Reise",
    pastTravels: "Vergangene Reisen",
    currentTravels: "Aktuelle Reisen",
    plannedTravels: "Geplante Reisen",
    draftTravels: "Entwürfe",
    noTravelsYet: "Noch keine Reisen",
    startPlanningTravel:
      "Beginnen Sie mit der Planung Ihrer ersten Reise, um Gesundheitsempfehlungen und -anforderungen zu erhalten",
    travelTitle: "Reisetitel",
    travelPurpose: "Reisezweck",
    travelDestination: "Reiseziel",
    startDate: "Startdatum",
    endDate: "Enddatum",
    status: "Status",
    actions: "Aktionen",
    viewTravel: "Reise anzeigen",
    editTravel: "Reise bearbeiten",
    deleteTravel: "Reise löschen",
    archiveTravel: "Reise archivieren",
    confirmDeleteTravel:
      "Sind Sie sicher, dass Sie diese Reise löschen möchten?",
    travelDeleted: "Reise erfolgreich gelöscht",
    travelArchived: "Reise erfolgreich archiviert",

    // Travel Status
    statusDraft: "Entwurf",
    statusPlanned: "Geplant",
    statusCurrent: "Aktuell",
    statusCompleted: "Abgeschlossen",
    statusCancelled: "Storniert",
    statusArchived: "Archiviert",
    statusModeManual: "Manuell festlegen",
    statusModeAutoHint:
      "Status wird automatisch anhand der Reisedaten berechnet.",

    // Travel Purpose
    purposeLeisure: "Freizeit",
    purposeBusiness: "Geschäftlich",
    purposeMedical: "Medizinisch",
    purposeEducation: "Bildung",
    purposeVolunteering: "Freiwilligenarbeit",
    purposeOther: "Sonstiges",
    tripTitle: "Reise-Titel",
    tripType: "Reise-Art",
    tripOperator: "Reiseveranstalter",
    bookingReference: "Buchungsnummer",
    startLocation: "Startort",
    endLocation: "Endort",
    tripTypeCruise: "Kreuzfahrt",
    tripTypeBeach: "Strand",
    tripTypeMountains: "Berge",
    tripTypeSports: "Sport",
    tripTypeEducation: "Bildung",
    tripTypeEvent: "Veranstaltung",
    tripTypeCulture: "Kultur",
    tripTypeExpedition: "Expedition",
    plannedActivities: "Geplante Aktivitäten",
    activity_diving: "Tauchen",
    activity_climbing: "Klettern",
    activity_skiing: "Skifahren",
    activity_hiking: "Wandern",
    activity_biking: "Radfahren",
    activity_swimming: "Schwimmen",
    activity_safari: "Safari",
    healthcareFacilities: "Gesundheitseinrichtungen",
    nearbyHospitals: "Krankenhäuser in der Nähe",
    nearbyPharmacies: "Apotheken in der Nähe",
    nearbyClinics: "Kliniken in der Nähe",
    noFacilitiesFound: "Keine Einrichtungen in diesem Bereich gefunden",
    facilityDistance: "Entfernung",
    forumHealthSummary: "Reisende berichten",
    commonHealthIssues: "Häufige Gesundheitsprobleme",
    travelerTips: "Reisetipps",
    unexpectedRisks: "Unerwartete Risiken",
    forumSummaryDisclaimer:
      "KI-generierte Zusammenfassung. Mit aktuellen Reiseberichten überprüfen.",
    searchForumsFurther: "Aktuelle Berichte suchen",
    loadForumSummary: "Reiseberichte laden",
    operatorHealthInfo: "Gesundheitsinfo des Veranstalters",
    cruiseHealthRequirements: "Gesundheitsanforderungen für Kreuzfahrten",
    tripSegments: "Reiseabschnitte",
    addSegment: "Abschnitt hinzufügen",
    editSegment: "Abschnitt bearbeiten",
    deleteSegment: "Abschnitt löschen",
    noSegmentsYet: "Noch keine Abschnitte hinzugefügt",
    addSegmentsDescription:
      "Fügen Sie Flug-, Hotel- oder Aktivitätsabschnitte hinzu, um Ihre Reise zu organisieren",
    addFirstSegment: "Ersten Abschnitt hinzufügen",
    operator: "Anbieter",
    transportation: "Transportmittel",
    segmentInformation: "Abschnittsinformationen",
    segmentTitle: "Abschnittstitel",
    segmentType: "Abschnittstyp",
    segmentTypeTransfer: "Transfer",
    segmentTypeDestination: "Ziel",
    segmentTypeAccommodation: "Unterkunft",
    segmentTypeActivity: "Aktivität",
    locations: "Orte",
    startCountry: "Startland",
    endCountry: "Zielland",
    dateTime: "Datum & Uhrzeit",
    startDateTime: "Startdatum & -uhrzeit",
    endDateTime: "Enddatum & -uhrzeit",
    selectOption: "Auswählen...",
    transportFlight: "Flug",
    transportTrain: "Zug",
    transportBus: "Bus",
    transportCar: "Auto",
    transportShip: "Schiff",
    accommodation: "Unterkunft",
    accommodationHotel: "Hotel",
    accommodationHostel: "Hostel",
    accommodationApartment: "Apartment",
    accommodationCruiseCabin: "Kreuzfahrtkabine",
    healthRisk: "Gesundheitsrisiko",
    travelReadiness: "Reisebereitschaft",
    vaccinations: "Impfungen",
    recommendations: "Empfehlungen",
    available: "verfügbar",
    riskManagement: "Risikomanagement",
    vaccinationDetails: "Impfdetails",
    readinessChecklist: "Bereitschafts-Checkliste",
    personalRecommendations: "Persönliche Empfehlungen",
    bookAppointment: "Termin buchen",

    // Travel Details
    travelCompanions: "Reisebegleiter",
    companions: "Begleiter",
    tourOperator: "Reiseveranstalter",
    travelNotes: "Reisenotizen",
    notes: "Notizen",
    segments: "Abschnitte",
    travelSegment: "Reiseabschnitt",
    transportationType: "Transportart",
    accommodationType: "Unterkunftsart",

    // Filter & Sort
    filterByStatus: "Nach Status filtern",
    filterByPurpose: "Nach Zweck filtern",
    sortByDate: "Nach Datum sortieren",
    sortByDestination: "Nach Ziel sortieren",
    searchTravels: "Reisen durchsuchen...",
    showAll: "Alle anzeigen",
    showActive: "Aktive anzeigen",
    showCompleted: "Abgeschlossene anzeigen",

    // Time Indicators
    daysUntilDeparture: "Tage bis zur Abreise",
    daysRemaining: "Verbleibende Tage",
    inProgress: "In Arbeit",
    departed: "Abgereist",
    returned: "Zurückgekehrt",

    // Travel Form
    travelDates: "Reisedaten",
    travelDetails: "Reisedetails",
    healthInformationNotice: "Gesundheitsinformationen",
    healthRequirementsDescription:
      "Reisegesundheitsanforderungen werden hier basierend auf Ihrem Reiseziel angezeigt. Diese Informationen stammen von CDC, WHO und lokalen Gesundheitsbehörden.",
    vaccinationInfoLoading:
      "Impfanforderungen werden basierend auf Ihrem Reiseziel und Reisedaten geladen.",
    healthWarningsLoading:
      "Gesundheitswarnungen und -hinweise werden hier angezeigt.",
    apiIntegrationPending: "API-Integration mit CDC/WHO ausstehend",
    checkBackLater:
      "Informationen werden verfügbar sein, sobald Zielgesundheitsdaten integriert sind",
    healthWarnings: "Gesundheitswarnungen",
    lastUpdated: "Zuletzt aktualisiert",
    source: "Quelle",
    noVaccinationsRequired: "Keine spezifischen Impfungen erforderlich",
    noHealthWarnings: "Keine spezifischen Gesundheitswarnungen",
    risk: "Risiko",

    // Health Recommendations
    departure: "Abreise",
    mandatory: "Pflicht",
    recommended: "Empfohlen",
    suggested: "Vorgeschlagen",
    recommendedVaccinations: "Empfohlene Impfungen",
    activeDiseaseOutbreaks: "Aktive Krankheitsausbrüche",
    dataSources: "Datenquellen",
    from: "Von",
    to: "Bis",
    isoCode: "ISO-Code",
    riskLevel: "Risikostufe",
    healthDataLoadError: "Gesundheitsdaten konnten nicht geladen werden.",
    dataFrom: "Daten von",
    overallRisk: "Gesamtrisikobewertung",
    quickInstallButton: "App installieren",
    quickInstallInstalled: "Bereits installiert",
    updateAvailable: "Neue App-Version laden.",
    quickInstallAndroidApkStep1: "Code scannen, dann auf Herunterladen tippen.",
    quickInstallAndroidApkStep2:
      "Heruntergeladene Datei öffnen und Installation aus Chrome erlauben.",
    quickInstallAndroidApkStep3: "HEALTHPASS über den Startbildschirm öffnen.",
    quickInstallTitle: "In drei Schritten installieren",
    quickInstallSubtitle:
      "Richten Sie die Handykamera auf den Code. HEALTHPASS installiert sich wie eine normale App, läuft im Vollbild und funktioniert offline.",
    quickInstallScanHint: "Scannen, um auf dem Handy zu öffnen",
    quickInstallAndroidTitle: "Android · Chrome",
    quickInstallAndroidStep1:
      "Code scannen oder travelhealthpass.app in Chrome öffnen.",
    quickInstallAndroidStep2:
      "Auf das Drei-Punkte-Menü tippen, dann „App installieren“.",
    quickInstallAndroidStep3: "HEALTHPASS über den Startbildschirm öffnen.",
    quickInstallIosTitle: "iPhone & iPad · Safari",
    quickInstallIosStep1:
      "Code scannen oder travelhealthpass.app in Safari öffnen.",
    quickInstallIosStep2: "Auf „Teilen“ tippen, dann „Zum Home-Bildschirm“.",
    quickInstallIosStep3: "HEALTHPASS über den Startbildschirm öffnen.",
    quickInstallIosNote:
      "Nur Safari — Chrome und Firefox auf dem iPhone können keine Web-Apps installieren.",
    appInstallTitle: "HealthPass auf Ihr Smartphone holen",
    appInstallSubtitle:
      "Installieren Sie HealthPass auf Android oder iPhone und haben Sie Gesundheitsakte, Notfallkarte und Einreisebestimmungen auch offline dabei.",
    appInstallAndroidTitle: "Android",
    appInstallAndroidSubtitle: "APK direkt installieren",
    appInstallScanToDownload:
      "Scannen Sie diesen Code mit der Kamera Ihres Smartphones, um die APK herunterzuladen, oder nutzen Sie die Schaltfläche unten.",
    appInstallDownloadApk: "HealthPass App herunterladen",
    appInstallApkUnavailable: "Die Android-APK ist noch nicht verfügbar.",
    appInstallApkUnavailableHint:
      'In der Zwischenzeit läuft HealthPass als installierbare Web-App: Öffnen Sie diese Seite in Chrome auf Ihrem Android-Gerät, tippen Sie auf das Menü und wählen Sie "App installieren".',
    appInstallAndroidStep1:
      'Scannen Sie den QR-Code oder tippen Sie auf Ihrem Android-Gerät auf "APK herunterladen".',
    appInstallAndroidStep2:
      'Wenn Android warnt, dass die Datei Ihr Gerät schädigen könnte, wählen Sie "Trotzdem herunterladen" - diese Warnung erscheint bei jeder App außerhalb des Play Store.',
    appInstallAndroidStep3:
      'Öffnen Sie die heruntergeladene Datei. Android fragt, ob Installationen aus dieser Quelle erlaubt sind; tippen Sie auf "Einstellungen" und aktivieren Sie die Berechtigung.',
    appInstallAndroidStep4:
      'Gehen Sie zurück und tippen Sie auf "Installieren".',
    appInstallAndroidStep5:
      "Öffnen Sie HealthPass und melden Sie sich mit Ihrem bestehenden Konto an.",
    appInstallAndroidPwaTitle: "Stattdessen über Chrome installieren",
    appInstallAndroidPwaStep1:
      "Öffnen Sie diese Seite in Chrome auf Ihrem Android-Gerät - scannen Sie den Code oben oder geben Sie die Adresse ein.",
    appInstallAndroidPwaStep2:
      "Tippen Sie oben rechts auf das Drei-Punkte-Menü.",
    appInstallAndroidPwaStep3:
      'Tippen Sie auf "Zum Startbildschirm zufügen" bzw. "App installieren".',
    appInstallAndroidPwaStep4: 'Bestätigen Sie mit "Installieren".',
    appInstallAndroidPwaStep5:
      "Starten Sie HealthPass über den Startbildschirm und melden Sie sich an. Die App läuft im Vollbild und funktioniert offline.",
    appInstallIosTitle: "iPhone und iPad",
    appInstallIosSubtitle: "Über Safari zum Home-Bildschirm hinzufügen",
    appInstallScanToOpen:
      "Scannen Sie diesen Code mit der iPhone-Kamera, um HealthPass in Safari zu öffnen.",
    appInstallIosStep1:
      "Öffnen Sie diese Seite in Safari. Chrome und Firefox unter iOS können keine Web-Apps installieren.",
    appInstallIosStep2:
      "Tippen Sie auf die Teilen-Schaltfläche - das Quadrat mit dem Pfeil nach oben am unteren Bildschirmrand.",
    appInstallIosStep3:
      'Scrollen Sie nach unten und tippen Sie auf "Zum Home-Bildschirm".',
    appInstallIosStep4: 'Tippen Sie oben rechts auf "Hinzufügen".',
    appInstallIosStep5:
      "Starten Sie HealthPass über den Home-Bildschirm und melden Sie sich an. Die App läuft im Vollbild und funktioniert offline.",
    appInstallIosSafariNote:
      "Apple erlaubt nur Safari die Installation von Web-Apps - führen Sie diese Schritte daher in Safari aus, auch wenn ein anderer Browser Ihr Standard ist.",
    appInstallAndroidGuideLink: "Vollständige Android-Anleitung",
    androidInstallPageTitle: "HealthPass auf Android installieren",
    androidInstallPageSubtitle:
      "Bringen Sie HealthPass in wenigen Schritten auf Ihr Android-Telefon. Es läuft im Vollbild und funktioniert offline.",
    androidInstallScanPageQr:
      "Sie lesen das an einem Computer? Scannen Sie diesen Code, um diese Seite auf dem Android-Telefon zu öffnen, auf dem Sie HealthPass installieren möchten.",
    androidInstallSecurityNote:
      "Android fragt vor der Installation einer App außerhalb des Play Store nach Ihrer Erlaubnis. Diese Abfrage ist hier zu erwarten. Die Berechtigung gilt nur für den Browser, dem Sie sie erteilen, und Sie können sie nach der Installation wieder entziehen.",
    androidInstallIosHint:
      "Sie nutzen ein iPhone? Folgen Sie stattdessen der iOS-Anleitung.",
    androidInstallBackHome: "Zurück zur Startseite",
    aiAnalysisError: "KI-Analyse nicht verfügbar.",
    personalizedHealthRecommendations: "Persönliche Gesundheitsempfehlungen",
    recommendationsIntro:
      "Basierend auf Reiseziel, Reisedauer und Gesundheitsprofil – evidenzbasierte Empfehlungen aus mehreren Quellen.",
    aiTripAnalysis: "KI-Reisegesundheitsanalyse",
    analyzing: "Analyse läuft…",
    runAiAnalysis: "KI-Analyse starten",
    aiAnalysisHint:
      "Analyse von Klimaeinfluss und persönlichem Gesundheitsprofil mit KI-Unterstützung.",
    climateRiskSummary: "Zusammenfassung der Klimarisiken",
    healthProfileRisks: "Risiken aus Ihrem Gesundheitsprofil",
    urgentActions: "Dringende Maßnahmen",
    noRecommendationsAvailable:
      "Für dieses Reiseziel wurden keine spezifischen Empfehlungen gefunden.",
    travelStatus: "Reisestatus",
    readiness: "Reisebereitschaft",
    overallReadiness: "Gesamtbereitschaft",
    urgent: "Dringend",
    addCustomTask: "Eigene Aufgabe hinzufügen",
    taskDescription: "Aufgabenbeschreibung…",
    vaccinationRequired: "Vorgeschrieben",
    vaccinationRecommended: "Empfohlen",
    atLeast: "Mindestens",
    daysBeforeDeparture: "Tage vor der Abreise",
    validFor: "Gültig für",
    loadingHealthData: "Gesundheitsdaten werden geladen…",
    noRequiredVaccinations:
      "Für dieses Reiseziel sind keine Impfungen vorgeschrieben.",
    noRecommendedVaccinations: "Keine weiteren Impfungen empfohlen.",
    vaccinationTimeline: "Impfzeitplan",
    before: "vorher",
    riskLevelDescLow: "Geringes Gesundheitsrisiko für dieses Reiseziel",
    riskLevelDescMedium:
      "Mittleres Gesundheitsrisiko - Vorsichtsmaßnahmen treffen",
    riskLevelDescHigh:
      "Hohes Gesundheitsrisiko - vor der Abreise ärztlichen Rat einholen",
    riskLevelDescCritical:
      "Kritisches Gesundheitsrisiko - Reise ohne fachärztliche Beratung nicht empfohlen",
    activeOutbreaks: "Aktive Krankheitsausbrüche",
    activeOutbreaksDetected: "aktive(r) Krankheitsausbruch/-ausbrüche erkannt",
    cases: "Fälle",
    lastReported: "Zuletzt gemeldet",
    travelWarnings: "Reisewarnungen",
    transportModeRisks: "Risiken nach Verkehrsmittel",
    activitySpecificRisks: "Aktivitätsspezifische Gesundheitsrisiken",
    riskCategoryVaccination: "Impfungen",
    riskCategoryMedication: "Medikamente",
    riskCategoryPrecaution: "Vorsichtsmaßnahmen",
    riskCategoryAdvisory: "Hinweise",
    riskCategoryWarning: "Warnungen",
    trend: "Trend",
    increasing: "Steigend",
    decreasing: "Sinkend",
    stable: "Stabil",
    casesReported: "Gemeldete Fälle",
    lastReport: "Letzter Bericht",
    mayAffectTravel: "Kann Reisen beeinträchtigen",
    validity: "Gültigkeit",
    years: "Jahre",
    minDaysBeforeDeparture: "Min. Tage vor Abreise",
    boosterRequired: "Auffrischung erforderlich",
    contraindications: "Kontraindikationen",
    relatedDiseases: "Verwandte Krankheiten",
    ageRestrictions: "Altersbeschränkungen",
    updated: "Aktualisiert",
    noHealthDataAvailable: "Keine Gesundheitsdaten verfügbar",
    failedToLoadHealthRecommendations:
      "Gesundheitsempfehlungen konnten nicht geladen werden",
    backToDashboard: "Zurück zum Dashboard",

    uploadDocument: "Dokument hochladen",
    documentType: "Dokumenttyp",
    uploadDate: "Upload-Datum",
    fileSize: "Dateigröße",
    downloadDocument: "Dokument herunterladen",
    documentName: "Dokumentname",

    // Chat
    chatWithTina: "Chat mit Tina",
    tinaHealthAssistant: "Tina - Deine Gesundheitsassistentin",
    askQuestion: "Frage stellen",
    askMeAnything: "Frag mich alles über deine Gesundheit",
    typeMessage: "Nachricht eingeben...",
    typeYourMessage: "Schreibe deine Nachricht...",
    sendMessage: "Senden",
    chatHistory: "Chat-Verlauf",
    newConversation: "Neue Unterhaltung",
    conversations: "Unterhaltungen",
    includeHealthContext: "Meine Gesundheitsdaten einbeziehen",
    healthContextEnabled:
      "Gesundheitskontext aktiviert - Tina kann deine Medikamente, Allergien und Vitalwerte sehen",
    healthContextDisabled:
      "Gesundheitskontext deaktiviert - Tina antwortet ohne deine Gesundheitsdaten",
    aiThinking: "Tina denkt nach...",
    noConversationsYet: "Noch keine Unterhaltungen",
    startNewConversation: "Starte eine neue Unterhaltung mit Tina",
    deleteConversation: "Unterhaltung löschen",
    conversationDeleted: "Unterhaltung gelöscht",
    aiUnavailable: "KI-Assistent nicht verfügbar",
    aiUnavailableMessage:
      "Der KI-Assistent ist derzeit nicht verfügbar. Bitte versuche es später erneut.",
    messagePlaceholder:
      "Frage nach Medikamenten, Symptomen, Reisegesundheit...",
    exampleQuestions: "Beispielfragen:",
    example1: "Welche Medikamente nehme ich aktuell?",
    example2: "Welche Impfungen brauche ich für die Reise?",
    example3: "Erkläre mir meine letzten Vitalwerte",

    // Profile
    personalInformation: "Persönliche Informationen",
    contactInformation: "Kontaktinformationen",
    emergencyContact: "Notfallkontakt",
    medicalInformation: "Medizinische Informationen",
    updateProfile: "Profil aktualisieren",
    changePassword: "Passwort ändern",
    phone: "Telefon",
    dateOfBirth: "Geburtsdatum",
    country: "Land",
    nationality: "Staatsangehörigkeit",
    currentPassword: "Aktuelles Passwort",
    newPassword: "Neues Passwort",
    confirmNewPassword: "Neues Passwort bestätigen",
    languageSettings: "Spracheinstellungen",
    notificationSettings: "Benachrichtigungseinstellungen",
    privacySettings: "Datenschutzeinstellungen",
    accountSettings: "Kontoeinstellungen",
    dataExport: "Datenexport",
    deleteAccount: "Konto löschen",

    // Account Deletion
    dangerZone: "Gefahrenzone",
    deleteAccountWarning:
      "Sobald Sie Ihr Konto löschen, gibt es kein Zurück mehr. Alle Ihre Gesundheitsdaten, Dokumente und persönlichen Informationen werden dauerhaft aus unseren Systemen gelöscht.",
    deleteAccountConfirmationPrompt:
      "Diese Aktion kann nicht rückgängig gemacht werden. Alle Ihre Daten werden dauerhaft gelöscht.",
    typeToConfirm: "Zur Bestätigung eingeben",
    enterYourPassword: "Geben Sie Ihr Passwort ein",
    deleteAccountWarningDetails:
      "Alle Ihre Gesundheitsakten, Dokumente, Reisepläne und Unterhaltungen werden dauerhaft gelöscht. Ein bezahltes Abo wird vorher gekündigt und nicht mehr abgebucht. Audit-Logs werden aus Compliance-Gründen aufbewahrt.",
    confirmDeletion: "Konto endgültig löschen",
    deleting: "Wird gelöscht...",
    accountDeletedSuccessfully:
      "Ihr Konto wurde erfolgreich gelöscht. Sie werden in Kürze abgemeldet.",
    deleteAccountError:
      "Konto konnte nicht gelöscht werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.",
    deleteAccountConfirmationError:
      'Bitte geben Sie "DELETE MY ACCOUNT" exakt ein, um zu bestätigen',
    invalidPassword: "Ungültiges Passwort. Bitte versuchen Sie es erneut.",
    passwordRequired: "Passwort erforderlich",
    warning: "Warnung",

    // Emergency Profile
    emergencyProfile: "Notfallprofil",
    myEmergencyProfile: "Mein Notfallprofil",
    emergencyData: "Notfalldaten",
    completenessScore: "Vollständigkeit",
    dataQuality: "Datenqualität",
    dataQualityGood: "Gut - Alle kritischen Daten vollständig",
    dataQualityFair: "Ausreichend - Einige Daten fehlen",
    dataQualityPoor: "Mangelhaft - Kritische Daten fehlen",
    criticalData: "Kritische Daten",
    priority1Data: "Priorität 1: Kritisch",
    priority2Data: "Priorität 2: Wichtig",
    priority3Data: "Priorität 3: Hilfreich",
    missingCriticalData: "Fehlende kritische Daten",
    quickAdd: "Schnell hinzufügen",
    editEmergencyData: "Notfalldaten bearbeiten",
    shareEmergencyAccess: "Notfallzugriff teilen",
    accessDuration: "Zugriffsdauer",
    duration15min: "15 Minuten",
    duration1hour: "1 Stunde",
    duration6hours: "6 Stunden",
    duration24hours: "24 Stunden",
    durationCustom: "Benutzerdefinierte Dauer",
    dataScope: "Datenumfang",
    selectDataToShare: "Zu teilende Daten auswählen",
    emergencyQRCode: "Notfall-QR-Code",
    refreshQRCode: "QR-Code aktualisieren",
    downloadQRCode: "QR-Code herunterladen",
    printQRCode: "QR-Code drucken",
    copyAccessLink: "Zugriffslink kopieren",
    qrCodeExpiry: "QR-Code Ablauf",
    addEmergencyContact: "Notfallkontakt hinzufügen",
    primaryContact: "Hauptkontakt",
    secondaryContact: "Zweitkontakt",
    tertiaryContact: "Drittkontakt",
    contactName: "Kontaktname",
    relationship: "Beziehung",
    phoneNumber: "Telefonnummer",
    notifyOnAccess: "Bei Notfallzugriff benachrichtigen",
    availabilityNotes: "Erreichbarkeitshinweise",
    reorderContacts: "Kontakte neu ordnen",
    devices: "Medizinische Geräte",
    allergySubstance: "Allergen",
    allergySeverity: "Schweregrad",
    allergyReaction: "Reaktion",
    conditionName: "Erkrankungsname",
    conditionStatus: "Status",
    deviceType: "Gerätetyp",
    deviceUDI: "Geräte-UDI",
    verifiedDate: "Verifizierungsdatum",
    outdatedWarning: "Diese Daten sind veraltet und müssen überprüft werden",
    incompleteWarning: "Ihr Notfallprofil ist unvollständig",
    updateRequired: "Aktualisierung erforderlich",
    dataVerifiedRecently: "Daten kürzlich verifiziert",

    add: "Hinzufügen",
    update: "Aktualisieren",
    remove: "Entfernen",
    search: "Suchen",
    filter: "Filtern",
    sort: "Sortieren",
    export: "Exportieren",
    import: "Importieren",
    refresh: "Aktualisieren",
    active: "Aktiv",
    inactive: "Inaktiv",
    pending: "Ausstehend",
    completed: "Abgeschlossen",
    cancelled: "Abgebrochen",
    savedSuccessfully: "Erfolgreich gespeichert",
    deletedSuccessfully: "Erfolgreich gelöscht",
    errorOccurred: "Ein Fehler ist aufgetreten",
    confirmDelete: "Löschung bestätigen",
    areYouSure: "Sind Sie sicher?",
    cannotBeUndone: "Diese Aktion kann nicht rückgängig gemacht werden",
    healthStatusOptimal: "Gesundheitsstatus: Optimal",
    allDataCurrent: "Alle Ihre Gesundheitsdaten sind aktuell und vollständig.",
    pulse: "Puls",
    normal: "Normal",

    // Vital Signs
    vitalSigns: "Vitaldaten",
    systolic: "Systolisch",
    diastolic: "Diastolisch",
    restingHeartRate: "Ruhepuls",
    bpm: "bpm",
    mmHg: "mmHg",
    lowBloodPressure: "Niedriger Blutdruck",
    elevated: "Erhöht",
    hypertensionGrade1: "Hypertonie Grad 1",
    hypertensionGrade2: "Hypertonie Grad 2",
    hypertensionGrade3: "Hypertonie Grad 3",
    bradycardia: "Bradykardie",
    tachycardia: "Tachykardie",
    systolicBpError:
      "Systolischer Blutdruck muss zwischen 70 und 250 mmHg liegen",
    diastolicBpError:
      "Diastolischer Blutdruck muss zwischen 40 und 150 mmHg liegen",
    heartRateError: "Ruhepuls muss zwischen 30 und 220 bpm liegen",
    systolicNormal: "Systolisch Normal: 120",
    systolicHigh: "Systolisch Hoch: 140",
    diastolicNormal: "Diastolisch Normal: 80",
    systolicBloodPressure: "Systolischer Blutdruck",
    diastolicBloodPressure: "Diastolischer Blutdruck",
    noVitalSignsData: "Keine Vitalzeichen-Daten verfügbar",

    // Wellness & Befinden
    wellbeing: "Befinden",
    physicalWellbeing: "Körperliches Befinden",
    physicalWellness: "Körperliches Befinden",
    emotionalWellbeing: "Emotionales Befinden",
    emotionalWellness: "Emotionales Befinden",
    howDoYouFeelPhysically: "Wie fühlst du dich körperlich?",
    howDoYouFeelEmotionally: "Wie fühlst du dich emotional?",
    currentStatus: "Aktuell",
    lastUpdate: "Letzte Aktualisierung",
    trendChart: "Verlaufsdiagramm",
    trendHistory: "Verlauf der letzten 365 Tage",
    noTrendData: "Keine Verlaufsdaten verfügbar",
    noTrendDataDesc:
      "Tragen Sie regelmäßig Ihr Befinden ein, um Ihre Entwicklung zu verfolgen",
    noWellbeingData: "Keine Befindlichkeitsdaten verfügbar",
    saveFirstWellbeingEntry:
      "Speichern Sie Ihre erste Befindlichkeitsbewertung, um Trends zu sehen",
    wellnessDataPoints: "Datenpunkte",
    wellnessScore: "Befinden (1-5)",
    average: "Durchschnitt",
    good: "Gut",
    low: "Niedrig",
    // Befinden-Stufen (1-5 Skala)
    veryPoor: "Sehr schlecht",
    poor: "Schlecht",
    fair: "Mittelmäßig",
    wellGood: "Gut",
    excellent: "Sehr gut",
    // Körperliche Befinden Labels
    physicalVeryPoor: "Sehr schlecht",
    physicalPoor: "Schlecht",
    physicalFair: "Mittelmäßig",
    physicalGood: "Gut",
    physicalExcellent: "Sehr gut",
    // Emotionale Befinden Labels
    emotionalVeryPoor: "Sehr schlecht gelaunt",
    emotionalPoor: "Traurig",
    emotionalFair: "Ausgeglichen",
    emotionalGood: "Gut gelaunt",
    emotionalExcellent: "Sehr gut gelaunt",

    // Lab Results
    labResults: "Laborwerte",
    currentValues: "Aktuelle Werte",
    allTests: "Alle Tests",
    laboratoryTest: "Labortest",
    evaluation: "Bewertung",
    value: "Wert",
    unit: "Einheit",
    performingDoctor: "Durchführender Arzt/Labor (optional)",
    pdfReport: "PDF Report",
    noLabResultsDocumented: "Keine Laborwerte dokumentiert",
    addNewLabResult: "Neuen Laborwert hinzufügen",
    showChart: "Diagramm anzeigen",
    hideChart: "Diagramm ausblenden",
    noWeightDataAvailable: "Keine Gewichtsdaten verfügbar",
    firstEntriesWillBeShown: "Erste Einträge werden hier angezeigt",
    weightKg: "Gewicht (kg)",
    current: "Aktuell",
    dataPoints: "Datenpunkte",
    fhirCompatibleData: "FHIR-kompatible Daten",
    recorded: "Aufgezeichnet",
    bodyFat: "Körperfett",
    bodyFatPercent: "Körperfett (%)",
    raised: "Erhöht",
    decreased: "Erniedrigt",
    critical: "Kritisch",
    lastMeasurement: "Letzte Messung",
    vitalSignsHistory: "Verlauf der letzten 365 Tage",

    addNewDocument: "Neues Dokument",
    addDocumentDesc: "Gesundheitsdokument hinzufügen",
    addMedicationDesc: "Neue Medikation verwalten",
    planTravel: "Reise planen",
    planTravelDesc: "Neue Reise mit Gesundheitscheck",
    addHealthDataDesc: "Vitaldaten erfassen",
    upcomingReminders: "Anstehende Termine & Erinnerungen",
    highPriority: "Hoch",
    mediumPriority: "Mittel",
    lowPriority: "Niedrig",
    vaccination: "Impfung",
    daysAgo: "Tage her",
    weeksAgo: "Wochen her",
    inDays: "in Tagen",
    immunizations: "Impfungen",
    manageImmunizations: "Verwalten Sie Ihre Immunisierungsaufzeichnungen",
    newImmunization: "Neue Impfung",
    editImmunization: "Impfung bearbeiten",
    deleteImmunization: "Impfung löschen",
    vaccineName: "Impfstoff Name",
    vaccineCode: "Impfstoff Code",
    manufacturer: "Hersteller",
    lotNumber: "Lot-Nummer",
    doseNumber: "Dosis Nummer",
    totalDoses: "Gesamt Dosen",
    administeredDate: "Verabreichungsdatum",
    expirationDate: "Ablaufdatum",
    nextDueDate: "Nächste Fälligkeit",
    location: "Verabreichungsort",
    administrator: "Verabreicht von",
    requiredForCountries: "Erforderlich für Länder",
    uploadCertificate: "Zertifikat hochladen",
    downloadCertificate: "Zertifikat herunterladen",
    certificateUploaded: "Zertifikat erfolgreich hochgeladen",
    noCertificateFound: "Kein Zertifikat für diese Impfung gefunden",
    certificateUploadFailed: "Zertifikat-Upload fehlgeschlagen",
    fhirData: "FHIR Daten",
    internationalCertificate: "Internationales Zertifikat",
    internationalVaccinationCertificate: "Internationaler Impfausweis",
    passportNumber: "Impfpass-Nummer",
    healthPassDocumentId: "HealthPass Dokument-ID",
    certificateNumber: "Zertifikatsnummer",
    issuerId: "Aussteller-ID",
    publicKeyFingerprint: "Öffentlicher Schlüssel-Fingerabdruck",
    signatureTimestamp: "Signatur-Zeitstempel",
    issuedBy: "Ausgestellt von",
    issuedDate: "Ausstellungsdatum",
    validFrom: "Gültig ab",
    patientInformation: "Patienteninformationen",
    certificateInformation: "Zertifikat-Informationen",
    digitalSignature: "Digitale Signatur",
    qrCodeValidation: "QR-Code zur Validierung",
    printCertificate: "Zertifikat drucken",
    copyCertificateData: "Zertifikat-Daten kopieren",
    deleteConfirmation:
      "Sind Sie sicher, dass Sie diese Impfung löschen möchten?",
    noImmunizationsFound: "Keine Impfungen gefunden",
    addFirstImmunization: "Fügen Sie Ihre erste Immunisierung hinzu",
    certificateValidUntil: "Zertifikat gültig bis",
    dosesCompleted: "Dosen abgeschlossen",
    of: "von",
    loginSuccessful: "Willkommen zurück",
    loginFailed: "Anmeldung fehlgeschlagen",
    loginInvalidOrPaused:
      "E-Mail oder Passwort falsch. Nach 10 Fehlversuchen ist die Anmeldung für dieses Konto bis zu 15 Minuten gesperrt.",
    registrationSuccessful: "Willkommen bei HealthPass",
    registrationFailed: "Registrierung fehlgeschlagen",
    logoutSuccessful: "Erfolgreich abgemeldet",
    sessionExpired:
      "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
    networkError:
      "Netzwerkfehler - Bitte überprüfen Sie Ihre Internetverbindung",
    accessDenied: "Zugriff verweigert - Unzureichende Berechtigung",
    validationError: "Validierungsfehler",
    tooManyRequests: "Zu viele Anfragen - Bitte versuchen Sie es später erneut",
    serverError: "Serverfehler - Bitte versuchen Sie es später erneut",
    labResultsSaved: "Laborwerte erfolgreich gespeichert",

    // Medication System
    medicationsPage: "Medikamente",
    medicationItem: "Medikament",
    medicationPlan: "Medikationsplan",
    addNewMedication: "Medikament hinzufügen",
    editExistingMedication: "Medikament bearbeiten",
    deleteMedicationItem: "Medikament löschen",
    medicationName: "Medikamentenname",
    genericName: "Wirkstoff (INN)",
    medicationDosage: "Dosierung",
    dosageForm: "Darreichungsform",
    medicationStrength: "Wirkstärke",
    medicationFrequency: "Häufigkeit",
    timingInstructions: "Einnahmezeitpunkte",
    mealTiming: "Mahlzeitenbezug",
    medicationStartDate: "Startdatum",
    medicationEndDate: "Enddatum",
    medicationPrescribedBy: "Verschrieben von",

    // Extended Medication Fields (FHIR R5)
    tradeName: "Handelsname",
    activeIngredient: "Wirkstoff",
    manufacturerName: "Hersteller",
    internationalCode: "Internationaler Code",
    codeSystem: "Code-System",
    packageImage: "Packungsbild",
    medicationImage: "Medikamentenbild/Tablettenfoto",
    uploadPackageImage: "Packungsbild hochladen",
    uploadMedicationImage: "Medikamentenbild hochladen",
    takePhoto: "Foto aufnehmen",
    removeImage: "Bild entfernen",
    imageUploadSuccess: "Bild erfolgreich hochgeladen",
    imageUploadFailed: "Bild-Upload fehlgeschlagen",
    imageDeleteSuccess: "Bild erfolgreich gelöscht",
    imageDeleteFailed: "Löschen des Bildes fehlgeschlagen",
    optional: "Optional",
    imagesSection: "Bilder",
    packagePhoto: "Packungsfoto",
    pillPhoto: "Tablettenfoto",
    gtinCode: "GTIN-Code",
    ndcCode: "NDC-Code (USA)",
    handelsname: "Handelsname",
    wirkstoff: "Wirkstoff",
    hersteller: "Hersteller",
    pharmaCode: "Pharmazeutischer Code",
    basicInformation: "Grundinformationen",
    fhirExtendedFields: "Erweiterte FHIR R5 Felder",
    medicationImages: "Medikamentenbilder",
    medicationInformation: "Medikamenteninformationen",
    dosageInformation: "Dosierung",
    prescription: "Rezept",
    note: "Hinweis",
    selfSignedInfo:
      'Wenn Sie das Medikament selbst eingeben, wird es automatisch als "Selbst signiert" markiert und mit Ihrem Reisenden-Konto verknüpft.',
    medicationImagesDescription:
      "Laden Sie Bilder der Medikamentenpackung und der Tabletten hoch (optional)",
    medicationImagesHint:
      "Hochwertige Bilder helfen bei der Identifizierung und können für Reisedokumentationen nützlich sein.",
    saveChanges: "Änderungen speichern",
    saveMedication: "Medikament speichern",
    choosePackageImage: "Packungsbild wählen",
    chooseMedicationImage: "Medikamentenbild wählen",
    category: "Kategorie",
    dosage: "Dosierung",
    pharmacy: "Apotheke",
    editMedication: "Medikament bearbeiten",
    updateMedication: "Medikament aktualisieren",
    uploadingImages: "Bilder werden hochgeladen...",
    saving: "Wird gespeichert...",
    medicationPharmacy: "Apotheke",
    medicationInstructions: "Anweisungen",
    medicationSideEffects: "Nebenwirkungen",
    medicationInteractions: "Wechselwirkungen",
    eanCode: "EAN-Code",
    pznCode: "PZN-Nummer",
    atcCode: "ATC-Code",
    tablet: "Tablette",
    capsule: "Kapsel",
    syrup: "Sirup",
    injection: "Injektion",
    cream: "Creme",
    ointment: "Salbe",
    drops: "Tropfen",
    spray: "Spray",
    patch: "Pflaster",
    suppository: "Zäpfchen",
    powder: "Pulver",
    other: "Sonstiges",
    inhaler: "Inhaliergerät",
    gel: "Gel",
    lotion: "Lotion",
    solution: "Lösung",
    beforeMeal: "Vor dem Essen",
    afterMeal: "Nach dem Essen",
    withMeal: "Zum Essen",
    independent: "Unabhängig von Mahlzeiten",
    onEmptyStomach: "Auf nüchternen Magen",
    medicationRegular: "Dauermedikation",
    medicationTravel: "Reisemedikament",
    medicationEmergency: "Notfallmedikament",
    medicationProphylaxis: "Prophylaxe",
    regular: "Regelmäßig",
    emergency: "Notfall",
    travelMedication: "Reisemedikament",
    prophylaxis: "Prophylaxe",
    cruise: "Kreuzfahrt",
    trekking: "Trekking",
    diving: "Tauchen",
    tropical: "Tropen",
    altitude: "Höhenlagen",
    business: "Geschäftsreise",
    adventure: "Abenteuerreise",
    urban: "Städtereise",
    recordIntake: "Einnahme dokumentieren",
    intakeHistory: "Einnahmehistorie",
    adherence: "Therapietreue",
    doseTaken: "Eingenommene Dosis",
    intakeNotes: "Einnahme-Notizen",
    timeline: "Zeitverlauf",
    lastTaken: "Zuletzt eingenommen",
    nextDue: "Nächste Einnahme",
    internationalMedicationPlan: "Internationaler Medikationsplan",
    generatePlan: "Plan erstellen",
    uploadGermanPlan: "Bundeseinheitlichen Medikationsplan hochladen",
    downloadPlan: "Plan herunterladen",
    printPlan: "Plan drucken",
    qrCodePlan: "QR-Code für Plan",
    scanBarcode: "Barcode scannen",
    travelRecommendations: "Reiseempfehlungen",
    supplementaryMedications: "Ergänzende Medikamente",
    interactionWarnings: "Wechselwirkungswarnungen",
    implementRecommendation: "Empfehlung umsetzen",
    medicationAdded: "Medikament erfolgreich hinzugefügt",
    medicationUpdated: "Medikament erfolgreich aktualisiert",
    medicationDeleted: "Medikament erfolgreich gelöscht",
    intakeRecorded: "Einnahme erfolgreich dokumentiert",
    planGenerated: "Medikationsplan erfolgreich erstellt",
    imageUploaded: "Packungsbild erfolgreich hochgeladen",

    // Avatar Upload
    profilePicture: "Profilbild",
    managePersonalDataSettings:
      "Verwalten Sie Ihre persönlichen Daten und Einstellungen",
    uploadPhoto: "Foto hochladen",
    deletePhoto: "Foto löschen",
    invalidFileType: "Ungültiger Dateityp. Nur JPEG, PNG, WebP, GIF erlaubt",
    fileTooLarge: "Datei zu groß. Maximale Größe ist 5MB",
    maxFileSize:
      "Max. Dateigröße: 5MB. Unterstützte Formate: JPEG, PNG, WebP, GIF",
    avatarUploadedSuccessfully: "Profilbild erfolgreich hochgeladen",
    avatarUploadFailed: "Fehler beim Hochladen des Profilbilds",
    confirmDeleteAvatar: "Möchten Sie Ihr Profilbild wirklich löschen?",
    avatarDeletedSuccessfully: "Profilbild erfolgreich gelöscht",
    avatarDeleteFailed: "Fehler beim Löschen des Profilbilds",
    cropImage: "Bild zuschneiden",
    rotate: "Drehen",

    // Profile Page - Form Fields
    notSpecified: "Nicht angegeben",
    day: "Tag",
    month: "Monat",
    year: "Jahr",
    yearsOld: "Jahre alt",

    // Date/Month names
    january: "Januar",
    february: "Februar",
    march: "März",
    april: "April",
    may: "Mai",
    june: "Juni",
    july: "Juli",
    august: "August",
    september: "September",
    october: "Oktober",
    november: "November",
    december: "Dezember",

    // Address Fields
    address: "Adresse",
    street: "Straße",
    houseNumber: "Hausnummer",
    addressSupplement: "Adresszusatz",
    postalCode: "PLZ",
    city: "Ort",
    state: "Bundesland/Region",
    streetName: "Straßenname",
    houseNo: "Nr.",
    apartmentFloorEtc: "Apartment, Etage, etc. (optional)",
    postalCodePlaceholder: "Postleitzahl",
    cityPlaceholder: "Stadt/Ort",

    // Nationalities
    nationality_german: "Deutsch",
    nationality_austrian: "Österreichisch",
    nationality_swiss: "Schweizerisch",
    nationality_french: "Französisch",
    nationality_italian: "Italienisch",
    nationality_spanish: "Spanisch",
    nationality_british: "Britisch",
    nationality_american: "Amerikanisch",
    nationality_canadian: "Kanadisch",
    nationality_dutch: "Niederländisch",
    nationality_belgian: "Belgisch",
    nationality_polish: "Polnisch",
    nationality_turkish: "Türkisch",
    nationality_russian: "Russisch",
    nationality_other: "Andere",

    // Profile Update Messages
    profileUpdatedSuccessfully: "Profil erfolgreich aktualisiert!",
    profileUpdateMaybeSuccessful:
      "Profil möglicherweise aktualisiert - bitte prüfen Sie die Änderungen.",
    profileUpdatedButDataNotLoaded:
      "Profil aktualisiert, aber Daten wurden nicht korrekt geladen. Bitte laden Sie die Seite neu.",
    correctFollowingInputs: "Bitte korrigieren Sie folgende Eingaben:",
    inputError: "Eingabefehler",
    invalidInputs:
      "Ungültige Eingaben. Bitte überprüfen Sie alle Felder und versuchen Sie es erneut.",
    unauthorizedPleaseLogin:
      "Nicht autorisiert. Bitte melden Sie sich erneut an.",
    noPermission: "Keine Berechtigung für diese Aktion.",
    userNotFoundPleaseLogin:
      "Benutzer nicht gefunden. Bitte melden Sie sich erneut an.",
    serverErrorTryLater:
      "Serverfehler. Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.",
    unknownError: "Unbekannter Fehler",
    noServerConnection:
      "Keine Verbindung zum Server möglich. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.",
    unexpectedError:
      "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.",

    apply: "Anwenden",
    capture: "Aufnehmen",
    retake: "Erneut aufnehmen",
    usePhoto: "Foto verwenden",
    switchCamera: "Kamera wechseln",
    cameraAccessDenied: "Kamerazugriff verweigert",
    photoCaptureFailed:
      "Das Foto konnte nicht verwendet werden. Bitte erneut versuchen.",
    checkCameraPermissions:
      "Bitte prüfen Sie die Kameraberechtigungen in Ihren Browsereinstellungen",

    // Version History
    version: "Version",
    versionHistory: "Versionshistorie",
    versionHistoryDescription: "Alle Versionen und Änderungen von HealthPass",
    currentVersion: "Aktuelle Version",
    newFeatures: "Neue Features",
    changes: "Änderungen",
    bugFixes: "Fehlerbehebungen",
    versionHistoryNote:
      "Diese Versionshistorie wird automatisch aus den Git-Commits und Release-Notes generiert.",
    back: "Zurück",
    subscription: "Abonnement",

    faqTitle: "Häufig gestellte Fragen",
    faqSubtitle: "Alles, was Sie über HealthPass wissen müssen",
    faqQ1: "Was ist HealthPass?",
    faqA1:
      "HealthPass ist eine digitale Gesundheitsplattform, die Ihre medizinischen Notfallinformationen speichert und weltweit per QR-Code zugänglich macht. Sie wurde speziell für Reisende entwickelt, die im Notfall sofortigen Zugriff auf kritische Gesundheitsdaten benötigen.",
    faqQ2: "Wie funktioniert HealthPass?",
    faqA2:
      "Erstellen Sie einfach ein Konto, laden Sie Ihre medizinischen Informationen hoch (Allergien, Blutgruppe, Medikamente, Notfallkontakte) und generieren Sie Ihren persönlichen QR-Code. Ersthelfer und medizinisches Fachpersonal können diesen Code scannen, um sofortigen Zugriff auf Ihre wichtigsten Gesundheitsinformationen zu erhalten, selbst wenn Sie bewusstlos sind.",
    faqQ3: "Ist HealthPass DSGVO-konform?",
    faqA3:
      "Ja. HealthPass ist nach der DSGVO gebaut: deine Daten werden verschlüsselt übertragen, du entscheidest, wer was sehen darf, und jedes Mal, wenn jemand deine Notfallkarte oder einen geteilten Link öffnet, wird das festgehalten und du kannst es sehen. Unsere Server betreibt Hetzner für uns in den USA auf Grundlage der EU-Standardvertragsklauseln — was das bedeutet, steht genau in der Datenschutzerklärung. Wir geben deine Daten nie ohne deine ausdrückliche Einwilligung an Dritte weiter.",
    faqQ4: "Was kostet HealthPass?",
    faqA4:
      "Deine Karte und der Notfallzugang sind kostenlos. PLUS kostet 9 und PREMIUM 15 im Monat, in Euro oder US-Dollar je nach Land, und beide beginnen mit 14 Tagen kostenlosem Test. Für eine einzelne Reise kostet PLUS 25 und PREMIUM 39, einmal bezahlt für 90 Tage. Ersthelfer zahlen nie, um deine Notfalldaten zu lesen.",
    faqQ5: "Kann HealthPass Leben retten?",
    faqA5:
      "Ja. Bei medizinischen Notfällen im Ausland können Sprachbarrieren und fehlende Krankengeschichte lebensbedrohlich sein. HealthPass bietet sofortigen Zugriff auf kritische Informationen wie Allergien, Blutgruppe und aktuelle Medikamente und ermöglicht so schnellere und sicherere Behandlungsentscheidungen.",
    faqQ6: "Wie funktioniert der QR-Code-Zugriff?",
    faqA6:
      "Ihr HealthPass-QR-Code kann ausgedruckt, auf dem Sperrbildschirm Ihres Telefons hinzugefügt oder als Armband getragen werden. Beim Scannen öffnet sich eine sichere Notfallansicht mit Ihren kritischen Gesundheitsinformationen. Für medizinisches Personal ist keine App-Installation erforderlich.",
    faqQ7: "Was sind häufige Reise-Anwendungsfälle?",
    faqA7:
      "HealthPass ist unverzichtbar für:\n• Abenteuerreisende in abgelegenen Gebieten\n• Senioren auf internationalen Reisen\n• Menschen mit chronischen Erkrankungen oder Allergien\n• Familien mit Kindern auf Reisen\n• Alleinreisende in nicht-deutschsprachigen Ländern\n• Personen, die regelmäßig Medikamente benötigen",
    faqQ8: "Wie sicher sind meine Daten?",
    faqA8:
      "Ihre Daten werden bei der Übertragung und im Ruhezustand verschlüsselt, die Anmeldung läuft über einen eigenen Identitätsserver mit kurzlebigen Tokens, und jeder Zugriff auf eine Gesundheitsakte wird protokolliert — das Protokoll können Sie anfordern. Unsere Server werden für uns von Hetzner in den USA betrieben, auf Grundlage der EU-Standardvertragsklauseln. Die Seite „Security“ beschreibt das im Einzelnen.",
    faqQ9: "Welche Sprachen werden unterstützt?",
    faqA9:
      "HealthPass ist auf Englisch, Deutsch, Französisch, Spanisch und Portugiesisch verfügbar. Ihre Notfall-Gesundheitsinformationen können in mehreren Sprachen gleichzeitig angezeigt werden, sodass medizinisches Personal weltweit sie lesen kann.",
    faqQ10: "Wie greifen Rettungsdienste auf meine Daten zu?",
    faqA10:
      "Rettungsdienste scannen Ihren QR-Code mit jeder Smartphone-Kamera. Sie sehen sofort Ihre kritischen Informationen, ohne sich anmelden oder eine App herunterladen zu müssen. Der Zugriff wird zu Ihrer Sicherheit protokolliert, und Sie können Notfallzugriffscodes jederzeit widerrufen.",
    faqStillHaveQuestions: "Haben Sie noch Fragen?",
    faqContactUs: "Unser Support-Team hilft Ihnen rund um die Uhr",
    contactSupport: "Support kontaktieren",

    // Medical Incidents & Intolerances
    medicalIncidents: "Medizinische Vorfälle",
    addNewIncident: "Neuen Vorfall hinzufügen",
    icdCodeOptional: "ICD-Code (optional)",
    diagnosisDesignation: "Diagnose/Bezeichnung",
    diagnosisPlaceholder: "z.B. Herzinfarkt, Fraktur, Operation...",
    incidentDetailsPlaceholder: "Weitere Details zum Vorfall...",
    addIncident: "Vorfall hinzufügen",
    noMedicalIncidentsDocumented: "Keine medizinischen Vorfälle dokumentiert",
    intolerances: "Unverträglichkeiten",
    foodIntolerances: "Lebensmittelunverträglichkeiten",
    foodIntolerancesPlaceholder: "z.B. Laktose, Gluten",
    specificDiseases: "Spezifische Erkrankungen",
    coagulationDisorder: "Gerinnungsstörung",
    heartAttack: "Herzinfarkt",
    heartAttackDatePlaceholder: "Datum des Herzinfarkts",
    stroke: "Schlaganfall",
    strokeDatePlaceholder: "Datum des Schlaganfalls",

    // Extended Health Components
    allergiesAndIntolerances: "Allergien & Unverträglichkeiten",
    noAllergiesDocumented: "Keine Allergien dokumentiert",
    medicalConditions: "Medizinische Zustände",
    noMedicalConditionsDocumented: "Keine medizinischen Zustände dokumentiert",
    medicalProcedures: "Medizinische Eingriffe",
    noMedicalProceduresDocumented: "Keine medizinischen Eingriffe dokumentiert",
    treatmentHistory: "Behandlungshistorie",
    noTreatmentHistoryDocumented: "Keine Behandlungshistorie dokumentiert",

    // Vital Signs
    allFieldsRequired: "Alle Felder sind erforderlich",
    systolicMustBeHigher:
      "Systolischer Wert muss höher sein als diastolischer Wert",
    recordFirstMeasurement: "Erste Messung erfassen",
    loadingHistory: "Lade Verlauf...",
    noHistoryData: "Keine Verlaufsdaten verfügbar",
    firstMeasurementsWillAppear: "Erste Messungen werden hier angezeigt",

    // Unit Inputs
    enterValue: "Wert eingeben",

    // Gender
    gender: "Geschlecht",
    male: "Männlich",
    female: "Weiblich",
    preferNotToSay: "Keine Angabe",

    // Emergency System
    emergencyQRDescription:
      "Teilen Sie Ihre medizinischen Daten mit Rettungskräften",
    noActiveQRCode: "Kein aktiver QR-Code",
    generateQRCodeDescription:
      "Generieren Sie einen QR-Code zum Teilen Ihrer Notfalldaten",
    qrCodeDuration: "QR-Code Gültigkeit",
    generateQRCode: "QR-Code generieren",
    regenerate: "Neu generieren",
    generating: "Wird generiert...",
    qrCodeGenerated: "QR-Code erfolgreich generiert",
    qrCodeGenerationFailed: "QR-Code Generierung fehlgeschlagen",
    timeRemaining: "Verbleibende Zeit",
    accessCount: "Zugriffszähler",
    accessUrl: "Zugriffs-URL",
    urlCopied: "URL in Zwischenablage kopiert",
    copyFailed: "Kopieren fehlgeschlagen",
    copied: "Kopiert",
    copy: "Kopieren",
    downloadQR: "QR-Code herunterladen",
    qrCodeDownloaded: "QR-Code heruntergeladen",
    shareEmergencyData: "Notfalldaten teilen",
    shareDialogDescription:
      "Wählen Sie, wie Sie Ihre Notfalldaten teilen möchten",
    expired: "Abgelaufen",
    pleaseRegenerate: "Bitte neu generieren",
    howItWorks: "Wie es funktioniert",
    emergencyQRHowItWorksText:
      "Rettungskräfte scannen diesen QR-Code mit einem Smartphone",
    share: "Teilen",
    emergencyDataAccess: "Notfalldaten-Zugriff",
    forEmergencyRespondersOnly: "Nur für Rettungskräfte",
    scanQROrEnterToken: "QR-Code scannen oder Token eingeben",
    qrScannerPlaceholder: "QR-Scanner",
    qrScannerInstructions: "Richten Sie die Kamera auf den QR-Code",
    orEnterTokenManually: "Oder Token manuell eingeben",
    enterAccessToken: "Zugriffstoken eingeben",
    continue: "Weiter",
    responderInformation: "Rettungskraft-Informationen",
    responderInformationDescription: "Bitte geben Sie Ihre Informationen an",
    fullName: "Vollständiger Name",
    enterYourFullName: "Geben Sie Ihren vollständigen Namen ein",
    organization: "Organisation",
    hospitalOrganizationName: "Krankenhaus oder Organisation",
    professionalId: "Berufsausweis-Nummer",
    licenseBadgeNumber: "Lizenz- oder Ausweisnummer",
    yourEmail: "Ihre E-Mail-Adresse",
    yourPhone: "Ihre Telefonnummer",
    reasonForAccess: "Grund für Zugriff",
    briefDescriptionEmergency: "Kurze Beschreibung",
    emergencyAccessConsent: "Notfallzugriff-Einwilligung",
    emergencyAccessConsentText:
      "Ich bestätige, dass ich auf diese Daten in einer Notfallsituation zugreife",
    pleaseAcceptConsent: "Bitte akzeptieren Sie die Einwilligung",
    accessEmergencyData: "Auf Notfalldaten zugreifen",
    accessing: "Zugriff wird ausgeführt...",
    emergencyDataAccessGranted: "Notfalldaten-Zugriff gewährt",
    pleaseEnterToken: "Bitte geben Sie einen Zugriffstoken ein",
    pleaseProvideResponderInfo: "Bitte geben Sie Ihre Informationen an",
    accessTokenInvalid: "Ungültiger oder abgelaufener Zugriffstoken",
    criticalAllergies: "KRITISCHE ALLERGIEN",
    emergencyContacts: "Notfallkontakte",
    noEmergencyContactsAvailable: "Keine Notfallkontakte verfügbar",
    age: "Alter",
    name: "Name",
    unknown: "Unbekannt",
    disclaimer: "Haftungsausschluss",
    emergencyDataDisclaimerText:
      "Diese Informationen werden vom Patienten bereitgestellt",

    // PWA & Offline
    installApp: "HealthPass installieren",
    installAppDescription:
      "Installiere HealthPass für schnellen Zugriff und Offline-Nutzung",
    install: "Installieren",
    later: "Später",
    connectionRestored: "Verbindung wiederhergestellt",
    connectionRestoredSyncing:
      "Verbindung wiederhergestellt - Daten werden synchronisiert",
    offlineMode: "Offline-Modus",
    offlineModeDescription:
      "Offline-Modus - Bereits geladene Daten sind verfügbar",
    slowConnection: "Langsame Verbindung",
    slowConnectionDescription:
      "Langsame Verbindung - Einige Funktionen können verzögert sein",

    // Sync Status
    never: "Nie",
    justNow: "Gerade eben",
    pendingSync: "ausstehend",
    lastSync: "Letzte Synchronisation",

    // Trip Wizard
    tripWizardTitle: "Neue Reise planen",
    tripWizardStep1: "Basis",
    tripWizardStep2: "Details",
    tripWizardStep3: "Etappen",
    tripWizardStep4: "Gesundheits-Check",
    tripWizardStep5: "Überprüfen",
    tripWizardNext: "Weiter",
    tripWizardPrevious: "Zurück",
    tripWizardSave: "Reise erstellen",
    tripWizardSaving: "Wird erstellt...",
    tripWizardStep1Title: "Basis-Informationen",
    tripWizardStep2Title: "Reise-Details",
    tripWizardStep3Title: "Etappen / Segmente",
    tripWizardStep4Title: "Gesundheits-Check",
    tripWizardStep5Title: "Überprüfen & Speichern",
    tripWizardStep1Desc: "Titel, Ziel und Reisedaten",
    tripWizardStep2Desc: "Zweck, Typ, Veranstalter & Begleiter",
    tripWizardStep3Desc: "Flüge, Hotels und Aktivitäten hinzufügen",
    tripWizardStep4Desc: "Gesundheitsrisiken und Impfanforderungen",
    tripWizardStep5Desc: "Alles überprüfen vor dem Erstellen",
    tripWizardReviewSummary: "Zusammenfassung",
    tripWizardDuration: "Dauer",
    tripWizardSegmentCount: "Etappen",
    tripWizardAddSegment: "Etappe hinzufügen",
    tripWizardSkipSegments: "Überspringen – später hinzufügen",
    tripHealthOptional:
      "Dieser Schritt ist optional. Sie können ohne ihn weiterplanen und Ihre Reise speichern.",
    tripHealthUpsellTitle: "Mehr Sicherheit für eine gesunde Reise",
    tripHealthUpsellIntro:
      "Der Gesundheits-Check für Reiseziele ist Teil von PLUS. Damit sind Sie besser vorbereitet auf {country}:",
    tripHealthBenefitVaccinations:
      "Pflicht- und empfohlene Impfungen für Ihr Reiseziel",
    tripHealthBenefitWarnings:
      "Malariarisiko und aktuelle Gesundheitswarnungen für Ihr Reiseziel",
    tripHealthBenefitTracking: "Impfverfolgung für Ihren eigenen Impfstatus",
    tripHealthBenefitDocuments:
      "Dokumentenablage für Nachweise und Reiseunterlagen",
    tripHealthBenefitOffline:
      "Offline-Zugriff auf Ihre Gesundheitsdaten unterwegs",
    tripHealthSingleTrip:
      "Planen Sie nur diese eine Reise? PLUS Single Trip ist eine Einmalzahlung für 90 Tage, ohne anschließendes Abonnement.",
    tripHealthUpgradeCta: "Auf PLUS upgraden",
    tripHealthSkipCta: "Ohne Gesundheits-Check fortfahren",
    tripHealthDraftKept:
      "Ihr Reiseentwurf bleibt erhalten. Kehren Sie zu „Neue Reise planen“ zurück, um dort weiterzumachen.",
    tripHealthEmpty:
      "Unsere Quellen nennen keine Impfungen oder Gesundheitswarnungen für {country}.",
    tripHealthLoadFailed:
      "Der Gesundheits-Check konnte gerade nicht geladen werden. Sie können fortfahren und ihn später auf der Reiseseite erneut prüfen.",

    // Trip Types (missing)
    tripTypeHiking: "Wandern",
    tripTypeCamping: "Camping",
    tripTypeRoadTrip: "Roadtrip",
    purposeScience: "Wissenschaft",

    // Companion Management
    inviteCompanion: "Begleiter einladen",
    companionEmail: "E-Mail des Begleiters",
    companionEmailPlaceholder: "email@beispiel.de",
    sendInvitation: "Einladung senden",
    companionInviteSent: "Einladung erfolgreich gesendet",
    companionInviteFailed: "Einladung konnte nicht gesendet werden",
    companionStatusPending: "Ausstehend",
    companionStatusAccepted: "Angenommen",
    companionStatusDeclined: "Abgelehnt",
    removeCompanion: "Entfernen",
    companionRemoved: "Begleiter entfernt",
    noCompanionsYet: "Noch keine Begleiter eingeladen",
    companionInviteDesc:
      "Laden Sie andere HealthPass-Nutzer zu dieser Reise ein",
    companionRequiresPLUS: "Begleiter einladen erfordert ein PLUS-Abonnement",

    // Segment Documents
    segmentDocuments: "Dokumente",
    uploadSegmentDocument: "Dokument hochladen",
    docCategoryTicket: "Ticket",
    docCategoryBooking: "Buchungsbestätigung",
    docCategoryVoucher: "Voucher",
    docCategoryInsurance: "Versicherung",
    docCategoryOther: "Sonstiges",
    documentUploaded: "Dokument erfolgreich hochgeladen",
    documentDeleted: "Dokument gelöscht",
    documentUploadFailed: "Hochladen fehlgeschlagen",
    maxFileSizeHint: "Max. 10 MB · PDF, JPEG, PNG",

    // Trip Map
    tripMap: "Reisekarte",
    tripMapLoading: "Karte wird geladen...",
    tripMapNoData: "Keine Etappenkoordinaten verfügbar",
    tripMapRequiresPREMIUM:
      "Interaktive Karte erfordert ein PREMIUM-Abonnement",

    // Subscription Gate
    featureRequiresPLUS: "Diese Funktion erfordert ein PLUS-Abonnement",
    featureRequiresPREMIUM: "Diese Funktion erfordert ein PREMIUM-Abonnement",
    upgradeToUnlock: "Upgrade zum Freischalten",
    upgradePlanLabel: "Plan upgraden",

    // Status override
    statusAutomatic: "Automatisch",
    statusManual: "Manuell",
    statusOverrideHint:
      "Status wird automatisch nach Reisedaten gesetzt. Manuelle Überschreibung möglich.",
    markAsManual: "Manuell setzen",

    // HealthPass Card
    healthpassCard: "HealthPass Card",
    cardWallet: "Karten-Wallet",
    cardNumber: "Kartennummer",
    validUntil: "Gültig bis",
    showFront: "Vorderseite",
    showBack: "Wallet anzeigen",
    downloadPDF: "PDF herunterladen",
    shareCard: "Karte teilen",
    generateQR: "QR-Code generieren",
    cardShares: "Geteilte Links",
    createShareLink: "Share-Link erstellen",
    shareExpiry: "Link läuft ab in",
    shareExpiry24h: "24 Stunden",
    shareExpiry72h: "72 Stunden",
    shareExpiry7d: "7 Tagen",
    sharePassword: "Passwort (optional)",
    revokeShare: "Zugang widerrufen",
    shareRevoked: "Link widerrufen",
    shareCreated: "Share-Link erstellt",
    shareCopied: "Link kopiert",
    noSharesYet: "Keine aktiven Share-Links",
    shareAccessCount: "Zugegriffen",
    shareTimes: "Mal",
    walletEmergency: "Notfall-Gesundheitsakte",
    walletMedications: "Medikationsplan",
    walletVaccinations: "Impfausweis",
    walletCheckin: "Check-in Dokumente",
    walletAppointments: "Termine",
    walletInsurance: "Versicherungen",
    walletTravelDocs: "Reisedokumente",
    walletLabResults: "Laborbefunde",
    upgradeForWalletItem: "Upgrade auf {tier} erforderlich",
    cardLoading: "Karte wird geladen...",
    cardError: "Karte konnte nicht geladen werden",
    printPreview: "Druckvorschau",
    printFormatA4: "A4",
    printFormatA5: "A5",
    printFormatLetter: "US Letter",
    printFormatCard: "Kreditkartenformat",
    // Traveller navigation (UX_CONCEPT.md §4)
    navHome: "Start",
    navTrips: "Reisen",
    navCardWallet: "Karte & Wallet",
    navCard: "Karte",
    navHealth: "Gesundheit",
    navMe: "Konto",
    navWallet: "Wallet",
    navShares: "Geteilte Links",
    navAccessRequests: "Zugriffsanfragen",
    navCheckIn: "Check-in",
    navConsentRequests: "Einwilligungsanfragen",
    navAssistant: "Assistent",
    navPlan: "Tarif",
    navAccount: "Konto",
    navSubscriptions: "Abonnements",
    navPayment: "Zahlung",
    navInvoices: "Rechnungen",
    navCompliance: "Compliance",
    navNotifications: "Benachrichtigungen",
    complianceDescription:
      "Ihre Daten und Ihre Rechte: was gespeichert ist, wer es angesehen hat, wozu Sie eingewilligt haben, eine Kopie von allem und das Löschen Ihres Kontos.",
    complianceStatistics: "Statistik",
    complianceLog: "Protokoll",
    complianceLogLead: "Jeder Zugriff auf Ihre Notfalldaten, mit wem und wann.",
    complianceConsents: "Einwilligungen",
    complianceExport: "Export",
    complianceDeleteAccount: "Konto löschen",
    statsMemberSince: "Mitglied seit",
    statsTrips: "Reisen",
    statsMedications: "Medikamente",
    statsImmunizations: "Impfungen",
    statsUnavailable: "Gerade nicht verfügbar",
    settingsNotificationsDescription:
      "Wählen Sie, worüber HealthPass Sie informiert und wie.",
    navCollapseMenu: "Menü einklappen",
    navExpandMenu: "Menü ausklappen",
    navSectionPages: "Seiten in diesem Bereich",

    // Home (UX_CONCEPT.md §5.1)
    homeGoodMorning: "Guten Morgen",
    homeGoodAfternoon: "Guten Tag",
    homeGoodEvening: "Guten Abend",
    homeBetaNotice:
      "HealthPass ist in der Beta-Phase und führt seine Dienste schrittweise ein. Wir halten Sie auf dem Laufenden.",
    homeDismiss: "Schließen",
    homeNextTrip: "Nächste Reise",
    homeInDays: "in {n} Tagen",
    homeToday: "heute",
    homeTomorrow: "morgen",
    homeDepart: "Abreise",
    homeReturn: "Rückkehr",
    homeNights: "{n} Nächte",
    homeCheckTrip: "Sehen, was diese Reise braucht",
    homeMoreTrips: "{n} weitere Reisen geplant",
    homeNoTripTitle: "Wohin geht es als Nächstes?",
    homeNoTripBody:
      "Planen Sie eine Reise, und HealthPass ermittelt, was Sie dafür brauchen.",
    homePlanTrip: "Reise planen",
    homeYourCard: "Ihr HealthPass",
    homeShowWallet: "Wallet-Seite zeigen",
    homeShowFront: "Vorderseite zeigen",
    homeCardHintFront:
      "Zeigen Sie beim Check-in die Vorderseite. Tippen Sie auf die Karte für Ihr Wallet.",
    homeCardHintBack:
      "Ihr Wallet: alles, was die Karte trägt. Tippen zum Zurückdrehen.",
    homeNextStep: "Nächster Schritt",
    homeNotNow: "Nicht jetzt",
    stepEmergencyTitle: "Blutgruppe ergänzen",
    stepEmergencyBody:
      "Sie kommt auf Ihre Karte, damit Ersthelfer sie überall auf der Welt lesen können.",
    stepEmergencyAction: "Notfallprofil vervollständigen",
    stepBpTitle: "Blutdruck erneut messen",
    stepBpBodyAbnormal:
      "Ihr letzter Wert, {value}, war {status}. Eine neue Messung dauert zwei Minuten.",
    stepBpBodyStale:
      "Ihr letzter Wert, {value}, wurde {age} gemessen. Eine neue Messung dauert zwei Minuten.",
    stepBpAction: "Wert erfassen",
    stepTripTitle: "Bereit machen für {destination}",
    stepTripBody:
      "Sehen Sie, welche Impfungen, Medikamente und Papiere diese Reise braucht.",
    stepTripAction: "Checkliste öffnen",
    stepPlanTitle: "Nächste Reise planen",
    stepPlanBody:
      "Sagen Sie uns, wohin und wann, und wir prüfen, was Sie vorher brauchen.",
    homeAtAGlance: "Auf einen Blick",
    homeAllHealthData: "Alle Gesundheitsdaten",
    homeNoVitals:
      "Noch keine Messwerte. Erfassen Sie einen, dann erscheint er hier.",
    homeVitalsError: "Ihre Messwerte konnten nicht geladen werden.",
    homeRetry: "Erneut versuchen",
    ageToday: "heute",
    ageDays: "vor {n} T.",
    ageMonths: "vor {n} Mon.",
    ageYears: "vor {n} J.",
    ageUnknown: "Datum unbekannt",
    vitalLow: "Niedrig",
    vitalElevated: "Erhöht",
    vitalHigh: "Hoch",
    vitalUnderweight: "Untergewicht",
    vitalOverweight: "Übergewicht",
    vitalObese: "Adipositas",
    vitalRecorded: "Erfasst",
    weightLabel: "Gewicht",
    bmiLabel: "BMI",
    stepBpBodyUndated:
      "Ihr letzter Wert, {value}, hat kein Datum. Eine neue Messung dauert zwei Minuten.",
    homeDayUnit: "Tag",
    homeDaysUnit: "Tage",

    // Trip readiness (UX_CONCEPT.md §6.1)
    readinessCaption: "reisebereit",
    readinessAllDone: "Reisebereit!",
    readinessStepsLeft: "Schritte offen",
    readinessSaveFailed:
      "Ihre Checkliste konnte nicht gespeichert werden, Häkchen bleiben daher nicht erhalten. Laden Sie die Seite neu.",
    readinessNotForMe: "Trifft nicht zu",
    readinessCountAgain: "Wieder mitzählen",
    readinessNotChecked: "Noch nicht geprüft",
    homeRemindTonight: "Heute Abend erinnern",
    homeReminderSet: "Erinnerung um {time}",
    homeReminderFailed: "Nicht gespeichert – erneut versuchen",
    // Passport stamps and levels (UX_CONCEPT.md §7)
    passportTitle: "Dein Reisepass",
    passportStamps: "{count} Stempel",
    passportOneStamp: "1 Stempel",
    passportAt100: "bei 100 %",
    passportNextStamp:
      "Mach dich bereit für {city}, und der Stempel gehört dir.",
    passportOneMore: "Noch einer, und du bist {level}.",
    passportFirst:
      "Bring eine Reise vor der Abfahrt auf 100 %, und ihr Stempel landet hier.",
    passportToNext: "Noch {count} bis {level}.",
    passportTop: "Höchste Stufe erreicht. Gute Reise.",
    passportFailed: "Dein Reisepass konnte nicht geladen werden.",
    passportOpenTrip: "Checkliste für {city} öffnen",
    levelStarter: "Starter",
    levelExplorer: "Entdecker",
    levelGlobetrotter: "Globetrotter",
    levelNomad: "Nomade",
    stampEarned: "Stempel erhalten",
    stampEarnedBody: "Er ist in deinem Reisepass. Eine wunderbare Reise!",
    stampEarnedLevelUp: "Du bist jetzt {level}. Eine wunderbare Reise!",
    // First run (UX_CONCEPT.md §5.5)
    frStepOf: "Schritt {n} von 3",
    frTitleYou: "Lass uns deine Karte machen.",
    frLeadYou: "Jede Antwort landet sofort auf deiner Karte.",
    frTitleBasics: "Deine Karte nimmt Gestalt an.",
    frLeadBasics:
      "Noch ein paar Antworten, und Ersthelfer können dir überall auf der Welt helfen.",
    frTitleTrip: "Deine Karte ist fertig.",
    frLeadTrip:
      "Scannen, zeigen, teilen. Und jetzt: Wohin geht es als Nächstes?",
    frTitleDone: "Deine erste Reise ist geplant.",
    frLeadDone:
      "Wir haben eine Liste begonnen, was {city} braucht. Hak sie ab und hol dir deinen ersten Reisepass-Stempel.",
    frStepYou: "Du auf der Karte",
    frStepYouHint: "Name, Foto, Staatsangehörigkeit",
    frStepBasics: "Notfall-Grundlagen",
    frStepBasicsHint: "Blutgruppe, Allergien, Medikamente, ein Kontakt",
    frStepTrip: "Deine nächste Reise",
    frStepTripHint: "Wohin und wann — wir prüfen, was du brauchst",
    frYourSteps: "Deine drei Schritte",
    frDone: "Erledigt",
    frMinLeft: "noch {n} Min.",
    frQrAfter: "QR nach Schritt 2",
    frBasicsLabel: "NOTFALL-GRUNDLAGEN · {n} VON 4",
    frYouQ: "Für wen ist diese Karte?",
    frFirstName: "Vorname",
    frLastName: "Nachname",
    frDateOfBirth: "Geburtsdatum",
    frNationality: "Staatsangehörigkeit",
    frNationalityPlaceholder: "z. B. deutsch",
    frAddPhoto: "Foto hinzufügen",
    frChangePhoto: "Foto ändern",
    frPhotoFailed:
      "Das Foto konnte nicht hochgeladen werden. Versuch ein anderes.",
    frPutOnCard: "Auf meine Karte",
    frAddToCard: "Auf meine Karte",
    frBloodQ: "Welche Blutgruppe hast du?",
    frBloodHint: "Das prüft ein Krankenhaus als Erstes.",
    frBloodUnknown: "Weiß ich nicht",
    frBloodChip: "Blutgruppe {type}",
    frBloodChipEmpty: "Blutgruppe?",
    frAllergyQ: "Bist du gegen etwas allergisch?",
    frAllergyHint:
      "Wird jedem auf Englisch angezeigt, der deine Karte im Notfall scannt.",
    frAlreadyOnCard: "Schon auf deiner Karte",
    frSomethingElse: "Etwas anderes",
    frAllergyPlaceholder: "z. B. Latex, Bienenstiche",
    frNoAllergies: "Keine Allergien",
    frNothingToAdd: "Nichts hinzuzufügen",
    frAllergyChips: "{n} Allergien",
    frAllergyChipOne: "1 Allergie",
    frAllergyChipNone: "Keine Allergien",
    frAllergyChipEmpty: "Allergien?",
    frMedsQ: "Nimmst du regelmäßig Medikamente?",
    frMedsHint: "Ersthelfer müssen das wissen, bevor sie dir etwas geben.",
    frMedsPlaceholder: "z. B. Metformin 500 mg",
    frAdd: "Hinzufügen",
    frRemove: "Entfernen",
    frNoMeds: "Keine",
    frContactQ: "Wen sollen wir anrufen?",
    frContactHint:
      "Dein Notfallkontakt, sichtbar für Ersthelfer, die deine Karte scannen.",
    frContactName: "Name",
    frContactPhone: "Telefon, mit Ländervorwahl",
    frContactPhoneInvalid:
      "Gib eine Telefonnummer in Ziffern ein, z. B. +49 30 1234567.",
    frRelationship: "Beziehung",
    frRelPartner: "Partner/in",
    frRelParent: "Elternteil",
    frRelChild: "Kind",
    frRelSibling: "Geschwister",
    frRelFriend: "Freund/in",
    frSkip: "Später",
    frTripQ: "Wohin geht deine nächste Reise?",
    frTripCity: "Stadt oder Ort",
    frTripCountry: "Land",
    frTripDeparture: "Abreise",
    frTripReturn: "Rückreise",
    frTripPlan: "Reise planen",
    frTripLater: "Noch keine Reise geplant",
    frOpenChecklist: "Sieh, was {city} braucht",
    frGoHome: "Zur Startseite",
    frSaveFailed:
      "Das wurde nicht gespeichert. Prüf deine Verbindung und versuch es noch einmal.",
    frPrivacy:
      "Deine Gesundheitsdaten sind verschlüsselt und gehören dir. Du entscheidest, wer sie sieht, und siehst jeden Zugriff.",
    // Card page (UX_CONCEPT.md §5.2)
    cardSideFront: "Vorderseite",
    cardSideWallet: "Wallet-Seite",
    cardDownload: "Karte herunterladen",
    cardDownloadFailed:
      "Die Karte konnte nicht heruntergeladen werden. Versuch es noch einmal.",
    cardOfflineCodeShow: "Kein Netz? Offline-Code zeigen",
    cardOnlineCodeShow: "Online-Code zeigen",
    cardOfflineCodeHint:
      "Dieser Code enthält deine Notfallübersicht und funktioniert ohne Internet.",
    // Shared card page
    sharedCardTitle: "Eine geteilte HealthPass-Karte",
    sharedCardLead:
      "Die Person hat diese Karte mit dir geteilt. Sie zeigt, wer sie ist, nicht ihre Gesundheitsdaten.",
    sharedCardNoCode: "Geteilte Karte",
    sharedCardPasswordLead: "Die Karte ist mit einem Passwort geschützt.",
    sharedCardPassword: "Passwort",
    sharedCardOpen: "Karte öffnen",
    sharedCardWrongPassword: "Das Passwort stimmt nicht.",
    sharedCardGone: "Dieser Link ist abgelaufen oder wurde zurückgezogen.",
    sharedCardMissing:
      "Diesen Link gibt es nicht. Prüf, ob er vollständig kopiert wurde.",
    sharedCardError:
      "Die Karte konnte nicht geladen werden. Versuch es gleich noch einmal.",
    settingsExportLead:
      "Lade alles herunter, was HealthPass über dich gespeichert hat, als Datei zum Aufbewahren oder Mitnehmen.",
    settingsExportDone: "Deine Daten wurden heruntergeladen.",
    settingsExportFailed:
      "Der Download ist fehlgeschlagen. Bitte versuch es noch einmal.",
    // Payment success page
    payConfirming: "Zahlung wird bestätigt …",
    payConfirmingLine: "Das dauert ein paar Sekunden.",
    payWelcome: "Willkommen bei {plan}",
    payTrialLine:
      "Dein kostenloser Test läuft bis {date}. Danach verlängert sich dein Abo monatlich, wenn du nicht vorher kündigst.",
    payTripLine: "Dein Reise-Plan läuft bis {date}.",
    payActiveLine: "Dein Plan ist jetzt aktiv.",
    payPendingTitle: "Deine Zahlung ist unterwegs",
    payPendingLine:
      "Manche Zahlungsarten dauern länger. Dein Plan wird aktiv, sobald die Zahlung da ist, und wir sagen dir Bescheid.",
    payFailedTitle: "Dieser Bezahlvorgang wurde nicht abgeschlossen",
    payFailedLine:
      "Es wurde nichts abgebucht. Du kannst jederzeit wieder einen Plan wählen.",
    payContinue: "Weiter",
    payBuildCard: "Karte anlegen",
    payBackToPlans: "Zurück zu den Plänen",
    renewTermsMonthly:
      "Neue Mitglieder testen 14 Tage kostenlos, danach {price} im Monat. Das Abo verlängert sich monatlich, bis du kündigst – jederzeit in deinen Abrechnungseinstellungen.",
    renewTermsTrip:
      "Eine Zahlung von {price} für 90 Tage. Sie verlängert sich nicht.",
    // Partner offers (REQ-025)
    marketplaceTitle: "Partnerangebote",
    marketplaceIntro:
      "Versicherungsangebote unserer Partner für diese Reise. HealthPass vergleicht oder empfiehlt sie nicht; Fragen zum Schutz beantwortet der Versicherer.",
    marketplaceDisclosurePerContract:
      "Angebot von {insurer}. HealthPass erhält eine Vergütung, wenn du einen Vertrag abschließt.",
    marketplaceDisclosurePerVisit:
      "Angebot von {insurer}. HealthPass erhält eine Vergütung, wenn du {insurer} besuchst.",
    marketplaceVisit: "Zu {insurer}",
    marketplaceSeeAll: "Seite mit Partnerangeboten öffnen",
    marketplaceEmpty: "Für diese Reise gibt es keine Partnerangebote.",
    marketplaceBackToTrip: "Zurück zur Reise",
    marketplaceOpenFailed:
      "Das Angebot konnte nicht geöffnet werden. Bitte versuche es erneut.",
    marketplaceSettingTitle: "Partnerangebote anzeigen",
    marketplaceSettingHint:
      "Versicherungsangebote von Partnern bei deinen Reisen. Sie werden nur nach Reiseziel, Reisedaten und Wohnsitzland ausgewählt, nie nach deinen Gesundheitsdaten. Ausgeschaltet sind sie überall verborgen.",
    marketplaceSettingSaved: "Gespeichert.",
    marketplaceSettingFailed:
      "Die Einstellung konnte nicht gespeichert werden.",
  },

  fr: {
    // Navigation
    login: "Connexion",
    register: "S'inscrire",
    dashboard: "Tableau de bord",
    profile: "Profil",
    health: "Santé",
    medications: "Médicaments",
    travel: "Voyage",
    documents: "Documents",
    chat: "Chat",
    settings: "Paramètres",
    logout: "Déconnexion",
    appearance: "Apparence",
    security: "Sécurité",

    // Auth
    welcomeBack: "Voyagez l'esprit tranquille",
    signInToAccount: "Connectez-vous à votre compte",
    createAccount: "Créez votre compte",
    createAccountSubtitle: "Configurez votre compte HealthPass",
    settingsGeneralDescription: "Notifications, données et aperçu du compte",
    settingsSecurityDescription:
      "Connexion, authentification à deux facteurs et confidentialité",
    settingsAppearanceDescription: "Langue et thème de couleurs",
    themeTitle: "Thème",
    themeLight: "Clair",
    themeDark: "Sombre",
    themeSystem: "Système",
    themeHint: "Système suit le réglage de votre appareil et change avec lui.",
    orientationTitle: "Orientation de l'écran",
    orientationPortrait: "Portrait fixe",
    orientationSystem: "Orientation du système",
    orientationHint:
      "Le portrait fixe garde l'application droite quelle que soit la façon dont vous tenez le téléphone. L'orientation du système suit le réglage de rotation de votre appareil.",
    accountStatistics: "Statistiques du compte",
    notificationsEmailHint: "Mises à jour et rappels importants par e-mail",
    remindersTitle: "Rappels",
    fhirStandardNote: "Données médicales dans un format normalisé.",
    email: "E-mail",
    password: "Mot de passe",
    confirmPassword: "Confirmez le mot de passe",
    firstName: "Prénom",
    lastName: "Nom",
    forgotPassword: "Mot de passe oublié?",
    rememberMe: "Se souvenir de moi",
    signIn: "Se connecter",
    signUp: "S'inscrire",
    alreadyHaveAccount: "Vous avez déjà un compte?",
    noAccountYet: "Nouveau sur HealthPass?",
    createFreeAccount: "Créer un compte gratuit",
    backToLogin: "Retour à la connexion",

    // Common
    loading: "Chargement...",
    error: "Erreur",
    success: "Succès",
    cancel: "Annuler",
    save: "Enregistrer",
    delete: "Supprimer",
    edit: "Modifier",
    view: "Voir",
    viewAll: "Tout voir",
    close: "Fermer",
    pleaseSelect: "Veuillez sélectionner...",

    termsAndConditions: "Conditions d'utilisation",
    privacyPolicy: "Politique de confidentialité",
    acceptTermsRequired: "Veuillez accepter les conditions pour continuer",
    consentTerms: "J'accepte les {terms} et j'ai lu la {privacy}.",
    consentTermsLink: "Conditions d'utilisation",
    consentHealth:
      "Je consens expressément à ce que HealthPass enregistre les informations de santé que je saisis (maladies, allergies, médicaments, vaccinations, documents) afin de me les afficher, ainsi que sur ma carte d'urgence (art. 9, par. 2, a) du RGPD). Je peux retirer ce consentement à tout moment.",
    consentHealthWithout:
      "Sans ce consentement, vous pouvez planifier vos voyages et utiliser la liste de contrôle, mais pas enregistrer d'informations de santé.",
    consentAi:
      "Je consens à ce que la partie utile de mes données de santé soit transmise à OpenAI aux États-Unis lorsque j'utilise les conseils santé par IA ou l'assistant IA. Je peux retirer ce consentement à tout moment.",
    consentAiWithout:
      "Sans ce consentement, les fonctions d'IA restent désactivées ; les informations sur la destination fonctionnent toujours.",
    consentMarketing:
      "Envoyez-moi des conseils et des actualités sur la santé en voyage par e-mail. Désinscription possible à tout moment.",
    consentOptional: "Facultatif",
    consentGateTitle: "Vos données, votre choix",
    consentGateLead:
      "Dites-nous ce que HealthPass peut faire de vos données. Rien de nouveau n'est enregistré et rien n'est envoyé à l'IA tant que vous n'avez pas décidé.",
    consentGateSave: "Enregistrer mes choix",
    consentSaveFailed:
      "Vos choix n'ont pas pu être enregistrés. Veuillez réessayer.",
    consentNeededHealthTitle:
      "Autoriser HealthPass à enregistrer des informations de santé ?",
    consentNeededAiTitle: "Autoriser les conseils santé par IA ?",
    consentAllow: "Autoriser",
    consentNotNow: "Pas maintenant",
    consentWithdrawHint:
      "Vous pouvez retirer ce consentement à tout moment dans les paramètres.",
    settingsConsentsTitle: "Vos consentements",
    settingsConsentsLead:
      "Un retrait arrête tout nouveau traitement. Ce qui est déjà enregistré reste jusqu'à ce que vous le supprimiez.",
    consentSaved: "Enregistré",
    immediateStartTitle: "Avant de payer",
    immediateStartAck:
      "Je demande que mon abonnement commence maintenant. Je sais que si je me rétracte dans les 14 jours, je paie les jours déjà utilisés.",
    immediateStartTrial:
      "Si votre abonnement commence par un essai gratuit, rien n'est prélevé avant sa fin, et une résiliation avant cette date ne coûte rien.",
    immediateStartContinue: "Continuer vers le paiement",
    immediateStartBack: "Retour",
    withdrawTitle: "Se rétracter du contrat",
    withdrawLead:
      "Vous pouvez vous rétracter de cet abonnement sans motif jusqu'au {deadline}.",
    withdrawRefund:
      "{amount} vous seraient remboursés pour les jours non utilisés.",
    withdrawTrial:
      "Vous êtes dans votre essai gratuit, rien n'a donc été prélevé. La rétractation met fin à l'abonnement immédiatement.",
    withdrawButton: "Se rétracter du contrat",
    withdrawConfirm:
      "Se rétracter maintenant ? Votre abonnement prend fin immédiatement et votre compte continue avec l'offre gratuite. Vos données sont conservées.",
    withdrawConfirmYes: "Oui, me rétracter",
    withdrawDone:
      "Votre rétractation est confirmée. {refund}Nous vous avons envoyé une confirmation par e-mail.",
    withdrawRefundIssued:
      "{amount} sont en cours de remboursement sur votre moyen de paiement d'origine. ",
    withdrawRefundPending:
      "{amount} vous seront remboursés sur votre moyen de paiement d'origine dans les 14 jours. ",
    withdrawFailed:
      "La rétractation n'a pas pu être effectuée. Veuillez réessayer ou écrire au support.",
    copyright: "Gérez vos données de santé en toute sécurité",

    // Landing Page
    landingHeroTitle: "Voyagez l'esprit tranquille",
    landingHeroSubtitle:
      "Vos informations de santé d'urgence, disponibles mondialement en quelques secondes. Accès par code QR pour les premiers intervenants. Conforme RGPD. Toujours avec vous.",
    landingStartFree: "Commencer gratuitement",
    landingSeeHowItWorks: "Voir comment ça marche",
    landingNoCreditCard: "Aucune carte de crédit requise",
    landingFreeTrial: "Essai gratuit de 30 jours",
    landingFeaturesTitle:
      "Tout ce dont vous avez besoin pour voyager en sécurité",
    landingFeaturesSubtitle:
      "Créé par des voyageurs, pour les voyageurs. Toutes les fonctionnalités essentielles sur une plateforme sécurisée.",
    landingHowItWorksTitle: "Démarrez en 3 étapes simples",
    landingHowItWorksSubtitle:
      "De l'inscription à la préparation au voyage en moins de 10 minutes",
    landingPricingTitle: "Tarifs simples et transparents",
    landingPricingSubtitle:
      "Commencez gratuitement, mettez à niveau à tout moment. Pas de frais cachés.",
    landingCtaTitle: "Prêt à voyager l'esprit tranquille ?",
    landingCtaSubtitle:
      "Rejoignez plus de 50 000 voyageurs qui font confiance à HealthPass pour leurs données de santé d'urgence.",
    landingCtaButton: "Commencer gratuitement",

    invalidEmail: "Veuillez entrer une adresse e-mail valide",
    passwordTooShort: "Le mot de passe doit contenir au moins 8 caractères",
    passwordsNotMatch: "Les mots de passe ne correspondent pas",

    vitalSigns: "Signes Vitaux",
    bloodPressure: "Tension Artérielle",
    systolic: "Systolique",
    diastolic: "Diastolique",
    restingHeartRate: "Fréquence Cardiaque au Repos",
    bpm: "bpm",
    mmHg: "mmHg",
    normal: "Normal",
    lowBloodPressure: "Tension Artérielle Basse",
    elevated: "Élevé",
    hypertensionGrade1: "Hypertension Grade 1",
    hypertensionGrade2: "Hypertension Grade 2",
    hypertensionGrade3: "Hypertension Grade 3",
    bradycardia: "Bradycardie",
    tachycardia: "Tachycardie",
    systolicBpError: "La tension systolique doit être entre 70 et 250 mmHg",
    diastolicBpError: "La tension diastolique doit être entre 40 et 150 mmHg",
    heartRateError:
      "La fréquence cardiaque au repos doit être entre 30 et 220 bpm",
    systolicNormal: "Systolique Normal: 120",
    systolicHigh: "Systolique Élevé: 140",
    diastolicNormal: "Diastolique Normal: 80",
    systolicBloodPressure: "Tension Artérielle Systolique",
    diastolicBloodPressure: "Tension Artérielle Diastolique",
    noVitalSignsData: "Aucune donnée de signes vitaux disponible",

    // Lab Results
    labResults: "Résultats de Laboratoire",
    currentValues: "Valeurs Actuelles",
    allTests: "Tous les Tests",
    laboratoryTest: "Test de Laboratoire",
    evaluation: "Évaluation",
    value: "Valeur",
    unit: "Unité",
    performingDoctor: "Médecin/Laboratoire exécutant (optionnel)",
    pdfReport: "Rapport PDF",
    noLabResultsDocumented: "Aucun résultat de laboratoire documenté",
    addNewLabResult: "Ajouter un Nouveau Résultat de Laboratoire",
    showChart: "Afficher le Graphique",
    hideChart: "Masquer le Graphique",
    noWeightDataAvailable: "Aucune donnée de poids disponible",
    firstEntriesWillBeShown: "Les premières entrées seront affichées ici",
    weightKg: "Poids (kg)",
    current: "Actuel",
    dataPoints: "Points de données",
    fhirCompatibleData: "Données compatibles FHIR",
    recorded: "Enregistré",
    bodyFat: "Graisse corporelle",
    bodyFatPercent: "Graisse corporelle (%)",
    raised: "Élevé",
    decreased: "Diminué",
    critical: "Critique",
    lastMeasurement: "Dernière Mesure",
    vitalSignsHistory: "Historique des Signes Vitaux (365 Jours)",
    labResultsSaved: "Résultats de laboratoire enregistrés avec succès",

    // Wellness & Wellbeing
    wellbeing: "Bien-être",
    physicalWellbeing: "Bien-être Physique",
    physicalWellness: "Bien-être Physique",
    emotionalWellbeing: "Bien-être Émotionnel",
    emotionalWellness: "Bien-être Émotionnel",
    howDoYouFeelPhysically: "Comment vous sentez-vous physiquement?",
    howDoYouFeelEmotionally: "Comment vous sentez-vous émotionnellement?",
    currentStatus: "Actuellement",
    lastUpdate: "Dernière mise à jour",
    trendChart: "Graphique de Tendance",
    trendHistory: "Tendance des 365 derniers jours",
    noTrendData: "Aucune donnée de tendance disponible",
    noTrendDataDesc:
      "Suivez régulièrement votre bien-être pour voir votre progression",
    noWellbeingData: "Aucune donnée de bien-être disponible",
    saveFirstWellbeingEntry:
      "Enregistrez votre première évaluation de bien-être pour voir les tendances",
    wellnessDataPoints: "Points de Données",
    wellnessScore: "Score de Bien-être (1-5)",
    average: "Moyenne",
    good: "Bon",
    low: "Faible",
    veryPoor: "Très Mauvais",
    poor: "Mauvais",
    fair: "Moyen",
    wellGood: "Bon",
    excellent: "Excellent",
    physicalVeryPoor: "Très Mauvais",
    physicalPoor: "Mauvais",
    physicalFair: "Moyen",
    physicalGood: "Bon",
    physicalExcellent: "Excellent",
    emotionalVeryPoor: "Très Déprimé",
    emotionalPoor: "Triste",
    emotionalFair: "Équilibré",
    emotionalGood: "Heureux",
    emotionalExcellent: "Très Heureux",

    // Additional fields from English
    resetPassword: "Réinitialiser le mot de passe",
    resetPasswordDescription:
      "Entrez votre adresse e-mail et nous vous enverrons un lien de réinitialisation",
    sendResetLink: "Envoyer le lien de réinitialisation",
    emailSent: "E-mail envoyé!",
    checkYourEmail: "Vérifiez votre boîte e-mail",
    resetEmailSent: "Nous avons envoyé un e-mail de réinitialisation à",
    healthPass: "HealthPass",
    secureHealthData: "Gestion sécurisée des données de santé",
    manageHealthSecurely: "Gérez vos données de santé en toute sécurité",
    testAccount: "Compte de test",
    useTestAccount: "Utiliser le compte de test",
    overview: "Aperçu",
    healthRecords: "Dossiers de Santé",
    appointments: "Rendez-vous",
    reports: "Rapports",
    recentActivity: "Activité Récente",
    quickActions: "Actions Rapides",
    upcomingAppointments: "Rendez-vous à Venir",
    healthSummary: "Résumé de Santé",
    addNewRecord: "Ajouter un Nouveau Dossier",
    viewAllRecords: "Voir Tous les Dossiers",
    noDataAvailable: "Aucune Donnée Disponible",
    upcomingTravels: "Voyages à Venir",
    noUpcomingTravels: "Aucun voyage à venir",
    planNewTravel: "Planifier un Nouveau Voyage",
    today: "Aujourd'hui",
    tomorrow: "Demain",
    days: "jours",
    stayHydrated: "Restez hydraté tout au long de la journée",
    drinkWaterRecommendation: "Visez 2-3 litres d'eau par jour",
    regularExercise: "Activité physique régulière",
    exerciseRecommendation: "30 minutes d'activité modérée, 5 jours/semaine",
    qualitySleep: "Sommeil de qualité",
    sleepRecommendation: "7-9 heures de sommeil par nuit",
    thinkAbout: "Pensez à",
    travelInsurance: "Assurance Voyage",
    travelInsuranceDesc: "Protégez votre voyage",
    firstAidKit: "Trousse de Premiers Soins",
    firstAidKitDesc: "Soyez préparé",
    medicalCheckup: "Bilan de Santé",
    medicalCheckupDesc: "Examen annuel de santé",
    vaccinationAppointment: "Rendez-vous de Vaccination",
    vaccinationAppointmentDesc: "Restez protégé",
    tryOutPlus: "Essayez HealthPass PLUS",
    plusDescription:
      "Débloquez des fonctionnalités premium pour une meilleure gestion de la santé",
    plusFeature1: "Analyses de santé avancées",
    plusFeature2: "Support prioritaire",
    plusFeature3: "Stockage de documents illimité",
    plusFeature4: "Insights pilotés par l'IA",
    upgradeToPlusButton: "Passer à PLUS",
    addHealthRecord: "Ajouter un Dossier de Santé",
    editHealthRecord: "Modifier le Dossier de Santé",
    deleteHealthRecord: "Supprimer le Dossier de Santé",
    recordType: "Type de Dossier",
    date: "Date",
    description: "Description",
    attachments: "Pièces jointes",
    weight: "Poids",
    height: "Taille",
    temperature: "Température",
    heartRate: "Fréquence Cardiaque",
    currentMedications: "Médicaments Actuels",
    addMedication: "Ajouter un Médicament",
    prescribedBy: "Prescrit par",
    sideEffects: "Effets Secondaires",
    instructions: "Instructions",
    travelPlans: "Plans de Voyage",
    addTravelPlan: "Ajouter un Plan de Voyage",
    destination: "Destination",
    departureDate: "Date de Départ",
    returnDate: "Date de Retour",
    travelType: "Type de Voyage",
    requiredVaccinations: "Vaccinations Requises",
    healthRecommendations: "Recommandations de Santé",

    // Travel List Page
    travels: "Voyages",
    myTravels: "Mes Voyages",
    planYourTrips:
      "Planifiez vos voyages et gérez les exigences de santé liées aux voyages",
    newTravel: "Nouveau Voyage",
    pastTravels: "Voyages Passés",
    currentTravels: "Voyages Actuels",
    plannedTravels: "Voyages Planifiés",
    draftTravels: "Brouillons",
    noTravelsYet: "Aucun voyage encore",
    startPlanningTravel:
      "Commencez à planifier votre premier voyage pour obtenir des recommandations et des exigences de santé",
    travelTitle: "Titre du Voyage",
    travelPurpose: "Objectif du Voyage",
    travelDestination: "Destination du Voyage",
    startDate: "Date de Début",
    endDate: "Date de Fin",
    status: "Statut",
    actions: "Actions",
    viewTravel: "Voir le Voyage",
    editTravel: "Modifier le Voyage",
    deleteTravel: "Supprimer le Voyage",
    archiveTravel: "Archiver le Voyage",
    confirmDeleteTravel: "Êtes-vous sûr de vouloir supprimer ce voyage ?",
    travelDeleted: "Voyage supprimé avec succès",
    travelArchived: "Voyage archivé avec succès",

    // Travel Status
    statusDraft: "Brouillon",
    statusPlanned: "Planifié",
    statusCurrent: "Actuel",
    statusCompleted: "Terminé",
    statusCancelled: "Annulé",
    statusArchived: "Archivé",
    statusModeManual: "Définir manuellement",
    statusModeAutoHint:
      "Le statut est calculé automatiquement à partir des dates du voyage.",

    // Travel Purpose
    purposeLeisure: "Loisirs",
    purposeBusiness: "Affaires",
    purposeMedical: "Médical",
    purposeEducation: "Éducation",
    purposeVolunteering: "Bénévolat",
    purposeOther: "Autre",
    tripTitle: "Titre du Voyage",
    tripType: "Type de Voyage",
    tripOperator: "Tour-opérateur",
    bookingReference: "Référence de Réservation",
    startLocation: "Lieu de Départ",
    endLocation: "Lieu d'Arrivée",
    tripTypeCruise: "Croisière",
    tripTypeBeach: "Plage",
    tripTypeMountains: "Montagnes",
    tripTypeSports: "Sports",
    tripTypeEducation: "Éducation",
    tripTypeEvent: "Événement",
    tripTypeCulture: "Culture",
    tripTypeExpedition: "Expédition",
    plannedActivities: "Activités prévues",
    activity_diving: "Plongée",
    activity_climbing: "Escalade",
    activity_skiing: "Ski",
    activity_hiking: "Randonnée",
    activity_biking: "Vélo",
    activity_swimming: "Natation",
    activity_safari: "Safari",
    healthcareFacilities: "Établissements de santé",
    nearbyHospitals: "Hôpitaux à proximité",
    nearbyPharmacies: "Pharmacies à proximité",
    nearbyClinics: "Cliniques à proximité",
    noFacilitiesFound: "Aucun établissement trouvé dans cette zone",
    facilityDistance: "Distance",
    forumHealthSummary: "Rapports des voyageurs",
    commonHealthIssues: "Problèmes de santé courants",
    travelerTips: "Conseils de voyageurs",
    unexpectedRisks: "Risques inattendus",
    forumSummaryDisclaimer:
      "Résumé généré par IA. Vérifier avec les rapports actuels.",
    searchForumsFurther: "Rechercher des rapports récents",
    loadForumSummary: "Charger les rapports",
    operatorHealthInfo: "Infos santé de l'opérateur",
    cruiseHealthRequirements: "Exigences sanitaires croisière",
    tripSegments: "Segments de Voyage",
    addSegment: "Ajouter un Segment",
    editSegment: "Modifier le Segment",
    deleteSegment: "Supprimer le Segment",
    noSegmentsYet: "Aucun segment ajouté",
    addSegmentsDescription:
      "Ajoutez des segments de vol, hôtel ou activité pour organiser votre voyage",
    addFirstSegment: "Ajouter le Premier Segment",
    operator: "Opérateur",
    transportation: "Transport",
    segmentInformation: "Informations du Segment",
    segmentTitle: "Titre du Segment",
    segmentType: "Type de Segment",
    segmentTypeTransfer: "Transfert",
    segmentTypeDestination: "Destination",
    segmentTypeAccommodation: "Hébergement",
    segmentTypeActivity: "Activité",
    locations: "Emplacements",
    startCountry: "Pays de Départ",
    endCountry: "Pays d'Arrivée",
    dateTime: "Date & Heure",
    startDateTime: "Date & Heure de Départ",
    endDateTime: "Date & Heure d'Arrivée",
    selectOption: "Sélectionner...",
    transportFlight: "Vol",
    transportTrain: "Train",
    transportBus: "Bus",
    transportCar: "Voiture",
    transportShip: "Navire",
    accommodation: "Hébergement",
    accommodationHotel: "Hôtel",
    accommodationHostel: "Auberge",
    accommodationApartment: "Appartement",
    accommodationCruiseCabin: "Cabine de Croisière",
    healthRisk: "Risque Sanitaire",
    travelReadiness: "Préparation au Voyage",
    vaccinations: "Vaccinations",
    recommendations: "Recommandations",
    required: "requis",
    available: "disponible",
    riskManagement: "Gestion des Risques",
    vaccinationDetails: "Détails des Vaccinations",
    readinessChecklist: "Liste de Contrôle",
    personalRecommendations: "Recommandations Personnalisées",
    bookAppointment: "Prendre Rendez-vous",

    // Travel Details
    travelCompanions: "Compagnons de Voyage",
    companions: "Compagnons",
    tourOperator: "Tour Opérateur",
    travelNotes: "Notes de Voyage",
    notes: "Notes",
    segments: "Segments",
    travelSegment: "Segment de Voyage",
    transportationType: "Type de Transport",
    accommodationType: "Type d'Hébergement",

    // Filter & Sort
    filterByStatus: "Filtrer par Statut",
    filterByPurpose: "Filtrer par Objectif",
    sortByDate: "Trier par Date",
    sortByDestination: "Trier par Destination",
    searchTravels: "Rechercher des voyages...",
    showAll: "Tout Afficher",
    showActive: "Afficher Actifs",
    showCompleted: "Afficher Terminés",

    // Time Indicators
    daysUntilDeparture: "Jours avant le départ",
    daysRemaining: "Jours restants",
    inProgress: "En Cours",
    departed: "Parti",
    returned: "Retourné",

    // Travel Form
    travelDates: "Dates de Voyage",
    travelDetails: "Détails du Voyage",
    healthInformationNotice: "Avis d'Information Sanitaire",
    healthRequirementsDescription:
      "Les exigences sanitaires de voyage seront affichées ici en fonction de votre destination. Ces informations proviennent du CDC, de l'OMS et des autorités sanitaires locales.",
    vaccinationInfoLoading:
      "Les exigences de vaccination seront chargées en fonction de votre destination et de vos dates de voyage.",
    healthWarningsLoading:
      "Les avertissements et avis sanitaires seront affichés ici.",
    apiIntegrationPending: "Intégration API avec CDC/OMS en attente",
    checkBackLater:
      "Les informations seront disponibles une fois les données sanitaires de destination intégrées",
    healthWarnings: "Avertissements Sanitaires",
    lastUpdated: "Dernière mise à jour",
    source: "Source",
    noVaccinationsRequired: "Aucune vaccination spécifique requise",
    noHealthWarnings: "Aucun avertissement sanitaire spécifique",
    risk: "risque",

    // Health Recommendations
    departure: "Départ",
    mandatory: "Obligatoire",
    recommended: "Recommandé",
    suggested: "Suggéré",
    recommendedVaccinations: "Vaccinations Recommandées",
    activeDiseaseOutbreaks: "Épidémies Actives",
    dataSources: "Sources de Données",
    from: "De",
    to: "À",
    isoCode: "Code ISO",
    riskLevel: "Niveau de Risque",
    healthDataLoadError: "Impossible de charger les données de santé.",
    dataFrom: "Données de",
    overallRisk: "Évaluation globale des risques",
    quickInstallButton: "Installer l'application",
    quickInstallInstalled: "Déjà installée",
    updateAvailable: "Charger la nouvelle version.",
    quickInstallAndroidApkStep1:
      "Scannez le code, puis appuyez sur Télécharger.",
    quickInstallAndroidApkStep2:
      "Ouvrez le fichier téléchargé et autorisez les installations depuis Chrome.",
    quickInstallAndroidApkStep3:
      "Ouvrez HEALTHPASS depuis votre écran d'accueil.",
    quickInstallTitle: "Installez l'application en trois étapes",
    quickInstallSubtitle:
      "Dirigez l'appareil photo de votre téléphone vers le code. HEALTHPASS s'installe comme une application, s'affiche en plein écran et fonctionne hors ligne.",
    quickInstallScanHint: "Scannez pour ouvrir sur votre téléphone",
    quickInstallAndroidTitle: "Android · Chrome",
    quickInstallAndroidStep1:
      "Scannez le code ou ouvrez travelhealthpass.app dans Chrome.",
    quickInstallAndroidStep2:
      "Touchez le menu à trois points, puis « Installer l'application ».",
    quickInstallAndroidStep3: "Ouvrez HEALTHPASS depuis votre écran d'accueil.",
    quickInstallIosTitle: "iPhone et iPad · Safari",
    quickInstallIosStep1:
      "Scannez le code ou ouvrez travelhealthpass.app dans Safari.",
    quickInstallIosStep2: "Touchez Partager, puis « Sur l'écran d'accueil ».",
    quickInstallIosStep3: "Ouvrez HEALTHPASS depuis votre écran d'accueil.",
    quickInstallIosNote:
      "Safari uniquement — Chrome et Firefox sur iPhone ne peuvent pas installer d'applications web.",
    appInstallTitle: "HealthPass sur votre téléphone",
    appInstallSubtitle:
      "Installez HealthPass sur Android ou iPhone et emportez votre dossier de santé, votre carte d'urgence et les exigences sanitaires de voyage, même hors ligne.",
    appInstallAndroidTitle: "Android",
    appInstallAndroidSubtitle: "Installer l'APK directement",
    appInstallScanToDownload:
      "Scannez ce code avec l'appareil photo de votre téléphone pour télécharger l'APK, ou utilisez le bouton ci-dessous.",
    appInstallDownloadApk: "Télécharger l'app HealthPass",
    appInstallApkUnavailable: "L'APK Android n'est pas encore disponible.",
    appInstallApkUnavailableHint:
      "En attendant, HealthPass fonctionne comme une application web installable : ouvrez ce site dans Chrome sur votre téléphone Android, appuyez sur le menu et choisissez « Installer l'application ».",
    appInstallAndroidStep1:
      "Scannez le QR code ou appuyez sur « Télécharger l'APK » depuis votre téléphone Android.",
    appInstallAndroidStep2:
      "Lorsque Android avertit que le fichier peut endommager votre appareil, choisissez « Télécharger quand même » - cet avertissement s'affiche pour toute application installée hors du Play Store.",
    appInstallAndroidStep3:
      "Ouvrez le fichier téléchargé. Android demande d'autoriser les installations depuis cette source ; appuyez sur « Paramètres » et activez l'autorisation.",
    appInstallAndroidStep4: "Revenez en arrière et appuyez sur « Installer ».",
    appInstallAndroidStep5:
      "Ouvrez HealthPass et connectez-vous avec votre compte existant.",
    appInstallAndroidPwaTitle: "Installer depuis Chrome à la place",
    appInstallAndroidPwaStep1:
      "Ouvrez ce site dans Chrome sur votre téléphone Android - scannez le code ci-dessus ou saisissez l'adresse.",
    appInstallAndroidPwaStep2:
      "Appuyez sur le menu à trois points en haut à droite.",
    appInstallAndroidPwaStep3:
      "Appuyez sur « Ajouter à l'écran d'accueil » ou « Installer l'application ».",
    appInstallAndroidPwaStep4: "Confirmez avec « Installer ».",
    appInstallAndroidPwaStep5:
      "Lancez HealthPass depuis votre écran d'accueil et connectez-vous. L'application s'affiche en plein écran et fonctionne hors ligne.",
    appInstallIosTitle: "iPhone et iPad",
    appInstallIosSubtitle: "Ajouter à l'écran d'accueil depuis Safari",
    appInstallScanToOpen:
      "Scannez ce code avec l'appareil photo de votre iPhone pour ouvrir HealthPass dans Safari.",
    appInstallIosStep1:
      "Ouvrez ce site dans Safari. Chrome et Firefox sur iOS ne peuvent pas installer d'applications web.",
    appInstallIosStep2:
      "Appuyez sur le bouton Partager - le carré avec une flèche vers le haut, en bas de l'écran.",
    appInstallIosStep3:
      "Faites défiler vers le bas et appuyez sur « Sur l'écran d'accueil ».",
    appInstallIosStep4: "Appuyez sur « Ajouter » en haut à droite.",
    appInstallIosStep5:
      "Lancez HealthPass depuis votre écran d'accueil et connectez-vous. L'application s'affiche en plein écran et fonctionne hors ligne.",
    appInstallIosSafariNote:
      "Apple n'autorise que Safari à installer des applications web : effectuez donc ces étapes dans Safari, même si un autre navigateur est votre navigateur par défaut.",
    appInstallAndroidGuideLink: "Guide d'installation Android complet",
    androidInstallPageTitle: "Installer HealthPass sur Android",
    androidInstallPageSubtitle:
      "Installez HealthPass sur votre téléphone Android en quelques étapes. L'application s'affiche en plein écran et fonctionne hors ligne.",
    androidInstallScanPageQr:
      "Vous lisez ceci sur un ordinateur ? Scannez ce code pour ouvrir cette page sur le téléphone Android sur lequel vous voulez HealthPass.",
    androidInstallSecurityNote:
      "Android demande votre autorisation avant d'installer une application ne provenant pas du Play Store. Cette demande est normale ici. L'autorisation ne s'applique qu'au navigateur auquel vous l'accordez, et vous pouvez la retirer une fois l'installation terminée.",
    androidInstallIosHint:
      "Vous utilisez un iPhone ? Suivez plutôt les instructions iOS.",
    androidInstallBackHome: "Retour à l'accueil",
    aiAnalysisError: "Analyse IA non disponible.",
    personalizedHealthRecommendations:
      "Recommandations de santé personnalisées",
    recommendationsIntro:
      "Basées sur votre destination, la durée du voyage et votre profil de santé – recommandations fondées sur des preuves et issues de plusieurs sources.",
    aiTripAnalysis: "Analyse santé du voyage par IA",
    analyzing: "Analyse en cours…",
    runAiAnalysis: "Lancer l'analyse IA",
    aiAnalysisHint:
      "Analyse de l'impact climatique et du profil de santé personnel assistée par IA.",
    climateRiskSummary: "Synthèse des risques climatiques",
    healthProfileRisks: "Risques liés à votre profil de santé",
    urgentActions: "Actions urgentes",
    noRecommendationsAvailable:
      "Aucune recommandation spécifique trouvée pour cette destination.",
    travelStatus: "Statut du voyage",
    readiness: "Préparation",
    overallReadiness: "Préparation globale",
    urgent: "Urgent",
    addCustomTask: "Ajouter une tâche",
    taskDescription: "Description de la tâche…",
    vaccinationRequired: "Obligatoire",
    vaccinationRecommended: "Recommandé",
    atLeast: "Au moins",
    daysBeforeDeparture: "jours avant le départ",
    validFor: "Valable",
    loadingHealthData: "Chargement des données de santé…",
    noRequiredVaccinations:
      "Aucune vaccination obligatoire pour cette destination.",
    noRecommendedVaccinations: "Aucune vaccination supplémentaire recommandée.",
    vaccinationTimeline: "Calendrier vaccinal",
    before: "avant",
    riskLevelDescLow: "Risque sanitaire faible pour cette destination",
    riskLevelDescMedium: "Risque sanitaire modéré - prenez des précautions",
    riskLevelDescHigh:
      "Risque sanitaire élevé - consultez un médecin avant le départ",
    riskLevelDescCritical:
      "Risque sanitaire critique - voyage déconseillé sans avis spécialisé",
    activeOutbreaks: "Épidémies actives",
    activeOutbreaksDetected: "épidémie(s) active(s) détectée(s)",
    cases: "Cas",
    lastReported: "Dernier signalement",
    travelWarnings: "Avertissements aux voyageurs",
    transportModeRisks: "Risques par mode de transport",
    activitySpecificRisks: "Risques sanitaires liés aux activités",
    riskCategoryVaccination: "Vaccinations",
    riskCategoryMedication: "Médicaments",
    riskCategoryPrecaution: "Précautions",
    riskCategoryAdvisory: "Recommandations",
    riskCategoryWarning: "Avertissements",
    trend: "Tendance",
    increasing: "En hausse",
    decreasing: "En baisse",
    stable: "Stable",
    casesReported: "Cas Signalés",
    lastReport: "Dernier Rapport",
    mayAffectTravel: "Peut affecter le voyage",
    validity: "Validité",
    years: "ans",
    minDaysBeforeDeparture: "Jours min. avant départ",
    boosterRequired: "Rappel requis",
    contraindications: "Contre-indications",
    relatedDiseases: "Maladies connexes",
    ageRestrictions: "Restrictions d'âge",
    updated: "Mis à jour",
    noHealthDataAvailable: "Aucune donnée sanitaire disponible",
    failedToLoadHealthRecommendations:
      "Échec du chargement des recommandations sanitaires",
    backToDashboard: "Retour au Tableau de Bord",

    uploadDocument: "Télécharger un Document",
    documentType: "Type de Document",
    uploadDate: "Date de Téléchargement",
    fileSize: "Taille du Fichier",
    downloadDocument: "Télécharger le Document",
    documentName: "Nom du Document",

    // Chat
    chatWithTina: "Discuter avec Tina",
    tinaHealthAssistant: "Tina - Votre assistante santé",
    askQuestion: "Poser une Question",
    askMeAnything: "Demandez-moi tout sur votre santé",
    typeMessage: "Tapez un message...",
    typeYourMessage: "Tapez votre message...",
    sendMessage: "Envoyer",
    chatHistory: "Historique du Chat",
    newConversation: "Nouvelle conversation",
    conversations: "Conversations",
    includeHealthContext: "Inclure mes données de santé",
    healthContextEnabled:
      "Contexte santé activé - Tina peut voir vos médicaments, allergies et signes vitaux",
    healthContextDisabled:
      "Contexte santé désactivé - Tina répondra sans vos données de santé",
    aiThinking: "Tina réfléchit...",
    noConversationsYet: "Pas encore de conversations",
    startNewConversation: "Commencer une nouvelle conversation avec Tina",
    deleteConversation: "Supprimer la conversation",
    conversationDeleted: "Conversation supprimée",
    aiUnavailable: "Assistant IA indisponible",
    aiUnavailableMessage:
      "L'assistant IA n'est actuellement pas disponible. Veuillez réessayer plus tard.",
    messagePlaceholder:
      "Posez des questions sur les médicaments, symptômes, santé en voyage...",
    exampleQuestions: "Questions exemples:",
    example1: "Quels sont mes médicaments actuels?",
    example2: "Quels vaccins ai-je besoin pour voyager?",
    example3: "Expliquez mes derniers signes vitaux",

    // Profile
    personalInformation: "Informations Personnelles",
    contactInformation: "Informations de Contact",
    emergencyContact: "Contact d'Urgence",
    medicalInformation: "Informations Médicales",
    updateProfile: "Mettre à jour le Profil",
    changePassword: "Changer le Mot de Passe",
    phone: "Téléphone",
    dateOfBirth: "Date de Naissance",
    country: "Pays",
    nationality: "Nationalité",
    currentPassword: "Mot de Passe Actuel",
    newPassword: "Nouveau Mot de Passe",
    confirmNewPassword: "Confirmer le Nouveau Mot de Passe",
    languageSettings: "Paramètres de Langue",
    notificationSettings: "Paramètres de Notification",
    privacySettings: "Paramètres de Confidentialité",
    accountSettings: "Paramètres du Compte",
    dataExport: "Export de Données",
    deleteAccount: "Supprimer le Compte",

    // Account Deletion
    dangerZone: "Zone Dangereuse",
    deleteAccountWarning:
      "Une fois que vous supprimez votre compte, il n'y a pas de retour en arrière. Toutes vos données de santé, documents et informations personnelles seront définitivement supprimés de nos systèmes.",
    deleteAccountConfirmationPrompt:
      "Cette action ne peut pas être annulée. Toutes vos données seront définitivement supprimées.",
    typeToConfirm: "Tapez pour confirmer",
    enterYourPassword: "Entrez votre mot de passe",
    deleteAccountWarningDetails:
      "Tous vos dossiers de santé, documents, plans de voyage et conversations seront définitivement supprimés. Un abonnement payant est d'abord résilié et ne sera plus prélevé. Les journaux d'audit seront conservés à des fins de conformité.",
    confirmDeletion: "Supprimer Définitivement le Compte",
    deleting: "Suppression en cours...",
    accountDeletedSuccessfully:
      "Votre compte a été supprimé avec succès. Vous serez déconnecté sous peu.",
    deleteAccountError:
      "Échec de la suppression du compte. Veuillez réessayer ou contacter le support.",
    deleteAccountConfirmationError:
      'Veuillez taper "DELETE MY ACCOUNT" exactement pour confirmer',
    invalidPassword: "Mot de passe invalide. Veuillez réessayer.",
    passwordRequired: "Mot de passe requis",
    warning: "Avertissement",

    // Emergency Profile
    emergencyProfile: "Profil d'Urgence",
    myEmergencyProfile: "Mon Profil d'Urgence",
    emergencyData: "Données d'Urgence",
    completenessScore: "Score de Complétude",
    dataQuality: "Qualité des Données",
    dataQualityGood: "Bon - Toutes les données critiques complètes",
    dataQualityFair: "Correct - Certaines données manquantes",
    dataQualityPoor: "Insuffisant - Données critiques manquantes",
    criticalData: "Données Critiques",
    priority1Data: "Priorité 1: Critique",
    priority2Data: "Priorité 2: Important",
    priority3Data: "Priorité 3: Utile",
    missingCriticalData: "Données Critiques Manquantes",
    quickAdd: "Ajout Rapide",
    editEmergencyData: "Modifier les Données d'Urgence",
    shareEmergencyAccess: "Partager l'Accès d'Urgence",
    accessDuration: "Durée d'Accès",
    duration15min: "15 Minutes",
    duration1hour: "1 Heure",
    duration6hours: "6 Heures",
    duration24hours: "24 Heures",
    durationCustom: "Durée Personnalisée",
    dataScope: "Portée des Données",
    selectDataToShare: "Sélectionner les Données à Partager",
    emergencyQRCode: "Code QR d'Urgence",
    refreshQRCode: "Actualiser le Code QR",
    downloadQRCode: "Télécharger le Code QR",
    printQRCode: "Imprimer le Code QR",
    copyAccessLink: "Copier le Lien d'Accès",
    qrCodeExpiry: "Expiration du Code QR",
    addEmergencyContact: "Ajouter un Contact d'Urgence",
    primaryContact: "Contact Principal",
    secondaryContact: "Contact Secondaire",
    tertiaryContact: "Contact Tertiaire",
    contactName: "Nom du Contact",
    relationship: "Relation",
    phoneNumber: "Numéro de Téléphone",
    notifyOnAccess: "Notifier en Cas d'Accès d'Urgence",
    availabilityNotes: "Notes de Disponibilité",
    reorderContacts: "Réorganiser les Contacts",
    devices: "Dispositifs Médicaux",
    allergySubstance: "Substance Allergène",
    allergySeverity: "Sévérité",
    allergyReaction: "Réaction",
    conditionName: "Nom de la Condition",
    conditionStatus: "Statut",
    deviceType: "Type de Dispositif",
    deviceUDI: "UDI du Dispositif",
    verifiedDate: "Date de Vérification",
    outdatedWarning:
      "Ces données sont obsolètes et nécessitent une vérification",
    incompleteWarning: "Votre profil d'urgence est incomplet",
    updateRequired: "Mise à Jour Requise",
    dataVerifiedRecently: "Données vérifiées récemment",

    add: "Ajouter",
    update: "Mettre à jour",
    remove: "Retirer",
    search: "Rechercher",
    filter: "Filtrer",
    sort: "Trier",
    export: "Exporter",
    import: "Importer",
    refresh: "Actualiser",
    active: "Actif",
    inactive: "Inactif",
    pending: "En Attente",
    completed: "Terminé",
    cancelled: "Annulé",
    savedSuccessfully: "Enregistré avec Succès",
    deletedSuccessfully: "Supprimé avec Succès",
    errorOccurred: "Une Erreur s'est Produite",
    confirmDelete: "Confirmer la Suppression",
    areYouSure: "Êtes-vous Sûr?",
    cannotBeUndone: "Cette action ne peut pas être annulée",
    healthStatusOptimal: "État de Santé: Optimal",
    allDataCurrent: "Toutes vos données de santé sont à jour et complètes",
    pulse: "Pouls",
    addNewDocument: "Ajouter un Nouveau Document",
    addDocumentDesc: "Ajouter un document de santé",
    addMedicationDesc: "Gérer une nouvelle médication",
    planTravel: "Planifier un Voyage",
    planTravelDesc: "Nouveau voyage avec contrôle de santé",
    addHealthDataDesc: "Enregistrer les signes vitaux",
    upcomingReminders: "Rendez-vous et Rappels à Venir",
    highPriority: "Haute Priorité",
    mediumPriority: "Priorité Moyenne",
    lowPriority: "Faible Priorité",
    vaccination: "Vaccination",
    daysAgo: "Il y a jours",
    weeksAgo: "Il y a semaines",
    inDays: "Dans jours",
    immunizations: "Immunisations",
    manageImmunizations: "Gérez vos dossiers d'immunisation",
    newImmunization: "Nouvelle Immunisation",
    editImmunization: "Modifier l'Immunisation",
    deleteImmunization: "Supprimer l'Immunisation",
    vaccineName: "Nom du Vaccin",
    vaccineCode: "Code du Vaccin",
    manufacturer: "Fabricant",
    lotNumber: "Numéro de Lot",
    doseNumber: "Numéro de Dose",
    totalDoses: "Total des Doses",
    administeredDate: "Date d'Administration",
    expirationDate: "Date d'Expiration",
    nextDueDate: "Prochaine Date d'Échéance",
    location: "Lieu d'Administration",
    administrator: "Administré par",
    requiredForCountries: "Requis pour les Pays",
    uploadCertificate: "Télécharger le Certificat",
    downloadCertificate: "Télécharger le Certificat",
    certificateUploaded: "Certificat téléchargé avec succès",
    noCertificateFound: "Aucun certificat trouvé pour cette immunisation",
    certificateUploadFailed: "Échec du téléchargement du certificat",
    fhirData: "Données FHIR",
    internationalCertificate: "Certificat International",
    internationalVaccinationCertificate:
      "Certificat International de Vaccination",
    passportNumber: "Numéro de Passeport de Vaccination",
    healthPassDocumentId: "ID du Document HealthPass",
    certificateNumber: "Numéro de Certificat",
    issuerId: "ID de l'Émetteur",
    publicKeyFingerprint: "Empreinte de Clé Publique",
    signatureTimestamp: "Horodatage de la Signature",
    issuedBy: "Émis par",
    issuedDate: "Date d'Émission",
    validFrom: "Valide à partir de",
    patientInformation: "Informations sur le Patient",
    certificateInformation: "Informations sur le Certificat",
    digitalSignature: "Signature Numérique",
    qrCodeValidation: "QR Code pour Validation",
    printCertificate: "Imprimer le Certificat",
    copyCertificateData: "Copier les Données du Certificat",
    deleteConfirmation:
      "Êtes-vous sûr de vouloir supprimer cette immunisation?",
    noImmunizationsFound: "Aucune Immunisation Trouvée",
    addFirstImmunization: "Ajoutez votre première immunisation",
    certificateValidUntil: "Certificat valide jusqu'au",
    dosesCompleted: "Doses terminées",
    of: "de",
    loginSuccessful: "Bienvenue",
    loginFailed: "Échec de la connexion",
    loginInvalidOrPaused:
      "E-mail ou mot de passe incorrect. Après 10 tentatives échouées, la connexion à ce compte est suspendue jusqu'à 15 minutes.",
    registrationSuccessful: "Bienvenue sur HealthPass",
    registrationFailed: "Échec de l'inscription",
    logoutSuccessful: "Déconnexion réussie",
    sessionExpired: "Votre session a expiré. Veuillez vous reconnecter.",
    networkError: "Erreur réseau - Veuillez vérifier votre connexion internet",
    accessDenied: "Accès refusé - Autorisation insuffisante",
    validationError: "Erreur de validation",
    tooManyRequests: "Trop de requêtes - Veuillez réessayer plus tard",
    serverError: "Erreur serveur - Veuillez réessayer plus tard",

    // Medication System
    medicationsPage: "Médicaments",
    medicationItem: "Médicament",
    medicationPlan: "Plan de Médication",
    addNewMedication: "Ajouter un Médicament",
    editExistingMedication: "Modifier le Médicament",
    deleteMedicationItem: "Supprimer le Médicament",
    medicationName: "Nom du Médicament",
    genericName: "Nom Générique (DCI)",
    medicationDosage: "Dosage",
    dosageForm: "Forme Pharmaceutique",
    medicationStrength: "Dosage",
    medicationFrequency: "Fréquence",
    timingInstructions: "Instructions de Prise",
    mealTiming: "Rapport aux Repas",
    medicationStartDate: "Date de Début",
    medicationEndDate: "Date de Fin",
    medicationPrescribedBy: "Prescrit par",
    medicationPharmacy: "Pharmacie",
    medicationInstructions: "Instructions",
    medicationSideEffects: "Effets Secondaires",
    medicationInteractions: "Interactions",
    eanCode: "Code EAN",
    pznCode: "Code PZN",
    atcCode: "Code ATC",
    tablet: "Comprimé",
    capsule: "Capsule",
    syrup: "Sirop",
    injection: "Injection",
    cream: "Crème",
    ointment: "Pommade",
    drops: "Gouttes",
    spray: "Spray",
    patch: "Patch",
    suppository: "Suppositoire",
    powder: "Poudre",
    other: "Autre",
    inhaler: "Inhalateur",
    gel: "Gel",
    lotion: "Lotion",
    solution: "Solution",
    beforeMeal: "Avant le Repas",
    afterMeal: "Après le Repas",
    withMeal: "Avec le Repas",
    independent: "Indépendant des Repas",
    onEmptyStomach: "À Jeun",
    medicationRegular: "Médicament Régulier",
    medicationTravel: "Médicament de Voyage",
    medicationEmergency: "Médicament d'Urgence",
    medicationProphylaxis: "Prophylaxie",
    regular: "Régulier",
    emergency: "Urgence",
    travelMedication: "Médicament de Voyage",
    prophylaxis: "Prophylaxie",
    cruise: "Croisière",
    trekking: "Trekking",
    diving: "Plongée",
    tropical: "Tropiques",
    altitude: "Altitude",
    business: "Voyage d'Affaires",
    adventure: "Voyage d'Aventure",
    urban: "Voyage Urbain",
    recordIntake: "Enregistrer la Prise",
    intakeHistory: "Historique des Prises",
    adherence: "Observance",
    doseTaken: "Dose Prise",
    intakeNotes: "Notes de Prise",
    timeline: "Chronologie",
    lastTaken: "Dernière Prise",
    nextDue: "Prochaine Prise",
    internationalMedicationPlan: "Plan de Médication International",
    generatePlan: "Générer le Plan",
    uploadGermanPlan: "Télécharger le Plan de Médication Allemand",
    downloadPlan: "Télécharger le Plan",
    printPlan: "Imprimer le Plan",
    qrCodePlan: "QR Code du Plan",
    packageImage: "Image de l'Emballage",
    medicationImage: "Image du Médicament/Comprimé",
    uploadPackageImage: "Télécharger l'Image de l'Emballage",
    uploadMedicationImage: "Télécharger l'Image du Médicament",
    takePhoto: "Prendre une Photo",
    scanBarcode: "Scanner le Code-Barres",
    tradeName: "Nom Commercial",
    activeIngredient: "Principe Actif",
    manufacturerName: "Fabricant",
    internationalCode: "Code International",
    codeSystem: "Système de Codage",
    removeImage: "Supprimer l'Image",
    imageUploadSuccess: "Image téléchargée avec succès",
    imageUploadFailed: "Échec du téléchargement de l'image",
    imageDeleteSuccess: "Image supprimée avec succès",
    imageDeleteFailed: "Échec de la suppression de l'image",
    optional: "Optionnel",
    imagesSection: "Images",
    packagePhoto: "Photo de l'Emballage",
    pillPhoto: "Photo du Comprimé",
    gtinCode: "Code GTIN",
    ndcCode: "Code NDC (USA)",
    handelsname: "Nom Commercial",
    wirkstoff: "Principe Actif",
    hersteller: "Fabricant",
    pharmaCode: "Code Pharmaceutique",
    basicInformation: "Informations de Base",
    fhirExtendedFields: "Champs FHIR R5 Étendus",
    medicationImages: "Images du Médicament",
    medicationInformation: "Informations sur le Médicament",
    dosageInformation: "Dosage",
    prescription: "Ordonnance",
    note: "Note",
    selfSignedInfo:
      'Lorsque vous saisissez le médicament vous-même, il sera automatiquement marqué comme "Auto-signé" et lié à votre compte Voyageur.',
    medicationImagesDescription:
      "Téléchargez des images de l'emballage et des comprimés (optionnel)",
    medicationImagesHint:
      "Des images de haute qualité facilitent l'identification et peuvent être utiles pour la documentation de voyage.",
    saveChanges: "Enregistrer les Modifications",
    saveMedication: "Enregistrer le Médicament",
    choosePackageImage: "Choisir l'Image de l'Emballage",
    chooseMedicationImage: "Choisir l'Image du Médicament",
    category: "Catégorie",
    dosage: "Dosage",
    pharmacy: "Pharmacie",
    editMedication: "Modifier le Médicament",
    updateMedication: "Mettre à jour le Médicament",
    uploadingImages: "Téléchargement des Images...",
    saving: "Enregistrement...",
    travelRecommendations: "Recommandations de Voyage",
    supplementaryMedications: "Médicaments Complémentaires",
    interactionWarnings: "Avertissements d'Interactions",
    implementRecommendation: "Mettre en Œuvre la Recommandation",
    medicationAdded: "Médicament ajouté avec succès",
    medicationUpdated: "Médicament mis à jour avec succès",
    medicationDeleted: "Médicament supprimé avec succès",
    intakeRecorded: "Prise enregistrée avec succès",
    planGenerated: "Plan de médication généré avec succès",
    imageUploaded: "Image de l'emballage téléchargée avec succès",

    // Avatar Upload
    profilePicture: "Photo de Profil",
    managePersonalDataSettings: "Gérez vos données personnelles et paramètres",
    uploadPhoto: "Télécharger une Photo",
    deletePhoto: "Supprimer la Photo",
    invalidFileType:
      "Type de fichier invalide. Seuls JPEG, PNG, WebP, GIF sont autorisés",
    fileTooLarge: "Fichier trop volumineux. Taille maximale 5MB",
    maxFileSize:
      "Taille maximale: 5MB. Formats supportés: JPEG, PNG, WebP, GIF",
    avatarUploadedSuccessfully: "Avatar téléchargé avec succès",
    avatarUploadFailed: "Échec du téléchargement de l'avatar",
    confirmDeleteAvatar: "Êtes-vous sûr de vouloir supprimer votre avatar?",
    avatarDeletedSuccessfully: "Avatar supprimé avec succès",
    avatarDeleteFailed: "Échec de la suppression de l'avatar",
    cropImage: "Recadrer l'Image",
    rotate: "Pivoter",

    // Profile Page - Form Fields
    notSpecified: "Non spécifié",
    day: "Jour",
    month: "Mois",
    year: "Année",
    yearsOld: "ans",

    // Date/Month names
    january: "Janvier",
    february: "Février",
    march: "Mars",
    april: "Avril",
    may: "Mai",
    june: "Juin",
    july: "Juillet",
    august: "Août",
    september: "Septembre",
    october: "Octobre",
    november: "Novembre",
    december: "Décembre",

    // Address Fields
    address: "Adresse",
    street: "Rue",
    houseNumber: "Numéro",
    addressSupplement: "Complément d'adresse",
    postalCode: "Code postal",
    city: "Ville",
    state: "État/Région",
    streetName: "Nom de rue",
    houseNo: "N°",
    apartmentFloorEtc: "Appartement, Étage, etc. (optionnel)",
    postalCodePlaceholder: "Code postal",
    cityPlaceholder: "Ville",

    // Nationalities
    nationality_german: "Allemand",
    nationality_austrian: "Autrichien",
    nationality_swiss: "Suisse",
    nationality_french: "Français",
    nationality_italian: "Italien",
    nationality_spanish: "Espagnol",
    nationality_british: "Britannique",
    nationality_american: "Américain",
    nationality_canadian: "Canadien",
    nationality_dutch: "Néerlandais",
    nationality_belgian: "Belge",
    nationality_polish: "Polonais",
    nationality_turkish: "Turc",
    nationality_russian: "Russe",
    nationality_other: "Autre",

    // Profile Update Messages
    profileUpdatedSuccessfully: "Profil mis à jour avec succès !",
    profileUpdateMaybeSuccessful:
      "Le profil a peut-être été mis à jour - veuillez vérifier les modifications.",
    profileUpdatedButDataNotLoaded:
      "Profil mis à jour, mais les données n'ont pas été chargées correctement. Veuillez recharger la page.",
    correctFollowingInputs: "Veuillez corriger les entrées suivantes :",
    inputError: "Erreur de saisie",
    invalidInputs:
      "Entrées invalides. Veuillez vérifier tous les champs et réessayer.",
    unauthorizedPleaseLogin: "Non autorisé. Veuillez vous reconnecter.",
    noPermission: "Aucune autorisation pour cette action.",
    userNotFoundPleaseLogin:
      "Utilisateur introuvable. Veuillez vous reconnecter.",
    serverErrorTryLater:
      "Erreur du serveur. Veuillez réessayer plus tard ou contacter le support.",
    unknownError: "Erreur inconnue",
    noServerConnection:
      "Aucune connexion au serveur possible. Veuillez vérifier votre connexion Internet et réessayer.",
    unexpectedError:
      "Une erreur inattendue s'est produite. Veuillez réessayer ou contacter le support.",

    apply: "Appliquer",
    capture: "Capturer",
    retake: "Reprendre",
    usePhoto: "Utiliser la Photo",
    switchCamera: "Changer de Caméra",
    cameraAccessDenied: "Accès à la caméra refusé",
    photoCaptureFailed: "Impossible d'utiliser la photo. Veuillez réessayer.",
    checkCameraPermissions:
      "Veuillez vérifier les autorisations de la caméra dans les paramètres du navigateur",

    // Version History
    version: "Version",
    versionHistory: "Historique des versions",
    versionHistoryDescription:
      "Toutes les versions et modifications de HealthPass",
    currentVersion: "Version actuelle",
    newFeatures: "Nouvelles fonctionnalités",
    changes: "Modifications",
    bugFixes: "Corrections de bugs",
    versionHistoryNote:
      "Cet historique des versions est généré automatiquement à partir des commits Git et des notes de version.",
    back: "Retour",
    subscription: "Abonnement",

    faqTitle: "Questions fréquemment posées",
    faqSubtitle: "Tout ce que vous devez savoir sur HealthPass",
    faqQ1: "Qu'est-ce que HealthPass ?",
    faqA1:
      "HealthPass est une plateforme de santé numérique qui stocke vos informations médicales d'urgence et les rend accessibles dans le monde entier via un code QR. Elle est conçue spécialement pour les voyageurs qui ont besoin d'un accès instantané aux données de santé critiques en cas d'urgence.",
    faqQ2: "Comment fonctionne HealthPass ?",
    faqA2:
      "Créez simplement un compte, téléchargez vos informations médicales (allergies, groupe sanguin, médicaments, contacts d'urgence) et générez votre code QR personnel. Les premiers intervenants et les professionnels de santé peuvent scanner ce code pour accéder instantanément à vos informations de santé vitales, même si vous êtes inconscient.",
    faqQ3: "HealthPass est-il conforme au RGPD ?",
    faqA3:
      "Oui. HealthPass est conçu selon le RGPD : vos données circulent chiffrées, vous décidez qui voit quoi, et chaque fois que quelqu'un ouvre votre carte d'urgence ou un lien que vous avez partagé, c'est enregistré et vous pouvez le voir. Nos serveurs sont exploités pour nous par Hetzner aux États-Unis, dans le cadre des clauses contractuelles types de l'UE — la politique de confidentialité explique précisément ce que cela signifie. Nous ne partageons jamais vos données avec des tiers sans votre consentement explicite.",
    faqQ4: "Combien coûte HealthPass ?",
    faqA4:
      "Votre carte et l'accès d'urgence sont gratuits. PLUS coûte 9 et PREMIUM 15 par mois, en euros ou en dollars américains selon votre pays, et les deux commencent par 14 jours d'essai gratuit. Pour un seul voyage, PLUS coûte 25 et PREMIUM 39, payés une fois pour 90 jours. Les secours ne paient jamais pour lire vos données d'urgence.",
    faqQ5: "HealthPass peut-il sauver des vies ?",
    faqA5:
      "Oui. Lors d'urgences médicales à l'étranger, les barrières linguistiques et le manque d'historique médical peuvent être mortels. HealthPass fournit un accès instantané aux informations critiques comme les allergies, le groupe sanguin et les médicaments actuels, permettant des décisions de traitement plus rapides et plus sûres.",
    faqQ6: "Comment fonctionne l'accès par code QR ?",
    faqA6:
      "Votre code QR HealthPass peut être imprimé, ajouté à l'écran de verrouillage de votre téléphone ou porté comme bracelet. Lors du scan, il ouvre une vue d'urgence sécurisée avec vos informations de santé critiques. Aucune installation d'application n'est requise pour le personnel médical.",
    faqQ7: "Quels sont les cas d'utilisation courants en voyage ?",
    faqA7:
      "HealthPass est essentiel pour :\n• Les voyageurs aventuriers visitant des zones reculées\n• Les seniors voyageant à l'international\n• Les personnes atteintes de maladies chroniques ou d'allergies\n• Les familles voyageant avec des enfants\n• Les voyageurs solo dans des pays non francophones\n• Toute personne nécessitant des médicaments réguliers",
    faqQ8: "Quelle est la sécurité de mes données ?",
    faqA8:
      "Vos données sont chiffrées en transit et au repos, la connexion passe par un serveur d'identité dédié avec des jetons de courte durée, et chaque consultation d'un dossier de santé est journalisée — vous pouvez demander ce journal. Nos serveurs sont exploités pour nous par Hetzner aux États-Unis, sur la base des clauses contractuelles types de l'UE. La page Sécurité détaille l'ensemble.",
    faqQ9: "Quelles langues sont prises en charge ?",
    faqA9:
      "HealthPass est disponible en anglais, allemand, français, espagnol et portugais. Vos informations de santé d'urgence peuvent être affichées dans plusieurs langues simultanément, garantissant que le personnel médical du monde entier peut les lire.",
    faqQ10: "Comment les services d'urgence accèdent-ils à mes données ?",
    faqA10:
      "Les services d'urgence scannent votre code QR avec n'importe quel appareil photo de smartphone. Ils voient instantanément vos informations critiques sans avoir besoin de se connecter ou de télécharger une application. L'accès est enregistré pour votre sécurité et vous pouvez révoquer les codes d'accès d'urgence à tout moment.",
    faqStillHaveQuestions: "Vous avez encore des questions ?",
    faqContactUs:
      "Notre équipe d'assistance est là pour vous aider 24h/24 et 7j/7",
    contactSupport: "Contacter le support",

    // Medical Incidents & Intolerances
    medicalIncidents: "Incidents médicaux",
    addNewIncident: "Ajouter un nouvel incident",
    icdCodeOptional: "Code ICD (optionnel)",
    diagnosisDesignation: "Diagnostic/Désignation",
    diagnosisPlaceholder: "p. ex. Infarctus, fracture, chirurgie...",
    incidentDetailsPlaceholder: "Détails supplémentaires sur l'incident...",
    addIncident: "Ajouter un incident",
    noMedicalIncidentsDocumented: "Aucun incident médical documenté",
    intolerances: "Intolérances",
    foodIntolerances: "Intolérances alimentaires",
    foodIntolerancesPlaceholder: "p. ex. Lactose, Gluten",
    specificDiseases: "Maladies spécifiques",
    coagulationDisorder: "Trouble de la coagulation",
    heartAttack: "Infarctus",
    heartAttackDatePlaceholder: "Date de l'infarctus",
    stroke: "AVC",
    strokeDatePlaceholder: "Date de l'AVC",

    // Extended Health Components
    allergiesAndIntolerances: "Allergies & Intolérances",
    noAllergiesDocumented: "Aucune allergie documentée",
    medicalConditions: "Conditions Médicales",
    noMedicalConditionsDocumented: "Aucune condition médicale documentée",
    medicalProcedures: "Procédures Médicales",
    noMedicalProceduresDocumented: "Aucune procédure médicale documentée",
    treatmentHistory: "Historique des Traitements",
    noTreatmentHistoryDocumented: "Aucun historique de traitement documenté",

    // Vital Signs
    allFieldsRequired: "Tous les champs sont requis",
    systolicMustBeHigher:
      "La pression systémique doit être supérieure à la diastolique",
    recordFirstMeasurement: "Enregistrer la première mesure",
    loadingHistory: "Chargement de l'historique...",
    noHistoryData: "Aucune donnée d'historique disponible",
    firstMeasurementsWillAppear: "Les premières mesures apparaîtront ici",

    // Unit Inputs
    enterValue: "Entrer la valeur",

    // Gender
    gender: "Sexe",
    male: "Homme",
    female: "Femme",
    preferNotToSay: "Ne souhaite pas répondre",

    // Emergency System
    // Emergency System
    emergencyQRDescription: "Partagez vos données médicales avec les secours",
    noActiveQRCode: "Aucun QR code actif",
    generateQRCodeDescription:
      "Générer un QR code pour partager vos données d'urgence",
    qrCodeDuration: "Durée du QR code",
    generateQRCode: "Générer un QR code",
    regenerate: "Régénérer",
    generating: "Génération en cours...",
    qrCodeGenerated: "QR code généré avec succès",
    qrCodeGenerationFailed: "Échec de la génération du QR code",
    timeRemaining: "Temps restant",
    accessCount: "Compteur d'accès",
    accessUrl: "URL d'accès",
    urlCopied: "URL copiée dans le presse-papiers",
    copyFailed: "Échec de la copie",
    copied: "Copié",
    copy: "Copier",
    downloadQR: "Télécharger le QR code",
    qrCodeDownloaded: "QR code téléchargé",
    shareEmergencyData: "Partager les données d'urgence",
    shareDialogDescription: "Choisissez comment partager vos données d'urgence",
    expired: "Expiré",
    pleaseRegenerate: "Veuillez régénérer",
    howItWorks: "Comment ça marche",
    emergencyQRHowItWorksText:
      "Les secours scannent ce QR code avec un smartphone",
    share: "Partager",
    emergencyDataAccess: "Accès aux données d'urgence",
    forEmergencyRespondersOnly: "Réservé aux secours",
    scanQROrEnterToken: "Scanner le QR code ou entrer le jeton",
    qrScannerPlaceholder: "Scanner QR",
    qrScannerInstructions: "Dirigez la caméra vers le QR code",
    orEnterTokenManually: "Ou entrez le jeton manuellement",
    enterAccessToken: "Entrer le jeton d'accès",
    continue: "Continuer",
    responderInformation: "Informations du secouriste",
    responderInformationDescription: "Veuillez fournir vos informations",
    fullName: "Nom complet",
    enterYourFullName: "Entrez votre nom complet",
    organization: "Organisation",
    hospitalOrganizationName: "Hôpital ou organisation",
    professionalId: "Numéro professionnel",
    licenseBadgeNumber: "Numéro de licence ou badge",
    yourEmail: "Votre adresse e-mail",
    yourPhone: "Votre numéro de téléphone",
    reasonForAccess: "Raison de l'accès",
    briefDescriptionEmergency: "Brève description",
    emergencyAccessConsent: "Consentement d'accès d'urgence",
    emergencyAccessConsentText:
      "Je confirme que j'accède à ces données dans une situation d'urgence",
    pleaseAcceptConsent: "Veuillez accepter le consentement",
    accessEmergencyData: "Accéder aux données d'urgence",
    accessing: "Accès en cours...",
    emergencyDataAccessGranted: "Accès aux données d'urgence accordé",
    pleaseEnterToken: "Veuillez entrer un jeton d'accès",
    pleaseProvideResponderInfo: "Veuillez fournir vos informations",
    accessTokenInvalid: "Jeton d'accès invalide ou expiré",
    criticalAllergies: "ALLERGIES CRITIQUES",
    emergencyContacts: "Contacts d'urgence",
    noEmergencyContactsAvailable: "Aucun contact d'urgence disponible",
    bloodType: "Groupe sanguin",
    age: "Âge",
    name: "Nom",
    unknown: "Inconnu",
    disclaimer: "Avertissement",
    emergencyDataDisclaimerText:
      "Ces informations sont fournies par le patient",

    // PWA & Offline
    installApp: "Installer HealthPass",
    installAppDescription:
      "Installez HealthPass pour un accès rapide et une utilisation hors ligne",
    install: "Installer",
    later: "Plus tard",
    connectionRestored: "Connexion rétablie",
    connectionRestoredSyncing:
      "Connexion rétablie - Synchronisation des données",
    offlineMode: "Mode hors ligne",
    offlineModeDescription:
      "Mode hors ligne - Les données déjà chargées sont disponibles",
    slowConnection: "Connexion lente",
    slowConnectionDescription:
      "Connexion lente - Certaines fonctionnalités peuvent être ralenties",

    // Sync Status
    never: "Jamais",
    justNow: "À l'instant",
    pendingSync: "en attente",
    lastSync: "Dernière synchronisation",

    // Trip Wizard
    tripWizardTitle: "Planifier un nouveau voyage",
    tripWizardStep1: "Bases",
    tripWizardStep2: "Détails",
    tripWizardStep3: "Étapes",
    tripWizardStep4: "Santé",
    tripWizardStep5: "Réviser",
    tripWizardNext: "Suivant",
    tripWizardPrevious: "Retour",
    tripWizardSave: "Créer le voyage",
    tripWizardSaving: "Création en cours...",
    tripWizardStep1Title: "Informations de base",
    tripWizardStep2Title: "Détails du voyage",
    tripWizardStep3Title: "Étapes / Segments",
    tripWizardStep4Title: "Bilan de santé",
    tripWizardStep5Title: "Réviser et enregistrer",
    tripWizardStep1Desc: "Titre, destination et dates",
    tripWizardStep2Desc: "Objectif, type, opérateur et compagnons",
    tripWizardStep3Desc: "Ajouter vols, hôtels et activités",
    tripWizardStep4Desc: "Risques sanitaires et vaccinations requises",
    tripWizardStep5Desc: "Vérifiez tout avant de créer",
    tripWizardReviewSummary: "Résumé du voyage",
    tripWizardDuration: "Durée",
    tripWizardSegmentCount: "Étapes",
    tripWizardAddSegment: "Ajouter une étape",
    tripWizardSkipSegments: "Passer – ajouter plus tard",
    tripHealthOptional:
      "Cette étape est facultative. Vous pouvez poursuivre la planification et enregistrer votre voyage sans elle.",
    tripHealthUpsellTitle: "Plus de confiance pour un voyage en bonne santé",
    tripHealthUpsellIntro:
      "Le bilan de santé de la destination fait partie de PLUS. Avec lui, vous serez mieux préparé pour {country} :",
    tripHealthBenefitVaccinations:
      "Vaccins obligatoires et recommandés pour votre destination",
    tripHealthBenefitWarnings:
      "Risque de paludisme et alertes sanitaires actuelles pour votre destination",
    tripHealthBenefitTracking: "Suivi de vos propres vaccinations",
    tripHealthBenefitDocuments:
      "Stockage de documents pour vos certificats et papiers de voyage",
    tripHealthBenefitOffline:
      "Accès hors ligne à vos données de santé pendant le voyage",
    tripHealthSingleTrip:
      "Vous ne prévoyez que ce voyage ? PLUS Single Trip est un paiement unique qui couvre 90 jours, sans abonnement ensuite.",
    tripHealthUpgradeCta: "Passer à PLUS",
    tripHealthSkipCta: "Continuer sans bilan de santé",
    tripHealthDraftKept:
      "Votre brouillon de voyage est conservé. Revenez à « Planifier un nouveau voyage » pour reprendre là où vous en étiez.",
    tripHealthEmpty:
      "Nos sources n'indiquent ni vaccins ni alertes sanitaires pour {country}.",
    tripHealthLoadFailed:
      "Le bilan de santé n'a pas pu être chargé pour le moment. Vous pouvez continuer et le consulter plus tard sur la page du voyage.",
    tripTypeHiking: "Randonnée",
    tripTypeCamping: "Camping",
    tripTypeRoadTrip: "Road Trip",
    purposeScience: "Science",
    inviteCompanion: "Inviter un compagnon",
    companionEmail: "E-mail du compagnon",
    companionEmailPlaceholder: "email@exemple.fr",
    sendInvitation: "Envoyer l'invitation",
    companionInviteSent: "Invitation envoyée avec succès",
    companionInviteFailed: "Échec de l'envoi de l'invitation",
    companionStatusPending: "En attente",
    companionStatusAccepted: "Acceptée",
    companionStatusDeclined: "Refusée",
    removeCompanion: "Retirer",
    companionRemoved: "Compagnon retiré",
    noCompanionsYet: "Aucun compagnon invité pour l'instant",
    companionInviteDesc:
      "Invitez d'autres utilisateurs HealthPass à rejoindre ce voyage",
    companionRequiresPLUS:
      "Inviter des compagnons nécessite un abonnement PLUS",
    segmentDocuments: "Documents",
    uploadSegmentDocument: "Télécharger un document",
    docCategoryTicket: "Billet",
    docCategoryBooking: "Confirmation de réservation",
    docCategoryVoucher: "Bon",
    docCategoryInsurance: "Assurance",
    docCategoryOther: "Autre",
    documentUploaded: "Document téléchargé avec succès",
    documentDeleted: "Document supprimé",
    documentUploadFailed: "Échec du téléchargement",
    maxFileSizeHint: "Max. 10 Mo · PDF, JPEG, PNG",
    tripMap: "Carte du voyage",
    tripMapLoading: "Chargement de la carte...",
    tripMapNoData: "Aucune coordonnée d'étape disponible",
    tripMapRequiresPREMIUM:
      "La carte interactive nécessite un abonnement PREMIUM",
    featureRequiresPLUS: "Cette fonctionnalité nécessite un abonnement PLUS",
    featureRequiresPREMIUM:
      "Cette fonctionnalité nécessite un abonnement PREMIUM",
    upgradeToUnlock: "Mettre à niveau pour débloquer",
    upgradePlanLabel: "Mettre à niveau le plan",
    statusAutomatic: "Automatique",
    statusManual: "Manuel",
    statusOverrideHint:
      "Le statut est défini automatiquement selon les dates. Vous pouvez le remplacer manuellement.",
    markAsManual: "Définir manuellement",

    // HealthPass Card
    healthpassCard: "Carte HealthPass",
    cardWallet: "Portefeuille",
    cardNumber: "Numéro de carte",
    validUntil: "Valable jusqu'au",
    showFront: "Recto",
    showBack: "Voir le portefeuille",
    downloadPDF: "Télécharger PDF",
    shareCard: "Partager la carte",
    generateQR: "Générer QR Code",
    cardShares: "Liens partagés",
    createShareLink: "Créer un lien",
    shareExpiry: "Lien expire dans",
    shareExpiry24h: "24 heures",
    shareExpiry72h: "72 heures",
    shareExpiry7d: "7 jours",
    sharePassword: "Mot de passe (optionnel)",
    revokeShare: "Révoquer l'accès",
    shareRevoked: "Lien révoqué",
    shareCreated: "Lien de partage créé",
    shareCopied: "Lien copié",
    noSharesYet: "Aucun lien actif",
    shareAccessCount: "Accédé",
    shareTimes: "fois",
    walletEmergency: "Dossier d'urgence",
    walletMedications: "Plan de médication",
    walletVaccinations: "Carnet de vaccination",
    walletCheckin: "Documents d'embarquement",
    walletAppointments: "Rendez-vous",
    walletInsurance: "Assurances",
    walletTravelDocs: "Documents de voyage",
    walletLabResults: "Résultats de labo",
    upgradeForWalletItem: "Passez à {tier} pour débloquer",
    cardLoading: "Chargement de la carte...",
    cardError: "Impossible de charger la carte",
    printPreview: "Aperçu impression",
    printFormatA4: "A4",
    printFormatA5: "A5",
    printFormatLetter: "US Letter",
    printFormatCard: "Format carte de crédit",
    // Traveller navigation (UX_CONCEPT.md §4)
    navHome: "Accueil",
    navTrips: "Voyages",
    navCardWallet: "Carte et portefeuille",
    navCard: "Carte",
    navHealth: "Santé",
    navMe: "Compte",
    navWallet: "Portefeuille",
    navShares: "Liens partagés",
    navAccessRequests: "Demandes d'accès",
    navCheckIn: "Enregistrement",
    navConsentRequests: "Demandes de consentement",
    navAssistant: "Assistant",
    navPlan: "Abonnement",
    navAccount: "Compte",
    navSubscriptions: "Abonnements",
    navPayment: "Paiement",
    navInvoices: "Factures",
    navCompliance: "Conformité",
    navNotifications: "Notifications",
    complianceDescription:
      "Vos données et vos droits : ce qui est conservé, qui l'a consulté, ce que vous avez accepté, une copie de tout et la suppression de votre compte.",
    complianceStatistics: "Statistiques",
    complianceLog: "Journal",
    complianceLogLead:
      "Chaque consultation de vos données d'urgence, par qui et quand.",
    complianceConsents: "Consentements",
    complianceExport: "Export",
    complianceDeleteAccount: "Supprimer le compte",
    statsMemberSince: "Membre depuis",
    statsTrips: "Voyages",
    statsMedications: "Médicaments",
    statsImmunizations: "Vaccinations",
    statsUnavailable: "Indisponible pour le moment",
    settingsNotificationsDescription:
      "Choisissez ce dont HealthPass vous informe, et comment.",
    navCollapseMenu: "Réduire le menu",
    navExpandMenu: "Développer le menu",
    navSectionPages: "Pages de cette section",

    // Home (UX_CONCEPT.md §5.1)
    homeGoodMorning: "Bonjour",
    homeGoodAfternoon: "Bon après-midi",
    homeGoodEvening: "Bonsoir",
    homeBetaNotice:
      "HealthPass est en version bêta et déploie ses services progressivement. Nous vous tiendrons informé.",
    homeDismiss: "Fermer",
    homeNextTrip: "Prochain voyage",
    homeInDays: "dans {n} jours",
    homeToday: "aujourd'hui",
    homeTomorrow: "demain",
    homeDepart: "Départ",
    homeReturn: "Retour",
    homeNights: "{n} nuits",
    homeCheckTrip: "Voir ce dont ce voyage a besoin",
    homeMoreTrips: "{n} autres voyages prévus",
    homeNoTripTitle: "Où allez-vous ensuite ?",
    homeNoTripBody:
      "Planifiez un voyage et HealthPass détermine ce dont vous avez besoin.",
    homePlanTrip: "Planifier un voyage",
    homeYourCard: "Votre HealthPass",
    homeShowWallet: "Afficher le portefeuille",
    homeShowFront: "Afficher le recto",
    homeCardHintFront:
      "Montrez le recto à l'enregistrement. Touchez la carte pour voir votre portefeuille.",
    homeCardHintBack:
      "Votre portefeuille : tout ce que porte la carte. Touchez pour la retourner.",
    homeNextStep: "Prochaine étape",
    homeNotNow: "Pas maintenant",
    stepEmergencyTitle: "Ajoutez votre groupe sanguin",
    stepEmergencyBody:
      "Il figure sur votre carte, pour que les secours le lisent partout dans le monde.",
    stepEmergencyAction: "Compléter le profil d'urgence",
    stepBpTitle: "Contrôlez à nouveau votre tension",
    stepBpBodyAbnormal:
      "Votre dernière mesure, {value}, était {status}. Une nouvelle mesure prend deux minutes.",
    stepBpBodyStale:
      "Votre dernière mesure, {value}, a été prise {age}. Une nouvelle prend deux minutes.",
    stepBpAction: "Saisir une mesure",
    stepTripTitle: "Préparez-vous pour {destination}",
    stepTripBody:
      "Voyez quels vaccins, médicaments et papiers ce voyage demande.",
    stepTripAction: "Ouvrir la liste",
    stepPlanTitle: "Planifiez votre prochain voyage",
    stepPlanBody:
      "Dites-nous où et quand, et nous vérifions ce qu'il vous faut avant de partir.",
    homeAtAGlance: "En un coup d'œil",
    homeAllHealthData: "Toutes les données de santé",
    homeNoVitals:
      "Aucune mesure pour l'instant. Ajoutez-en une et elle apparaîtra ici.",
    homeVitalsError: "Vos mesures n'ont pas pu être chargées.",
    homeRetry: "Réessayer",
    ageToday: "aujourd'hui",
    ageDays: "il y a {n} j",
    ageMonths: "il y a {n} mois",
    ageYears: "il y a {n} an(s)",
    ageUnknown: "date inconnue",
    vitalLow: "Bas",
    vitalElevated: "Élevé",
    vitalHigh: "Haut",
    vitalUnderweight: "Insuffisance pondérale",
    vitalOverweight: "Surpoids",
    vitalObese: "Obésité",
    vitalRecorded: "Enregistré",
    weightLabel: "Poids",
    bmiLabel: "IMC",
    stepBpBodyUndated:
      "Votre dernière mesure, {value}, n'a pas de date. Une nouvelle prend deux minutes.",
    homeDayUnit: "jour",
    homeDaysUnit: "jours",

    // Trip readiness (UX_CONCEPT.md §6.1)
    readinessCaption: "prêt à partir",
    readinessAllDone: "Prêt à partir !",
    readinessStepsLeft: "étapes restantes",
    readinessSaveFailed:
      "Votre liste n'a pas pu être enregistrée, les coches ne seront pas conservées. Rechargez pour réessayer.",
    readinessNotForMe: "Pas pour moi",
    readinessCountAgain: "Compter à nouveau",
    readinessNotChecked: "Pas encore vérifié",
    homeRemindTonight: "Me le rappeler ce soir",
    homeReminderSet: "Rappel à {time}",
    homeReminderFailed: "Échec — réessayer",
    // Passport stamps and levels (UX_CONCEPT.md §7)
    passportTitle: "Votre passeport",
    passportStamps: "{count} tampons",
    passportOneStamp: "1 tampon",
    passportAt100: "à 100 %",
    passportNextStamp: "Soyez prêt pour {city} et le tampon est à vous.",
    passportOneMore: "Encore un et vous devenez {level}.",
    passportFirst:
      "Amenez un voyage à 100 % avant le départ, et son tampon arrive ici.",
    passportToNext: "Encore {count} pour {level}.",
    passportTop: "Niveau maximal atteint. Bon voyage.",
    passportFailed: "Votre passeport n'a pas pu être chargé.",
    passportOpenTrip: "Ouvrir la liste pour {city}",
    levelStarter: "Débutant",
    levelExplorer: "Explorateur",
    levelGlobetrotter: "Globe-trotteur",
    levelNomad: "Nomade",
    stampEarned: "Tampon obtenu",
    stampEarnedBody: "Il est dans votre passeport. Excellent voyage !",
    stampEarnedLevelUp: "Vous êtes {level} maintenant. Excellent voyage !",
    // First run (UX_CONCEPT.md §5.5)
    frStepOf: "Étape {n} sur 3",
    frTitleYou: "Créons votre carte.",
    frLeadYou: "Chaque réponse apparaît aussitôt sur votre carte.",
    frTitleBasics: "Votre carte prend forme.",
    frLeadBasics:
      "Encore quelques réponses et les secours pourront vous aider partout dans le monde.",
    frTitleTrip: "Votre carte est prête.",
    frLeadTrip:
      "Scannez-la, montrez-la, partagez-la. Et maintenant : où partez-vous ?",
    frTitleDone: "Votre premier voyage est prévu.",
    frLeadDone:
      "Nous avons commencé la liste de ce qu'il faut pour {city}. Cochez-la et gagnez votre premier tampon.",
    frStepYou: "Vous sur la carte",
    frStepYouHint: "Nom, photo, nationalité",
    frStepBasics: "Urgence : l'essentiel",
    frStepBasicsHint: "Groupe sanguin, allergies, médicaments, un contact",
    frStepTrip: "Votre prochain voyage",
    frStepTripHint: "Où et quand — nous vérifions ce qu'il vous faut",
    frYourSteps: "Vos trois étapes",
    frDone: "Terminé",
    frMinLeft: "encore {n} min",
    frQrAfter: "QR après l'étape 2",
    frBasicsLabel: "URGENCE · {n} SUR 4",
    frYouQ: "Pour qui est cette carte ?",
    frFirstName: "Prénom",
    frLastName: "Nom",
    frDateOfBirth: "Date de naissance",
    frNationality: "Nationalité",
    frNationalityPlaceholder: "p. ex. française",
    frAddPhoto: "Ajouter une photo",
    frChangePhoto: "Changer la photo",
    frPhotoFailed: "La photo n'a pas pu être envoyée. Essayez-en une autre.",
    frPutOnCard: "Mettre sur ma carte",
    frAddToCard: "Ajouter à ma carte",
    frBloodQ: "Quel est votre groupe sanguin ?",
    frBloodHint: "C'est la première chose qu'un hôpital vérifie.",
    frBloodUnknown: "Je ne sais pas",
    frBloodChip: "Groupe {type}",
    frBloodChipEmpty: "Groupe sanguin ?",
    frAllergyQ: "Êtes-vous allergique à quelque chose ?",
    frAllergyHint:
      "Affiché en anglais à toute personne qui scanne votre carte en urgence.",
    frAlreadyOnCard: "Déjà sur votre carte",
    frSomethingElse: "Autre chose",
    frAllergyPlaceholder: "p. ex. latex, piqûres d'abeille",
    frNoAllergies: "Aucune allergie",
    frNothingToAdd: "Rien à ajouter",
    frAllergyChips: "{n} allergies",
    frAllergyChipOne: "1 allergie",
    frAllergyChipNone: "Aucune allergie",
    frAllergyChipEmpty: "Allergies ?",
    frMedsQ: "Prenez-vous des médicaments régulièrement ?",
    frMedsHint:
      "Les secours doivent le savoir avant de vous donner quoi que ce soit.",
    frMedsPlaceholder: "p. ex. Metformine 500 mg",
    frAdd: "Ajouter",
    frRemove: "Retirer",
    frNoMeds: "Aucun",
    frContactQ: "Qui devons-nous appeler ?",
    frContactHint:
      "Votre contact d'urgence, visible par les secours qui scannent votre carte.",
    frContactName: "Nom",
    frContactPhone: "Téléphone, avec l'indicatif",
    frContactPhoneInvalid:
      "Saisissez un numéro en chiffres, p. ex. +33 1 23 45 67 89.",
    frRelationship: "Lien",
    frRelPartner: "Partenaire",
    frRelParent: "Parent",
    frRelChild: "Enfant",
    frRelSibling: "Frère ou sœur",
    frRelFriend: "Ami(e)",
    frSkip: "Plus tard",
    frTripQ: "Où partez-vous ensuite ?",
    frTripCity: "Ville ou lieu",
    frTripCountry: "Pays",
    frTripDeparture: "Départ",
    frTripReturn: "Retour",
    frTripPlan: "Prévoir ce voyage",
    frTripLater: "Pas encore de voyage prévu",
    frOpenChecklist: "Voir ce qu'il faut pour {city}",
    frGoHome: "Aller à l'accueil",
    frSaveFailed:
      "Cela n'a pas été enregistré. Vérifiez votre connexion et réessayez.",
    frPrivacy:
      "Vos données de santé sont chiffrées et restent les vôtres. Vous décidez qui les voit, et vous voyez chaque accès.",
    // Card page (UX_CONCEPT.md §5.2)
    cardSideFront: "Recto",
    cardSideWallet: "Côté portefeuille",
    cardDownload: "Télécharger la carte",
    cardDownloadFailed: "La carte n'a pas pu être téléchargée. Réessayez.",
    cardOfflineCodeShow: "Pas de réseau ? Afficher le code hors ligne",
    cardOnlineCodeShow: "Afficher le code en ligne",
    cardOfflineCodeHint:
      "Ce code contient votre résumé d'urgence et fonctionne sans internet.",
    // Shared card page
    sharedCardTitle: "Une carte HealthPass partagée",
    sharedCardLead:
      "Son titulaire vous a partagé cette carte. Elle indique qui il est, pas ses données de santé.",
    sharedCardNoCode: "Carte partagée",
    sharedCardPasswordLead:
      "Le titulaire a protégé cette carte par un mot de passe.",
    sharedCardPassword: "Mot de passe",
    sharedCardOpen: "Ouvrir la carte",
    sharedCardWrongPassword: "Ce mot de passe n'est pas le bon.",
    sharedCardGone: "Ce lien a expiré ou a été retiré par son titulaire.",
    sharedCardMissing:
      "Ce lien n'existe pas. Vérifiez qu'il a été copié en entier.",
    sharedCardError:
      "La carte n'a pas pu être chargée. Réessayez dans un instant.",
    settingsExportLead:
      "Téléchargez tout ce que HealthPass conserve sur vous, dans un fichier à garder ou à emporter vers un autre service.",
    settingsExportDone: "Vos données ont été téléchargées.",
    settingsExportFailed: "Le téléchargement a échoué. Veuillez réessayer.",
    // Payment success page
    payConfirming: "Confirmation de votre paiement…",
    payConfirmingLine: "Cela prend quelques secondes.",
    payWelcome: "Bienvenue dans {plan}",
    payTrialLine:
      "Votre essai gratuit court jusqu'au {date}. Ensuite, votre abonnement se renouvelle chaque mois, sauf résiliation avant cette date.",
    payTripLine: "Votre forfait voyage court jusqu'au {date}.",
    payActiveLine: "Votre forfait est actif.",
    payPendingTitle: "Votre paiement est en route",
    payPendingLine:
      "Certains moyens de paiement prennent plus de temps. Votre forfait s'active dès réception du paiement, et nous vous préviendrons.",
    payFailedTitle: "Ce paiement n'a pas abouti",
    payFailedLine:
      "Rien n'a été débité. Vous pouvez choisir un forfait quand vous le souhaitez.",
    payContinue: "Continuer",
    payBuildCard: "Créer votre carte",
    payBackToPlans: "Retour aux forfaits",
    renewTermsMonthly:
      "Les nouveaux membres ont 14 jours gratuits, puis {price} par mois. L'abonnement se renouvelle chaque mois jusqu'à résiliation, possible à tout moment dans vos paramètres de facturation.",
    renewTermsTrip:
      "Un paiement unique de {price} pour 90 jours. Sans renouvellement.",
    // Partner offers (REQ-025)
    marketplaceTitle: "Offres partenaires",
    marketplaceIntro:
      "Offres d'assurance de nos partenaires pour ce voyage. HealthPass ne les compare ni ne les recommande ; l'assureur répond aux questions sur la couverture.",
    marketplaceDisclosurePerContract:
      "Offre de {insurer}. HealthPass perçoit une rémunération si vous souscrivez un contrat.",
    marketplaceDisclosurePerVisit:
      "Offre de {insurer}. HealthPass perçoit une rémunération lorsque vous visitez {insurer}.",
    marketplaceVisit: "Aller sur {insurer}",
    marketplaceSeeAll: "Ouvrir la page des offres partenaires",
    marketplaceEmpty: "Aucune offre partenaire pour ce voyage.",
    marketplaceBackToTrip: "Retour au voyage",
    marketplaceOpenFailed:
      "L'offre n'a pas pu être ouverte. Veuillez réessayer.",
    marketplaceSettingTitle: "Afficher les offres partenaires",
    marketplaceSettingHint:
      "Offres d'assurance de partenaires sur vos voyages. Elles sont choisies uniquement selon la destination, les dates et le pays de résidence, jamais selon vos données de santé. Désactivé, elles sont masquées partout.",
    marketplaceSettingSaved: "Enregistré.",
    marketplaceSettingFailed: "Le réglage n'a pas pu être enregistré.",
  },

  es: {
    // Navigation
    login: "Iniciar sesión",
    register: "Registrarse",
    dashboard: "Panel",
    profile: "Perfil",
    health: "Salud",
    medications: "Medicamentos",
    travel: "Viaje",
    documents: "Documentos",
    chat: "Chat",
    settings: "Configuración",
    logout: "Cerrar sesión",
    appearance: "Apariencia",
    security: "Seguridad",

    // Auth
    welcomeBack: "Viaja con tranquilidad",
    signInToAccount: "Inicia sesión en tu cuenta",
    createAccount: "Crea tu cuenta",
    createAccountSubtitle: "Configure su cuenta de HealthPass",
    settingsGeneralDescription: "Notificaciones, datos y resumen de la cuenta",
    settingsSecurityDescription:
      "Inicio de sesión, verificación en dos pasos y privacidad",
    settingsAppearanceDescription: "Idioma y tema de color",
    themeTitle: "Tema",
    themeLight: "Claro",
    themeDark: "Oscuro",
    themeSystem: "Sistema",
    themeHint:
      "Sistema sigue la configuración de su dispositivo y cambia con ella.",
    orientationTitle: "Orientación de la pantalla",
    orientationPortrait: "Vertical fijo",
    orientationSystem: "Orientación del sistema",
    orientationHint:
      "El vertical fijo mantiene la aplicación derecha sostenga como sostenga el teléfono. La orientación del sistema sigue el ajuste de rotación de su dispositivo.",
    accountStatistics: "Estadísticas de la cuenta",
    notificationsEmailHint:
      "Actualizaciones y recordatorios importantes por correo electrónico",
    remindersTitle: "Recordatorios",
    fhirStandardNote: "Datos médicos en un formato normalizado.",
    email: "Correo electrónico",
    password: "Contraseña",
    confirmPassword: "Confirmar contraseña",
    firstName: "Nombre",
    lastName: "Apellido",
    forgotPassword: "¿Olvidaste tu contraseña?",
    rememberMe: "Recuérdame",
    signIn: "Iniciar sesión",
    signUp: "Registrarse",
    alreadyHaveAccount: "¿Ya tienes una cuenta?",
    noAccountYet: "¿Nuevo en HealthPass?",
    createFreeAccount: "Crear cuenta gratuita",
    backToLogin: "Volver al inicio de sesión",

    // Common
    loading: "Cargando...",
    error: "Error",
    success: "Éxito",
    cancel: "Cancelar",
    save: "Guardar",
    delete: "Eliminar",
    edit: "Editar",
    view: "Ver",
    viewAll: "Ver todo",
    close: "Cerrar",
    pleaseSelect: "Por favor seleccione...",

    termsAndConditions: "Términos y condiciones",
    privacyPolicy: "Política de privacidad",
    acceptTermsRequired: "Acepta los términos para continuar",
    consentTerms: "Acepto los {terms} y he leído la {privacy}.",
    consentTermsLink: "Términos del servicio",
    consentHealth:
      "Consiento expresamente que HealthPass guarde la información de salud que introduzco (enfermedades, alergias, medicamentos, vacunas, documentos) para mostrármela a mí y en mi tarjeta de emergencia (art. 9.2.a RGPD). Puedo retirar este consentimiento en cualquier momento.",
    consentHealthWithout:
      "Sin él puedes planificar viajes y usar la lista de comprobación, pero no guardar información de salud.",
    consentAi:
      "Consiento que la parte necesaria de mis datos de salud se envíe a OpenAI en Estados Unidos cuando use los consejos de salud con IA o el asistente de IA. Puedo retirar este consentimiento en cualquier momento.",
    consentAiWithout:
      "Sin él, las funciones de IA quedan desactivadas; la información del destino sigue funcionando.",
    consentMarketing:
      "Enviadme consejos y novedades sobre salud en viajes por correo electrónico. Puedo darme de baja en cualquier momento.",
    consentOptional: "Opcional",
    consentGateTitle: "Tus datos, tu decisión",
    consentGateLead:
      "Dinos qué puede hacer HealthPass con tus datos. No se guarda nada nuevo ni se envía nada a la IA hasta que decidas.",
    consentGateSave: "Guardar mi elección",
    consentSaveFailed:
      "No se ha podido guardar tu elección. Inténtalo de nuevo.",
    consentNeededHealthTitle:
      "¿Permitir que HealthPass guarde información de salud?",
    consentNeededAiTitle: "¿Permitir consejos de salud con IA?",
    consentAllow: "Permitir",
    consentNotNow: "Ahora no",
    consentWithdrawHint: "Puedes retirarlo en cualquier momento en Ajustes.",
    settingsConsentsTitle: "Tus consentimientos",
    settingsConsentsLead:
      "Retirar un consentimiento detiene todo tratamiento nuevo. Lo ya guardado permanece hasta que lo borres.",
    consentSaved: "Guardado",
    immediateStartTitle: "Antes de pagar",
    immediateStartAck:
      "Quiero que mi plan empiece ahora. Sé que si desisto en un plazo de 14 días, pago los días ya utilizados.",
    immediateStartTrial:
      "Si tu plan empieza con una prueba gratuita, no se cobra nada antes de que termine, y cancelar antes no cuesta nada.",
    immediateStartContinue: "Continuar al pago",
    immediateStartBack: "Volver",
    withdrawTitle: "Desistir del contrato",
    withdrawLead:
      "Puedes desistir de este plan sin dar motivos hasta el {deadline}.",
    withdrawRefund: "Se te reembolsarían {amount} por los días no utilizados.",
    withdrawTrial:
      "Estás en tu prueba gratuita, así que no se ha cobrado nada. Desistir termina el plan ahora.",
    withdrawButton: "Desistir del contrato",
    withdrawConfirm:
      "¿Desistir ahora? Tu plan termina de inmediato y tu cuenta sigue con el plan gratuito. Tus datos se conservan.",
    withdrawConfirmYes: "Sí, desistir",
    withdrawDone:
      "Tu desistimiento está confirmado. {refund}Te hemos enviado una confirmación por correo electrónico.",
    withdrawRefundIssued:
      "{amount} están de camino a tu medio de pago original. ",
    withdrawRefundPending:
      "Se te reembolsarán {amount} en tu medio de pago original en un plazo de 14 días. ",
    withdrawFailed:
      "No se ha podido completar el desistimiento. Inténtalo de nuevo o escribe al soporte.",
    copyright: "Gestiona tus datos de salud de forma segura",

    // Landing Page
    landingHeroTitle: "Viaja con tranquilidad",
    landingHeroSubtitle:
      "Tu información de salud de emergencia, disponible en todo el mundo en segundos. Acceso por código QR para primeros auxilios. Conforme con GDPR. Siempre contigo.",
    landingStartFree: "Comenzar gratis hoy",
    landingSeeHowItWorks: "Ver cómo funciona",
    landingNoCreditCard: "No se requiere tarjeta de crédito",
    landingFreeTrial: "Prueba gratuita de 30 días",
    landingFeaturesTitle: "Todo lo que necesitas para viajar seguro",
    landingFeaturesSubtitle:
      "Construido por viajeros, para viajeros. Todas las funciones esenciales en una plataforma segura.",
    landingHowItWorksTitle: "Comienza en 3 pasos sencillos",
    landingHowItWorksSubtitle:
      "Desde el registro hasta listo para viajar en menos de 10 minutos",
    landingPricingTitle: "Precios simples y transparentes",
    landingPricingSubtitle:
      "Comienza gratis, actualiza en cualquier momento. Sin tarifas ocultas.",
    landingCtaTitle: "¿Listo para viajar con tranquilidad?",
    landingCtaSubtitle:
      "Únete a más de 50.000 viajeros que confían en HealthPass con sus datos de salud de emergencia.",
    landingCtaButton: "Comenzar gratis hoy",

    required: "Este campo es obligatorio",
    invalidEmail:
      "Por favor introduce una dirección de correo electrónico válida",
    passwordTooShort: "La contraseña debe tener al menos 8 caracteres",
    passwordsNotMatch: "Las contraseñas no coinciden",

    vitalSigns: "Signos Vitales",
    bloodPressure: "Presión Arterial",
    systolic: "Sistólica",
    diastolic: "Diastólica",
    restingHeartRate: "Frecuencia Cardíaca en Reposo",
    bpm: "lpm",
    mmHg: "mmHg",
    normal: "Normal",
    lowBloodPressure: "Presión Arterial Baja",
    elevated: "Elevada",
    hypertensionGrade1: "Hipertensión Grado 1",
    hypertensionGrade2: "Hipertensión Grado 2",
    hypertensionGrade3: "Hipertensión Grado 3",
    bradycardia: "Bradicardia",
    tachycardia: "Taquicardia",
    systolicBpError: "La presión sistólica debe estar entre 70 y 250 mmHg",
    diastolicBpError: "La presión diastólica debe estar entre 40 y 150 mmHg",
    heartRateError:
      "La frecuencia cardíaca en reposo debe estar entre 30 y 220 lpm",
    systolicNormal: "Sistólica Normal: 120",
    systolicHigh: "Sistólica Alta: 140",
    diastolicNormal: "Diastólica Normal: 80",
    systolicBloodPressure: "Presión Arterial Sistólica",
    diastolicBloodPressure: "Presión Arterial Diastólica",
    noVitalSignsData: "No hay datos de signos vitales disponibles",

    // Lab Results
    labResults: "Resultados de Laboratorio",
    currentValues: "Valores Actuales",
    allTests: "Todas las Pruebas",
    laboratoryTest: "Prueba de Laboratorio",
    evaluation: "Evaluación",
    value: "Valor",
    unit: "Unidad",
    performingDoctor: "Médico/Laboratorio ejecutor (opcional)",
    pdfReport: "Informe PDF",
    noLabResultsDocumented: "No hay resultados de laboratorio documentados",
    addNewLabResult: "Agregar Nuevo Resultado de Laboratorio",
    showChart: "Mostrar Gráfico",
    hideChart: "Ocultar Gráfico",
    noWeightDataAvailable: "No hay datos de peso disponibles",
    firstEntriesWillBeShown: "Las primeras entradas se mostrarán aquí",
    weightKg: "Peso (kg)",
    current: "Actual",
    dataPoints: "Puntos de datos",
    fhirCompatibleData: "Datos compatibles con FHIR",
    recorded: "Registrado",
    bodyFat: "Grasa Corporal",
    bodyFatPercent: "Grasa Corporal (%)",
    raised: "Elevado",
    decreased: "Disminuido",
    critical: "Crítico",
    lastMeasurement: "Última Medición",
    vitalSignsHistory: "Historial de Signos Vitales (365 Días)",
    labResultsSaved: "Resultados de laboratorio guardados exitosamente",

    // Wellness & Wellbeing
    wellbeing: "Bienestar",
    physicalWellbeing: "Bienestar Físico",
    physicalWellness: "Bienestar Físico",
    emotionalWellbeing: "Bienestar Emocional",
    emotionalWellness: "Bienestar Emocional",
    howDoYouFeelPhysically: "¿Cómo te sientes físicamente?",
    howDoYouFeelEmotionally: "¿Cómo te sientes emocionalmente?",
    currentStatus: "Actualmente",
    lastUpdate: "Última Actualización",
    trendChart: "Gráfico de Tendencia",
    trendHistory: "Tendencia de los últimos 365 días",
    noTrendData: "No hay datos de tendencia disponibles",
    noTrendDataDesc: "Registre su bienestar regularmente para ver su progreso",
    noWellbeingData: "No hay datos de bienestar disponibles",
    saveFirstWellbeingEntry:
      "Guarde su primera evaluación de bienestar para ver tendencias",
    wellnessDataPoints: "Puntos de Datos",
    wellnessScore: "Puntuación de Bienestar (1-5)",
    average: "Promedio",
    good: "Bueno",
    low: "Bajo",
    veryPoor: "Muy Malo",
    poor: "Malo",
    fair: "Regular",
    wellGood: "Bueno",
    excellent: "Excelente",
    physicalVeryPoor: "Muy Malo",
    physicalPoor: "Malo",
    physicalFair: "Regular",
    physicalGood: "Bueno",
    physicalExcellent: "Excelente",
    emotionalVeryPoor: "Muy Deprimido",
    emotionalPoor: "Triste",
    emotionalFair: "Equilibrado",
    emotionalGood: "Feliz",
    emotionalExcellent: "Muy Feliz",

    // Additional fields from English
    resetPassword: "Restablecer Contraseña",
    resetPasswordDescription:
      "Introduce tu dirección de correo electrónico y te enviaremos un enlace de restablecimiento",
    sendResetLink: "Enviar Enlace de Restablecimiento",
    emailSent: "¡Correo Enviado!",
    checkYourEmail: "Revisa tu Correo Electrónico",
    resetEmailSent: "Hemos enviado un correo de restablecimiento a",
    healthPass: "HealthPass",
    secureHealthData: "Gestión segura de datos de salud",
    manageHealthSecurely: "Gestiona tus datos de salud de forma segura",
    testAccount: "Cuenta de prueba",
    useTestAccount: "Usar cuenta de prueba",
    overview: "Resumen",
    healthRecords: "Registros de Salud",
    appointments: "Citas",
    reports: "Informes",
    recentActivity: "Actividad Reciente",
    quickActions: "Acciones Rápidas",
    upcomingAppointments: "Próximas Citas",
    healthSummary: "Resumen de Salud",
    addNewRecord: "Agregar Nuevo Registro",
    viewAllRecords: "Ver Todos los Registros",
    noDataAvailable: "No Hay Datos Disponibles",
    upcomingTravels: "Próximos Viajes",
    noUpcomingTravels: "No hay viajes próximos",
    planNewTravel: "Planificar Nuevo Viaje",
    today: "Hoy",
    tomorrow: "Mañana",
    days: "días",
    healthRecommendations: "Recomendaciones de Salud",
    stayHydrated: "Manténgase hidratado durante todo el día",
    drinkWaterRecommendation: "Apunte a 2-3 litros de agua diariamente",
    regularExercise: "Actividad física regular",
    exerciseRecommendation: "30 minutos de actividad moderada, 5 días/semana",
    qualitySleep: "Sueño de calidad",
    sleepRecommendation: "7-9 horas de sueño por noche",
    thinkAbout: "Piensa en",
    travelInsurance: "Seguro de Viaje",
    travelInsuranceDesc: "Protege tu viaje",
    firstAidKit: "Botiquín de Primeros Auxilios",
    firstAidKitDesc: "Esté preparado",
    medicalCheckup: "Revisión Médica",
    medicalCheckupDesc: "Examen anual de salud",
    vaccinationAppointment: "Cita de Vacunación",
    vaccinationAppointmentDesc: "Manténgase protegido",
    tryOutPlus: "Prueba HealthPass PLUS",
    plusDescription:
      "Desbloquea funciones premium para una mejor gestión de la salud",
    plusFeature1: "Análisis de salud avanzados",
    plusFeature2: "Soporte prioritario",
    plusFeature3: "Almacenamiento ilimitado de documentos",
    plusFeature4: "Información impulsada por IA",
    upgradeToPlusButton: "Actualizar a PLUS",
    addHealthRecord: "Agregar Registro de Salud",
    editHealthRecord: "Editar Registro de Salud",
    deleteHealthRecord: "Eliminar Registro de Salud",
    recordType: "Tipo de Registro",
    date: "Fecha",
    description: "Descripción",
    attachments: "Adjuntos",
    weight: "Peso",
    height: "Altura",
    temperature: "Temperatura",
    heartRate: "Frecuencia Cardíaca",
    currentMedications: "Medicamentos Actuales",
    addMedication: "Agregar Medicamento",
    prescribedBy: "Prescrito por",
    sideEffects: "Efectos Secundarios",
    instructions: "Instrucciones",
    travelPlans: "Planes de Viaje",
    addTravelPlan: "Agregar Plan de Viaje",
    destination: "Destino",
    departureDate: "Fecha de Salida",
    returnDate: "Fecha de Regreso",
    travelType: "Tipo de Viaje",
    requiredVaccinations: "Vacunaciones Requeridas",

    // Travel List Page
    travels: "Viajes",
    myTravels: "Mis Viajes",
    planYourTrips:
      "Planifique sus viajes y gestione los requisitos de salud relacionados con los viajes",
    newTravel: "Nuevo Viaje",
    pastTravels: "Viajes Pasados",
    currentTravels: "Viajes Actuales",
    plannedTravels: "Viajes Planificados",
    draftTravels: "Borradores",
    noTravelsYet: "Aún no hay viajes",
    startPlanningTravel:
      "Comience a planificar su primer viaje para obtener recomendaciones y requisitos de salud",
    travelTitle: "Título del Viaje",
    travelPurpose: "Propósito del Viaje",
    travelDestination: "Destino del Viaje",
    startDate: "Fecha de Inicio",
    endDate: "Fecha de Fin",
    status: "Estado",
    actions: "Acciones",
    viewTravel: "Ver Viaje",
    editTravel: "Editar Viaje",
    deleteTravel: "Eliminar Viaje",
    archiveTravel: "Archivar Viaje",
    confirmDeleteTravel: "¿Está seguro de que desea eliminar este viaje?",
    travelDeleted: "Viaje eliminado con éxito",
    travelArchived: "Viaje archivado con éxito",

    // Travel Status
    statusDraft: "Borrador",
    statusPlanned: "Planificado",
    statusCurrent: "Actual",
    statusCompleted: "Completado",
    statusCancelled: "Cancelado",
    statusArchived: "Archivado",
    statusModeManual: "Establecer manualmente",
    statusModeAutoHint:
      "El estado se calcula automáticamente según las fechas del viaje.",

    // Travel Purpose
    purposeLeisure: "Ocio",
    purposeBusiness: "Negocios",
    purposeMedical: "Médico",
    purposeEducation: "Educación",
    purposeVolunteering: "Voluntariado",
    purposeOther: "Otro",
    tripTitle: "Título del Viaje",
    tripType: "Tipo de Viaje",
    tripOperator: "Operador Turístico",
    bookingReference: "Referencia de Reserva",
    startLocation: "Lugar de Inicio",
    endLocation: "Lugar de Fin",
    tripTypeCruise: "Crucero",
    tripTypeBeach: "Playa",
    tripTypeMountains: "Montañas",
    tripTypeSports: "Deportes",
    tripTypeEducation: "Educación",
    tripTypeEvent: "Evento",
    tripTypeCulture: "Cultura",
    tripTypeExpedition: "Expedición",
    plannedActivities: "Actividades planificadas",
    activity_diving: "Buceo",
    activity_climbing: "Escalada",
    activity_skiing: "Esquí",
    activity_hiking: "Senderismo",
    activity_biking: "Ciclismo",
    activity_swimming: "Natación",
    activity_safari: "Safari",
    healthcareFacilities: "Instalaciones sanitarias",
    nearbyHospitals: "Hospitales cercanos",
    nearbyPharmacies: "Farmacias cercanas",
    nearbyClinics: "Clínicas cercanas",
    noFacilitiesFound: "No se encontraron instalaciones en esta área",
    facilityDistance: "Distancia",
    forumHealthSummary: "Informes de viajeros",
    commonHealthIssues: "Problemas de salud comunes",
    travelerTips: "Consejos de viajeros",
    unexpectedRisks: "Riesgos inesperados",
    forumSummaryDisclaimer:
      "Resumen generado por IA. Verificar con informes actuales.",
    searchForumsFurther: "Buscar informes recientes",
    loadForumSummary: "Cargar informes",
    operatorHealthInfo: "Info de salud del operador",
    cruiseHealthRequirements: "Requisitos de salud para cruceros",
    tripSegments: "Segmentos del Viaje",
    addSegment: "Agregar Segmento",
    editSegment: "Editar Segmento",
    deleteSegment: "Eliminar Segmento",
    noSegmentsYet: "Aún no hay segmentos agregados",
    addSegmentsDescription:
      "Agregue segmentos de vuelo, hotel o actividad para organizar su viaje",
    addFirstSegment: "Agregar Primer Segmento",
    operator: "Operador",
    transportation: "Transporte",
    segmentInformation: "Información del Segmento",
    segmentTitle: "Título del Segmento",
    segmentType: "Tipo de Segmento",
    segmentTypeTransfer: "Transferencia",
    segmentTypeDestination: "Destino",
    segmentTypeAccommodation: "Alojamiento",
    segmentTypeActivity: "Actividad",
    locations: "Ubicaciones",
    startCountry: "País de Inicio",
    endCountry: "País de Fin",
    dateTime: "Fecha y Hora",
    startDateTime: "Fecha y Hora de Inicio",
    endDateTime: "Fecha y Hora de Fin",
    selectOption: "Seleccionar...",
    transportFlight: "Vuelo",
    transportTrain: "Tren",
    transportBus: "Autobús",
    transportCar: "Coche",
    transportShip: "Barco",
    accommodation: "Alojamiento",
    accommodationHotel: "Hotel",
    accommodationHostel: "Hostal",
    accommodationApartment: "Apartamento",
    accommodationCruiseCabin: "Camarote de Crucero",
    healthRisk: "Riesgo de Salud",
    travelReadiness: "Preparación para Viaje",
    vaccinations: "Vacunaciones",
    recommendations: "Recomendaciones",
    available: "disponible",
    riskManagement: "Gestión de Riesgos",
    vaccinationDetails: "Detalles de Vacunación",
    readinessChecklist: "Lista de Preparación",
    personalRecommendations: "Recomendaciones Personales",
    bookAppointment: "Reservar Cita",

    // Travel Details
    travelCompanions: "Compañeros de Viaje",
    companions: "Compañeros",
    tourOperator: "Operador Turístico",
    travelNotes: "Notas de Viaje",
    notes: "Notas",
    segments: "Segmentos",
    travelSegment: "Segmento de Viaje",
    transportationType: "Tipo de Transporte",
    accommodationType: "Tipo de Alojamiento",

    // Filter & Sort
    filterByStatus: "Filtrar por Estado",
    filterByPurpose: "Filtrar por Propósito",
    sortByDate: "Ordenar por Fecha",
    sortByDestination: "Ordenar por Destino",
    searchTravels: "Buscar viajes...",
    showAll: "Mostrar Todos",
    showActive: "Mostrar Activos",
    showCompleted: "Mostrar Completados",

    // Time Indicators
    daysUntilDeparture: "Días hasta la salida",
    daysRemaining: "Días restantes",
    inProgress: "En Progreso",
    departed: "Partido",
    returned: "Regresado",

    // Travel Form
    travelDates: "Fechas de Viaje",
    travelDetails: "Detalles del Viaje",
    healthInformationNotice: "Aviso de Información de Salud",
    healthRequirementsDescription:
      "Los requisitos de salud de viaje se mostrarán aquí según su destino. Esta información proviene de CDC, OMS y autoridades sanitarias locales.",
    vaccinationInfoLoading:
      "Los requisitos de vacunación se cargarán según su destino y fechas de viaje.",
    healthWarningsLoading:
      "Las advertencias y avisos de salud se mostrarán aquí.",
    apiIntegrationPending: "Integración API con CDC/OMS pendiente",
    checkBackLater:
      "La información estará disponible una vez que se integren los datos de salud del destino",
    healthWarnings: "Advertencias de Salud",
    lastUpdated: "Última actualización",
    source: "Fuente",
    noVaccinationsRequired: "No se requieren vacunas específicas",
    noHealthWarnings: "No hay advertencias de salud específicas",
    risk: "riesgo",

    // Health Recommendations
    departure: "Salida",
    mandatory: "Obligatorio",
    recommended: "Recomendado",
    suggested: "Sugerido",
    recommendedVaccinations: "Vacunas Recomendadas",
    activeDiseaseOutbreaks: "Brotes Activos de Enfermedades",
    dataSources: "Fuentes de Datos",
    from: "Desde",
    to: "Hasta",
    isoCode: "Código ISO",
    riskLevel: "Nivel de Riesgo",
    healthDataLoadError: "No se pudieron cargar los datos de salud.",
    dataFrom: "Datos de",
    overallRisk: "Evaluación general del riesgo",
    quickInstallButton: "Instalar aplicación",
    quickInstallInstalled: "Ya instalada",
    updateAvailable: "Cargar la nueva versión.",
    quickInstallAndroidApkStep1: "Escanee el código y pulse Descargar.",
    quickInstallAndroidApkStep2:
      "Abra el archivo descargado y permita instalaciones desde Chrome.",
    quickInstallAndroidApkStep3: "Abra HEALTHPASS desde su pantalla de inicio.",
    quickInstallTitle: "Instale la aplicación en tres pasos",
    quickInstallSubtitle:
      "Apunte la cámara del teléfono al código. HEALTHPASS se instala como una aplicación normal, ocupa toda la pantalla y funciona sin conexión.",
    quickInstallScanHint: "Escanee para abrir en su teléfono",
    quickInstallAndroidTitle: "Android · Chrome",
    quickInstallAndroidStep1:
      "Escanee el código o abra travelhealthpass.app en Chrome.",
    quickInstallAndroidStep2:
      "Toque el menú de tres puntos y luego «Instalar aplicación».",
    quickInstallAndroidStep3: "Abra HEALTHPASS desde su pantalla de inicio.",
    quickInstallIosTitle: "iPhone y iPad · Safari",
    quickInstallIosStep1:
      "Escanee el código o abra travelhealthpass.app en Safari.",
    quickInstallIosStep2:
      "Toque Compartir y luego «Añadir a pantalla de inicio».",
    quickInstallIosStep3: "Abra HEALTHPASS desde su pantalla de inicio.",
    quickInstallIosNote:
      "Solo Safari — Chrome y Firefox en iPhone no pueden instalar aplicaciones web.",
    appInstallTitle: "HealthPass en su teléfono",
    appInstallSubtitle:
      "Instale HealthPass en Android o iPhone y lleve consigo su historial de salud, su tarjeta de emergencia y los requisitos sanitarios de viaje, incluso sin conexión.",
    appInstallAndroidTitle: "Android",
    appInstallAndroidSubtitle: "Instalar el APK directamente",
    appInstallScanToDownload:
      "Escanee este código con la cámara de su teléfono para descargar el APK, o use el botón de abajo.",
    appInstallDownloadApk: "Descargar la app HealthPass",
    appInstallApkUnavailable: "El APK de Android aún no está disponible.",
    appInstallApkUnavailableHint:
      "Mientras tanto, HealthPass funciona como una aplicación web instalable: abra este sitio en Chrome en su teléfono Android, toque el menú y elija «Instalar aplicación».",
    appInstallAndroidStep1:
      "Escanee el código QR o toque «Descargar APK» en su teléfono Android.",
    appInstallAndroidStep2:
      "Cuando Android advierta que el archivo puede dañar su dispositivo, elija «Descargar de todos modos»: es la advertencia habitual para cualquier aplicación instalada fuera de Play Store.",
    appInstallAndroidStep3:
      "Abra el archivo descargado. Android preguntará si permite instalaciones desde esta fuente; toque «Ajustes» y active el permiso.",
    appInstallAndroidStep4: "Vuelva atrás y toque «Instalar».",
    appInstallAndroidStep5:
      "Abra HealthPass e inicie sesión con su cuenta existente.",
    appInstallAndroidPwaTitle: "Instalar desde Chrome",
    appInstallAndroidPwaStep1:
      "Abra este sitio en Chrome en su teléfono Android: escanee el código de arriba o escriba la dirección.",
    appInstallAndroidPwaStep2:
      "Toque el menú de tres puntos en la esquina superior derecha.",
    appInstallAndroidPwaStep3:
      "Toque «Añadir a pantalla de inicio» o «Instalar aplicación».",
    appInstallAndroidPwaStep4: "Confirme con «Instalar».",
    appInstallAndroidPwaStep5:
      "Abra HealthPass desde su pantalla de inicio e inicie sesión. Se muestra a pantalla completa y funciona sin conexión.",
    appInstallIosTitle: "iPhone y iPad",
    appInstallIosSubtitle: "Añadir a la pantalla de inicio desde Safari",
    appInstallScanToOpen:
      "Escanee este código con la cámara del iPhone para abrir HealthPass en Safari.",
    appInstallIosStep1:
      "Abra este sitio en Safari. Chrome y Firefox en iOS no pueden instalar aplicaciones web.",
    appInstallIosStep2:
      "Toque el botón Compartir: el cuadrado con una flecha hacia arriba, en la parte inferior de la pantalla.",
    appInstallIosStep3:
      "Desplácese hacia abajo y toque «Añadir a pantalla de inicio».",
    appInstallIosStep4: "Toque «Añadir» en la esquina superior derecha.",
    appInstallIosStep5:
      "Abra HealthPass desde su pantalla de inicio e inicie sesión. Ahora se muestra a pantalla completa y funciona sin conexión.",
    appInstallIosSafariNote:
      "Apple solo permite instalar aplicaciones web desde Safari, así que realice estos pasos en Safari aunque otro navegador sea el predeterminado.",
    appInstallAndroidGuideLink: "Guía completa de instalación en Android",
    androidInstallPageTitle: "Instalar HealthPass en Android",
    androidInstallPageSubtitle:
      "Instale HealthPass en su teléfono Android en unos pocos pasos. Funciona a pantalla completa y sin conexión.",
    androidInstallScanPageQr:
      "¿Está leyendo esto en un ordenador? Escanee este código para abrir esta página en el teléfono Android en el que quiere HealthPass.",
    androidInstallSecurityNote:
      "Android pide permiso antes de instalar una aplicación ajena a Play Store. Ese aviso es normal aquí. El permiso se aplica solo al navegador al que se lo concede, y puede retirarlo cuando termine la instalación.",
    androidInstallIosHint: "¿Usa un iPhone? Siga las instrucciones para iOS.",
    androidInstallBackHome: "Volver al inicio",
    aiAnalysisError: "Análisis de IA no disponible.",
    personalizedHealthRecommendations:
      "Recomendaciones de salud personalizadas",
    recommendationsIntro:
      "Basadas en su destino, la duración del viaje y su perfil de salud – recomendaciones basadas en evidencia y en múltiples fuentes.",
    aiTripAnalysis: "Análisis de salud del viaje con IA",
    analyzing: "Analizando…",
    runAiAnalysis: "Ejecutar análisis de IA",
    aiAnalysisHint:
      "Análisis del impacto climático y del perfil de salud personal asistido por IA.",
    climateRiskSummary: "Resumen de riesgos climáticos",
    healthProfileRisks: "Riesgos de su perfil de salud",
    urgentActions: "Acciones urgentes",
    noRecommendationsAvailable:
      "No se encontraron recomendaciones específicas para este destino.",
    travelStatus: "Estado del viaje",
    readiness: "Preparación",
    overallReadiness: "Preparación general",
    urgent: "Urgente",
    addCustomTask: "Añadir tarea personalizada",
    taskDescription: "Descripción de la tarea…",
    vaccinationRequired: "Obligatoria",
    vaccinationRecommended: "Recomendada",
    atLeast: "Al menos",
    daysBeforeDeparture: "días antes de la salida",
    validFor: "Válida durante",
    loadingHealthData: "Cargando datos de salud…",
    noRequiredVaccinations: "No hay vacunas obligatorias para este destino.",
    noRecommendedVaccinations: "No se recomiendan vacunas adicionales.",
    vaccinationTimeline: "Calendario de vacunación",
    before: "antes",
    riskLevelDescLow: "Riesgo sanitario bajo para este destino",
    riskLevelDescMedium: "Riesgo sanitario moderado - tome precauciones",
    riskLevelDescHigh:
      "Riesgo sanitario alto - consulte a un médico antes de viajar",
    riskLevelDescCritical:
      "Riesgo sanitario crítico - no se aconseja viajar sin asesoramiento especializado",
    activeOutbreaks: "Brotes de enfermedades activos",
    activeOutbreaksDetected: "brote(s) de enfermedad activo(s) detectado(s)",
    cases: "Casos",
    lastReported: "Último reporte",
    travelWarnings: "Advertencias de viaje",
    transportModeRisks: "Riesgos por medio de transporte",
    activitySpecificRisks: "Riesgos sanitarios por actividad",
    riskCategoryVaccination: "Vacunas",
    riskCategoryMedication: "Medicación",
    riskCategoryPrecaution: "Precauciones",
    riskCategoryAdvisory: "Avisos",
    riskCategoryWarning: "Advertencias",
    trend: "Tendencia",
    increasing: "Aumentando",
    decreasing: "Disminuyendo",
    stable: "Estable",
    casesReported: "Casos Reportados",
    lastReport: "Último Informe",
    mayAffectTravel: "Puede afectar el viaje",
    validity: "Validez",
    years: "años",
    minDaysBeforeDeparture: "Días mín. antes de salida",
    boosterRequired: "Refuerzo requerido",
    contraindications: "Contraindicaciones",
    relatedDiseases: "Enfermedades relacionadas",
    ageRestrictions: "Restricciones de edad",
    updated: "Actualizado",
    noHealthDataAvailable: "No hay datos sanitarios disponibles",
    failedToLoadHealthRecommendations:
      "Error al cargar recomendaciones sanitarias",
    backToDashboard: "Volver al Panel",

    uploadDocument: "Subir Documento",
    documentType: "Tipo de Documento",
    uploadDate: "Fecha de Carga",
    fileSize: "Tamaño del Archivo",
    downloadDocument: "Descargar Documento",
    documentName: "Nombre del Documento",

    // Chat
    chatWithTina: "Chatear con Tina",
    tinaHealthAssistant: "Tina - Tu asistente de salud",
    askQuestion: "Hacer una Pregunta",
    askMeAnything: "Pregúntame cualquier cosa sobre tu salud",
    typeMessage: "Escribe un mensaje...",
    typeYourMessage: "Escribe tu mensaje...",
    sendMessage: "Enviar",
    chatHistory: "Historial del Chat",
    newConversation: "Nueva conversación",
    conversations: "Conversaciones",
    includeHealthContext: "Incluir mis datos de salud",
    healthContextEnabled:
      "Contexto de salud activado - Tina puede ver tus medicamentos, alergias y signos vitales",
    healthContextDisabled:
      "Contexto de salud desactivado - Tina responderá sin tus datos de salud",
    aiThinking: "Tina está pensando...",
    noConversationsYet: "Aún no hay conversaciones",
    startNewConversation: "Iniciar una nueva conversación con Tina",
    deleteConversation: "Eliminar conversación",
    conversationDeleted: "Conversación eliminada",
    aiUnavailable: "Asistente IA no disponible",
    aiUnavailableMessage:
      "El asistente de IA no está disponible actualmente. Inténtalo de nuevo más tarde.",
    messagePlaceholder:
      "Pregunta sobre medicamentos, síntomas, salud de viaje...",
    exampleQuestions: "Preguntas de ejemplo:",
    example1: "¿Cuáles son mis medicamentos actuales?",
    example2: "¿Qué vacunas necesito para viajar?",
    example3: "Explica mis últimos signos vitales",

    // Profile
    personalInformation: "Información Personal",
    contactInformation: "Información de Contacto",
    emergencyContact: "Contacto de Emergencia",
    medicalInformation: "Información Médica",
    updateProfile: "Actualizar Perfil",
    changePassword: "Cambiar Contraseña",
    phone: "Teléfono",
    dateOfBirth: "Fecha de Nacimiento",
    country: "País",
    nationality: "Nacionalidad",
    currentPassword: "Contraseña Actual",
    newPassword: "Nueva Contraseña",
    confirmNewPassword: "Confirmar Nueva Contraseña",
    languageSettings: "Configuración de Idioma",
    notificationSettings: "Configuración de Notificaciones",
    privacySettings: "Configuración de Privacidad",
    accountSettings: "Configuración de Cuenta",
    dataExport: "Exportar Datos",
    deleteAccount: "Eliminar Cuenta",

    // Account Deletion
    dangerZone: "Zona Peligrosa",
    deleteAccountWarning:
      "Una vez que elimines tu cuenta, no hay vuelta atrás. Todos tus datos de salud, documentos e información personal se eliminarán permanentemente de nuestros sistemas.",
    deleteAccountConfirmationPrompt:
      "Esta acción no se puede deshacer. Todos tus datos se eliminarán permanentemente.",
    typeToConfirm: "Escribe para confirmar",
    enterYourPassword: "Introduce tu contraseña",
    deleteAccountWarningDetails:
      "Todos tus registros de salud, documentos, planes de viaje y conversaciones se eliminarán permanentemente. Una suscripción de pago se cancela antes y no se vuelve a cobrar. Los registros de auditoría se conservarán por motivos de cumplimiento.",
    confirmDeletion: "Eliminar Cuenta Permanentemente",
    deleting: "Eliminando...",
    accountDeletedSuccessfully:
      "Tu cuenta ha sido eliminada exitosamente. Serás desconectado en breve.",
    deleteAccountError:
      "Error al eliminar la cuenta. Por favor intenta de nuevo o contacta al soporte.",
    deleteAccountConfirmationError:
      'Por favor escribe "DELETE MY ACCOUNT" exactamente para confirmar',
    invalidPassword: "Contraseña inválida. Por favor intenta de nuevo.",
    passwordRequired: "Contraseña requerida",
    warning: "Advertencia",

    // Emergency Profile
    emergencyProfile: "Perfil de Emergencia",
    myEmergencyProfile: "Mi Perfil de Emergencia",
    emergencyData: "Datos de Emergencia",
    completenessScore: "Puntuación de Completitud",
    dataQuality: "Calidad de Datos",
    dataQualityGood: "Buena - Todos los datos críticos completos",
    dataQualityFair: "Regular - Faltan algunos datos",
    dataQualityPoor: "Mala - Faltan datos críticos",
    criticalData: "Datos Críticos",
    priority1Data: "Prioridad 1: Crítico",
    priority2Data: "Prioridad 2: Importante",
    priority3Data: "Prioridad 3: Útil",
    missingCriticalData: "Datos Críticos Faltantes",
    quickAdd: "Añadir Rápido",
    editEmergencyData: "Editar Datos de Emergencia",
    shareEmergencyAccess: "Compartir Acceso de Emergencia",
    accessDuration: "Duración del Acceso",
    duration15min: "15 Minutos",
    duration1hour: "1 Hora",
    duration6hours: "6 Horas",
    duration24hours: "24 Horas",
    durationCustom: "Duración Personalizada",
    dataScope: "Alcance de Datos",
    selectDataToShare: "Seleccionar Datos para Compartir",
    emergencyQRCode: "Código QR de Emergencia",
    refreshQRCode: "Actualizar Código QR",
    downloadQRCode: "Descargar Código QR",
    printQRCode: "Imprimir Código QR",
    copyAccessLink: "Copiar Enlace de Acceso",
    qrCodeExpiry: "Caducidad del Código QR",
    addEmergencyContact: "Añadir Contacto de Emergencia",
    primaryContact: "Contacto Principal",
    secondaryContact: "Contacto Secundario",
    tertiaryContact: "Contacto Terciario",
    contactName: "Nombre del Contacto",
    relationship: "Relación",
    phoneNumber: "Número de Teléfono",
    notifyOnAccess: "Notificar en Acceso de Emergencia",
    availabilityNotes: "Notas de Disponibilidad",
    reorderContacts: "Reordenar Contactos",
    devices: "Dispositivos Médicos",
    allergySubstance: "Sustancia Alérgena",
    allergySeverity: "Severidad",
    allergyReaction: "Reacción",
    conditionName: "Nombre de la Condición",
    conditionStatus: "Estado",
    deviceType: "Tipo de Dispositivo",
    deviceUDI: "UDI del Dispositivo",
    verifiedDate: "Fecha de Verificación",
    outdatedWarning:
      "Estos datos están desactualizados y necesitan verificación",
    incompleteWarning: "Tu perfil de emergencia está incompleto",
    updateRequired: "Actualización Requerida",
    dataVerifiedRecently: "Datos verificados recientemente",

    add: "Agregar",
    update: "Actualizar",
    remove: "Eliminar",
    search: "Buscar",
    filter: "Filtrar",
    sort: "Ordenar",
    export: "Exportar",
    import: "Importar",
    refresh: "Actualizar",
    active: "Activo",
    inactive: "Inactivo",
    pending: "Pendiente",
    completed: "Completado",
    cancelled: "Cancelado",
    savedSuccessfully: "Guardado Exitosamente",
    deletedSuccessfully: "Eliminado Exitosamente",
    errorOccurred: "Ocurrió un Error",
    confirmDelete: "Confirmar Eliminación",
    areYouSure: "¿Estás Seguro?",
    cannotBeUndone: "Esta acción no se puede deshacer",
    healthStatusOptimal: "Estado de Salud: Óptimo",
    allDataCurrent: "Todos tus datos de salud están actualizados y completos",
    pulse: "Pulso",
    addNewDocument: "Agregar Nuevo Documento",
    addDocumentDesc: "Agregar documento de salud",
    addMedicationDesc: "Gestionar nueva medicación",
    planTravel: "Planificar Viaje",
    planTravelDesc: "Nuevo viaje con control de salud",
    addHealthDataDesc: "Registrar signos vitales",
    upcomingReminders: "Citas y Recordatorios Próximos",
    highPriority: "Alta Prioridad",
    mediumPriority: "Prioridad Media",
    lowPriority: "Baja Prioridad",
    vaccination: "Vacunación",
    daysAgo: "Hace días",
    weeksAgo: "Hace semanas",
    inDays: "En días",
    immunizations: "Inmunizaciones",
    manageImmunizations: "Gestiona tus registros de inmunización",
    newImmunization: "Nueva Inmunización",
    editImmunization: "Editar Inmunización",
    deleteImmunization: "Eliminar Inmunización",
    vaccineName: "Nombre de la Vacuna",
    vaccineCode: "Código de la Vacuna",
    manufacturer: "Fabricante",
    lotNumber: "Número de Lote",
    doseNumber: "Número de Dosis",
    totalDoses: "Total de Dosis",
    administeredDate: "Fecha de Administración",
    expirationDate: "Fecha de Caducidad",
    nextDueDate: "Próxima Fecha de Vencimiento",
    location: "Lugar de Administración",
    administrator: "Administrado por",
    requiredForCountries: "Requerido para Países",
    uploadCertificate: "Subir Certificado",
    downloadCertificate: "Descargar Certificado",
    certificateUploaded: "Certificado subido exitosamente",
    noCertificateFound: "No se encontró certificado para esta inmunización",
    certificateUploadFailed: "Fallo al subir certificado",
    fhirData: "Datos FHIR",
    internationalCertificate: "Certificado Internacional",
    internationalVaccinationCertificate:
      "Certificado Internacional de Vacunación",
    passportNumber: "Número de Pasaporte de Vacunación",
    healthPassDocumentId: "ID del Documento HealthPass",
    certificateNumber: "Número de Certificado",
    issuerId: "ID del Emisor",
    publicKeyFingerprint: "Huella Digital de Clave Pública",
    signatureTimestamp: "Marca de Tiempo de la Firma",
    issuedBy: "Emitido por",
    issuedDate: "Fecha de Emisión",
    validFrom: "Válido desde",
    patientInformation: "Información del Paciente",
    certificateInformation: "Información del Certificado",
    digitalSignature: "Firma Digital",
    qrCodeValidation: "Código QR para Validación",
    printCertificate: "Imprimir Certificado",
    copyCertificateData: "Copiar Datos del Certificado",
    deleteConfirmation:
      "¿Estás seguro de que deseas eliminar esta inmunización?",
    noImmunizationsFound: "No se Encontraron Inmunizaciones",
    addFirstImmunization: "Agrega tu primera inmunización",
    certificateValidUntil: "Certificado válido hasta",
    dosesCompleted: "Dosis completadas",
    of: "de",
    loginSuccessful: "Bienvenido",
    loginFailed: "Inicio de sesión fallido",
    loginInvalidOrPaused:
      "Correo o contraseña incorrectos. Tras 10 intentos fallidos, el inicio de sesión de esta cuenta se pausa hasta 15 minutos.",
    registrationSuccessful: "Bienvenido a HealthPass",
    registrationFailed: "Registro fallido",
    logoutSuccessful: "Sesión cerrada exitosamente",
    sessionExpired:
      "Tu sesión ha expirado. Por favor inicia sesión nuevamente.",
    networkError: "Error de red - Por favor verifica tu conexión a internet",
    accessDenied: "Acceso denegado - Permisos insuficientes",
    validationError: "Error de validación",
    tooManyRequests:
      "Demasiadas solicitudes - Por favor inténtalo de nuevo más tarde",
    serverError: "Error del servidor - Por favor inténtalo de nuevo más tarde",

    // Medication System
    medicationsPage: "Medicamentos",
    medicationItem: "Medicamento",
    medicationPlan: "Plan de Medicación",
    addNewMedication: "Agregar Medicamento",
    editExistingMedication: "Editar Medicamento",
    deleteMedicationItem: "Eliminar Medicamento",
    medicationName: "Nombre del Medicamento",
    genericName: "Nombre Genérico (DCI)",
    medicationDosage: "Dosis",
    dosageForm: "Forma Farmacéutica",
    medicationStrength: "Concentración",
    medicationFrequency: "Frecuencia",
    timingInstructions: "Instrucciones de Administración",
    mealTiming: "Relación con Comidas",
    medicationStartDate: "Fecha de Inicio",
    medicationEndDate: "Fecha de Fin",
    medicationPrescribedBy: "Prescrito por",
    medicationPharmacy: "Farmacia",
    medicationInstructions: "Instrucciones",
    medicationSideEffects: "Efectos Secundarios",
    medicationInteractions: "Interacciones",
    eanCode: "Código EAN",
    pznCode: "Código PZN",
    atcCode: "Código ATC",
    tablet: "Comprimido",
    capsule: "Cápsula",
    syrup: "Jarabe",
    injection: "Inyección",
    cream: "Crema",
    ointment: "Pomada",
    drops: "Gotas",
    spray: "Spray",
    patch: "Parche",
    suppository: "Supositorio",
    powder: "Polvo",
    other: "Otro",
    inhaler: "Inhalador",
    gel: "Gel",
    lotion: "Loción",
    solution: "Solución",
    beforeMeal: "Antes de la Comida",
    afterMeal: "Después de la Comida",
    withMeal: "Con la Comida",
    independent: "Independiente de las Comidas",
    onEmptyStomach: "En Ayunas",
    medicationRegular: "Medicación Regular",
    medicationTravel: "Medicamento de Viaje",
    medicationEmergency: "Medicamento de Emergencia",
    medicationProphylaxis: "Profilaxis",
    regular: "Regular",
    emergency: "Emergencia",
    travelMedication: "Medicamento de Viaje",
    prophylaxis: "Profilaxis",
    cruise: "Crucero",
    trekking: "Trekking",
    diving: "Buceo",
    tropical: "Tropical",
    altitude: "Altitud",
    business: "Viaje de Negocios",
    adventure: "Viaje de Aventura",
    urban: "Viaje Urbano",
    recordIntake: "Registrar Toma",
    intakeHistory: "Historial de Tomas",
    adherence: "Adherencia",
    doseTaken: "Dosis Tomada",
    intakeNotes: "Notas de Toma",
    timeline: "Línea de Tiempo",
    lastTaken: "Última Toma",
    nextDue: "Próxima Toma",
    internationalMedicationPlan: "Plan de Medicación Internacional",
    generatePlan: "Generar Plan",
    uploadGermanPlan: "Subir Plan de Medicación Alemán",
    downloadPlan: "Descargar Plan",
    printPlan: "Imprimir Plan",
    qrCodePlan: "Código QR del Plan",
    packageImage: "Imagen del Envase",
    medicationImage: "Imagen del Medicamento/Comprimido",
    uploadPackageImage: "Subir Imagen del Envase",
    uploadMedicationImage: "Subir Imagen del Medicamento",
    takePhoto: "Tomar Foto",
    scanBarcode: "Escanear Código de Barras",
    tradeName: "Nombre Comercial",
    activeIngredient: "Principio Activo",
    manufacturerName: "Fabricante",
    internationalCode: "Código Internacional",
    codeSystem: "Sistema de Codificación",
    removeImage: "Eliminar Imagen",
    imageUploadSuccess: "Imagen subida exitosamente",
    imageUploadFailed: "Fallo al subir imagen",
    imageDeleteSuccess: "Imagen eliminada exitosamente",
    imageDeleteFailed: "Fallo al eliminar imagen",
    optional: "Opcional",
    imagesSection: "Imágenes",
    packagePhoto: "Foto del Envase",
    pillPhoto: "Foto del Comprimido",
    gtinCode: "Código GTIN",
    ndcCode: "Código NDC (EE.UU.)",
    handelsname: "Nombre Comercial",
    wirkstoff: "Principio Activo",
    hersteller: "Fabricante",
    pharmaCode: "Código Farmacéutico",
    basicInformation: "Información Básica",
    fhirExtendedFields: "Campos FHIR R5 Extendidos",
    medicationImages: "Imágenes del Medicamento",
    medicationInformation: "Información del Medicamento",
    dosageInformation: "Dosis",
    prescription: "Receta",
    note: "Nota",
    selfSignedInfo:
      'Cuando ingresas el medicamento tú mismo, se marcará automáticamente como "Auto-firmado" y se vinculará a tu cuenta de Viajero.',
    medicationImagesDescription:
      "Sube imágenes del envase y los comprimidos (opcional)",
    medicationImagesHint:
      "Las imágenes de alta calidad ayudan con la identificación y pueden ser útiles para la documentación de viaje.",
    saveChanges: "Guardar Cambios",
    saveMedication: "Guardar Medicamento",
    choosePackageImage: "Elegir Imagen del Envase",
    chooseMedicationImage: "Elegir Imagen del Medicamento",
    category: "Categoría",
    dosage: "Dosis",
    pharmacy: "Farmacia",
    editMedication: "Editar Medicamento",
    updateMedication: "Actualizar Medicamento",
    uploadingImages: "Subiendo Imágenes...",
    saving: "Guardando...",
    travelRecommendations: "Recomendaciones de Viaje",
    supplementaryMedications: "Medicamentos Complementarios",
    interactionWarnings: "Advertencias de Interacciones",
    implementRecommendation: "Implementar Recomendación",
    medicationAdded: "Medicamento agregado exitosamente",
    medicationUpdated: "Medicamento actualizado exitosamente",
    medicationDeleted: "Medicamento eliminado exitosamente",
    intakeRecorded: "Toma registrada exitosamente",
    planGenerated: "Plan de medicación generado exitosamente",
    imageUploaded: "Imagen del envase subida exitosamente",

    // Avatar Upload
    profilePicture: "Foto de Perfil",
    managePersonalDataSettings:
      "Gestiona tus datos personales y configuraciones",
    uploadPhoto: "Subir Foto",
    deletePhoto: "Eliminar Foto",
    invalidFileType:
      "Tipo de archivo inválido. Solo JPEG, PNG, WebP, GIF permitidos",
    fileTooLarge: "Archivo demasiado grande. Tamaño máximo 5MB",
    maxFileSize:
      "Tamaño máximo: 5MB. Formatos soportados: JPEG, PNG, WebP, GIF",
    avatarUploadedSuccessfully: "Avatar subido exitosamente",
    avatarUploadFailed: "Error al subir el avatar",
    confirmDeleteAvatar: "¿Estás seguro de que quieres eliminar tu avatar?",
    avatarDeletedSuccessfully: "Avatar eliminado exitosamente",
    avatarDeleteFailed: "Error al eliminar el avatar",
    cropImage: "Recortar Imagen",
    rotate: "Rotar",

    // Profile Page - Form Fields
    notSpecified: "No especificado",
    day: "Día",
    month: "Mes",
    year: "Año",
    yearsOld: "años",

    // Date/Month names
    january: "Enero",
    february: "Febrero",
    march: "Marzo",
    april: "Abril",
    may: "Mayo",
    june: "Junio",
    july: "Julio",
    august: "Agosto",
    september: "Septiembre",
    october: "Octubre",
    november: "Noviembre",
    december: "Diciembre",

    // Address Fields
    address: "Dirección",
    street: "Calle",
    houseNumber: "Número",
    addressSupplement: "Complemento de dirección",
    postalCode: "Código postal",
    city: "Ciudad",
    state: "Estado/Región",
    streetName: "Nombre de la calle",
    houseNo: "Nº",
    apartmentFloorEtc: "Apartamento, Piso, etc. (opcional)",
    postalCodePlaceholder: "Código postal",
    cityPlaceholder: "Ciudad",

    // Nationalities
    nationality_german: "Alemán",
    nationality_austrian: "Austriaco",
    nationality_swiss: "Suizo",
    nationality_french: "Francés",
    nationality_italian: "Italiano",
    nationality_spanish: "Español",
    nationality_british: "Británico",
    nationality_american: "Estadounidense",
    nationality_canadian: "Canadiense",
    nationality_dutch: "Holandés",
    nationality_belgian: "Belga",
    nationality_polish: "Polaco",
    nationality_turkish: "Turco",
    nationality_russian: "Ruso",
    nationality_other: "Otro",

    // Profile Update Messages
    profileUpdatedSuccessfully: "¡Perfil actualizado correctamente!",
    profileUpdateMaybeSuccessful:
      "Es posible que el perfil se haya actualizado - por favor verifique los cambios.",
    profileUpdatedButDataNotLoaded:
      "Perfil actualizado, pero los datos no se cargaron correctamente. Por favor recargue la página.",
    correctFollowingInputs: "Por favor corrija las siguientes entradas:",
    inputError: "Error de entrada",
    invalidInputs:
      "Entradas inválidas. Por favor verifique todos los campos e inténtelo de nuevo.",
    unauthorizedPleaseLogin:
      "No autorizado. Por favor inicie sesión nuevamente.",
    noPermission: "Sin permiso para esta acción.",
    userNotFoundPleaseLogin:
      "Usuario no encontrado. Por favor inicie sesión nuevamente.",
    serverErrorTryLater:
      "Error del servidor. Por favor inténtelo más tarde o contacte al soporte.",
    unknownError: "Error desconocido",
    noServerConnection:
      "No es posible conectarse al servidor. Por favor verifique su conexión a Internet e inténtelo de nuevo.",
    unexpectedError:
      "Ocurrió un error inesperado. Por favor inténtelo de nuevo o contacte al soporte.",

    apply: "Aplicar",
    capture: "Capturar",
    retake: "Repetir",
    usePhoto: "Usar Foto",
    switchCamera: "Cambiar Cámara",
    cameraAccessDenied: "Acceso a la cámara denegado",
    photoCaptureFailed: "No se pudo usar la foto. Inténtalo de nuevo.",
    checkCameraPermissions:
      "Por favor verifica los permisos de la cámara en la configuración del navegador",

    // Version History
    version: "Versión",
    versionHistory: "Historial de versiones",
    versionHistoryDescription: "Todas las versiones y cambios de HealthPass",
    currentVersion: "Versión actual",
    newFeatures: "Nuevas funciones",
    changes: "Cambios",
    bugFixes: "Correcciones de errores",
    versionHistoryNote:
      "Este historial de versiones se genera automáticamente a partir de commits de Git y notas de lanzamiento.",
    back: "Volver",
    subscription: "Suscripción",

    faqTitle: "Preguntas frecuentes",
    faqSubtitle: "Todo lo que necesitas saber sobre HealthPass",
    faqQ1: "¿Qué es HealthPass?",
    faqA1:
      "HealthPass es una plataforma de salud digital que almacena su información médica de emergencia y la hace accesible en todo el mundo a través de código QR. Está diseñada específicamente para viajeros que necesitan acceso instantáneo a datos de salud críticos en emergencias.",
    faqQ2: "¿Cómo funciona HealthPass?",
    faqA2:
      "Simplemente cree una cuenta, cargue su información médica (alergias, tipo de sangre, medicamentos, contactos de emergencia) y genere su código QR personal. Los primeros respondedores y profesionales médicos pueden escanear este código para acceder instantáneamente a su información de salud vital, incluso si está inconsciente.",
    faqQ3: "¿HealthPass cumple con el RGPD?",
    faqA3:
      "Sí. HealthPass está construido conforme al RGPD: tus datos viajan cifrados, tú decides quién ve qué y, cada vez que alguien abre tu tarjeta de emergencia o un enlace que compartiste, queda registrado y puedes verlo. Hetzner opera nuestros servidores para nosotros en Estados Unidos bajo las cláusulas contractuales tipo de la UE; la Política de privacidad explica exactamente qué significa. Nunca compartimos tus datos con terceros sin tu consentimiento explícito.",
    faqQ4: "¿Cuánto cuesta HealthPass?",
    faqA4:
      "Tu tarjeta y el acceso de emergencia son gratis. PLUS cuesta 9 y PREMIUM 15 al mes, en euros o dólares estadounidenses según dónde estés, y ambos empiezan con 14 días de prueba gratis. Para un solo viaje, PLUS cuesta 25 y PREMIUM 39, en un único pago para 90 días. Los servicios de emergencia nunca pagan por leer tus datos de emergencia.",
    faqQ5: "¿Puede HealthPass salvar vidas?",
    faqA5:
      "Sí. En emergencias médicas en el extranjero, las barreras del idioma y la falta de historial médico pueden ser mortales. HealthPass proporciona acceso instantáneo a información crítica como alergias, tipo de sangre y medicamentos actuales, permitiendo decisiones de tratamiento más rápidas y seguras.",
    faqQ6: "¿Cómo funciona el acceso por código QR?",
    faqA6:
      "Su código QR de HealthPass se puede imprimir, agregar a la pantalla de bloqueo de su teléfono o usar como pulsera. Al escanearlo, abre una vista de emergencia segura con su información de salud crítica. No se requiere instalación de aplicaciones para el personal médico.",
    faqQ7: "¿Cuáles son los casos de uso comunes de viaje?",
    faqA7:
      "HealthPass es esencial para:\n• Viajeros de aventura que visitan áreas remotas\n• Personas mayores que viajan internacionalmente\n• Personas con condiciones crónicas o alergias\n• Familias que viajan con niños\n• Viajeros solitarios en países de habla no hispana\n• Cualquier persona que requiera medicamentos regulares",
    faqQ8: "¿Qué tan seguros están mis datos?",
    faqA8:
      "Sus datos se cifran en tránsito y en reposo, el inicio de sesión pasa por un servidor de identidad propio con tokens de corta duración, y cada consulta de un historial de salud queda registrada — puede pedirnos ese registro. Nuestros servidores los opera para nosotros Hetzner en Estados Unidos, al amparo de las cláusulas contractuales tipo de la UE. La página de Seguridad lo explica en detalle.",
    faqQ9: "¿Qué idiomas son compatibles?",
    faqA9:
      "HealthPass está disponible en inglés, alemán, francés, español y portugués. Su información de salud de emergencia se puede mostrar en varios idiomas simultáneamente, asegurando que el personal médico de todo el mundo pueda leerla.",
    faqQ10: "¿Cómo acceden los servicios de emergencia a mis datos?",
    faqA10:
      "Los servicios de emergencia escanean su código QR con cualquier cámara de smartphone. Ven instantáneamente su información crítica sin necesidad de iniciar sesión o descargar una aplicación. El acceso se registra para su seguridad y puede revocar códigos de acceso de emergencia en cualquier momento.",
    faqStillHaveQuestions: "¿Todavía tienes preguntas?",
    faqContactUs:
      "Nuestro equipo de soporte está aquí para ayudarte las 24 horas",
    contactSupport: "Contactar soporte",

    // Medical Incidents & Intolerances
    medicalIncidents: "Incidentes médicos",
    addNewIncident: "Agregar nuevo incidente",
    icdCodeOptional: "Código ICD (opcional)",
    diagnosisDesignation: "Diagnóstico/Designación",
    diagnosisPlaceholder: "p. ej. Infarto, fractura, cirugía...",
    incidentDetailsPlaceholder: "Más detalles sobre el incidente...",
    addIncident: "Agregar incidente",
    noMedicalIncidentsDocumented: "No hay incidentes médicos documentados",
    intolerances: "Intolerancias",
    foodIntolerances: "Intolerancias alimentarias",
    foodIntolerancesPlaceholder: "p. ej. Lactosa, Gluten",
    specificDiseases: "Enfermedades específicas",
    coagulationDisorder: "Trastorno de coagulación",
    heartAttack: "Infarto",
    heartAttackDatePlaceholder: "Fecha del infarto",
    stroke: "Accidente cerebrovascular",
    strokeDatePlaceholder: "Fecha del accidente cerebrovascular",

    // Extended Health Components
    allergiesAndIntolerances: "Alergias e Intolerancias",
    noAllergiesDocumented: "No hay alergias documentadas",
    medicalConditions: "Condiciones Médicas",
    noMedicalConditionsDocumented: "No hay condiciones médicas documentadas",
    medicalProcedures: "Procedimientos Médicos",
    noMedicalProceduresDocumented: "No hay procedimientos médicos documentados",
    treatmentHistory: "Historial de Tratamientos",
    noTreatmentHistoryDocumented:
      "No hay historial de tratamientos documentado",

    // Vital Signs
    allFieldsRequired: "Todos los campos son obligatorios",
    systolicMustBeHigher:
      "La presión sistólica debe ser mayor que la diastólica",
    recordFirstMeasurement: "Registrar primera medición",
    loadingHistory: "Cargando historial...",
    noHistoryData: "No hay datos de historial disponibles",
    firstMeasurementsWillAppear: "Las primeras mediciones aparecerán aquí",

    // Unit Inputs
    enterValue: "Ingresar valor",

    // Gender
    gender: "Género",
    male: "Masculino",
    female: "Femenino",
    preferNotToSay: "Prefiero no decirlo",

    // Emergency System
    // Emergency System
    emergencyQRDescription:
      "Comparte tus datos médicos con servicios de emergencia",
    noActiveQRCode: "Sin código QR activo",
    generateQRCodeDescription:
      "Genera un código QR para compartir tus datos de emergencia",
    qrCodeDuration: "Duración del código QR",
    generateQRCode: "Generar código QR",
    regenerate: "Regenerar",
    generating: "Generando...",
    qrCodeGenerated: "Código QR generado con éxito",
    qrCodeGenerationFailed: "Error al generar código QR",
    timeRemaining: "Tiempo restante",
    accessCount: "Contador de accesos",
    accessUrl: "URL de acceso",
    urlCopied: "URL copiada al portapapeles",
    copyFailed: "Error al copiar",
    copied: "Copiado",
    copy: "Copiar",
    downloadQR: "Descargar código QR",
    qrCodeDownloaded: "Código QR descargado",
    shareEmergencyData: "Compartir datos de emergencia",
    shareDialogDescription: "Elige cómo compartir tus datos de emergencia",
    expired: "Caducado",
    pleaseRegenerate: "Por favor regenera",
    howItWorks: "Cómo funciona",
    emergencyQRHowItWorksText:
      "Los servicios de emergencia escanean este código QR con un smartphone",
    share: "Compartir",
    emergencyDataAccess: "Acceso a datos de emergencia",
    forEmergencyRespondersOnly: "Solo para servicios de emergencia",
    scanQROrEnterToken: "Escanea el código QR o ingresa el token",
    qrScannerPlaceholder: "Escáner QR",
    qrScannerInstructions: "Apunta la cámara al código QR",
    orEnterTokenManually: "O ingresa el token manualmente",
    enterAccessToken: "Ingresar token de acceso",
    continue: "Continuar",
    responderInformation: "Información del respondedor",
    responderInformationDescription: "Por favor proporciona tu información",
    fullName: "Nombre completo",
    enterYourFullName: "Ingresa tu nombre completo",
    organization: "Organización",
    hospitalOrganizationName: "Hospital u organización",
    professionalId: "ID profesional",
    licenseBadgeNumber: "Número de licencia o identificación",
    yourEmail: "Tu dirección de correo electrónico",
    yourPhone: "Tu número de teléfono",
    reasonForAccess: "Motivo del acceso",
    briefDescriptionEmergency: "Breve descripción",
    emergencyAccessConsent: "Consentimiento de acceso de emergencia",
    emergencyAccessConsentText:
      "Confirmo que accedo a estos datos en una situación de emergencia",
    pleaseAcceptConsent: "Por favor acepta el consentimiento",
    accessEmergencyData: "Acceder a datos de emergencia",
    accessing: "Accediendo...",
    emergencyDataAccessGranted: "Acceso a datos de emergencia concedido",
    pleaseEnterToken: "Por favor ingresa un token de acceso",
    pleaseProvideResponderInfo: "Por favor proporciona tu información",
    accessTokenInvalid: "Token de acceso inválido o caducado",
    criticalAllergies: "ALERGIAS CRÍTICAS",
    emergencyContacts: "Contactos de emergencia",
    noEmergencyContactsAvailable: "Sin contactos de emergencia disponibles",
    bloodType: "Grupo sanguíneo",
    age: "Edad",
    name: "Nombre",
    unknown: "Desconocido",
    disclaimer: "Descargo de responsabilidad",
    emergencyDataDisclaimerText:
      "Esta información es proporcionada por el paciente",

    // PWA & Offline
    installApp: "Instalar HealthPass",
    installAppDescription:
      "Instala HealthPass para acceso rápido y uso sin conexión",
    install: "Instalar",
    later: "Más tarde",
    connectionRestored: "Conexión restaurada",
    connectionRestoredSyncing: "Conexión restaurada - Sincronizando datos",
    offlineMode: "Modo sin conexión",
    offlineModeDescription:
      "Modo sin conexión - Datos ya cargados están disponibles",
    slowConnection: "Conexión lenta",
    slowConnectionDescription:
      "Conexión lenta - Algunas funciones pueden retrasarse",

    // Sync Status
    never: "Nunca",
    justNow: "Justo ahora",
    pendingSync: "pendiente",
    lastSync: "Última sincronización",

    // Trip Wizard
    tripWizardTitle: "Planificar nuevo viaje",
    tripWizardStep1: "Básico",
    tripWizardStep2: "Detalles",
    tripWizardStep3: "Etapas",
    tripWizardStep4: "Salud",
    tripWizardStep5: "Revisar",
    tripWizardNext: "Siguiente",
    tripWizardPrevious: "Atrás",
    tripWizardSave: "Crear viaje",
    tripWizardSaving: "Creando...",
    tripWizardStep1Title: "Información básica",
    tripWizardStep2Title: "Detalles del viaje",
    tripWizardStep3Title: "Etapas / Segmentos",
    tripWizardStep4Title: "Revisión de salud",
    tripWizardStep5Title: "Revisar y guardar",
    tripWizardStep1Desc: "Título, destino y fechas",
    tripWizardStep2Desc: "Propósito, tipo, operador y acompañantes",
    tripWizardStep3Desc: "Agregar vuelos, hoteles y actividades",
    tripWizardStep4Desc: "Riesgos de salud y vacunas requeridas",
    tripWizardStep5Desc: "Revise todo antes de crear",
    tripWizardReviewSummary: "Resumen del viaje",
    tripWizardDuration: "Duración",
    tripWizardSegmentCount: "Etapas",
    tripWizardAddSegment: "Agregar etapa",
    tripWizardSkipSegments: "Omitir – agregar más tarde",
    tripHealthOptional:
      "Este paso es opcional. Puede seguir planificando y guardar su viaje sin él.",
    tripHealthUpsellTitle: "Más confianza para un viaje saludable",
    tripHealthUpsellIntro:
      "La revisión de salud del destino forma parte de PLUS. Con ella estará mejor preparado para {country}:",
    tripHealthBenefitVaccinations:
      "Vacunas obligatorias y recomendadas para su destino",
    tripHealthBenefitWarnings:
      "Riesgo de malaria y advertencias sanitarias actuales para su destino",
    tripHealthBenefitTracking: "Seguimiento de sus propias vacunas",
    tripHealthBenefitDocuments:
      "Almacenamiento de documentos para certificados y papeles de viaje",
    tripHealthBenefitOffline:
      "Acceso sin conexión a sus datos de salud durante el viaje",
    tripHealthSingleTrip:
      "¿Solo planifica este viaje? PLUS Single Trip es un pago único que cubre 90 días, sin suscripción posterior.",
    tripHealthUpgradeCta: "Mejorar a PLUS",
    tripHealthSkipCta: "Continuar sin revisión de salud",
    tripHealthDraftKept:
      "Su borrador de viaje se conserva. Vuelva a «Planificar nuevo viaje» para continuar donde lo dejó.",
    tripHealthEmpty:
      "Nuestras fuentes no indican vacunas ni advertencias sanitarias para {country}.",
    tripHealthLoadFailed:
      "No se pudo cargar la revisión de salud en este momento. Puede continuar y consultarla más tarde en la página del viaje.",
    tripTypeHiking: "Senderismo",
    tripTypeCamping: "Camping",
    tripTypeRoadTrip: "Viaje por carretera",
    purposeScience: "Ciencia",
    inviteCompanion: "Invitar acompañante",
    companionEmail: "Correo del acompañante",
    companionEmailPlaceholder: "email@ejemplo.com",
    sendInvitation: "Enviar invitación",
    companionInviteSent: "Invitación enviada con éxito",
    companionInviteFailed: "Error al enviar la invitación",
    companionStatusPending: "Pendiente",
    companionStatusAccepted: "Aceptada",
    companionStatusDeclined: "Rechazada",
    removeCompanion: "Eliminar",
    companionRemoved: "Acompañante eliminado",
    noCompanionsYet: "Aún no se han invitado acompañantes",
    companionInviteDesc:
      "Invite a otros usuarios de HealthPass a unirse a este viaje",
    companionRequiresPLUS: "Invitar acompañantes requiere una suscripción PLUS",
    segmentDocuments: "Documentos",
    uploadSegmentDocument: "Subir documento",
    docCategoryTicket: "Billete",
    docCategoryBooking: "Confirmación de reserva",
    docCategoryVoucher: "Bono",
    docCategoryInsurance: "Seguro",
    docCategoryOther: "Otro",
    documentUploaded: "Documento subido con éxito",
    documentDeleted: "Documento eliminado",
    documentUploadFailed: "Error al subir",
    maxFileSizeHint: "Máx. 10 MB · PDF, JPEG, PNG",
    tripMap: "Mapa del viaje",
    tripMapLoading: "Cargando mapa...",
    tripMapNoData: "No hay coordenadas de etapas disponibles",
    tripMapRequiresPREMIUM:
      "El mapa interactivo requiere una suscripción PREMIUM",
    featureRequiresPLUS: "Esta función requiere una suscripción PLUS",
    featureRequiresPREMIUM: "Esta función requiere una suscripción PREMIUM",
    upgradeToUnlock: "Actualizar para desbloquear",
    upgradePlanLabel: "Actualizar plan",
    statusAutomatic: "Automático",
    statusManual: "Manual",
    statusOverrideHint:
      "El estado se establece automáticamente según las fechas. Puede anularlo manualmente.",
    markAsManual: "Establecer manualmente",

    // HealthPass Card
    healthpassCard: "Tarjeta HealthPass",
    cardWallet: "Cartera",
    cardNumber: "Número de tarjeta",
    validUntil: "Válida hasta",
    showFront: "Anverso",
    showBack: "Ver cartera",
    downloadPDF: "Descargar PDF",
    shareCard: "Compartir tarjeta",
    generateQR: "Generar código QR",
    cardShares: "Enlaces compartidos",
    createShareLink: "Crear enlace",
    shareExpiry: "El enlace expira en",
    shareExpiry24h: "24 horas",
    shareExpiry72h: "72 horas",
    shareExpiry7d: "7 días",
    sharePassword: "Contraseña (opcional)",
    revokeShare: "Revocar acceso",
    shareRevoked: "Enlace revocado",
    shareCreated: "Enlace creado",
    shareCopied: "Enlace copiado",
    noSharesYet: "Sin enlaces activos",
    shareAccessCount: "Accedido",
    shareTimes: "veces",
    walletEmergency: "Historial de emergencias",
    walletMedications: "Plan de medicación",
    walletVaccinations: "Cartilla de vacunación",
    walletCheckin: "Documentos de embarque",
    walletAppointments: "Citas",
    walletInsurance: "Seguros",
    walletTravelDocs: "Documentos de viaje",
    walletLabResults: "Resultados de laboratorio",
    upgradeForWalletItem: "Actualiza a {tier} para desbloquear",
    cardLoading: "Cargando tarjeta...",
    cardError: "No se pudo cargar la tarjeta",
    printPreview: "Vista previa de impresión",
    printFormatA4: "A4",
    printFormatA5: "A5",
    printFormatLetter: "US Letter",
    printFormatCard: "Tamaño tarjeta de crédito",
    // Traveller navigation (UX_CONCEPT.md §4)
    navHome: "Inicio",
    navTrips: "Viajes",
    navCardWallet: "Tarjeta y cartera",
    navCard: "Tarjeta",
    navHealth: "Salud",
    navMe: "Cuenta",
    navWallet: "Cartera",
    navShares: "Enlaces compartidos",
    navAccessRequests: "Solicitudes de acceso",
    navCheckIn: "Check-in",
    navConsentRequests: "Solicitudes de consentimiento",
    navAssistant: "Asistente",
    navPlan: "Plan",
    navAccount: "Cuenta",
    navSubscriptions: "Suscripciones",
    navPayment: "Pago",
    navInvoices: "Facturas",
    navCompliance: "Cumplimiento",
    navNotifications: "Notificaciones",
    complianceDescription:
      "Sus datos y sus derechos: qué se guarda, quién lo consultó, qué aceptó, una copia de todo y la eliminación de su cuenta.",
    complianceStatistics: "Estadísticas",
    complianceLog: "Registro",
    complianceLogLead:
      "Cada vez que alguien abrió sus datos de emergencia, quién y cuándo.",
    complianceConsents: "Consentimientos",
    complianceExport: "Exportar",
    complianceDeleteAccount: "Eliminar cuenta",
    statsMemberSince: "Miembro desde",
    statsTrips: "Viajes",
    statsMedications: "Medicamentos",
    statsImmunizations: "Vacunas",
    statsUnavailable: "No disponible ahora",
    settingsNotificationsDescription:
      "Elija sobre qué le informa HealthPass y cómo.",
    navCollapseMenu: "Contraer menú",
    navExpandMenu: "Expandir menú",
    navSectionPages: "Páginas de esta sección",

    // Home (UX_CONCEPT.md §5.1)
    homeGoodMorning: "Buenos días",
    homeGoodAfternoon: "Buenas tardes",
    homeGoodEvening: "Buenas noches",
    homeBetaNotice:
      "HealthPass está en beta y va lanzando sus servicios poco a poco. Le mantendremos informado.",
    homeDismiss: "Cerrar",
    homeNextTrip: "Próximo viaje",
    homeInDays: "en {n} días",
    homeToday: "hoy",
    homeTomorrow: "mañana",
    homeDepart: "Salida",
    homeReturn: "Regreso",
    homeNights: "{n} noches",
    homeCheckTrip: "Ver qué necesita este viaje",
    homeMoreTrips: "{n} viajes más planificados",
    homeNoTripTitle: "¿A dónde ahora?",
    homeNoTripBody:
      "Planifique un viaje y HealthPass averigua lo que necesita.",
    homePlanTrip: "Planificar un viaje",
    homeYourCard: "Su HealthPass",
    homeShowWallet: "Mostrar la cartera",
    homeShowFront: "Mostrar el anverso",
    homeCardHintFront:
      "Muestre el anverso al facturar. Toque la tarjeta para ver su cartera.",
    homeCardHintBack:
      "Su cartera: todo lo que lleva la tarjeta. Toque para darle la vuelta.",
    homeNextStep: "Siguiente paso",
    homeNotNow: "Ahora no",
    stepEmergencyTitle: "Añada su grupo sanguíneo",
    stepEmergencyBody:
      "Aparece en su tarjeta para que los servicios de emergencia lo lean en cualquier lugar.",
    stepEmergencyAction: "Completar perfil de emergencia",
    stepBpTitle: "Vuelva a medir su tensión",
    stepBpBodyAbnormal:
      "Su última medición, {value}, fue {status}. Una nueva tarda dos minutos.",
    stepBpBodyStale:
      "Su última medición, {value}, se tomó {age}. Una nueva tarda dos minutos.",
    stepBpAction: "Registrar una medición",
    stepTripTitle: "Prepárese para {destination}",
    stepTripBody:
      "Vea qué vacunas, medicamentos y documentos necesita este viaje.",
    stepTripAction: "Abrir la lista",
    stepPlanTitle: "Planifique su próximo viaje",
    stepPlanBody:
      "Díganos dónde y cuándo, y comprobamos lo que necesita antes de salir.",
    homeAtAGlance: "De un vistazo",
    homeAllHealthData: "Todos los datos de salud",
    homeNoVitals: "Aún no hay mediciones. Añada una y aparecerá aquí.",
    homeVitalsError: "No se pudieron cargar sus mediciones.",
    homeRetry: "Reintentar",
    ageToday: "hoy",
    ageDays: "hace {n} d",
    ageMonths: "hace {n} meses",
    ageYears: "hace {n} años",
    ageUnknown: "fecha desconocida",
    vitalLow: "Bajo",
    vitalElevated: "Elevado",
    vitalHigh: "Alto",
    vitalUnderweight: "Bajo peso",
    vitalOverweight: "Sobrepeso",
    vitalObese: "Obesidad",
    vitalRecorded: "Registrado",
    weightLabel: "Peso",
    bmiLabel: "IMC",
    stepBpBodyUndated:
      "Su última medición, {value}, no tiene fecha. Una nueva tarda dos minutos.",
    homeDayUnit: "día",
    homeDaysUnit: "días",

    // Trip readiness (UX_CONCEPT.md §6.1)
    readinessCaption: "listo para viajar",
    readinessAllDone: "¡Listo para viajar!",
    readinessStepsLeft: "pasos pendientes",
    readinessSaveFailed:
      "No se pudo guardar su lista, así que las marcas no se conservarán. Recargue para intentarlo de nuevo.",
    readinessNotForMe: "No es para mí",
    readinessCountAgain: "Volver a contar",
    readinessNotChecked: "Aún sin revisar",
    homeRemindTonight: "Recordármelo esta noche",
    homeReminderSet: "Recordatorio a las {time}",
    homeReminderFailed: "No se pudo — reintentar",
    // Passport stamps and levels (UX_CONCEPT.md §7)
    passportTitle: "Tu pasaporte",
    passportStamps: "{count} sellos",
    passportOneStamp: "1 sello",
    passportAt100: "al 100 %",
    passportNextStamp: "Prepárate para {city} y el sello es tuyo.",
    passportOneMore: "Uno más y llegas a {level}.",
    passportFirst:
      "Lleva un viaje al 100 % antes de salir y su sello aparecerá aquí.",
    passportToNext: "{count} más para {level}.",
    passportTop: "Nivel máximo alcanzado. Buen viaje.",
    passportFailed: "No se pudo cargar tu pasaporte.",
    passportOpenTrip: "Abrir la lista de {city}",
    levelStarter: "Principiante",
    levelExplorer: "Explorador",
    levelGlobetrotter: "Trotamundos",
    levelNomad: "Nómada",
    stampEarned: "Sello conseguido",
    stampEarnedBody: "Ya está en tu pasaporte. ¡Que tengas un viaje estupendo!",
    stampEarnedLevelUp: "Ahora eres {level}. ¡Que tengas un viaje estupendo!",
    // First run (UX_CONCEPT.md §5.5)
    frStepOf: "Paso {n} de 3",
    frTitleYou: "Hagamos tu tarjeta.",
    frLeadYou: "Cada respuesta aparece al instante en tu tarjeta.",
    frTitleBasics: "Tu tarjeta va tomando forma.",
    frLeadBasics:
      "Unas respuestas más y los servicios de emergencia podrán ayudarte en cualquier parte del mundo.",
    frTitleTrip: "Tu tarjeta está lista.",
    frLeadTrip: "Escanéala, muéstrala, compártela. Y ahora: ¿adónde vas?",
    frTitleDone: "Tu primer viaje está planeado.",
    frLeadDone:
      "Hemos empezado la lista de lo que necesita {city}. Márcala y gana tu primer sello.",
    frStepYou: "Tú en la tarjeta",
    frStepYouHint: "Nombre, foto, nacionalidad",
    frStepBasics: "Lo básico para emergencias",
    frStepBasicsHint: "Grupo sanguíneo, alergias, medicación, un contacto",
    frStepTrip: "Tu próximo viaje",
    frStepTripHint: "Adónde y cuándo — comprobamos lo que necesitas",
    frYourSteps: "Tus tres pasos",
    frDone: "Hecho",
    frMinLeft: "quedan {n} min",
    frQrAfter: "QR tras el paso 2",
    frBasicsLabel: "EMERGENCIAS · {n} DE 4",
    frYouQ: "¿Para quién es esta tarjeta?",
    frFirstName: "Nombre",
    frLastName: "Apellidos",
    frDateOfBirth: "Fecha de nacimiento",
    frNationality: "Nacionalidad",
    frNationalityPlaceholder: "p. ej. española",
    frAddPhoto: "Añadir una foto",
    frChangePhoto: "Cambiar la foto",
    frPhotoFailed: "No se pudo subir la foto. Prueba con otra.",
    frPutOnCard: "Ponerlo en mi tarjeta",
    frAddToCard: "Añadir a mi tarjeta",
    frBloodQ: "¿Cuál es tu grupo sanguíneo?",
    frBloodHint: "Es lo primero que comprueba un hospital.",
    frBloodUnknown: "No lo sé",
    frBloodChip: "Grupo {type}",
    frBloodChipEmpty: "¿Grupo sanguíneo?",
    frAllergyQ: "¿Eres alérgico a algo?",
    frAllergyHint:
      "Se muestra en inglés a quien escanee tu tarjeta en una emergencia.",
    frAlreadyOnCard: "Ya en tu tarjeta",
    frSomethingElse: "Otra cosa",
    frAllergyPlaceholder: "p. ej. látex, picaduras de abeja",
    frNoAllergies: "Sin alergias",
    frNothingToAdd: "Nada que añadir",
    frAllergyChips: "{n} alergias",
    frAllergyChipOne: "1 alergia",
    frAllergyChipNone: "Sin alergias",
    frAllergyChipEmpty: "¿Alergias?",
    frMedsQ: "¿Tomas alguna medicación habitualmente?",
    frMedsHint:
      "Los servicios de emergencia deben saberlo antes de darte nada.",
    frMedsPlaceholder: "p. ej. Metformina 500 mg",
    frAdd: "Añadir",
    frRemove: "Quitar",
    frNoMeds: "Ninguna",
    frContactQ: "¿A quién llamamos?",
    frContactHint:
      "Tu contacto de emergencia, visible para quien escanee tu tarjeta.",
    frContactName: "Nombre",
    frContactPhone: "Teléfono, con prefijo",
    frContactPhoneInvalid:
      "Introduce un número en cifras, p. ej. +34 912 345 678.",
    frRelationship: "Relación",
    frRelPartner: "Pareja",
    frRelParent: "Padre o madre",
    frRelChild: "Hijo o hija",
    frRelSibling: "Hermano o hermana",
    frRelFriend: "Amigo o amiga",
    frSkip: "Más tarde",
    frTripQ: "¿Adónde vas ahora?",
    frTripCity: "Ciudad o lugar",
    frTripCountry: "País",
    frTripDeparture: "Salida",
    frTripReturn: "Regreso",
    frTripPlan: "Planear este viaje",
    frTripLater: "Aún no hay viaje",
    frOpenChecklist: "Ver lo que necesita {city}",
    frGoHome: "Ir al inicio",
    frSaveFailed:
      "No se ha guardado. Comprueba la conexión e inténtalo de nuevo.",
    frPrivacy:
      "Tus datos de salud están cifrados y son tuyos. Tú decides quién los ve y ves cada acceso.",
    // Card page (UX_CONCEPT.md §5.2)
    cardSideFront: "Anverso",
    cardSideWallet: "Lado cartera",
    cardDownload: "Descargar la tarjeta",
    cardDownloadFailed: "No se pudo descargar la tarjeta. Inténtalo de nuevo.",
    cardOfflineCodeShow: "¿Sin cobertura? Mostrar el código sin conexión",
    cardOnlineCodeShow: "Mostrar el código en línea",
    cardOfflineCodeHint:
      "Este código contiene tu resumen de emergencia y funciona sin internet.",
    // Shared card page
    sharedCardTitle: "Una tarjeta HealthPass compartida",
    sharedCardLead:
      "Su titular ha compartido esta tarjeta contigo. Muestra quién es, no sus datos de salud.",
    sharedCardNoCode: "Tarjeta compartida",
    sharedCardPasswordLead:
      "El titular protegió esta tarjeta con una contraseña.",
    sharedCardPassword: "Contraseña",
    sharedCardOpen: "Abrir la tarjeta",
    sharedCardWrongPassword: "Esa contraseña no es correcta.",
    sharedCardGone: "Este enlace ha caducado o su titular lo ha retirado.",
    sharedCardMissing:
      "Este enlace no existe. Comprueba que se copió completo.",
    sharedCardError:
      "No se pudo cargar la tarjeta. Inténtalo de nuevo en un momento.",
    settingsExportLead:
      "Descarga todo lo que HealthPass guarda sobre ti, en un archivo para conservarlo o llevarlo a otro servicio.",
    settingsExportDone: "Se han descargado tus datos.",
    settingsExportFailed: "La descarga ha fallado. Inténtalo de nuevo.",
    // Payment success page
    payConfirming: "Confirmando tu pago…",
    payConfirmingLine: "Tarda unos segundos.",
    payWelcome: "Bienvenido a {plan}",
    payTrialLine:
      "Tu prueba gratis dura hasta el {date}. Después, tu plan se renueva cada mes salvo que lo canceles antes.",
    payTripLine: "Tu plan de viaje dura hasta el {date}.",
    payActiveLine: "Tu plan ya está activo.",
    payPendingTitle: "Tu pago está en camino",
    payPendingLine:
      "Algunos métodos de pago tardan más. Tu plan se activa en cuanto llega el pago y te avisaremos.",
    payFailedTitle: "Este pago no se completó",
    payFailedLine:
      "No se ha cobrado nada. Puedes elegir un plan cuando quieras.",
    payContinue: "Continuar",
    payBuildCard: "Crear tu tarjeta",
    payBackToPlans: "Volver a los planes",
    renewTermsMonthly:
      "Los nuevos miembros tienen 14 días gratis y después {price} al mes. Se renueva cada mes hasta que lo canceles, cuando quieras, en tus ajustes de facturación.",
    renewTermsTrip: "Un único pago de {price} para 90 días. No se renueva.",
    // Partner offers (REQ-025)
    marketplaceTitle: "Ofertas de socios",
    marketplaceIntro:
      "Ofertas de seguro de nuestros socios para este viaje. HealthPass no las compara ni las recomienda; la aseguradora responde a las preguntas sobre la cobertura.",
    marketplaceDisclosurePerContract:
      "Oferta de {insurer}. HealthPass recibe una comisión si contratas una póliza.",
    marketplaceDisclosurePerVisit:
      "Oferta de {insurer}. HealthPass recibe una comisión cuando visitas {insurer}.",
    marketplaceVisit: "Ir a {insurer}",
    marketplaceSeeAll: "Abrir la página de ofertas de socios",
    marketplaceEmpty: "No hay ofertas de socios para este viaje.",
    marketplaceBackToTrip: "Volver al viaje",
    marketplaceOpenFailed: "No se pudo abrir la oferta. Inténtalo de nuevo.",
    marketplaceSettingTitle: "Mostrar ofertas de socios",
    marketplaceSettingHint:
      "Ofertas de seguro de socios en tus viajes. Se eligen solo por destino, fechas y país de residencia, nunca por tus datos de salud. Si lo desactivas, se ocultan en todas partes.",
    marketplaceSettingSaved: "Guardado.",
    marketplaceSettingFailed: "No se pudo guardar el ajuste.",
  },

  pt: {
    // Navigation
    login: "Entrar",
    register: "Registrar",
    dashboard: "Painel",
    profile: "Perfil",
    health: "Saúde",
    medications: "Medicamentos",
    travel: "Viagem",
    documents: "Documentos",
    chat: "Chat",
    settings: "Configurações",
    logout: "Sair",
    appearance: "Aparência",
    security: "Segurança",

    // Auth
    welcomeBack: "Viaje com tranquilidade",
    signInToAccount: "Entre na sua conta",
    createAccount: "Crie sua conta",
    createAccountSubtitle: "Configure a sua conta HealthPass",
    settingsGeneralDescription: "Notificações, dados e resumo da conta",
    settingsSecurityDescription:
      "Início de sessão, autenticação de dois fatores e privacidade",
    settingsAppearanceDescription: "Idioma e tema de cores",
    themeTitle: "Tema",
    themeLight: "Claro",
    themeDark: "Escuro",
    themeSystem: "Sistema",
    themeHint: "Sistema segue a definição do seu dispositivo e muda com ela.",
    orientationTitle: "Orientação do ecrã",
    orientationPortrait: "Retrato fixo",
    orientationSystem: "Orientação do sistema",
    orientationHint:
      "O retrato fixo mantém a aplicação direita, independentemente de como segura o telefone. A orientação do sistema segue a definição de rotação do seu dispositivo.",
    accountStatistics: "Estatísticas da conta",
    notificationsEmailHint: "Atualizações e lembretes importantes por e-mail",
    remindersTitle: "Lembretes",
    fhirStandardNote: "Dados médicos num formato normalizado.",
    email: "E-mail",
    password: "Senha",
    confirmPassword: "Confirmar senha",
    firstName: "Nome",
    lastName: "Sobrenome",
    forgotPassword: "Esqueceu a senha?",
    rememberMe: "Lembrar-me",
    signIn: "Entrar",
    signUp: "Registrar",
    alreadyHaveAccount: "Já tem uma conta?",
    noAccountYet: "Novo no HealthPass?",
    createFreeAccount: "Criar conta gratuita",
    backToLogin: "Voltar ao login",

    // Common
    loading: "Carregando...",
    error: "Erro",
    success: "Sucesso",
    cancel: "Cancelar",
    save: "Salvar",
    delete: "Excluir",
    edit: "Editar",
    view: "Ver",
    viewAll: "Ver tudo",
    close: "Fechar",
    pleaseSelect: "Por favor selecione...",

    termsAndConditions: "Termos e condições",
    privacyPolicy: "Política de privacidade",
    acceptTermsRequired: "Aceite os termos para continuar",
    consentTerms: "Aceito os {terms} e li a {privacy}.",
    consentTermsLink: "Termos de Serviço",
    consentHealth:
      "Consinto expressamente que a HealthPass guarde as informações de saúde que introduzo (doenças, alergias, medicamentos, vacinas, documentos) para mas mostrar a mim e no meu cartão de emergência (art. 9.º, n.º 2, alínea a) do RGPD). Posso retirar este consentimento a qualquer momento.",
    consentHealthWithout:
      "Sem ele pode planear viagens e usar a lista de verificação, mas não guardar informações de saúde.",
    consentAi:
      "Consinto que a parte necessária dos meus dados de saúde seja enviada à OpenAI nos Estados Unidos quando uso os conselhos de saúde com IA ou o assistente de IA. Posso retirar este consentimento a qualquer momento.",
    consentAiWithout:
      "Sem ele, as funções de IA ficam desligadas; as informações sobre o destino continuam a funcionar.",
    consentMarketing:
      "Enviem-me dicas e novidades sobre saúde em viagem por e-mail. Posso cancelar a qualquer momento.",
    consentOptional: "Opcional",
    consentGateTitle: "Os seus dados, a sua escolha",
    consentGateLead:
      "Diga-nos o que a HealthPass pode fazer com os seus dados. Nada de novo é guardado e nada é enviado à IA até decidir.",
    consentGateSave: "Guardar as minhas escolhas",
    consentSaveFailed:
      "Não foi possível guardar as suas escolhas. Tente novamente.",
    consentNeededHealthTitle:
      "Permitir que a HealthPass guarde informações de saúde?",
    consentNeededAiTitle: "Permitir conselhos de saúde com IA?",
    consentAllow: "Permitir",
    consentNotNow: "Agora não",
    consentWithdrawHint: "Pode retirar isto a qualquer momento nas Definições.",
    settingsConsentsTitle: "Os seus consentimentos",
    settingsConsentsLead:
      "Retirar um consentimento interrompe qualquer novo tratamento. O que já está guardado fica até o apagar.",
    consentSaved: "Guardado",
    immediateStartTitle: "Antes de pagar",
    immediateStartAck:
      "Quero que o meu plano comece agora. Sei que, se exercer o direito de livre resolução no prazo de 14 dias, pago os dias já utilizados.",
    immediateStartTrial:
      "Se o seu plano começar com um período experimental gratuito, nada é cobrado antes de terminar, e cancelar até lá não custa nada.",
    immediateStartContinue: "Continuar para o pagamento",
    immediateStartBack: "Voltar",
    withdrawTitle: "Resolver o contrato",
    withdrawLead: "Pode resolver este plano sem indicar motivo até {deadline}.",
    withdrawRefund: "Seriam reembolsados {amount} pelos dias não utilizados.",
    withdrawTrial:
      "Está no período experimental gratuito, por isso nada foi cobrado. Resolver o contrato termina o plano agora.",
    withdrawButton: "Resolver o contrato",
    withdrawConfirm:
      "Resolver agora? O seu plano termina de imediato e a sua conta continua no plano gratuito. Os seus dados mantêm-se.",
    withdrawConfirmYes: "Sim, resolver",
    withdrawDone:
      "A resolução do contrato está confirmada. {refund}Enviámos-lhe uma confirmação por e-mail.",
    withdrawRefundIssued:
      "{amount} estão a caminho do seu meio de pagamento original. ",
    withdrawRefundPending:
      "{amount} serão reembolsados no seu meio de pagamento original no prazo de 14 dias. ",
    withdrawFailed:
      "Não foi possível concluir a resolução. Tente novamente ou escreva ao suporte.",
    copyright: "Gerencie seus dados de saúde com segurança",

    // Landing Page
    landingHeroTitle: "Viaje com tranquilidade",
    landingHeroSubtitle:
      "Suas informações de saúde de emergência, disponíveis mundialmente em segundos. Acesso por código QR para socorristas. Conforme GDPR. Sempre com você.",
    landingStartFree: "Comece grátis hoje",
    landingSeeHowItWorks: "Veja como funciona",
    landingNoCreditCard: "Não é necessário cartão de crédito",
    landingFreeTrial: "Teste gratuito de 30 dias",
    landingFeaturesTitle: "Tudo o que você precisa para viajar com segurança",
    landingFeaturesSubtitle:
      "Criado por viajantes, para viajantes. Todos os recursos essenciais em uma plataforma segura.",
    landingHowItWorksTitle: "Comece em 3 passos simples",
    landingHowItWorksSubtitle:
      "Do cadastro até pronto para viajar em menos de 10 minutos",
    landingPricingTitle: "Preços simples e transparentes",
    landingPricingSubtitle:
      "Comece grátis, atualize a qualquer momento. Sem taxas ocultas.",
    landingCtaTitle: "Pronto para viajar com tranquilidade?",
    landingCtaSubtitle:
      "Junte-se a mais de 50.000 viajantes que confiam no HealthPass com seus dados de saúde de emergência.",
    landingCtaButton: "Comece grátis hoje",

    required: "Este campo é obrigatório",
    invalidEmail: "Por favor, insira um endereço de e-mail válido",
    passwordTooShort: "A senha deve ter pelo menos 8 caracteres",
    passwordsNotMatch: "As senhas não correspondem",

    vitalSigns: "Sinais Vitais",
    bloodPressure: "Pressão Arterial",
    systolic: "Sistólica",
    diastolic: "Diastólica",
    restingHeartRate: "Frequência Cardíaca de Repouso",
    bpm: "bpm",
    mmHg: "mmHg",
    normal: "Normal",
    lowBloodPressure: "Pressão Arterial Baixa",
    elevated: "Elevada",
    hypertensionGrade1: "Hipertensão Grau 1",
    hypertensionGrade2: "Hipertensão Grau 2",
    hypertensionGrade3: "Hipertensão Grau 3",
    bradycardia: "Bradicardia",
    tachycardia: "Taquicardia",
    systolicBpError: "A pressão sistólica deve estar entre 70 e 250 mmHg",
    diastolicBpError: "A pressão diastólica deve estar entre 40 e 150 mmHg",
    heartRateError:
      "A frequência cardíaca de repouso deve estar entre 30 e 220 bpm",
    systolicNormal: "Sistólica Normal: 120",
    systolicHigh: "Sistólica Alta: 140",
    diastolicNormal: "Diastólica Normal: 80",
    systolicBloodPressure: "Pressão Arterial Sistólica",
    diastolicBloodPressure: "Pressão Arterial Diastólica",
    noVitalSignsData: "Nenhum dado de sinais vitais disponível",

    // Lab Results
    labResults: "Resultados de Laboratório",
    currentValues: "Valores Atuais",
    allTests: "Todos os Testes",
    laboratoryTest: "Teste de Laboratório",
    evaluation: "Avaliação",
    value: "Valor",
    unit: "Unidade",
    performingDoctor: "Médico/Laboratório executor (opcional)",
    pdfReport: "Relatório PDF",
    noLabResultsDocumented: "Nenhum resultado de laboratório documentado",
    addNewLabResult: "Adicionar Novo Resultado de Laboratório",
    showChart: "Mostrar Gráfico",
    hideChart: "Ocultar Gráfico",
    noWeightDataAvailable: "Nenhum dado de peso disponível",
    firstEntriesWillBeShown: "As primeiras entradas serão mostradas aqui",
    weightKg: "Peso (kg)",
    current: "Atual",
    dataPoints: "Pontos de dados",
    fhirCompatibleData: "Dados compatíveis com FHIR",
    recorded: "Registrado",
    bodyFat: "Gordura Corporal",
    bodyFatPercent: "Gordura Corporal (%)",
    raised: "Elevado",
    decreased: "Diminuído",
    critical: "Crítico",
    lastMeasurement: "Última Medição",
    vitalSignsHistory: "Histórico de Sinais Vitais (365 Dias)",
    labResultsSaved: "Resultados de laboratório salvos com sucesso",
    resetPassword: "Redefinir Senha",
    resetPasswordDescription: "Digite seu e-mail para redefinir sua senha",
    sendResetLink: "Enviar Link de Redefinição",
    emailSent: "E-mail Enviado",
    checkYourEmail: "Verifique seu E-mail",
    resetEmailSent: "E-mail de redefinição enviado",
    healthPass: "HealthPass",
    secureHealthData: "Dados de Saúde Seguros",
    manageHealthSecurely: "Gerencie sua saúde com segurança",
    testAccount: "Conta de Teste",
    useTestAccount: "Usar Conta de Teste",
    overview: "Visão Geral",
    healthRecords: "Registros de Saúde",
    appointments: "Consultas",
    reports: "Relatórios",
    recentActivity: "Atividade Recente",
    quickActions: "Ações Rápidas",
    upcomingAppointments: "Próximas Consultas",
    healthSummary: "Resumo de Saúde",
    addNewRecord: "Adicionar Novo Registro",
    viewAllRecords: "Ver Todos os Registros",
    noDataAvailable: "Nenhum dado disponível",
    upcomingTravels: "Próximas Viagens",
    noUpcomingTravels: "Nenhuma viagem próxima",
    planNewTravel: "Planejar Nova Viagem",
    today: "Hoje",
    tomorrow: "Amanhã",
    healthRecommendations: "Recomendações de Saúde",
    stayHydrated: "Mantenha-se hidratado durante o dia",
    drinkWaterRecommendation: "Objetivo de 2-3 litros de água diários",
    regularExercise: "Atividade física regular",
    exerciseRecommendation: "30 minutos de atividade moderada, 5 dias/semana",
    qualitySleep: "Sono de qualidade",
    sleepRecommendation: "7-9 horas de sono por noite",
    thinkAbout: "Pense em",
    travelInsurance: "Seguro de Viagem",
    travelInsuranceDesc: "Proteja sua viagem",
    firstAidKit: "Kit de Primeiros Socorros",
    firstAidKitDesc: "Esteja preparado",
    medicalCheckup: "Check-up Médico",
    medicalCheckupDesc: "Revisão anual de saúde",
    vaccinationAppointment: "Consulta de Vacinação",
    vaccinationAppointmentDesc: "Mantenha-se protegido",
    tryOutPlus: "Experimente HealthPass PLUS",
    plusDescription: "Desbloqueie recursos premium para melhor gestão de saúde",
    plusFeature1: "Análises de saúde avançadas",
    plusFeature2: "Suporte prioritário",
    plusFeature3: "Armazenamento ilimitado de documentos",
    plusFeature4: "Insights alimentados por IA",
    upgradeToPlusButton: "Atualizar para PLUS",
    addHealthRecord: "Adicionar Registro de Saúde",
    editHealthRecord: "Editar Registro de Saúde",
    deleteHealthRecord: "Excluir Registro de Saúde",
    recordType: "Tipo de Registro",
    date: "Data",
    description: "Descrição",
    attachments: "Anexos",
    weight: "Peso",
    height: "Altura",
    temperature: "Temperatura",
    heartRate: "Frequência Cardíaca",
    currentMedications: "Medicamentos Atuais",
    addMedication: "Adicionar Medicamento",
    prescribedBy: "Prescrito por",
    sideEffects: "Efeitos Colaterais",
    instructions: "Instruções",
    travelPlans: "Planos de Viagem",
    addTravelPlan: "Adicionar Plano de Viagem",
    destination: "Destino",
    departureDate: "Data de Partida",
    returnDate: "Data de Retorno",
    travelType: "Tipo de Viagem",
    requiredVaccinations: "Vacinações Obrigatórias",

    // Travel List Page
    travels: "Viagens",
    myTravels: "Minhas Viagens",
    planYourTrips:
      "Planeje suas viagens e gerencie requisitos de saúde relacionados a viagens",
    newTravel: "Nova Viagem",
    pastTravels: "Viagens Passadas",
    currentTravels: "Viagens Atuais",
    plannedTravels: "Viagens Planejadas",
    draftTravels: "Rascunhos",
    noTravelsYet: "Ainda sem viagens",
    startPlanningTravel:
      "Comece a planejar sua primeira viagem para obter recomendações e requisitos de saúde",
    travelTitle: "Título da Viagem",
    travelPurpose: "Propósito da Viagem",
    travelDestination: "Destino da Viagem",
    startDate: "Data de Início",
    endDate: "Data de Término",
    status: "Status",
    actions: "Ações",
    viewTravel: "Ver Viagem",
    editTravel: "Editar Viagem",
    deleteTravel: "Excluir Viagem",
    archiveTravel: "Arquivar Viagem",
    confirmDeleteTravel: "Tem certeza de que deseja excluir esta viagem?",
    travelDeleted: "Viagem excluída com sucesso",
    travelArchived: "Viagem arquivada com sucesso",

    // Travel Status
    statusDraft: "Rascunho",
    statusPlanned: "Planejada",
    statusCurrent: "Atual",
    statusCompleted: "Concluída",
    statusCancelled: "Cancelada",
    statusArchived: "Arquivada",
    statusModeManual: "Definir manualmente",
    statusModeAutoHint:
      "O status é calculado automaticamente com base nas datas da viagem.",

    // Travel Purpose
    purposeLeisure: "Lazer",
    purposeBusiness: "Negócios",
    purposeMedical: "Médico",
    purposeEducation: "Educação",
    purposeVolunteering: "Voluntariado",
    purposeOther: "Outro",
    tripTitle: "Título da Viagem",
    tripType: "Tipo de Viagem",
    tripOperator: "Operador Turístico",
    bookingReference: "Referência de Reserva",
    startLocation: "Local de Partida",
    endLocation: "Local de Chegada",
    tripTypeCruise: "Cruzeiro",
    tripTypeBeach: "Praia",
    tripTypeMountains: "Montanhas",
    tripTypeSports: "Esportes",
    tripTypeEducation: "Educação",
    tripTypeEvent: "Evento",
    tripTypeCulture: "Cultura",
    tripTypeExpedition: "Expedição",
    plannedActivities: "Atividades planejadas",
    activity_diving: "Mergulho",
    activity_climbing: "Escalada",
    activity_skiing: "Esqui",
    activity_hiking: "Caminhada",
    activity_biking: "Ciclismo",
    activity_swimming: "Natação",
    activity_safari: "Safári",
    healthcareFacilities: "Instalações de saúde",
    nearbyHospitals: "Hospitais próximos",
    nearbyPharmacies: "Farmácias próximas",
    nearbyClinics: "Clínicas próximas",
    noFacilitiesFound: "Nenhuma instalação encontrada nesta área",
    facilityDistance: "Distância",
    forumHealthSummary: "Relatos de viajantes",
    commonHealthIssues: "Problemas de saúde comuns",
    travelerTips: "Dicas de viajantes",
    unexpectedRisks: "Riscos inesperados",
    forumSummaryDisclaimer:
      "Resumo gerado por IA. Verificar com relatórios atuais.",
    searchForumsFurther: "Pesquisar relatórios recentes",
    loadForumSummary: "Carregar relatórios",
    operatorHealthInfo: "Informações de saúde do operador",
    cruiseHealthRequirements: "Requisitos de saúde para cruzeiros",
    tripSegments: "Segmentos da Viagem",
    addSegment: "Adicionar Segmento",
    editSegment: "Editar Segmento",
    deleteSegment: "Excluir Segmento",
    noSegmentsYet: "Nenhum segmento adicionado ainda",
    addSegmentsDescription:
      "Adicione segmentos de voo, hotel ou atividade para organizar sua viagem",
    addFirstSegment: "Adicionar Primeiro Segmento",
    operator: "Operador",
    transportation: "Transporte",
    segmentInformation: "Informações do Segmento",
    segmentTitle: "Título do Segmento",
    segmentType: "Tipo de Segmento",
    segmentTypeTransfer: "Transferência",
    segmentTypeDestination: "Destino",
    segmentTypeAccommodation: "Acomodação",
    segmentTypeActivity: "Atividade",
    locations: "Locais",
    startCountry: "País de Partida",
    endCountry: "País de Chegada",
    dateTime: "Data e Hora",
    startDateTime: "Data e Hora de Partida",
    endDateTime: "Data e Hora de Chegada",
    selectOption: "Selecionar...",
    transportFlight: "Voo",
    transportTrain: "Trem",
    transportBus: "Ônibus",
    transportCar: "Carro",
    transportShip: "Navio",
    accommodation: "Acomodação",
    accommodationHotel: "Hotel",
    accommodationHostel: "Hostel",
    accommodationApartment: "Apartamento",
    accommodationCruiseCabin: "Cabine de Cruzeiro",
    healthRisk: "Risco de Saúde",
    travelReadiness: "Prontidão para Viagem",
    vaccinations: "Vacinações",
    recommendations: "Recomendações",
    available: "disponível",
    riskManagement: "Gestão de Riscos",
    vaccinationDetails: "Detalhes de Vacinação",
    readinessChecklist: "Lista de Prontidão",
    personalRecommendations: "Recomendações Pessoais",
    bookAppointment: "Agendar Consulta",

    // Travel Details
    travelCompanions: "Companheiros de Viagem",
    companions: "Companheiros",
    tourOperator: "Operador Turístico",
    travelNotes: "Notas de Viagem",
    notes: "Notas",
    segments: "Segmentos",
    travelSegment: "Segmento de Viagem",
    transportationType: "Tipo de Transporte",
    accommodationType: "Tipo de Acomodação",

    // Filter & Sort
    filterByStatus: "Filtrar por Status",
    filterByPurpose: "Filtrar por Propósito",
    sortByDate: "Ordenar por Data",
    sortByDestination: "Ordenar por Destino",
    searchTravels: "Pesquisar viagens...",
    showAll: "Mostrar Todos",
    showActive: "Mostrar Ativos",
    showCompleted: "Mostrar Concluídos",

    // Time Indicators
    daysUntilDeparture: "Dias até a partida",
    daysRemaining: "Dias restantes",
    inProgress: "Em Andamento",
    departed: "Partiu",
    returned: "Retornou",

    // Travel Form
    travelDates: "Datas de Viagem",
    travelDetails: "Detalhes da Viagem",
    healthInformationNotice: "Aviso de Informação de Saúde",
    healthRequirementsDescription:
      "Os requisitos de saúde de viagem serão exibidos aqui com base no seu destino. Essas informações são provenientes do CDC, OMS e autoridades de saúde locais.",
    vaccinationInfoLoading:
      "Os requisitos de vacinação serão carregados com base no seu destino e datas de viagem.",
    healthWarningsLoading:
      "Avisos e advertências de saúde serão exibidos aqui.",
    apiIntegrationPending: "Integração API com CDC/OMS pendente",
    checkBackLater:
      "As informações estarão disponíveis assim que os dados de saúde do destino forem integrados",
    healthWarnings: "Avisos de Saúde",
    lastUpdated: "Última atualização",
    source: "Fonte",
    noVaccinationsRequired: "Nenhuma vacina específica necessária",
    noHealthWarnings: "Nenhum aviso de saúde específico",
    risk: "risco",

    // Health Recommendations
    departure: "Partida",
    mandatory: "Obrigatório",
    recommended: "Recomendado",
    suggested: "Sugerido",
    recommendedVaccinations: "Vacinas Recomendadas",
    activeDiseaseOutbreaks: "Surtos Ativos de Doenças",
    dataSources: "Fontes de Dados",
    from: "De",
    to: "Para",
    isoCode: "Código ISO",
    riskLevel: "Nível de Risco",
    healthDataLoadError: "Não foi possível carregar os dados de saúde.",
    dataFrom: "Dados de",
    overallRisk: "Avaliação geral do risco",
    quickInstallButton: "Instalar aplicação",
    quickInstallInstalled: "Já instalada",
    updateAvailable: "Carregar a nova versão.",
    quickInstallAndroidApkStep1: "Digitalize o código e toque em Transferir.",
    quickInstallAndroidApkStep2:
      "Abra o ficheiro transferido e permita instalações a partir do Chrome.",
    quickInstallAndroidApkStep3:
      "Abra o HEALTHPASS a partir do ecrã principal.",
    quickInstallTitle: "Instale a aplicação em três passos",
    quickInstallSubtitle:
      "Aponte a câmara do telemóvel para o código. O HEALTHPASS instala-se como uma aplicação normal, ocupa o ecrã inteiro e funciona offline.",
    quickInstallScanHint: "Digitalize para abrir no seu telemóvel",
    quickInstallAndroidTitle: "Android · Chrome",
    quickInstallAndroidStep1:
      "Digitalize o código ou abra travelhealthpass.app no Chrome.",
    quickInstallAndroidStep2:
      "Toque no menu de três pontos e depois em «Instalar aplicação».",
    quickInstallAndroidStep3: "Abra o HEALTHPASS a partir do ecrã principal.",
    quickInstallIosTitle: "iPhone e iPad · Safari",
    quickInstallIosStep1:
      "Digitalize o código ou abra travelhealthpass.app no Safari.",
    quickInstallIosStep2:
      "Toque em Partilhar e depois em «Adicionar ao ecrã principal».",
    quickInstallIosStep3: "Abra o HEALTHPASS a partir do ecrã principal.",
    quickInstallIosNote:
      "Apenas Safari — o Chrome e o Firefox no iPhone não conseguem instalar aplicações web.",
    appInstallTitle: "HealthPass no seu telemóvel",
    appInstallSubtitle:
      "Instale o HealthPass em Android ou iPhone e leve consigo o seu registo de saúde, o cartão de emergência e os requisitos sanitários de viagem, mesmo offline.",
    appInstallAndroidTitle: "Android",
    appInstallAndroidSubtitle: "Instalar o APK diretamente",
    appInstallScanToDownload:
      "Digitalize este código com a câmara do telemóvel para transferir o APK, ou utilize o botão abaixo.",
    appInstallDownloadApk: "Transferir a app HealthPass",
    appInstallApkUnavailable: "O APK para Android ainda não está disponível.",
    appInstallApkUnavailableHint:
      "Entretanto, o HealthPass funciona como uma aplicação web instalável: abra este site no Chrome do seu telemóvel Android, toque no menu e escolha «Instalar aplicação».",
    appInstallAndroidStep1:
      "Digitalize o código QR ou toque em «Transferir APK» no seu telemóvel Android.",
    appInstallAndroidStep2:
      "Quando o Android avisar que o ficheiro pode danificar o dispositivo, escolha «Transferir mesmo assim» - este aviso surge em qualquer aplicação instalada fora da Play Store.",
    appInstallAndroidStep3:
      "Abra o ficheiro transferido. O Android perguntará se permite instalações desta origem; toque em «Definições» e ative a permissão.",
    appInstallAndroidStep4: "Volte atrás e toque em «Instalar».",
    appInstallAndroidStep5:
      "Abra o HealthPass e inicie sessão com a sua conta existente.",
    appInstallAndroidPwaTitle: "Instalar a partir do Chrome",
    appInstallAndroidPwaStep1:
      "Abra este site no Chrome do seu telemóvel Android - digitalize o código acima ou escreva o endereço.",
    appInstallAndroidPwaStep2:
      "Toque no menu de três pontos no canto superior direito.",
    appInstallAndroidPwaStep3:
      "Toque em «Adicionar ao ecrã principal» ou «Instalar aplicação».",
    appInstallAndroidPwaStep4: "Confirme com «Instalar».",
    appInstallAndroidPwaStep5:
      "Abra o HealthPass a partir do ecrã principal e inicie sessão. Funciona em ecrã inteiro e offline.",
    appInstallIosTitle: "iPhone e iPad",
    appInstallIosSubtitle: "Adicionar ao ecrã principal a partir do Safari",
    appInstallScanToOpen:
      "Digitalize este código com a câmara do iPhone para abrir o HealthPass no Safari.",
    appInstallIosStep1:
      "Abra este site no Safari. O Chrome e o Firefox em iOS não conseguem instalar aplicações web.",
    appInstallIosStep2:
      "Toque no botão Partilhar - o quadrado com uma seta para cima, na parte inferior do ecrã.",
    appInstallIosStep3:
      "Desloque para baixo e toque em «Adicionar ao ecrã principal».",
    appInstallIosStep4: "Toque em «Adicionar» no canto superior direito.",
    appInstallIosStep5:
      "Abra o HealthPass a partir do ecrã principal e inicie sessão. Passa a funcionar em ecrã inteiro e offline.",
    appInstallIosSafariNote:
      "A Apple só permite que o Safari instale aplicações web, por isso execute estes passos no Safari mesmo que outro navegador seja o predefinido.",
    appInstallAndroidGuideLink: "Guia completo de instalação no Android",
    androidInstallPageTitle: "Instalar o HealthPass no Android",
    androidInstallPageSubtitle:
      "Instale o HealthPass no seu telemóvel Android em poucos passos. Funciona em ecrã inteiro e offline.",
    androidInstallScanPageQr:
      "Está a ler isto num computador? Digitalize este código para abrir esta página no telemóvel Android onde quer o HealthPass.",
    androidInstallSecurityNote:
      "O Android pede autorização antes de instalar uma aplicação fora da Play Store. Esse pedido é esperado aqui. A autorização aplica-se apenas ao navegador a que a concede e pode retirá-la depois de concluída a instalação.",
    androidInstallIosHint:
      "Está a usar um iPhone? Siga antes as instruções para iOS.",
    androidInstallBackHome: "Voltar à página inicial",
    aiAnalysisError: "Análise de IA não disponível.",
    personalizedHealthRecommendations: "Recomendações de saúde personalizadas",
    recommendationsIntro:
      "Com base no seu destino, na duração da viagem e no seu perfil de saúde – recomendações baseadas em evidência e em várias fontes.",
    aiTripAnalysis: "Análise de saúde da viagem por IA",
    analyzing: "A analisar…",
    runAiAnalysis: "Executar análise de IA",
    aiAnalysisHint:
      "Análise do impacto climático e do perfil de saúde pessoal assistida por IA.",
    climateRiskSummary: "Resumo dos riscos climáticos",
    healthProfileRisks: "Riscos do seu perfil de saúde",
    urgentActions: "Ações urgentes",
    noRecommendationsAvailable:
      "Não foram encontradas recomendações específicas para este destino.",
    travelStatus: "Estado da viagem",
    readiness: "Preparação",
    overallReadiness: "Preparação geral",
    urgent: "Urgente",
    addCustomTask: "Adicionar tarefa personalizada",
    taskDescription: "Descrição da tarefa…",
    vaccinationRequired: "Obrigatória",
    vaccinationRecommended: "Recomendada",
    atLeast: "Pelo menos",
    daysBeforeDeparture: "dias antes da partida",
    validFor: "Válida por",
    loadingHealthData: "A carregar dados de saúde…",
    noRequiredVaccinations: "Não há vacinas obrigatórias para este destino.",
    noRecommendedVaccinations: "Não são recomendadas vacinas adicionais.",
    vaccinationTimeline: "Calendário de vacinação",
    before: "antes",
    riskLevelDescLow: "Risco de saúde baixo para este destino",
    riskLevelDescMedium: "Risco de saúde moderado - tome precauções",
    riskLevelDescHigh:
      "Risco de saúde elevado - procure aconselhamento médico antes de partir",
    riskLevelDescCritical:
      "Risco de saúde crítico - viagem não aconselhada sem parecer especializado",
    activeOutbreaks: "Surtos de doenças ativos",
    activeOutbreaksDetected: "surto(s) de doença ativo(s) detetado(s)",
    cases: "Casos",
    lastReported: "Último relato",
    travelWarnings: "Avisos de viagem",
    transportModeRisks: "Riscos por meio de transporte",
    activitySpecificRisks: "Riscos de saúde por atividade",
    riskCategoryVaccination: "Vacinas",
    riskCategoryMedication: "Medicação",
    riskCategoryPrecaution: "Precauções",
    riskCategoryAdvisory: "Recomendações",
    riskCategoryWarning: "Avisos",
    trend: "Tendência",
    increasing: "Aumentando",
    decreasing: "Diminuindo",
    stable: "Estável",
    casesReported: "Casos Reportados",
    lastReport: "Último Relatório",
    mayAffectTravel: "Pode afetar a viagem",
    validity: "Validade",
    years: "anos",
    minDaysBeforeDeparture: "Dias mín. antes da partida",
    boosterRequired: "Reforço necessário",
    contraindications: "Contraindicações",
    relatedDiseases: "Doenças relacionadas",
    ageRestrictions: "Restrições de idade",
    updated: "Atualizado",
    noHealthDataAvailable: "Nenhum dado de saúde disponível",
    failedToLoadHealthRecommendations:
      "Falha ao carregar recomendações de saúde",
    backToDashboard: "Voltar ao Painel",

    uploadDocument: "Enviar Documento",
    documentType: "Tipo de Documento",
    uploadDate: "Data de Envio",
    fileSize: "Tamanho do Arquivo",
    downloadDocument: "Baixar Documento",
    documentName: "Nome do Documento",

    // Chat
    chatWithTina: "Conversar com Tina",
    tinaHealthAssistant: "Tina - Sua assistente de saúde",
    askQuestion: "Fazer Pergunta",
    askMeAnything: "Pergunte-me qualquer coisa sobre sua saúde",
    typeMessage: "Digite uma mensagem",
    typeYourMessage: "Digite sua mensagem...",
    sendMessage: "Enviar",
    chatHistory: "Histórico de Chat",
    newConversation: "Nova conversa",
    conversations: "Conversas",
    includeHealthContext: "Incluir meus dados de saúde",
    healthContextEnabled:
      "Contexto de saúde ativado - Tina pode ver seus medicamentos, alergias e sinais vitais",
    healthContextDisabled:
      "Contexto de saúde desativado - Tina responderá sem seus dados de saúde",
    aiThinking: "Tina está pensando...",
    noConversationsYet: "Ainda não há conversas",
    startNewConversation: "Iniciar uma nova conversa com Tina",
    deleteConversation: "Excluir conversa",
    conversationDeleted: "Conversa excluída",
    aiUnavailable: "Assistente IA indisponível",
    aiUnavailableMessage:
      "O assistente de IA não está disponível no momento. Tente novamente mais tarde.",
    messagePlaceholder:
      "Pergunte sobre medicamentos, sintomas, saúde de viagem...",
    exampleQuestions: "Perguntas exemplo:",
    example1: "Quais são meus medicamentos atuais?",
    example2: "Quais vacinas preciso para viajar?",
    example3: "Explique meus últimos sinais vitais",

    // Profile
    personalInformation: "Informações Pessoais",
    contactInformation: "Informações de Contato",
    emergencyContact: "Contato de Emergência",
    medicalInformation: "Informações Médicas",
    updateProfile: "Atualizar Perfil",
    changePassword: "Alterar Senha",
    phone: "Telefone",
    dateOfBirth: "Data de Nascimento",
    country: "País",
    nationality: "Nacionalidade",
    currentPassword: "Senha Atual",
    newPassword: "Nova Senha",
    confirmNewPassword: "Confirmar Nova Senha",
    languageSettings: "Configurações de Idioma",
    notificationSettings: "Configurações de Notificação",
    privacySettings: "Configurações de Privacidade",
    accountSettings: "Configurações da Conta",
    dataExport: "Exportar Dados",
    deleteAccount: "Excluir Conta",

    // Account Deletion
    dangerZone: "Zona de Perigo",
    deleteAccountWarning:
      "Uma vez que você excluir sua conta, não há como voltar atrás. Todos os seus dados de saúde, documentos e informações pessoais serão permanentemente excluídos de nossos sistemas.",
    deleteAccountConfirmationPrompt:
      "Esta ação não pode ser desfeita. Todos os seus dados serão permanentemente excluídos.",
    typeToConfirm: "Digite para confirmar",
    enterYourPassword: "Digite sua senha",
    deleteAccountWarningDetails:
      "Todos os seus registros de saúde, documentos, planos de viagem e conversas serão permanentemente excluídos. Uma assinatura paga é cancelada antes e não volta a ser cobrada. Os registros de auditoria serão mantidos para fins de conformidade.",
    confirmDeletion: "Excluir Conta Permanentemente",
    deleting: "Excluindo...",
    accountDeletedSuccessfully:
      "Sua conta foi excluída com sucesso. Você será desconectado em breve.",
    deleteAccountError:
      "Falha ao excluir conta. Por favor tente novamente ou entre em contato com o suporte.",
    deleteAccountConfirmationError:
      'Por favor digite "DELETE MY ACCOUNT" exatamente para confirmar',
    invalidPassword: "Senha inválida. Por favor tente novamente.",
    passwordRequired: "Senha obrigatória",
    warning: "Aviso",

    // Emergency Profile
    emergencyProfile: "Perfil de Emergência",
    myEmergencyProfile: "Meu Perfil de Emergência",
    emergencyData: "Dados de Emergência",
    completenessScore: "Pontuação de Completude",
    dataQuality: "Qualidade dos Dados",
    dataQualityGood: "Boa - Todos os dados críticos completos",
    dataQualityFair: "Razoável - Alguns dados faltando",
    dataQualityPoor: "Ruim - Dados críticos faltando",
    criticalData: "Dados Críticos",
    priority1Data: "Prioridade 1: Crítico",
    priority2Data: "Prioridade 2: Importante",
    priority3Data: "Prioridade 3: Útil",
    missingCriticalData: "Dados Críticos Faltando",
    quickAdd: "Adicionar Rápido",
    editEmergencyData: "Editar Dados de Emergência",
    shareEmergencyAccess: "Compartilhar Acesso de Emergência",
    accessDuration: "Duração do Acesso",
    duration15min: "15 Minutos",
    duration1hour: "1 Hora",
    duration6hours: "6 Horas",
    duration24hours: "24 Horas",
    durationCustom: "Duração Personalizada",
    dataScope: "Escopo de Dados",
    selectDataToShare: "Selecionar Dados para Compartilhar",
    emergencyQRCode: "Código QR de Emergência",
    refreshQRCode: "Atualizar Código QR",
    downloadQRCode: "Baixar Código QR",
    printQRCode: "Imprimir Código QR",
    copyAccessLink: "Copiar Link de Acesso",
    qrCodeExpiry: "Expiração do Código QR",
    addEmergencyContact: "Adicionar Contato de Emergência",
    primaryContact: "Contato Primário",
    secondaryContact: "Contato Secundário",
    tertiaryContact: "Contato Terciário",
    contactName: "Nome do Contato",
    relationship: "Relacionamento",
    phoneNumber: "Número de Telefone",
    notifyOnAccess: "Notificar em Acesso de Emergência",
    availabilityNotes: "Notas de Disponibilidade",
    reorderContacts: "Reordenar Contatos",
    devices: "Dispositivos Médicos",
    allergySubstance: "Substância Alérgica",
    allergySeverity: "Severidade",
    allergyReaction: "Reação",
    conditionName: "Nome da Condição",
    conditionStatus: "Status",
    deviceType: "Tipo de Dispositivo",
    deviceUDI: "UDI do Dispositivo",
    verifiedDate: "Data de Verificação",
    outdatedWarning:
      "Estes dados estão desatualizados e precisam de verificação",
    incompleteWarning: "Seu perfil de emergência está incompleto",
    updateRequired: "Atualização Necessária",
    dataVerifiedRecently: "Dados verificados recentemente",

    add: "Adicionar",
    update: "Atualizar",
    remove: "Remover",
    search: "Pesquisar",
    filter: "Filtrar",
    sort: "Ordenar",
    export: "Exportar",
    import: "Importar",
    refresh: "Atualizar",
    active: "Ativo",
    inactive: "Inativo",
    pending: "Pendente",
    completed: "Concluído",
    cancelled: "Cancelado",
    savedSuccessfully: "Salvo com sucesso",
    deletedSuccessfully: "Excluído com sucesso",
    errorOccurred: "Ocorreu um erro",
    confirmDelete: "Confirmar Exclusão",
    areYouSure: "Tem certeza?",
    cannotBeUndone: "Isso não pode ser desfeito",
    healthStatusOptimal: "Estado de Saúde Ótimo",
    allDataCurrent: "Todos os dados estão atualizados",
    pulse: "Pulso",
    wellbeing: "Bem-estar",
    physicalWellbeing: "Bem-estar Físico",
    physicalWellness: "Bem-estar Físico",
    emotionalWellbeing: "Bem-estar Emocional",
    emotionalWellness: "Bem-estar Emocional",
    howDoYouFeelPhysically: "Como você se sente fisicamente?",
    howDoYouFeelEmotionally: "Como você se sente emocionalmente?",
    currentStatus: "Status Atual",
    lastUpdate: "Última Atualização",
    trendChart: "Gráfico de Tendência",
    trendHistory: "Tendência dos últimos 365 dias",
    noTrendData: "Nenhum dado de tendência disponível",
    noTrendDataDesc:
      "Acompanhe seu bem-estar regularmente para ver seu progresso",
    noWellbeingData: "Nenhum dado de bem-estar disponível",
    saveFirstWellbeingEntry:
      "Salve sua primeira avaliação de bem-estar para ver tendências",
    wellnessDataPoints: "Pontos de Dados",
    days: "Dias",
    wellnessScore: "Pontuação de Bem-estar (1-5)",
    average: "Média",
    good: "Bom",
    low: "Baixo",
    veryPoor: "Muito Ruim",
    poor: "Ruim",
    fair: "Razoável",
    wellGood: "Bom",
    excellent: "Excelente",
    physicalVeryPoor: "Muito Ruim",
    physicalPoor: "Ruim",
    physicalFair: "Razoável",
    physicalGood: "Bom",
    physicalExcellent: "Excelente",
    emotionalVeryPoor: "Muito Deprimido",
    emotionalPoor: "Triste",
    emotionalFair: "Equilibrado",
    emotionalGood: "Feliz",
    emotionalExcellent: "Muito Feliz",
    addNewDocument: "Adicionar Novo Documento",
    addDocumentDesc: "Carregar documentos médicos",
    addMedicationDesc: "Adicionar novo medicamento",
    planTravel: "Planejar Viagem",
    planTravelDesc: "Planejar viagem com requisitos de saúde",
    addHealthDataDesc: "Adicionar novos dados de saúde",
    upcomingReminders: "Lembretes Próximos",
    highPriority: "Alta Prioridade",
    mediumPriority: "Média Prioridade",
    lowPriority: "Baixa Prioridade",
    vaccination: "Vacinação",
    daysAgo: "dias atrás",
    weeksAgo: "semanas atrás",
    inDays: "em dias",
    immunizations: "Imunizações",
    manageImmunizations: "Gerenciar Imunizações",
    newImmunization: "Nova Imunização",
    editImmunization: "Editar Imunização",
    deleteImmunization: "Excluir Imunização",
    vaccineName: "Nome da Vacina",
    vaccineCode: "Código da Vacina",
    manufacturer: "Fabricante",
    lotNumber: "Número do Lote",
    doseNumber: "Número da Dose",
    totalDoses: "Total de Doses",
    administeredDate: "Data de Administração",
    expirationDate: "Data de Validade",
    nextDueDate: "Próxima Data",
    location: "Local",
    administrator: "Administrador",
    requiredForCountries: "Obrigatório para Países",
    uploadCertificate: "Carregar Certificado",
    downloadCertificate: "Baixar Certificado",
    certificateUploaded: "Certificado Carregado",
    noCertificateFound: "Nenhum Certificado Encontrado",
    certificateUploadFailed: "Falha ao Carregar Certificado",
    fhirData: "Dados FHIR",
    internationalCertificate: "Certificado Internacional",
    internationalVaccinationCertificate:
      "Certificado Internacional de Vacinação",
    passportNumber: "Número do Passaporte",
    healthPassDocumentId: "ID do Documento HealthPass",
    certificateNumber: "Número do Certificado",
    issuerId: "ID do Emissor",
    publicKeyFingerprint: "Impressão Digital da Chave Pública",
    signatureTimestamp: "Carimbo de Data/Hora da Assinatura",
    issuedBy: "Emitido por",
    issuedDate: "Data de Emissão",
    validFrom: "Válido a partir de",
    patientInformation: "Informações do Paciente",
    certificateInformation: "Informações do Certificado",
    digitalSignature: "Assinatura Digital",
    qrCodeValidation: "Validação do Código QR",
    printCertificate: "Imprimir Certificado",
    copyCertificateData: "Copiar Dados do Certificado",
    deleteConfirmation: "Confirmação de Exclusão",
    noImmunizationsFound: "Nenhuma Imunização Encontrada",
    addFirstImmunization: "Adicionar Primeira Imunização",
    certificateValidUntil: "Certificado Válido até",
    dosesCompleted: "Doses Concluídas",
    of: "de",
    loginSuccessful: "Login bem-sucedido",
    loginFailed: "Falha no login",
    loginInvalidOrPaused:
      "E-mail ou palavra-passe incorretos. Após 10 tentativas falhadas, o início de sessão desta conta fica em pausa até 15 minutos.",
    registrationSuccessful: "Registro bem-sucedido",
    registrationFailed: "Falha no registro",
    logoutSuccessful: "Logout bem-sucedido",
    sessionExpired: "Sessão expirada",
    networkError: "Erro de rede",
    accessDenied: "Acesso negado",
    validationError: "Erro de validação",
    tooManyRequests: "Muitas solicitações",
    serverError: "Erro do servidor",
    medicationsPage: "Medicamentos",
    medicationItem: "Medicamento",
    medicationPlan: "Plano de Medicação",
    addNewMedication: "Adicionar Novo Medicamento",
    editExistingMedication: "Editar Medicamento Existente",
    deleteMedicationItem: "Excluir Item de Medicamento",
    medicationName: "Nome do Medicamento",
    genericName: "Nome Genérico",
    medicationDosage: "Dosagem do Medicamento",
    dosageForm: "Forma de Dosagem",
    medicationStrength: "Concentração do Medicamento",
    medicationFrequency: "Frequência do Medicamento",
    timingInstructions: "Instruções de Horário",
    mealTiming: "Horário das Refeições",
    medicationStartDate: "Data de Início do Medicamento",
    medicationEndDate: "Data de Término do Medicamento",
    medicationPrescribedBy: "Medicamento Prescrito por",
    medicationPharmacy: "Farmácia",
    medicationInstructions: "Instruções do Medicamento",
    medicationSideEffects: "Efeitos Colaterais do Medicamento",
    medicationInteractions: "Interações Medicamentosas",
    eanCode: "Código EAN",
    pznCode: "Código PZN",
    atcCode: "Código ATC",
    tablet: "Comprimido",
    capsule: "Cápsula",
    syrup: "Xarope",
    injection: "Injeção",
    cream: "Creme",
    ointment: "Pomada",
    drops: "Gotas",
    spray: "Spray",
    patch: "Adesivo",
    suppository: "Supositório",
    powder: "Pó",
    other: "Outro",
    inhaler: "Inalador",
    gel: "Gel",
    lotion: "Loção",
    solution: "Solução",
    beforeMeal: "Antes da Refeição",
    afterMeal: "Após a Refeição",
    withMeal: "Com a Refeição",
    independent: "Independente",
    onEmptyStomach: "Em Jejum",
    medicationRegular: "Medicamento Regular",
    medicationTravel: "Medicamento de Viagem",
    medicationEmergency: "Medicamento de Emergência",
    medicationProphylaxis: "Medicamento Profilático",
    regular: "Regular",
    emergency: "Emergência",
    travelMedication: "Medicamento de Viagem",
    prophylaxis: "Profilaxia",
    cruise: "Cruzeiro",
    trekking: "Trekking",
    diving: "Mergulho",
    tropical: "Tropical",
    altitude: "Altitude",
    business: "Negócios",
    adventure: "Aventura",
    urban: "Urbano",
    recordIntake: "Registrar Ingestão",
    intakeHistory: "Histórico de Ingestão",
    adherence: "Aderência",
    doseTaken: "Dose Tomada",
    intakeNotes: "Notas de Ingestão",
    timeline: "Linha do Tempo",
    lastTaken: "Última Tomada",
    nextDue: "Próximo Vencimento",
    internationalMedicationPlan: "Plano Internacional de Medicação",
    generatePlan: "Gerar Plano",
    uploadGermanPlan: "Carregar Plano Alemão",
    downloadPlan: "Baixar Plano",
    printPlan: "Imprimir Plano",
    qrCodePlan: "Plano com Código QR",
    packageImage: "Imagem da Embalagem",
    medicationImage: "Imagem do Medicamento/Pílula",
    uploadPackageImage: "Carregar Imagem da Embalagem",
    uploadMedicationImage: "Carregar Imagem do Medicamento",
    takePhoto: "Tirar Foto",
    scanBarcode: "Escanear Código de Barras",
    tradeName: "Nome Comercial",
    activeIngredient: "Princípio Ativo",
    manufacturerName: "Fabricante",
    internationalCode: "Código Internacional",
    codeSystem: "Sistema de Código",
    removeImage: "Remover Imagem",
    imageUploadSuccess: "Imagem carregada com sucesso",
    imageUploadFailed: "Falha ao carregar imagem",
    imageDeleteSuccess: "Imagem excluída com sucesso",
    imageDeleteFailed: "Falha ao excluir imagem",
    optional: "Opcional",
    imagesSection: "Imagens",
    packagePhoto: "Foto da Embalagem",
    pillPhoto: "Foto da Pílula",
    gtinCode: "Código GTIN",
    ndcCode: "Código NDC (EUA)",
    handelsname: "Nome Comercial",
    wirkstoff: "Princípio Ativo",
    hersteller: "Fabricante",
    pharmaCode: "Código Farmacêutico",
    basicInformation: "Informações Básicas",
    fhirExtendedFields: "Campos Estendidos FHIR R5",
    medicationImages: "Imagens do Medicamento",
    medicationInformation: "Informações do Medicamento",
    dosageInformation: "Informações de Dosagem",
    prescription: "Prescrição",
    note: "Nota",
    selfSignedInfo:
      "Quando você insere o medicamento por conta própria, ele será automaticamente marcado como ",
    medicationImagesDescription:
      "Carregue imagens da embalagem e das pílulas do medicamento (opcional)",
    medicationImagesHint:
      "Imagens de alta qualidade ajudam na identificação e podem ser úteis para documentação de viagem.",
    saveChanges: "Salvar Alterações",
    saveMedication: "Salvar Medicamento",
    choosePackageImage: "Escolher Imagem da Embalagem",
    chooseMedicationImage: "Escolher Imagem do Medicamento",
    category: "Categoria",
    dosage: "Dosagem",
    pharmacy: "Farmácia",
    editMedication: "Editar Medicamento",
    updateMedication: "Atualizar Medicamento",
    uploadingImages: "Carregando Imagens...",
    saving: "Salvando...",
    travelRecommendations: "Recomendações de Viagem",
    supplementaryMedications: "Medicamentos Suplementares",
    interactionWarnings: "Avisos de Interações",
    implementRecommendation: "Implementar Recomendação",
    medicationAdded: "Medicamento Adicionado",
    medicationUpdated: "Medicamento Atualizado",
    medicationDeleted: "Medicamento Excluído",
    intakeRecorded: "Ingestão Registrada",
    planGenerated: "Plano Gerado",
    imageUploaded: "Imagem Carregada",

    // Avatar Upload
    profilePicture: "Foto de Perfil",
    managePersonalDataSettings: "Gerencie seus dados pessoais e configurações",
    uploadPhoto: "Enviar Foto",
    deletePhoto: "Excluir Foto",
    invalidFileType:
      "Tipo de arquivo inválido. Somente JPEG, PNG, WebP, GIF permitidos",
    fileTooLarge: "Arquivo muito grande. Tamanho máximo 5MB",
    maxFileSize:
      "Tamanho máximo: 5MB. Formatos suportados: JPEG, PNG, WebP, GIF",
    avatarUploadedSuccessfully: "Avatar enviado com sucesso",
    avatarUploadFailed: "Falha ao enviar avatar",
    confirmDeleteAvatar: "Tem certeza de que deseja excluir seu avatar?",
    avatarDeletedSuccessfully: "Avatar excluído com sucesso",
    avatarDeleteFailed: "Falha ao excluir avatar",
    cropImage: "Recortar Imagem",
    rotate: "Girar",

    // Profile Page - Form Fields
    notSpecified: "Não especificado",
    day: "Dia",
    month: "Mês",
    year: "Ano",
    yearsOld: "anos",

    // Date/Month names
    january: "Janeiro",
    february: "Fevereiro",
    march: "Março",
    april: "Abril",
    may: "Maio",
    june: "Junho",
    july: "Julho",
    august: "Agosto",
    september: "Setembro",
    october: "Outubro",
    november: "Novembro",
    december: "Dezembro",

    // Address Fields
    address: "Endereço",
    street: "Rua",
    houseNumber: "Número",
    addressSupplement: "Complemento",
    postalCode: "CEP",
    city: "Cidade",
    state: "Estado/Região",
    streetName: "Nome da rua",
    houseNo: "Nº",
    apartmentFloorEtc: "Apartamento, Andar, etc. (opcional)",
    postalCodePlaceholder: "CEP",
    cityPlaceholder: "Cidade",

    // Nationalities
    nationality_german: "Alemão",
    nationality_austrian: "Austríaco",
    nationality_swiss: "Suíço",
    nationality_french: "Francês",
    nationality_italian: "Italiano",
    nationality_spanish: "Espanhol",
    nationality_british: "Britânico",
    nationality_american: "Americano",
    nationality_canadian: "Canadense",
    nationality_dutch: "Holandês",
    nationality_belgian: "Belga",
    nationality_polish: "Polonês",
    nationality_turkish: "Turco",
    nationality_russian: "Russo",
    nationality_other: "Outro",

    // Profile Update Messages
    profileUpdatedSuccessfully: "Perfil atualizado com sucesso!",
    profileUpdateMaybeSuccessful:
      "O perfil pode ter sido atualizado - por favor verifique as alterações.",
    profileUpdatedButDataNotLoaded:
      "Perfil atualizado, mas os dados não foram carregados corretamente. Por favor recarregue a página.",
    correctFollowingInputs: "Por favor corrija as seguintes entradas:",
    inputError: "Erro de entrada",
    invalidInputs:
      "Entradas inválidas. Por favor verifique todos os campos e tente novamente.",
    unauthorizedPleaseLogin: "Não autorizado. Por favor faça login novamente.",
    noPermission: "Sem permissão para esta ação.",
    userNotFoundPleaseLogin:
      "Usuário não encontrado. Por favor faça login novamente.",
    serverErrorTryLater:
      "Erro do servidor. Por favor tente novamente mais tarde ou entre em contato com o suporte.",
    unknownError: "Erro desconhecido",
    noServerConnection:
      "Não é possível conectar ao servidor. Por favor verifique sua conexão com a Internet e tente novamente.",
    unexpectedError:
      "Ocorreu um erro inesperado. Por favor tente novamente ou entre em contato com o suporte.",

    apply: "Aplicar",
    capture: "Capturar",
    retake: "Refazer",
    usePhoto: "Usar Foto",
    switchCamera: "Trocar Câmera",
    cameraAccessDenied: "Acesso à câmera negado",
    photoCaptureFailed: "Não foi possível usar a foto. Tente novamente.",
    checkCameraPermissions:
      "Por favor, verifique as permissões da câmera nas configurações do navegador",

    // Version History
    version: "Versão",
    versionHistory: "Histórico de versões",
    versionHistoryDescription: "Todas as versões e alterações do HealthPass",
    currentVersion: "Versão atual",
    newFeatures: "Novos recursos",
    changes: "Alterações",
    bugFixes: "Correções de bugs",
    versionHistoryNote:
      "Este histórico de versões é gerado automaticamente a partir de commits do Git e notas de lançamento.",
    back: "Voltar",
    subscription: "Assinatura",

    faqTitle: "Perguntas frequentes",
    faqSubtitle: "Tudo o que você precisa saber sobre o HealthPass",
    faqQ1: "O que é HealthPass?",
    faqA1:
      "HealthPass é uma plataforma de saúde digital que armazena suas informações médicas de emergência e as torna acessíveis em todo o mundo via código QR. Foi projetada especificamente para viajantes que precisam de acesso instantâneo a dados de saúde críticos em emergências.",
    faqQ2: "Como funciona o HealthPass?",
    faqA2:
      "Basta criar uma conta, fazer upload de suas informações médicas (alergias, tipo sanguíneo, medicamentos, contatos de emergência) e gerar seu código QR pessoal. Socorristas e profissionais médicos podem escanear este código para acessar instantaneamente suas informações de saúde vitais, mesmo se você estiver inconsciente.",
    faqQ3: "O HealthPass está em conformidade com o RGPD?",
    faqA3:
      "Sim. O HealthPass foi construído segundo o RGPD: os seus dados circulam encriptados, decide quem vê o quê e, sempre que alguém abre o seu cartão de emergência ou uma ligação que partilhou, isso fica registado e pode vê-lo. Os nossos servidores são operados para nós pela Hetzner nos Estados Unidos, ao abrigo das cláusulas contratuais-tipo da UE — a Política de Privacidade explica exatamente o que isso significa. Nunca partilhamos os seus dados com terceiros sem o seu consentimento explícito.",
    faqQ4: "Quanto custa o HealthPass?",
    faqA4:
      "O seu cartão e o acesso de emergência são gratuitos. O PLUS custa 9 e o PREMIUM 15 por mês, em euros ou dólares americanos consoante o país, e ambos começam com 14 dias de teste gratuito. Para uma única viagem, o PLUS custa 25 e o PREMIUM 39, pagos uma vez por 90 dias. Os socorristas nunca pagam para ler os seus dados de emergência.",
    faqQ5: "O HealthPass pode salvar vidas?",
    faqA5:
      "Sim. Em emergências médicas no exterior, barreiras linguísticas e falta de histórico médico podem ser fatais. O HealthPass fornece acesso instantâneo a informações críticas como alergias, tipo sanguíneo e medicamentos atuais, permitindo decisões de tratamento mais rápidas e seguras.",
    faqQ6: "Como funciona o acesso por código QR?",
    faqA6:
      "Seu código QR HealthPass pode ser impresso, adicionado à tela de bloqueio do seu telefone ou usado como pulseira. Ao escaneá-lo, abre uma visualização de emergência segura com suas informações de saúde críticas. Nenhuma instalação de aplicativo é necessária para a equipe médica.",
    faqQ7: "Quais são os casos de uso comuns de viagem?",
    faqA7:
      "O HealthPass é essencial para:\n• Viajantes aventureiros visitando áreas remotas\n• Idosos viajando internacionalmente\n• Pessoas com condições crônicas ou alergias\n• Famílias viajando com crianças\n• Viajantes solo em países não lusófonos\n• Qualquer pessoa que necessite de medicamentos regulares",
    faqQ8: "Quão seguros estão meus dados?",
    faqA8:
      "Os seus dados são cifrados em trânsito e em repouso, a autenticação passa por um servidor de identidade próprio com tokens de curta duração, e cada consulta de um registo de saúde fica registada — pode pedir-nos esse registo. Os nossos servidores são operados para nós pela Hetzner nos Estados Unidos, ao abrigo das cláusulas contratuais-tipo da UE. A página de Segurança explica tudo isto.",
    faqQ9: "Quais idiomas são suportados?",
    faqA9:
      "O HealthPass está disponível em inglês, alemão, francês, espanhol e português. Suas informações de saúde de emergência podem ser exibidas em vários idiomas simultaneamente, garantindo que a equipe médica em todo o mundo possa lê-las.",
    faqQ10: "Como os serviços de emergência acessam meus dados?",
    faqA10:
      "Os serviços de emergência escaneiam seu código QR com qualquer câmera de smartphone. Eles veem instantaneamente suas informações críticas sem precisar fazer login ou baixar um aplicativo. O acesso é registrado para sua segurança e você pode revogar códigos de acesso de emergência a qualquer momento.",
    faqStillHaveQuestions: "Ainda tem perguntas?",
    faqContactUs:
      "Nossa equipe de suporte está aqui para ajudá-lo 24 horas por dia, 7 dias por semana",
    contactSupport: "Contatar suporte",

    // Medical Incidents & Intolerances
    medicalIncidents: "Incidentes médicos",
    addNewIncident: "Adicionar novo incidente",
    icdCodeOptional: "Código ICD (opcional)",
    diagnosisDesignation: "Diagnóstico/Designação",
    diagnosisPlaceholder: "p. ex. Infarto, fratura, cirurgia...",
    incidentDetailsPlaceholder: "Mais detalhes sobre o incidente...",
    addIncident: "Adicionar incidente",
    noMedicalIncidentsDocumented: "Nenhum incidente médico documentado",
    intolerances: "Intolerâncias",
    foodIntolerances: "Intolerâncias alimentares",
    foodIntolerancesPlaceholder: "p. ex. Lactose, Glúten",
    specificDiseases: "Doenças específicas",
    coagulationDisorder: "Distúrbio de coagulação",
    heartAttack: "Infarto",
    heartAttackDatePlaceholder: "Data do infarto",
    stroke: "AVC",
    strokeDatePlaceholder: "Data do AVC",

    // Extended Health Components
    allergiesAndIntolerances: "Alergias e Intolerancias",
    noAllergiesDocumented: "Nenhuma alergia documentada",
    medicalConditions: "Condições Médicas",
    noMedicalConditionsDocumented: "Nenhuma condição médica documentada",
    medicalProcedures: "Procedimentos Médicos",
    noMedicalProceduresDocumented: "Nenhum procedimento médico documentado",
    treatmentHistory: "Histórico de Tratamentos",
    noTreatmentHistoryDocumented: "Nenhum histórico de tratamento documentado",

    // Vital Signs
    allFieldsRequired: "Todos os campos são obrigatórios",
    systolicMustBeHigher: "A pressão sistólica deve ser maior que a diastólica",
    recordFirstMeasurement: "Registrar primeira medição",
    loadingHistory: "Carregando histórico...",
    noHistoryData: "Nenhum dado de histórico disponível",
    firstMeasurementsWillAppear: "As primeiras medições aparecerão aqui",

    // Unit Inputs
    enterValue: "Inserir valor",

    // Gender
    gender: "Gênero",
    male: "Masculino",
    female: "Feminino",
    preferNotToSay: "Prefiro não informar",

    // Emergency System
    // Emergency System
    emergencyQRDescription:
      "Compartilhe seus dados médicos com serviços de emergência",
    noActiveQRCode: "Sem código QR ativo",
    generateQRCodeDescription:
      "Gere um código QR para compartilhar seus dados de emergência",
    qrCodeDuration: "Duração do código QR",
    generateQRCode: "Gerar código QR",
    regenerate: "Regenerar",
    generating: "Gerando...",
    qrCodeGenerated: "Código QR gerado com sucesso",
    qrCodeGenerationFailed: "Erro ao gerar código QR",
    timeRemaining: "Tempo restante",
    accessCount: "Contador de acessos",
    accessUrl: "URL de acesso",
    urlCopied: "URL copiada para a área de transferência",
    copyFailed: "Erro ao copiar",
    copied: "Copiado",
    copy: "Copiar",
    downloadQR: "Baixar código QR",
    qrCodeDownloaded: "Código QR baixado",
    shareEmergencyData: "Compartilhar dados de emergência",
    shareDialogDescription:
      "Escolha como compartilhar seus dados de emergência",
    expired: "Expirado",
    pleaseRegenerate: "Por favor regenere",
    howItWorks: "Como funciona",
    emergencyQRHowItWorksText:
      "Serviços de emergência escaneiam este código QR com um smartphone",
    share: "Compartilhar",
    emergencyDataAccess: "Acesso a dados de emergência",
    forEmergencyRespondersOnly: "Apenas para serviços de emergência",
    scanQROrEnterToken: "Escaneie o código QR ou insira o token",
    qrScannerPlaceholder: "Scanner QR",
    qrScannerInstructions: "Aponte a câmera para o código QR",
    orEnterTokenManually: "Ou insira o token manualmente",
    enterAccessToken: "Inserir token de acesso",
    continue: "Continuar",
    responderInformation: "Informações do socorrista",
    responderInformationDescription: "Por favor forneça suas informações",
    fullName: "Nome completo",
    enterYourFullName: "Insira seu nome completo",
    organization: "Organização",
    hospitalOrganizationName: "Hospital ou organização",
    professionalId: "ID profissional",
    licenseBadgeNumber: "Número de licença ou identificação",
    yourEmail: "Seu endereço de e-mail",
    yourPhone: "Seu número de telefone",
    reasonForAccess: "Motivo do acesso",
    briefDescriptionEmergency: "Breve descrição",
    emergencyAccessConsent: "Consentimento de acesso de emergência",
    emergencyAccessConsentText:
      "Confirmo que estou acessando estes dados em uma situação de emergência",
    pleaseAcceptConsent: "Por favor aceite o consentimento",
    accessEmergencyData: "Acessar dados de emergência",
    accessing: "Acessando...",
    emergencyDataAccessGranted: "Acesso a dados de emergência concedido",
    pleaseEnterToken: "Por favor insira um token de acesso",
    pleaseProvideResponderInfo: "Por favor forneça suas informações",
    accessTokenInvalid: "Token de acesso inválido ou expirado",
    criticalAllergies: "ALERGIAS CRÍTICAS",
    emergencyContacts: "Contatos de emergência",
    noEmergencyContactsAvailable: "Sem contatos de emergência disponíveis",
    bloodType: "Tipo sanguíneo",
    age: "Idade",
    name: "Nome",
    unknown: "Desconhecido",
    disclaimer: "Isenção de responsabilidade",
    emergencyDataDisclaimerText:
      "Essas informações são fornecidas pelo paciente",

    // PWA & Offline
    installApp: "Instalar HealthPass",
    installAppDescription:
      "Instale o HealthPass para acesso rápido e uso offline",
    install: "Instalar",
    later: "Mais tarde",
    connectionRestored: "Conexão restaurada",
    connectionRestoredSyncing: "Conexão restaurada - Sincronizando dados",
    offlineMode: "Modo offline",
    offlineModeDescription:
      "Modo offline - Dados já carregados estão disponíveis",
    slowConnection: "Conexão lenta",
    slowConnectionDescription: "Conexão lenta - Algumas funções podem atrasar",

    // Sync Status
    never: "Nunca",
    justNow: "Agora mesmo",
    pendingSync: "pendente",
    lastSync: "Última sincronização",

    // Trip Wizard
    tripWizardTitle: "Planejar nova viagem",
    tripWizardStep1: "Básico",
    tripWizardStep2: "Detalhes",
    tripWizardStep3: "Etapas",
    tripWizardStep4: "Saúde",
    tripWizardStep5: "Revisar",
    tripWizardNext: "Próximo",
    tripWizardPrevious: "Voltar",
    tripWizardSave: "Criar viagem",
    tripWizardSaving: "Criando...",
    tripWizardStep1Title: "Informações básicas",
    tripWizardStep2Title: "Detalhes da viagem",
    tripWizardStep3Title: "Etapas / Segmentos",
    tripWizardStep4Title: "Verificação de saúde",
    tripWizardStep5Title: "Revisar e salvar",
    tripWizardStep1Desc: "Título, destino e datas",
    tripWizardStep2Desc: "Propósito, tipo, operador e acompanhantes",
    tripWizardStep3Desc: "Adicionar voos, hotéis e atividades",
    tripWizardStep4Desc: "Riscos de saúde e vacinas necessárias",
    tripWizardStep5Desc: "Revise tudo antes de criar",
    tripWizardReviewSummary: "Resumo da viagem",
    tripWizardDuration: "Duração",
    tripWizardSegmentCount: "Etapas",
    tripWizardAddSegment: "Adicionar etapa",
    tripWizardSkipSegments: "Pular – adicionar mais tarde",
    tripHealthOptional:
      "Esta etapa é opcional. Você pode continuar planejando e salvar sua viagem sem ela.",
    tripHealthUpsellTitle: "Mais confiança para uma viagem saudável",
    tripHealthUpsellIntro:
      "A verificação de saúde do destino faz parte do PLUS. Com ela, você fica mais bem preparado para {country}:",
    tripHealthBenefitVaccinations:
      "Vacinas obrigatórias e recomendadas para o seu destino",
    tripHealthBenefitWarnings:
      "Risco de malária e alertas de saúde atuais para o seu destino",
    tripHealthBenefitTracking: "Acompanhamento das suas próprias vacinas",
    tripHealthBenefitDocuments:
      "Armazenamento de documentos para certificados e papéis de viagem",
    tripHealthBenefitOffline:
      "Acesso offline aos seus dados de saúde durante a viagem",
    tripHealthSingleTrip:
      "Vai planejar só esta viagem? O PLUS Single Trip é um pagamento único que cobre 90 dias, sem assinatura depois.",
    tripHealthUpgradeCta: "Fazer upgrade para PLUS",
    tripHealthSkipCta: "Continuar sem verificação de saúde",
    tripHealthDraftKept:
      "O rascunho da sua viagem fica guardado. Volte a «Planejar nova viagem» para continuar de onde parou.",
    tripHealthEmpty:
      "Nossas fontes não indicam vacinas nem alertas de saúde para {country}.",
    tripHealthLoadFailed:
      "Não foi possível carregar a verificação de saúde agora. Você pode continuar e verificar mais tarde na página da viagem.",
    tripTypeHiking: "Caminhada",
    tripTypeCamping: "Acampamento",
    tripTypeRoadTrip: "Viagem de carro",
    purposeScience: "Ciência",
    inviteCompanion: "Convidar acompanhante",
    companionEmail: "E-mail do acompanhante",
    companionEmailPlaceholder: "email@exemplo.com",
    sendInvitation: "Enviar convite",
    companionInviteSent: "Convite enviado com sucesso",
    companionInviteFailed: "Falha ao enviar convite",
    companionStatusPending: "Pendente",
    companionStatusAccepted: "Aceito",
    companionStatusDeclined: "Recusado",
    removeCompanion: "Remover",
    companionRemoved: "Acompanhante removido",
    noCompanionsYet: "Nenhum acompanhante convidado ainda",
    companionInviteDesc:
      "Convide outros usuários do HealthPass para esta viagem",
    companionRequiresPLUS: "Convidar acompanhantes requer uma assinatura PLUS",
    segmentDocuments: "Documentos",
    uploadSegmentDocument: "Enviar documento",
    docCategoryTicket: "Bilhete",
    docCategoryBooking: "Confirmação de reserva",
    docCategoryVoucher: "Voucher",
    docCategoryInsurance: "Seguro",
    docCategoryOther: "Outro",
    documentUploaded: "Documento enviado com sucesso",
    documentDeleted: "Documento excluído",
    documentUploadFailed: "Falha no envio",
    maxFileSizeHint: "Máx. 10 MB · PDF, JPEG, PNG",
    tripMap: "Mapa da viagem",
    tripMapLoading: "Carregando mapa...",
    tripMapNoData: "Nenhuma coordenada de etapa disponível",
    tripMapRequiresPREMIUM: "O mapa interativo requer uma assinatura PREMIUM",
    featureRequiresPLUS: "Este recurso requer uma assinatura PLUS",
    featureRequiresPREMIUM: "Este recurso requer uma assinatura PREMIUM",
    upgradeToUnlock: "Atualizar para desbloquear",
    upgradePlanLabel: "Atualizar plano",
    statusAutomatic: "Automático",
    statusManual: "Manual",
    statusOverrideHint:
      "O status é definido automaticamente com base nas datas. Você pode substituí-lo manualmente.",
    markAsManual: "Definir manualmente",

    // HealthPass Card
    healthpassCard: "Cartão HealthPass",
    cardWallet: "Carteira",
    cardNumber: "Número do cartão",
    validUntil: "Válido até",
    showFront: "Frente",
    showBack: "Ver carteira",
    downloadPDF: "Baixar PDF",
    shareCard: "Compartilhar cartão",
    generateQR: "Gerar QR Code",
    cardShares: "Links compartilhados",
    createShareLink: "Criar link",
    shareExpiry: "Link expira em",
    shareExpiry24h: "24 horas",
    shareExpiry72h: "72 horas",
    shareExpiry7d: "7 dias",
    sharePassword: "Senha (opcional)",
    revokeShare: "Revogar acesso",
    shareRevoked: "Link revogado",
    shareCreated: "Link criado",
    shareCopied: "Link copiado",
    noSharesYet: "Sem links ativos",
    shareAccessCount: "Acessado",
    shareTimes: "vezes",
    walletEmergency: "Registro de emergência",
    walletMedications: "Plano de medicação",
    walletVaccinations: "Caderneta de vacinação",
    walletCheckin: "Documentos de embarque",
    walletAppointments: "Consultas",
    walletInsurance: "Seguros",
    walletTravelDocs: "Documentos de viagem",
    walletLabResults: "Resultados de laboratório",
    upgradeForWalletItem: "Atualize para {tier} para desbloquear",
    cardLoading: "Carregando cartão...",
    cardError: "Não foi possível carregar o cartão",
    printPreview: "Pré-visualização de impressão",
    printFormatA4: "A4",
    printFormatA5: "A5",
    printFormatLetter: "US Letter",
    printFormatCard: "Tamanho cartão de crédito",
    // Traveller navigation (UX_CONCEPT.md §4)
    navHome: "Início",
    navTrips: "Viagens",
    navCardWallet: "Cartão e carteira",
    navCard: "Cartão",
    navHealth: "Saúde",
    navMe: "Conta",
    navWallet: "Carteira",
    navShares: "Links partilhados",
    navAccessRequests: "Pedidos de acesso",
    navCheckIn: "Check-in",
    navConsentRequests: "Pedidos de consentimento",
    navAssistant: "Assistente",
    navPlan: "Plano",
    navAccount: "Conta",
    navSubscriptions: "Assinaturas",
    navPayment: "Pagamento",
    navInvoices: "Faturas",
    navCompliance: "Conformidade",
    navNotifications: "Notificações",
    complianceDescription:
      "Os seus dados e os seus direitos: o que está guardado, quem o consultou, o que aceitou, uma cópia de tudo e a eliminação da sua conta.",
    complianceStatistics: "Estatísticas",
    complianceLog: "Registo",
    complianceLogLead:
      "Cada acesso aos seus dados de emergência, por quem e quando.",
    complianceConsents: "Consentimentos",
    complianceExport: "Exportar",
    complianceDeleteAccount: "Eliminar conta",
    statsMemberSince: "Membro desde",
    statsTrips: "Viagens",
    statsMedications: "Medicamentos",
    statsImmunizations: "Vacinas",
    statsUnavailable: "Indisponível de momento",
    settingsNotificationsDescription:
      "Escolha sobre o que o HealthPass o informa e como.",
    navCollapseMenu: "Recolher menu",
    navExpandMenu: "Expandir menu",
    navSectionPages: "Páginas desta secção",

    // Home (UX_CONCEPT.md §5.1)
    homeGoodMorning: "Bom dia",
    homeGoodAfternoon: "Boa tarde",
    homeGoodEvening: "Boa noite",
    homeBetaNotice:
      "O HealthPass está em beta e a lançar os seus serviços aos poucos. Manteremos você informado.",
    homeDismiss: "Fechar",
    homeNextTrip: "Próxima viagem",
    homeInDays: "em {n} dias",
    homeToday: "hoje",
    homeTomorrow: "amanhã",
    homeDepart: "Partida",
    homeReturn: "Regresso",
    homeNights: "{n} noites",
    homeCheckTrip: "Ver o que esta viagem precisa",
    homeMoreTrips: "{n} outras viagens planeadas",
    homeNoTripTitle: "Para onde a seguir?",
    homeNoTripBody: "Planeie uma viagem e o HealthPass descobre o que precisa.",
    homePlanTrip: "Planear uma viagem",
    homeYourCard: "O seu HealthPass",
    homeShowWallet: "Mostrar a carteira",
    homeShowFront: "Mostrar a frente",
    homeCardHintFront:
      "Mostre a frente no check-in. Toque no cartão para ver a sua carteira.",
    homeCardHintBack:
      "A sua carteira: tudo o que o cartão leva. Toque para virar.",
    homeNextStep: "Próximo passo",
    homeNotNow: "Agora não",
    stepEmergencyTitle: "Adicione o seu tipo sanguíneo",
    stepEmergencyBody:
      "Fica no seu cartão, para que os socorristas o leiam em qualquer parte do mundo.",
    stepEmergencyAction: "Completar perfil de emergência",
    stepBpTitle: "Volte a medir a sua tensão",
    stepBpBodyAbnormal:
      "A sua última medição, {value}, foi {status}. Uma nova demora dois minutos.",
    stepBpBodyStale:
      "A sua última medição, {value}, foi feita {age}. Uma nova demora dois minutos.",
    stepBpAction: "Registar uma medição",
    stepTripTitle: "Prepare-se para {destination}",
    stepTripBody:
      "Veja que vacinas, medicação e documentos esta viagem precisa.",
    stepTripAction: "Abrir a lista",
    stepPlanTitle: "Planeie a sua próxima viagem",
    stepPlanBody:
      "Diga-nos onde e quando, e verificamos o que precisa antes de partir.",
    homeAtAGlance: "Num relance",
    homeAllHealthData: "Todos os dados de saúde",
    homeNoVitals: "Ainda sem medições. Adicione uma e ela aparece aqui.",
    homeVitalsError: "Não foi possível carregar as suas medições.",
    homeRetry: "Tentar novamente",
    ageToday: "hoje",
    ageDays: "há {n} d",
    ageMonths: "há {n} meses",
    ageYears: "há {n} anos",
    ageUnknown: "data desconhecida",
    vitalLow: "Baixo",
    vitalElevated: "Elevado",
    vitalHigh: "Alto",
    vitalUnderweight: "Baixo peso",
    vitalOverweight: "Excesso de peso",
    vitalObese: "Obesidade",
    vitalRecorded: "Registado",
    weightLabel: "Peso",
    bmiLabel: "IMC",
    stepBpBodyUndated:
      "A sua última medição, {value}, não tem data. Uma nova demora dois minutos.",
    homeDayUnit: "dia",
    homeDaysUnit: "dias",

    // Trip readiness (UX_CONCEPT.md §6.1)
    readinessCaption: "pronto a viajar",
    readinessAllDone: "Pronto a viajar!",
    readinessStepsLeft: "passos em falta",
    readinessSaveFailed:
      "Não foi possível guardar a sua lista, por isso as marcas não ficam guardadas. Recarregue para tentar de novo.",
    readinessNotForMe: "Não se aplica",
    readinessCountAgain: "Contar de novo",
    readinessNotChecked: "Ainda não verificado",
    homeRemindTonight: "Lembrar-me esta noite",
    homeReminderSet: "Lembrete às {time}",
    homeReminderFailed: "Falhou — tentar de novo",
    // Passport stamps and levels (UX_CONCEPT.md §7)
    passportTitle: "O seu passaporte",
    passportStamps: "{count} carimbos",
    passportOneStamp: "1 carimbo",
    passportAt100: "a 100 %",
    passportNextStamp: "Prepare-se para {city} e o carimbo é seu.",
    passportOneMore: "Mais um e chega a {level}.",
    passportFirst:
      "Leve uma viagem a 100 % antes de partir e o carimbo aparece aqui.",
    passportToNext: "Mais {count} para {level}.",
    passportTop: "Nível máximo alcançado. Boa viagem.",
    passportFailed: "Não foi possível carregar o seu passaporte.",
    passportOpenTrip: "Abrir a lista de {city}",
    levelStarter: "Iniciante",
    levelExplorer: "Explorador",
    levelGlobetrotter: "Globetrotter",
    levelNomad: "Nómada",
    stampEarned: "Carimbo conquistado",
    stampEarnedBody: "Já está no seu passaporte. Tenha uma ótima viagem!",
    stampEarnedLevelUp: "Agora é {level}. Tenha uma ótima viagem!",
    // First run (UX_CONCEPT.md §5.5)
    frStepOf: "Passo {n} de 3",
    frTitleYou: "Vamos criar o seu cartão.",
    frLeadYou: "Cada resposta aparece logo no seu cartão.",
    frTitleBasics: "O seu cartão está a ganhar forma.",
    frLeadBasics:
      "Mais algumas respostas e os socorristas podem ajudá-lo em qualquer parte do mundo.",
    frTitleTrip: "O seu cartão está pronto.",
    frLeadTrip:
      "Digitalize-o, mostre-o, partilhe-o. E agora: para onde vai a seguir?",
    frTitleDone: "A sua primeira viagem está planeada.",
    frLeadDone:
      "Começámos a lista do que {city} exige. Complete-a e ganhe o seu primeiro carimbo.",
    frStepYou: "Você no cartão",
    frStepYouHint: "Nome, foto, nacionalidade",
    frStepBasics: "O essencial para emergências",
    frStepBasicsHint: "Tipo sanguíneo, alergias, medicação, um contacto",
    frStepTrip: "A sua próxima viagem",
    frStepTripHint: "Para onde e quando — verificamos o que precisa",
    frYourSteps: "Os seus três passos",
    frDone: "Concluído",
    frMinLeft: "faltam {n} min",
    frQrAfter: "QR após o passo 2",
    frBasicsLabel: "EMERGÊNCIA · {n} DE 4",
    frYouQ: "Para quem é este cartão?",
    frFirstName: "Nome próprio",
    frLastName: "Apelido",
    frDateOfBirth: "Data de nascimento",
    frNationality: "Nacionalidade",
    frNationalityPlaceholder: "p. ex. portuguesa",
    frAddPhoto: "Adicionar foto",
    frChangePhoto: "Mudar foto",
    frPhotoFailed: "Não foi possível carregar a foto. Experimente outra.",
    frPutOnCard: "Pôr no meu cartão",
    frAddToCard: "Adicionar ao meu cartão",
    frBloodQ: "Qual é o seu tipo sanguíneo?",
    frBloodHint: "É a primeira coisa que um hospital verifica.",
    frBloodUnknown: "Não sei",
    frBloodChip: "Tipo {type}",
    frBloodChipEmpty: "Tipo sanguíneo?",
    frAllergyQ: "Tem alergia a alguma coisa?",
    frAllergyHint:
      "Mostrado em inglês a quem digitalizar o seu cartão numa emergência.",
    frAlreadyOnCard: "Já no seu cartão",
    frSomethingElse: "Outra coisa",
    frAllergyPlaceholder: "p. ex. látex, picadas de abelha",
    frNoAllergies: "Sem alergias",
    frNothingToAdd: "Nada a acrescentar",
    frAllergyChips: "{n} alergias",
    frAllergyChipOne: "1 alergia",
    frAllergyChipNone: "Sem alergias",
    frAllergyChipEmpty: "Alergias?",
    frMedsQ: "Toma medicação regularmente?",
    frMedsHint:
      "Os socorristas precisam de saber antes de lhe darem alguma coisa.",
    frMedsPlaceholder: "p. ex. Metformina 500 mg",
    frAdd: "Adicionar",
    frRemove: "Remover",
    frNoMeds: "Nenhuma",
    frContactQ: "A quem devemos ligar?",
    frContactHint:
      "O seu contacto de emergência, visível para quem digitalizar o seu cartão.",
    frContactName: "Nome",
    frContactPhone: "Telefone, com indicativo",
    frContactPhoneInvalid:
      "Introduza um número em algarismos, p. ex. +351 912 345 678.",
    frRelationship: "Relação",
    frRelPartner: "Companheiro/a",
    frRelParent: "Pai ou mãe",
    frRelChild: "Filho/a",
    frRelSibling: "Irmão/irmã",
    frRelFriend: "Amigo/a",
    frSkip: "Mais tarde",
    frTripQ: "Para onde vai a seguir?",
    frTripCity: "Cidade ou local",
    frTripCountry: "País",
    frTripDeparture: "Partida",
    frTripReturn: "Regresso",
    frTripPlan: "Planear esta viagem",
    frTripLater: "Ainda sem viagem",
    frOpenChecklist: "Ver o que {city} exige",
    frGoHome: "Ir para o início",
    frSaveFailed: "Não foi guardado. Verifique a ligação e tente novamente.",
    frPrivacy:
      "Os seus dados de saúde estão encriptados e são seus. Decide quem os vê e vê cada acesso.",
    // Card page (UX_CONCEPT.md §5.2)
    cardSideFront: "Frente",
    cardSideWallet: "Lado da carteira",
    cardDownload: "Descarregar o cartão",
    cardDownloadFailed:
      "Não foi possível descarregar o cartão. Tente novamente.",
    cardOfflineCodeShow: "Sem rede? Mostrar o código offline",
    cardOnlineCodeShow: "Mostrar o código online",
    cardOfflineCodeHint:
      "Este código contém o seu resumo de emergência e funciona sem internet.",
    // Shared card page
    sharedCardTitle: "Um cartão HealthPass partilhado",
    sharedCardLead:
      "O titular partilhou este cartão consigo. Mostra quem é, não os seus dados de saúde.",
    sharedCardNoCode: "Cartão partilhado",
    sharedCardPasswordLead:
      "O titular protegeu este cartão com uma palavra-passe.",
    sharedCardPassword: "Palavra-passe",
    sharedCardOpen: "Abrir o cartão",
    sharedCardWrongPassword: "Essa palavra-passe não está correta.",
    sharedCardGone: "Esta ligação expirou ou foi retirada pelo titular.",
    sharedCardMissing:
      "Esta ligação não existe. Verifique se foi copiada por inteiro.",
    sharedCardError:
      "Não foi possível carregar o cartão. Tente de novo dentro de momentos.",
    settingsExportLead:
      "Descarregue tudo o que o HealthPass guarda sobre si, num ficheiro para guardar ou levar para outro serviço.",
    settingsExportDone: "Os seus dados foram descarregados.",
    settingsExportFailed: "A transferência falhou. Tente novamente.",
    // Payment success page
    payConfirming: "A confirmar o seu pagamento…",
    payConfirmingLine: "Demora alguns segundos.",
    payWelcome: "Bem-vindo ao {plan}",
    payTrialLine:
      "O seu teste gratuito dura até {date}. Depois, o plano renova-se todos os meses, a menos que cancele antes.",
    payTripLine: "O seu plano de viagem dura até {date}.",
    payActiveLine: "O seu plano já está ativo.",
    payPendingTitle: "O seu pagamento está a caminho",
    payPendingLine:
      "Alguns métodos de pagamento demoram mais. O plano é ativado assim que o pagamento chegar, e avisamos.",
    payFailedTitle: "Este pagamento não foi concluído",
    payFailedLine: "Nada foi cobrado. Pode escolher um plano quando quiser.",
    payContinue: "Continuar",
    payBuildCard: "Criar o seu cartão",
    payBackToPlans: "Voltar aos planos",
    renewTermsMonthly:
      "Os novos membros têm 14 dias grátis e depois {price} por mês. Renova-se todos os meses até cancelar, o que pode fazer a qualquer momento nas definições de faturação.",
    renewTermsTrip: "Um pagamento de {price} por 90 dias. Não se renova.",
    // Partner offers (REQ-025)
    marketplaceTitle: "Ofertas de parceiros",
    marketplaceIntro:
      "Ofertas de seguro dos nossos parceiros para esta viagem. A HealthPass não as compara nem recomenda; a seguradora responde a perguntas sobre a cobertura.",
    marketplaceDisclosurePerContract:
      "Oferta de {insurer}. A HealthPass recebe uma comissão se fizer um contrato.",
    marketplaceDisclosurePerVisit:
      "Oferta de {insurer}. A HealthPass recebe uma comissão quando visita {insurer}.",
    marketplaceVisit: "Ir para {insurer}",
    marketplaceSeeAll: "Abrir a página de ofertas de parceiros",
    marketplaceEmpty: "Não há ofertas de parceiros para esta viagem.",
    marketplaceBackToTrip: "Voltar à viagem",
    marketplaceOpenFailed: "Não foi possível abrir a oferta. Tente novamente.",
    marketplaceSettingTitle: "Mostrar ofertas de parceiros",
    marketplaceSettingHint:
      "Ofertas de seguro de parceiros nas suas viagens. São escolhidas apenas pelo destino, datas e país de residência, nunca pelos seus dados de saúde. Desativado, ficam ocultas em todo o lado.",
    marketplaceSettingSaved: "Guardado.",
    marketplaceSettingFailed: "Não foi possível guardar a definição.",
  },
};

const withModules = (language: Language): Translation => ({
  ...baseTranslations[language],
  ...moduleStrings(language),
});

export const translations: Record<Language, Translation> = {
  en: withModules("en"),
  de: withModules("de"),
  fr: withModules("fr"),
  es: withModules("es"),
  pt: withModules("pt"),
};
